import classNames from 'classnames';
import React, { useEffect, useRef, useState, forwardRef, useMemo, Component, useLayoutEffect, useCallback, useReducer, createElement, Children, cloneElement } from 'react';
import { createPortal } from 'react-dom';
import { createDndContext, DndProvider, useDrop, useDrag } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Transition } from 'react-transition-group';
import { usePopper } from 'react-popper';
import { isEqual, indexOf, get, debounce, cloneDeep, isArray, isEmpty } from 'lodash';
import ResizeObserver from 'rc-resize-observer';
import { useRanger } from 'react-ranger';
import { useTable, useSortBy, useFlexLayout, useExpanded, usePagination as usePagination$1, useResizeColumns, useRowSelect, useRowState } from 'react-table';
import { safeInvoke as safeInvoke$1 } from '@blueprintjs/core/lib/esm/common/utils';
import { useSticky } from 'react-table-sticky';
import { Tooltip as Tooltip$1, Legend, Bar, Line, YAxis, XAxis, ResponsiveContainer, BarChart as BarChart$1, CartesianGrid, LineChart as LineChart$1, ComposedChart, Cell, LabelList } from 'recharts';
import DayPicker, { ModifiersUtils, DateUtils } from 'react-day-picker';
import composeRefs from '@seznam/compose-react-refs/composeRefs';
import { Info, DateTime, Interval } from 'luxon';
import { TimePicker } from '@blueprintjs/datetime';
import { SortableTreeWithoutDndContext } from 'react-sortable-tree';
import 'react-sortable-tree/style.css';

// eslint is incorrectly flagging this as an unnecessary class. See line 33, 36
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
var ColorTheme = /** @class */ (function () {
    function ColorTheme() {
    }
    ColorTheme.transparent = function (color, opacity) {
        var rgb = ColorTheme.hexToRgb(color);
        return "rgba(" + rgb.r + ", " + rgb.g + ", " + rgb.b + ", " + opacity + ")";
    };
    ColorTheme.hexToRgb = function (hex) {
        var red, green, blue;
        if (hex.length !== 4 && hex.length !== 7) {
            throw new Error("'" + hex + "' does not appear to be a valid hex color");
        }
        if (!hex.startsWith('#')) {
            throw new Error('This function expects your hex value to start with a #');
        }
        if (hex.length === 4) {
            red = "0x" + hex[1] + hex[1] + "}";
            green = "0x" + hex[2] + hex[2];
            blue = "0x" + hex[3] + hex[3];
        }
        else {
            red = "0x" + hex[1] + hex[2];
            green = "0x" + hex[3] + hex[4];
            blue = "0x" + hex[5] + hex[6];
        }
        return {
            r: Number(red),
            g: Number(green),
            b: Number(blue)
        };
    };
    ColorTheme.black = '#000000';
    ColorTheme.white = '#ffffff';
    ColorTheme.gray80 = '#303030';
    ColorTheme.gray70 = '#474747';
    ColorTheme.gray50 = '#777777';
    ColorTheme.gray30 = '#ababab';
    ColorTheme.gray10 = '#e2e2e2';
    ColorTheme.amobee70 = '#004f5f';
    ColorTheme.amobee50 = '#00849e';
    ColorTheme.amobee40 = '#009ebd';
    ColorTheme.amobee30 = '#40b6ce';
    ColorTheme.amobee10 = '#bfe7ef';
    ColorTheme.amobee05 = '#dff3f7';
    ColorTheme.amobee02 = '#f2fafc';
    ColorTheme.red70 = '#88191a';
    ColorTheme.red60 = '#b42221';
    ColorTheme.red50 = '#e02a2b';
    ColorTheme.red40 = '#e75556';
    ColorTheme.green70 = '#17520e';
    ColorTheme.green50 = '#278917';
    ColorTheme.green40 = '#52a145';
    ColorTheme.green30 = '#7db874';
    ColorTheme.yellow70 = '#574800';
    ColorTheme.yellow40 = '#ad9100';
    ColorTheme.yellow30 = '#caa900';
    ColorTheme.yellow20 = '#dcc655';
    ColorTheme.amobeePrimaryColor = ColorTheme.amobee40;
    ColorTheme.buttons = {
        neutral: ColorTheme.transparent(ColorTheme.black, 0.5),
        toggleMaxWidth: '176px'
    };
    return ColorTheme;
}());

var useDetectOutsideClick = function (ref, callback) {
    var clickIsOutside = function (e) {
        if (Array.isArray(ref)) {
            //const outsideClick = typeof e.composedPath === 'function' &&  !e.composedPath().includes(elem);
            var isOutside = ref.every(function (r) {
                return r &&
                    r.current &&
                    typeof e.composedPath === 'function' &&
                    !e.composedPath().includes(r.current);
            });
            //r.current.contains(e.target as Node)
            return isOutside;
        }
        return (ref &&
            ref.current &&
            typeof e.composedPath === 'function' &&
            !e.composedPath().includes(ref.current));
        //return ref && ref.current && !ref.current.contains(e.target as Node);
    };
    var allRefsExist = function () {
        if (Array.isArray(ref)) {
            return ref.every(function (r) { return !!r.current; });
        }
        return !!ref.current;
    };
    useEffect(function () {
        var clickCallback = function (e) {
            if (clickIsOutside(e)) {
                callback(e.target);
            }
        };
        if (allRefsExist()) {
            window.addEventListener('click', clickCallback);
        }
        return function () { return window.removeEventListener('click', clickCallback); };
    }, [ref]);
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}

var htmlInputAttrs = [
    // REACT
    'selected',
    'defaultValue',
    'defaultChecked',
    // LIMITED HTML PROPS
    'accept',
    'autoCapitalize',
    'autoComplete',
    'autoCorrect',
    'autoFocus',
    'checked',
    'disabled',
    'form',
    'id',
    'lang',
    'list',
    'max',
    'maxLength',
    'min',
    'minLength',
    'multiple',
    'name',
    'pattern',
    'placeholder',
    'readOnly',
    'required',
    'step',
    'title',
    'type',
    'value'
];
var htmlInputEvents = [
    // EVENTS
    // keyboard
    'onKeyDown',
    'onKeyPress',
    'onKeyUp',
    // focus
    'onFocus',
    'onBlur',
    // form
    'onChange',
    'onInput',
    // mouse
    'onClick',
    'onContextMenu',
    'onDrag',
    'onDragEnd',
    'onDragEnter',
    'onDragExit',
    'onDragLeave',
    'onDragOver',
    'onDragStart',
    'onDrop',
    'onMouseDown',
    'onMouseEnter',
    'onMouseLeave',
    'onMouseMove',
    'onMouseOut',
    'onMouseOver',
    'onMouseUp',
    // selection
    'onSelect',
    // touch
    'onTouchCancel',
    'onTouchEnd',
    'onTouchMove',
    'onTouchStart'
];
var htmlInputProps = __spreadArrays(htmlInputAttrs, htmlInputEvents);
/**
 * Returns an array of objects consisting of: props of html input element and rest.
 * @param {object} props A ReactElement props object
 * @returns {inputProps; rest}
 */
var partitionHtmlInputProps = function (props) {
    var inputProps = {};
    var rest = {};
    Object.keys(props).forEach(function (prop) {
        var target = htmlInputProps.indexOf(prop) !== -1 ? inputProps : rest;
        target[prop] = props[prop];
    });
    return { inputProps: inputProps, rest: rest };
};

var partitionedProps = [
    'fluid',
    'compact',
    'disabled',
    'table',
    'style',
    'className'
];
/**
 * Returns an array of objects consisting of: props of FormControl and rest.
 * @param {object} props A ReactElement props object
 * @returns {formControlProps; rest}
 */
var partitionFormControlProps = function (props) {
    var formControlProps = {};
    var rest = {};
    Object.keys(props).forEach(function (prop) {
        var target = partitionedProps.indexOf(prop) !== -1 ? formControlProps : rest;
        target[prop] = props[prop];
    });
    return { formControlProps: formControlProps, rest: rest };
};

var makeCancellable = function (promise) {
    var isCancelled = false;
    var wrappedPromise = new Promise(function (resolve, reject) {
        // @ts-ignore
        return promise.then(function (val) {
            return isCancelled
                ? reject(new Error(JSON.stringify(isCancelled)))
                : resolve(val);
        }
        // @ts-ignore
        );
    });
    return {
        promise: wrappedPromise,
        cancel: function () {
            isCancelled = true;
        }
    };
};
var useCancellablePromise = function (cancellable) {
    if (cancellable === void 0) { cancellable = makeCancellable; }
    var emptyPromise = Promise.resolve(true);
    // test if the input argument is a cancellable promise generator
    if (cancellable(emptyPromise).cancel === undefined) {
        throw new Error('promise wrapper argument must provide a cancel() function');
    }
    var promises = useRef([]);
    useEffect(function () {
        promises.current = promises.current || [];
        return function cancel() {
            promises.current.forEach(function (p) { return p.cancel(); });
            promises.current = [];
        };
    }, []);
    var cancellablePromise = function (p) {
        var cPromise = cancellable(p);
        promises.current.push(cPromise);
        return cPromise.promise;
    };
    return { cancellablePromise: cancellablePromise };
};

var useHandleKeyPress = function (key, callback, ref, defaultCallback, type) {
    if (type === void 0) { type = 'keyup'; }
    if (typeof callback !== 'function') {
        throw new Error('`callback` must be a function');
    }
    var eventType;
    // This is to ensure that we don't create an incorrect event listener when people aren't using TS
    if (type !== 'keyup' && type !== 'keydown') {
        console.warn('Unrecognized value for parameter `type`... defaulting to `keyup`');
        eventType = 'keyup';
    }
    else {
        eventType = type;
    }
    useEffect(function () {
        var onKeyPress = function (e) {
            if (e.key === key) {
                callback();
            }
            else {
                if (typeof defaultCallback === 'function') {
                    defaultCallback();
                }
            }
        };
        if (ref) {
            ref.addEventListener(eventType, onKeyPress);
            return function () { return ref.removeEventListener(eventType, onKeyPress); };
        }
        window.addEventListener(eventType, onKeyPress);
        return function () { return window.removeEventListener(eventType, onKeyPress); };
    });
};

var safeInvoke = function (call, args, error, throwError, consoleType) {
    if (throwError === void 0) { throwError = false; }
    if (consoleType === void 0) { consoleType = 'none'; }
    if (typeof call === 'function') {
        if (Array.isArray(args) && args.length > 0) {
            return call.apply(void 0, args);
        }
        return call();
    }
    if (throwError) {
        throw new Error(error);
    }
    switch (consoleType) {
        case 'none':
            return null;
        case 'warn':
            console.warn(error);
            break;
        case 'error':
            console.error(error);
            break;
        default:
            console.warn(error);
            break;
    }
    return null;
};

var useDetectContentOverflow = function (ref, callback) {
    useEffect(function () {
        if (ref.current) {
            var height = ref.current.getBoundingClientRect().height;
            var scrollHeight = ref.current.scrollHeight;
            if (scrollHeight > Math.round(height)) {
                callback();
            }
        }
    }, [ref.current]);
};

function makeRenderer(instance, original, meta) {
    if (meta === void 0) { meta = {}; }
    return function (type, userProps) {
        if (userProps === void 0) { userProps = {}; }
        var Comp = typeof type === 'string' ? original[type] : type;
        if (typeof Comp === 'undefined') {
            throw new Error('render error');
        }
        return flexRender(Comp, __assign(__assign(__assign(__assign({}, instance), { original: original }), meta), userProps));
    };
}
function flexRender(Comp, props) {
    return isReactComponent(Comp) ? React.createElement(Comp, __assign({}, props)) : Comp;
}
function isReactComponent(component) {
    return (isClassComponent(component) ||
        typeof component === 'function' ||
        isExoticComponent(component));
}
function isClassComponent(component) {
    return (typeof component === 'function' &&
        (function () {
            var proto = Object.getPrototypeOf(component);
            return proto.prototype && proto.prototype.isReactComponent;
        })());
}
function isExoticComponent(component) {
    return (typeof component === 'object' &&
        typeof component.$$typeof === 'symbol' &&
        ['react.memo', 'react.forward_ref'].includes(component.$$typeof.description));
}

/**
 * Returns a boolean of whether or not the supplied variable is a React fragment
 * @param variableToInspect
 */
var isReactFragment = function (variableToInspect) {
    if (variableToInspect.type) {
        return variableToInspect.type === React.Fragment;
    }
    return variableToInspect === React.Fragment;
};

var SvgIconActivityLog = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M2 4V3H1V0H0v16h1v-3h1v-1H1V4h1zm5.677.677a.252.252 0 000-.354l-.927-.927V2a.25.25 0 10-.5 0v1.604l1.073 1.073a.249.249 0 00.354 0zM11 6h5V5h-5v1zm0-2h5V3h-5v1zm0-2h5V1h-5v1zM6.5 0a3.5 3.5 0 100 7 3.5 3.5 0 000-7zm0 1C7.879 1 9 2.122 9 3.5S7.879 6 6.5 6A2.503 2.503 0 014 3.5C4 2.122 5.121 1 6.5 1zm1.177 12.677a.252.252 0 000-.354l-.927-.927V11a.25.25 0 10-.5 0v1.604l1.073 1.073a.249.249 0 00.354 0zM11 15h5v-1h-5v1zm0-2h5v-1h-5v1zm0-2h5v-1h-5v1zM6.5 9a3.5 3.5 0 100 7 3.5 3.5 0 000-7zm0 1C7.879 10 9 11.122 9 12.5S7.879 15 6.5 15A2.503 2.503 0 014 12.5C4 11.122 5.121 10 6.5 10z" }))); };

var SvgIconAdd = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14 0H2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V2a2 2 0 00-2-2m-2 9H9v3a1 1 0 11-2 0V9H4a1 1 0 110-2h3V4a1 1 0 112 0v3h3a1 1 0 110 2" }))); };

var SvgIconArchived = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M10 9H6a1 1 0 110-2h4a1 1 0 110 2zm5-4H1v9a2 2 0 002 2h10a2 2 0 002-2V5zm1-1H0V2a2 2 0 012-2h12a2 2 0 012 2v2z" }))); };

var SvgIconArrowDown = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M9 9h3l-4 5-4-5h3V3a1 1 0 112 0v6z" }))); };

var SvgIconArrowLeft = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M7 9v3L2 8l5-4v3h6a1 1 0 110 2H7z" }))); };

var SvgIconArrowOver = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M13 3l-6.364.707 2.121 2.122-5.464 5.464a.999.999 0 101.414 1.414l5.464-5.464 2.122 2.121L13 3z" }))); };

var SvgIconArrowRight = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M9 7V4l5 4-5 4V9H3a1 1 0 110-2h6z" }))); };

var SvgIconArrowUnder = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M13 13l-6.364-.707 2.121-2.122-5.464-5.464a.999.999 0 111.414-1.414l5.464 5.464 2.122-2.12L13 13z" }))); };

var SvgIconArrowUp = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M7 7H4l4-5 4 5H9v6a1 1 0 11-2 0V7z" }))); };

var SvgIconAssign = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M5 9.296c2.414 1.076 4.524 2.77 6 5.053v-1.432a16.502 16.502 0 00-2.53-2.262A13.425 13.425 0 005 8.855v.44zm0-1.068l6 .272v-1l-6 .273v.455zm0-1.524c2.414-1.075 4.524-2.77 6-5.052v1.43a16.45 16.45 0 01-2.53 2.263A13.46 13.46 0 015 7.145v-.441zM0 10h4V6H0v4zm12-6h4V0h-4v4zm1-1h2V1h-2v2zm-1 7h4V6h-4v4zm1-1h2V7h-2v2zm-1 7h4v-4h-4v4zm1-1h2v-2h-2v2z" }))); };

var SvgIconAttachment = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M15.312 6.827l-7.485 7.486a4.004 4.004 0 01-5.657 0 4.003 4.003 0 010-5.657L9.948.877a3.003 3.003 0 014.243 0 3.003 3.003 0 010 4.244L7.12 12.19c-.78.78-2.049.78-2.829 0a2.002 2.002 0 010-2.828L10.656 3a.5.5 0 01.707.707L4.999 10.07a.999.999 0 101.414 1.415l7.071-7.071a2.004 2.004 0 000-2.83 2.003 2.003 0 00-2.828 0l-7.779 7.78a3.004 3.004 0 000 4.241 3.003 3.003 0 004.243 0l7.485-7.485a.5.5 0 01.707.707" }))); };

var SvgIconAutoFix = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 20 20", fill: "currentColor" }, props),
    React.createElement("path", { d: "M6.192 3.428l-2.38 1.333 1.333-2.38L3.812.001l2.38 1.333L8.572.001l-1.333 2.38 1.333 2.38-2.38-1.333M17.62 12.76L20 11.427l-1.333 2.38L20 16.187l-2.38-1.333-2.38 1.333 1.333-2.38-1.333-2.38 2.38 1.333M20 0l-1.333 2.38L20 4.76l-2.38-1.333-2.38 1.333 1.333-2.38L15.24 0l2.38 1.333L20 0m-8.246 10.265l2.324-2.321-2.019-2.021-2.324 2.321 2.019 2.019m.981-5.228l2.228 2.228a.949.949 0 010 1.343L3.85 19.722a.949.949 0 01-1.343 0L.279 17.493a.949.949 0 010-1.343L11.392 5.037a.949.949 0 011.343 0z" }))); };

var SvgIconBack = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14 7H4.414l3.293-3.293a.999.999 0 10-1.414-1.414l-5 5a.999.999 0 000 1.414l5 5a.997.997 0 001.414 0 .999.999 0 000-1.414L4.414 9H14a1 1 0 100-2" }))); };

var SvgIconBranch = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14 10c-.738 0-1.376.405-1.723 1H11c-.638 0-1.36-.564-1.515-1.183l-1.03-4.119A3.186 3.186 0 008.19 5h4.087A1.994 1.994 0 0016 4a1.994 1.994 0 00-3.723-1H3.723A1.994 1.994 0 000 4a1.994 1.994 0 003.723 1H5c.638 0 1.36.564 1.515 1.183l1.03 4.119C7.923 11.815 9.44 13 11 13h1.277A1.994 1.994 0 0016 12a2 2 0 00-2-2" }))); };

var SvgIconBroadcaster = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8.5 12.5l.7.7-.311-1.089-.389.389zm-1.7.7l.7-.7-.389-.389L6.8 13.2zM8 9l-.667 2.333L8 12l.667-.667L8 9zm1-3a.988.988 0 01-.717.943L11 16h-1l-.4-1.4L8 13l-1.6 1.6L6 16H5l2.717-9.057A.988.988 0 017 6a1 1 0 112 0zM8 0a5.996 5.996 0 012.618 11.393l-.298-.991C11.905 9.563 13 7.915 13 6c0-2.757-2.243-5-5-5S3 3.243 3 6c0 1.915 1.095 3.563 2.68 4.402l-.298.991A5.996 5.996 0 018 0zm1.732 8.44A2.994 2.994 0 0011 6c0-1.655-1.346-3-3-3-1.655 0-3 1.345-3 3 0 1.007.503 1.896 1.267 2.44l-.297.99A3.983 3.983 0 014 6a4 4 0 018 0 3.984 3.984 0 01-1.97 3.43l-.298-.99z" }))); };

var SvgIconBudget = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8.613 6.826c-.83-.25-1.457-.518-1.884-.805-.427-.286-.64-.64-.64-1.065 0-.454.182-.819.546-1.097.364-.277.874-.416 1.531-.416.68 0 1.208.166 1.585.496.375.332.563.796.563 1.393H13c0-.78-.2-1.48-.605-2.095-.402-.614-.968-1.09-1.696-1.428A5.544 5.544 0 009 1.351V.001H7v1.404a5.814 5.814 0 00-1.29.364c-.745.311-1.315.742-1.712 1.293a3.149 3.149 0 00-.595 1.885c0 1.356.74 2.433 2.22 3.232.544.293 1.28.59 2.211.891.931.301 1.576.588 1.933.86.36.271.537.66.537 1.167 0 .465-.178.83-.537 1.088-.357.26-.856.39-1.494.39-1.719 0-2.578-.72-2.578-2.158H3c0 .842.217 1.582.65 2.22.433.638 1.06 1.142 1.884 1.508.46.205.95.347 1.466.437V16h2v-1.347c1.102-.093 2.017-.39 2.72-.92.853-.64 1.28-1.526 1.28-2.653 0-1.015-.349-1.862-1.048-2.543-.697-.68-1.81-1.25-3.339-1.71" }))); };

var SvgIconBullet = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("circle", { cx: 8, cy: 8, r: 4 }))); };

var SvgIconChannelAmobeeSocial = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14.095 9.93l-.832-2.08.526-.466c.553-.49 1.21-1.302 1.21-2.384 0-2.168-2.747-4-6-4-3.251 0-6 1.832-6 4s2.749 4 6 4a8.47 8.47 0 002.139-.273l.363-.094.336.168 2.258 1.129zm.358-1.798L16 12l-4.61-2.305A9.504 9.504 0 019 10c-3.866 0-7-2.238-7-5 0-2.761 3.134-5 7-5s7 2.239 7 5c0 1.187-.581 2.275-1.547 3.132zm-3.184 2.62l.722.362C11.9 13.27 9.256 15 6 15a8.375 8.375 0 01-2.956-.522L0 16l1.272-2.543C.478 12.779 0 11.927 0 11c0-1.205.804-2.283 2.069-3.017C3.45 9.783 6.037 11 8.999 11c.764 0 1.525-.083 2.27-.248z" }))); };

var SvgIconChannelAudio = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M4 5.037H1a1 1 0 00-1 1v4a1 1 0 001 1h3l3 3v-12l-3 3zm7.408-2.408l-.71.71C11.505 4.581 12 6.226 12 8.037c0 1.811-.495 3.456-1.302 4.698l.71.71C12.393 12.019 13 10.123 13 8.037c0-2.086-.607-3.982-1.592-5.408zM9 8.037c0 .968-.179 1.844-.465 2.535l.747.747c.446-.868.718-1.996.718-3.282s-.272-2.414-.718-3.282l-.747.747C8.821 6.193 9 7.069 9 8.037zm7 0c0 2.918-.94 5.566-2.463 7.537l-.706-.706C14.173 13.08 15 10.682 15 8.037c0-2.645-.827-5.043-2.169-6.831L13.537.5C15.06 2.471 16 5.119 16 8.037z" }))); };

var SvgIconChannelBanner = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M2.5 13.5a1 1 0 112 0 1 1 0 01-2 0zM2 12h3V6H2v6zm4-8a1 1 0 011 1v10a1 1 0 01-1 1H1a1 1 0 01-1-1V5a1 1 0 011-1h5zM1 15h5V5H1v10zm7-9h3V5H8v1zm7-6H6a1 1 0 00-1 1v2h1V1h9v12H8v1h7a1 1 0 001-1V1a1 1 0 00-1-1zM8 8h3V7H8v1zm4 4h2V5h-2v7zm2-10H7v1.277c.3.175.548.423.723.723H14V2zm-6 8h3V9H8v1zm0 2h3v-1H8v1z" }))); };

var SvgIconChannelDisplay = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M4 14h4v-1H4v1zm0-4h4V9H4v1zm-1 5h10V1H3v14zM14 1v14a1 1 0 01-1 1H3a1 1 0 01-1-1V1a1 1 0 011-1h10a1 1 0 011 1zM4 12h4v-1H4v1zm5 2h3V5H9v9zM4 4h8V2H4v2zm0 4h4V7H4v1zm0-2h4V5H4v1z" }))); };

var SvgIconChannelSocial = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M11.96 10.46c-1.834-.7-2.422-1.289-2.422-2.55 0-.758.62-.615.868-2.003.102-.575.563.002.657-1.314 0-.522-.438-.436-.438-.436s.166-.936.219-1.531C10.91 1.883 10.548 0 8 0S5.089 1.883 5.156 2.626c.053.595.219 1.53.219 1.53s-.437-.085-.437.438c.093 1.314.554.737.656 1.313.247 1.388.868 1.245.868 2.003 0 1.261-.588 1.85-2.423 2.55-1.843.7-3.04 1.19-3.04 2.665V16h14v-2.874c0-1.476-1.195-1.965-3.038-2.666M0 10.624v2.5c0-2.198 1.873-2.912 3.683-3.6.219-.084.41-.162.579-.238-.44-.23-.603-.476-.603-.898 0-.324.266-.262.372-.857.044-.247.242 0 .282-.564 0-.223-.188-.186-.188-.186s.07-.402.094-.657C4.248 5.806 4.092 5 2.999 5c-1.091 0-1.246.806-1.218 1.125.023.255.094.657.094.657s-.187-.037-.187.186c.04.564.237.317.28.564.107.595.373.533.373.857 0 .542-.252.795-1.04 1.094-.788.3-1.301.51-1.301 1.143m16 0v2.5c0-2.2-1.872-2.912-3.683-3.601a10.912 10.912 0 01-.58-.238c.44-.23.604-.476.604-.898 0-.324-.267-.262-.372-.857-.044-.247-.241 0-.281-.564 0-.223.187-.186.187-.186s-.071-.402-.094-.657C11.75 5.806 11.908 5 13 5s1.248.806 1.219 1.125a10.01 10.01 0 01-.094.657s.188-.037.188.186c-.04.564-.238.317-.282.564-.105.595-.372.533-.372.857 0 .542.252.795 1.038 1.094.79.3 1.303.51 1.303 1.143" }))); };

var SvgIconChannelVideo = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M12 15.75v-2.5a.25.25 0 00-.25-.25h-1.5a.25.25 0 00-.25.25v2.5c0 .138.112.25.25.25h1.5a.25.25 0 00.25-.25zm.5-.75h2a.5.5 0 000-1h-2v1zm-3-1h-8a.5.5 0 000 1h8v-1zM11 7L6 4v6l5-3zm4-4v8c0 .55-.45 1-1 1H2c-.55 0-1-.45-1-1V3c0-.55.45-1 1-1h12c.55 0 1 .45 1 1z" }))); };

var SvgIconChartBar = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M1 15h15v1H0V0h1v15zm1-5h14v3H2v-3zm0-4h5v3H2V6zm0-4h10v3H2V2z" }))); };

var SvgIconChartBarBi = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8 4h8V.999H8V4zM6 16h1V1H6v15zM0 8h5V5H0v3zm8 4h6V8.999H8V12zm0 4h3v-3H8v3z" }))); };

var SvgIconChartColumn = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M7 14h3V0H7v14zM0 0v16h16v-1H1V0H0zm11 14h3.001V4H11v10zm-8 0h3.001V8H3v6z" }))); };

var SvgIconChartLine = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M3 13.75a.75.75 0 01-.624-1.166l4.507-6.761 2.98 2.98 4.5-7.2a.75.75 0 011.273.794l-5.5 8.8-3.02-3.02-3.491 5.239a.75.75 0 01-.626.334zM1 0H0v16h16v-1H1V0z" }))); };

var SvgIconChartPie = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M15.475 7C15.217 3.092 11.974 0 8 0v7h7.475zm-7.45 1l5.28 5.279A7.478 7.478 0 0016 8H8.026zM7 1.025C3.092 1.283 0 4.526 0 8.5a7.5 7.5 0 0012.28 5.779L7 9V1.025zm3.728 13.117A6.516 6.516 0 017.5 15 6.508 6.508 0 011 8.5c0-3.042 2.124-5.636 5-6.316v7.23l4.728 4.728z" }))); };

var SvgIconCheck = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M15.003 3.997L5 14 .997 9.997a1.41 1.41 0 010-1.994l.006-.006a1.41 1.41 0 011.994 0L5 10l8.003-8.003a1.41 1.41 0 011.994 0l.006.006a1.41 1.41 0 010 1.994" }))); };

var SvgIconCheckboxCheck = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M13.707 4.707L6 12.414 3.293 9.707a.999.999 0 111.414-1.414L6 9.586l6.293-6.293a.999.999 0 111.414 1.414M13 0H3a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V3a3 3 0 00-3-3" }))); };

var SvgIconCheckboxIndeterminate = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M13 8a1 1 0 01-1 1H4a1 1 0 110-2h8a1 1 0 011 1m0-8H3a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V3a3 3 0 00-3-3" }))); };

var SvgIconCheckboxNone = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M13 0H3a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V3a3 3 0 00-3-3m0 1c1.103 0 2 .897 2 2v10c0 1.103-.897 2-2 2H3c-1.103 0-2-.897-2-2V3c0-1.103.897-2 2-2h10" }))); };

var SvgIconChevronDown = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M12 5L8 9.001 4 5 3 6l5 5 5-5z" }))); };

var SvgIconChevronLeft = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M10 3L5 7.999 10 13l1-1-4.001-4.001L11 4z" }))); };

var SvgIconChevronRight = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M6 3L5 4l4 3.999L5 12l1 1 5-5.001z" }))); };

var SvgIconChevronUp = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8 5l-5 5 .999 1L8 7.001 12 11l.999-1z" }))); };

var SvgIconCloseLarge = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M5.879 8l-5.44 5.439a1.5 1.5 0 002.122 2.122L8 10.121l5.44 5.44a1.501 1.501 0 002.12-2.122L10.12 8l5.44-5.439A1.5 1.5 0 0013.44.439L8 5.879 2.56.439A1.501 1.501 0 00.44 2.561L5.879 8z" }))); };

var SvgIconCloseSmall = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M9.414 8l4.293-4.293a.999.999 0 10-1.414-1.414L8 6.586 3.707 2.293a.999.999 0 10-1.414 1.414L6.586 8l-4.293 4.293a.999.999 0 101.414 1.414L8 9.414l4.293 4.293a.997.997 0 001.414 0 .999.999 0 000-1.414L9.414 8z" }))); };

var SvgIconCloudUpload = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M12 5c-.138 0-.267.027-.4.04A5 5 0 002 7c0 .061.016.118.018.178A2.99 2.99 0 000 10a3 3 0 003 3h4V9H5l3-4 3 4H9v4h3a4 4 0 000-8" }))); };

var SvgIconColumnSettings = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M13 0v5.879l-.146.146a.476.476 0 01-.343.149c-.198 0-.511-.136-.511-.503V3H9v2.671c0 .367-.313.503-.511.503a.476.476 0 01-.343-.149L8 5.878V3H5v7h.671c1.337 0 2.006-1.616 1.061-2.561l-.475-.475.707-.707.475.475C8.384 7.677 10 7.008 10 5.671V5h1v.671c0 1.337 1.616 2.006 2.561 1.061l.475-.475.707.707-.475.475c-.945.945-.276 2.561 1.06 2.561H16v1h-.672c-1.336 0-2.005 1.616-1.06 2.561l.475.475-.707.707-.475-.475c-.945-.945-2.561-.276-2.561 1.06V16h-1v-.672c0-1.336-1.616-2.005-2.561-1.06l-.475.475-.707-.707.475-.475C7.677 12.616 7.008 11 5.671 11H5v1h.672c.301 0 .423.216.462.309a.485.485 0 01-.109.545L5.879 13H0V0h13zm-2.5 9a1.5 1.5 0 10-.001 2.999A1.5 1.5 0 0010.5 9zM4 3H1v9h3V3z" }))); };

var SvgIconCopy = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M1 12h9V1H4.5a.5.5 0 00-.5.5V4H1.5a.5.5 0 00-.5.5V12zm-1 1V4.414a1 1 0 01.293-.707L3.707.293A1 1 0 014.414 0H11v13H0zM16 3v13H5v-2h1v1h9V4h-3V3h4z" }))); };

var SvgIconCreatives = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M0 14V2h16v12H0zm1.095-1.095h13.809V3.096H1.095v9.809zM10 6l4 6H2l4-5 2 2 2-3zM3.5 7a1.5 1.5 0 11-.001-2.999A1.5 1.5 0 013.5 7z" }))); };

var SvgIconCurrencyEuro = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M10.544 9.717h-2.78c.027.813.244 1.425.65 1.834.407.41 1.04.615 1.898.615.58 0 1.106-.086 1.577-.258l.299 2.009a9.336 9.336 0 01-2.125.24c-1.456 0-2.608-.39-3.457-1.173-.85-.784-1.297-1.872-1.341-3.267H3.86V8.455h1.404v-1.03H3.86V6.165h1.42c.094-1.394.58-2.477 1.457-3.25.877-.771 2.04-1.158 3.49-1.158.582 0 1.235.086 1.96.258l-.3 2.017a4.754 4.754 0 00-1.534-.265c-1.567 0-2.424.799-2.575 2.398h2.765v1.262h-2.78v1.029h2.78v1.262z" }))); };

var SvgIconCurrencyPound = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M7.63 9.219l.05 1.154c0 .68-.171 1.223-.514 1.627h5.42v2h-8.79v-2h.764c.398-.1.597-.595.597-1.486l-.042-1.295H3.772v-1.96h1.277L4.99 5.376c0-1.117.342-2 1.026-2.647.683-.648 1.597-.972 2.743-.972 1.173 0 2.095.312 2.764.935.669.622 1.004 1.465 1.004 2.527h-2.381c0-.471-.121-.83-.362-1.08-.241-.248-.586-.372-1.033-.372-.365 0-.67.135-.91.406-.24.272-.36.672-.36 1.203l.075 1.885h2.564v1.959H7.63z" }))); };

var SvgIconCurrencyUsDollar = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M9.794 10.824c0-.453-.126-.814-.378-1.083s-.679-.516-1.282-.743a15.2 15.2 0 01-1.561-.676 4.998 4.998 0 01-1.125-.772 3.113 3.113 0 01-.73-1.025c-.174-.393-.261-.861-.261-1.403 0-.936.299-1.702.897-2.3.597-.598 1.391-.945 2.381-1.045V0h1.329v1.802c.979.138 1.746.546 2.299 1.224.553.678.83 1.556.83 2.636H9.794c0-.664-.137-1.161-.411-1.49-.274-.329-.641-.494-1.1-.494-.454 0-.804.128-1.054.385-.248.258-.373.614-.373 1.068 0 .42.122.757.365 1.011.243.255.696.516 1.357.782.661.265 1.205.515 1.631.751.426.235.786.501 1.079.796.293.296.518.634.673 1.013.154.379.232.82.232 1.324 0 .941-.294 1.704-.88 2.291-.586.586-1.395.932-2.424 1.038v1.652h-1.32v-1.645c-1.134-.121-2.012-.523-2.635-1.207C4.312 12.254 4 11.345 4 10.211h2.398c0 .658.158 1.163.47 1.514.313.352.762.527 1.349.527.487 0 .871-.128 1.154-.386.281-.257.423-.605.423-1.042" }))); };

var SvgIconCurrencyYen = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8.008 6.596l1.859-4.682h2.6L9.793 7.841h1.811v1.452H9.213v.955h2.391v1.445H9.213V14h-2.49v-2.307H4.182v-1.445h2.541v-.955H4.182V7.841h2.025L3.526 1.914h2.615z" }))); };

var SvgIconData = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M4 13.73v1a15.48 15.48 0 01-2-.41v-1a15.48 15.48 0 002 .41zM0 14c0 1.104 3.582 2 8 2s8-.896 8-2v-3c0 1.104-3.582 2-8 2s-8-.896-8-2v3zm4-4.27v1a15.48 15.48 0 01-2-.41v-1a15.48 15.48 0 002 .41zM0 10c0 1.104 3.582 2 8 2s8-.896 8-2V7c0 1.104-3.582 2-8 2s-8-.896-8-2v3zm4-4.27v1a15.48 15.48 0 01-2-.41v-1a15.48 15.48 0 002 .41zM0 6c0 1.104 3.582 2 8 2s8-.896 8-2V3c0 1.104-3.582 2-8 2s-8-.896-8-2v3zm16-4c0 1.104-3.582 2-8 2s-8-.896-8-2C0 .896 3.582 0 8 0s8 .895 8 2z" }))); };

var SvgIconDate = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M11.5 8h2V6h-2v2zm0 3h2V9h-2v2zm0 3h2v-2h-2v2zm-3-6h2V6h-2v2zm0 3h2V9h-2v2zm0 3h2v-2h-2v2zm-3-6h2V6h-2v2zm0 3h2V9h-2v2zm0 3h2v-2h-2v2zm-3-6h2V6h-2v2zm0 3h2V9h-2v2zm0 3h2v-2h-2v2zM16 4v11a1 1 0 01-1 1H1a1 1 0 01-1-1V4a1 1 0 011-1h2a2 2 0 004 0h2a2 2 0 004 0h2a1 1 0 011 1zM5 4a1 1 0 001-1V1a1 1 0 10-2 0v2a1 1 0 001 1zm5-1V1a1 1 0 112 0v2a1 1 0 11-2 0z" }))); };

var SvgIconDeal = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M5 4v3a2 2 0 104 0V6h3a1 1 0 01.117 1.993L12 8v1a1 1 0 01-.883.993L11 10v1a1 1 0 01-1 1H8.414a1 1 0 00-.707.293L4 16l-4-4 2.707-2.707A1 1 0 003 8.586V6.414a1 1 0 01.293-.707L5 4zm7-4l4 4-2 2a1 1 0 00-1-1H8v2a1 1 0 11-2 0V3.618a1 1 0 01.553-.894l1.236-.618A1 1 0 018.236 2h1.35a1 1 0 00.707-.293L12 0z" }))); };

var SvgIconDelete = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M6.5 2h3v-.987h-3V2zM13 2h-2.5V.75A.752.752 0 009.75 0h-3.5a.752.752 0 00-.75.75V2H3c-.55 0-1 .45-1 1v1h12V3c0-.55-.45-1-1-1zM4.5 6.5a.5.5 0 011 0v7a.5.5 0 01-1 0v-7zm2 0a.5.5 0 011 0v7a.5.5 0 01-1 0v-7zm2 0a.5.5 0 011 0v7a.5.5 0 01-1 0v-7zm2 0a.5.5 0 011 0v7a.5.5 0 01-1 0v-7zm-8 8.5a1 1 0 001 1h9a1 1 0 001-1V4.5h-11V15z" }))); };

var SvgIconDetails = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M3 12h2v-1H3v1zm0-2h2V9H3v1zm0-2h2V7H3v1zm0-2h2V5H3v1zm0-2h2V3H3v1zm7 8c1.654 0 3-1.346 3-3s-1.346-3-3-3-3 1.346-3 3 1.346 3 3 3zm5.854 3.354a.502.502 0 01-.708 0l-2.992-2.993c-.623.4-1.359.639-2.154.639a4 4 0 114-4 3.972 3.972 0 01-1.078 2.715l2.932 2.931a.502.502 0 010 .708zM11 13.745a4.977 4.977 0 001-.44V16H0V0h12v4.695a4.977 4.977 0 00-1-.44V1H1v14h10v-1.255z" }))); };

var SvgIconDeviceDesktop = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M7 12H1a1 1 0 01-1-1V2a1 1 0 011-1h14a1 1 0 011 1v9a1 1 0 01-1 1H9c0 2.059.473 2.5 3 2.5v.5H4v-.5c2.527 0 3-.441 3-2.5zm.34 0s.134.67.66.67.66-.67.66-.67H7.34zM1 11h14V2H1v9zm13.75.5a.25.25 0 10-.5 0 .25.25 0 00.5 0z" }))); };

var SvgIconDeviceLaptop = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M1 11.5h14V3H1v8.5zM16 3v8.5a1 1 0 01-1 1H8.847A.988.988 0 008 12a.988.988 0 00-.847.5H1a1 1 0 01-1-1V3a1 1 0 011-1h14a1 1 0 011 1zM9 13h7v1a1 1 0 01-1 1H1a1 1 0 01-1-1v-1h7a1 1 0 102 0z" }))); };

var SvgIconDevicePhone = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M11 0a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V1a1 1 0 011-1h6zM9 2a.5.5 0 000-1H7a.5.5 0 000 1h2zM5 12h6V3H5v9zm3 3a1 1 0 100-2 1 1 0 100 2z" }))); };

var SvgIconDeviceTablet = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M13 0a1 1 0 011 1v14a1 1 0 01-1 1H3a1 1 0 01-1-1V1a1 1 0 011-1h10zM3 13h10V1H3v12zm5 2.5a1 1 0 100-2 1 1 0 100 2z" }))); };

var SvgIconDownload = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14 10v4H2v-4a1 1 0 10-2 0v4a2 2 0 002 2h12a2 2 0 002-2v-4a1 1 0 10-2 0zM3 6h3V1a1 1 0 011-1h2a1 1 0 011 1v5h3l-5 6-5-6z" }))); };

var SvgIconDraft = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M11 0l-1 1H2a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V6l1-1v9a2 2 0 01-2 2H2a2 2 0 01-2-2V2a2 2 0 012-2h9zM3 13l1-3 2 2-3 1zm1.5-3.5l7-7 2 2-7 7-2-2zm10.793-8.207a.999.999 0 010 1.414L14 4l-2-2L13.293.707a.999.999 0 011.414 0l.586.586z" }))); };

var SvgIconEdit = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M3.5 12.5v-1h-.586l-.062.062-.654 1.636.604.604 1.636-.654.062-.062V12.5h-1zm2 0h1L5 14l-5 2 2-5 1.5-1.5v1h2v2zm10.146-9.854L13.354.354a.5.5 0 00-.708 0L11 2l3 3 1.646-1.646a.5.5 0 000-.708zM10.5 2.5l3 3L7 12H6v-2H4V9l6.5-6.5z" }))); };

var SvgIconEditBulk = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M15.646 5.646L15 5l-2 2 1 1 1.646-1.646a.5.5 0 000-.708zM7 13.002l-1.803.9L4 16.002l4-2 5.5-5.5-1-1L7 13zm6.646-9.354L13 3l-2 2 1 1 1.646-1.647a.5.5 0 000-.708zM5 11l-1.804.9L2 14.002l4-2 5.5-5.5-1-1L5 11zm6.646-8.647a.5.5 0 000-.708L10.354.355a.5.5 0 00-.707 0L8 2l2 2 1.646-1.648zM4 10l-4 2 2-4 5.5-5.5 2 2L4 10z" }))); };

var SvgIconEducation = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8 3L0 6l2.167.813 3.896-1.462c.516-.193 1.237-.239 1.604-.101.366.138.245.408-.271.601L3.5 7.312 8 9l8-3-8-3zm-5.75 7.051V7.912l-.5-.188v2.327c-.431.162-.75.729-.75 1.449 0 .854.5 1.649.5 2.5h1c0-.851.5-1.646.5-2.5 0-.72-.319-1.287-.75-1.449zm6.102-.114L12 8.568v2.239a1 1 0 01-.648.936l-2.298.862a3 3 0 01-2.108 0l-2.298-.862A1 1 0 014 10.807V8.568l3.648 1.369.352.131.352-.131z" }))); };

var SvgIconEmail = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M0 13h16V3H0v10zm1.86-9h12.28L8 8.386 1.86 4zM15 4.615V12H1V4.615l7 4.999 7-4.999z" }))); };

var SvgIconExport = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M6 14H2V2h4a1 1 0 100-2H2a2 2 0 00-2 2v12a2 2 0 002 2h4a1 1 0 100-2zm4-1v-3H5a1 1 0 01-1-1V7a1 1 0 011-1h5V3l6 5-6 5z" }))); };

var SvgIconFav = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M0 16h16V0H0v16zM6.408 2h3.184l5.074 12h-3.525l-.785-2H5.593l-.785 2H1.334L6.408 2zm.166 7.5h2.801l-1.4-3.567L6.574 9.5z" }))); };

var SvgIconFavorite = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M11.5 1c-1.423 0-2.676.97-3.5 2-.824-1.03-2.077-2-3.5-2A4.5 4.5 0 000 5.5c0 2.485 3.5 6 8 10.5 4.5-4.5 8-8.015 8-10.5A4.5 4.5 0 0011.5 1" }))); };

var SvgIconFeed = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M0 0c8.837 0 16 7.164 16 16h-3C13 8.82 7.18 3 0 3V0zm0 5c6.075 0 11 4.925 11 11H8a8 8 0 00-8-8V5zm2.5 6a2.5 2.5 0 110 5 2.5 2.5 0 010-5z" }))); };

var SvgIconFilter = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8 4C3.754 4 1.393 2.977 1.026 2.5 1.393 2.024 3.754 1 8 1s6.607 1.024 6.974 1.5C14.607 2.977 12.246 4 8 4m0-4C3.582 0 0 1.119 0 2.5c0 .671.851 1.279 2.228 1.729L7 9.001V15a1 1 0 102 0V9.001l4.772-4.772C15.149 3.779 16 3.171 16 2.5 16 1.119 12.418 0 8 0" }))); };

var SvgIconForward = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M2 7h9.586L8.293 3.707a.999.999 0 111.414-1.414l5 5a.999.999 0 010 1.414l-5 5a.997.997 0 01-1.414 0 .999.999 0 010-1.414L11.586 9H2a1 1 0 110-2" }))); };

var SvgIconFrequency = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M9 16c-1.274 0-1.522-1.087-2.49-7.858a149.265 149.265 0 00-.644-4.222c-.324 1.465-.652 3.435-.874 5.204A1 1 0 014 10H0V8h3.121C4.194 0 5.275 0 6 0c1.274 0 1.522 1.087 2.49 7.858.201 1.413.466 3.261.718 4.634.287-1.01.587-2.342.806-3.656A1 1 0 0111 8h5v2h-4.159C10.746 16 9.618 16 9 16" }))); };

var SvgIconGlobe = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8 15c-3.859 0-7-3.141-7-7 0-.738.116-1.448.329-2.117.321.363.812.885 1.171 1.117.578.375 2 1 2 1s-.453.359-.5.5A.985.985 0 004 9c.039.141.258.664.5 1 .242.336 1 1 1 1s-.047 1.555 0 2c.047.445.367.883.5 1 .133.117.5 0 .5 0s-.117-1.258 0-1.5.5-.5.5-.5.422-.289.5-.5c.078-.211-.094-.875 0-1s.336.102.5 0c.164-.102.469-.82.5-1 .031-.18.125-.836 0-1-.125-.164-.711.055-1 0-.289-.055-.242-.32-.5-.5s-1.273-.5-1.5-.5c-.227 0-.641.07-1 0s-1.445-.859-1.5-1C2.945 6.359 3 6 3 6s.258-.516.5-.5c.242.016 1 .5 1 .5s.852-.328 1-.5c.148-.172.469-.844.5-1 .031-.156-.031-.406 0-.5.031-.094.32-.516.5-.5.18.016.734.719 1 .5.266-.219.141-.836 0-1-.141-.164-.641-.5-1-.5s-.797.562-1 .5c-.203-.063-.5-.336-.5-.5 0-.164.375-.461.5-.5.125-.039 1.695.086 2 0 .305-.086-.203-.461-.5-.5a14.459 14.459 0 00-1.449-.049 6.94 6.94 0 016.919 1.167c-.062.341.03.882.03.882H12c-.148 0-.305-.422-.5-.5-.195-.078-.406-.141-.5 0-.094.141-.078.328 0 .5s.984.281 1 .5c.016.219-1.383.219-1.5.5-.117.281.133.5.5.5s1.437-.5 2-.5c.318 0 .879.268 1.329.529.148.313.275.635.376.971H13.5c-.242 0-.75-1-1.5-1h-.5c-.203 0-.625 1-1 1s-.453.203-.5.5c-.047.297-.047 1.25 0 1.5s.766.5 1 .5h1.5s.461.867.5 1.5c.039.633-.063 1.086 0 1.5a2 2 0 00.426.915A6.988 6.988 0 018 15M8 0a8 8 0 100 16A8 8 0 008 0" }))); };

var SvgIconGrip = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "none" }, props),
    React.createElement("path", { d: "M3 4h4V0h-.5s0 1.5-1 2.5-2.5 1-2.5 1V4zM9 4h4V0h-.5s0 1.5-1 2.5-2.5 1-2.5 1V4zM7 10H3v-.5s1.5 0 2.5-1 1-2.5 1-2.5H7v4zM9 10h4V6h-.5s0 1.5-1 2.5-2.5 1-2.5 1v.5zM7 16H3v-.5s1.5 0 2.5-1 1-2.5 1-2.5H7v4zM9 16h4v-4h-.5s0 1.5-1 2.5-2.5 1-2.5 1v.5z", fill: "currentColor" }))); };

var SvgIconHealth = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14 0a2 2 0 012 2v12a2 2 0 01-2 2H2a2 2 0 01-2-2V2a2 2 0 012-2h12zM6.881 9.676a.5.5 0 00-.81.067L4.716 12H2.5l-.09.008A.5.5 0 002.5 13H5l.086-.008a.5.5 0 00.343-.235l1.06-1.768 1.577 2.76.053.075a.5.5 0 00.81-.067l1.092-1.822.563.842.063.077A.5.5 0 0011 13h2.5l.09-.008A.5.5 0 0014 12.5l-.008-.09A.5.5 0 0013.5 12h-2.232l-.852-1.277-.056-.07a.5.5 0 00-.789.09L8.51 12.51 6.934 9.752zM9 2H7v2H5v2h2v2h2V6h2V4H9V2z" }))); };

var SvgIconHelp = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8 0C3.58 0 0 3.581 0 8a8 8 0 008 8 8 8 0 008-8c0-4.419-3.58-8-8-8zm2.616 7.411c-.149.217-.437.495-.86.834l-.42.332a1.296 1.296 0 00-.454.635c-.048.153-.074.39-.078.713H7.201c.023-.68.087-1.152.188-1.41.103-.26.367-.56.791-.899l.431-.345c.142-.108.256-.227.343-.356.156-.222.235-.466.235-.732 0-.306-.088-.584-.262-.836-.175-.252-.496-.378-.959-.378-.457 0-.78.155-.972.465-.19.31-.286.632-.286.967H5c.047-1.148.44-1.963 1.178-2.442.464-.306 1.037-.459 1.715-.459.892 0 1.632.218 2.222.652.59.436.885 1.08.885 1.935 0 .523-.128.965-.384 1.324zm-3.456 5.09v-1.748h1.77V12.5H7.16z" }))); };

var SvgIconHold = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M15.707 8.707a.997.997 0 000-1.414.999.999 0 00-1.414 0l-1.586 1.586c-.63.63-1.707.183-1.707-.707V2a1 1 0 10-2 0v4.5a.5.5 0 01-1 0V1a1 1 0 10-2 0v5.5a.5.5 0 01-1 0V2a1 1 0 10-2 0v5.5a.5.5 0 01-1 0V4a1 1 0 10-2 0v6a6 6 0 006 6h5v-.515c0-1.326.526-2.597 1.465-3.536l3.242-3.242z" }))); };

var SvgIconImport = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M6 14H2V2h4a1 1 0 100-2H2a2 2 0 00-2 2v12a2 2 0 002 2h4a1 1 0 100-2zM4 8l6-5v3h5a1 1 0 011 1v2a1 1 0 01-1 1h-5v3L4 8z" }))); };

var SvgIconInfo = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M10.5 4c0 .41-.148.764-.442 1.058A1.45 1.45 0 019 5.5c-.411 0-.764-.148-1.058-.442A1.442 1.442 0 017.5 4c0-.418.147-.771.442-1.062A1.445 1.445 0 019 2.5c.409 0 .763.145 1.058.438.294.291.442.644.442 1.062m-1.326 7.131H10c-.699 1.444-1.675 2.166-2.93 2.166-.508 0-.888-.103-1.145-.309-.257-.207-.385-.513-.385-.921 0-.238.106-.656.319-1.254l.983-2.738c.076-.213.114-.349.114-.413 0-.063-.016-.11-.046-.14-.029-.028-.077-.043-.142-.043-.279 0-.606.345-.983 1.032H5c.703-1.476 1.696-2.214 2.979-2.214.453 0 .804.107 1.055.321.251.214.376.509.376.885 0 .274-.111.711-.335 1.309l-.949 2.509c-.137.354-.204.575-.204.667 0 .059.017.103.053.134a.218.218 0 00.151.049c.142 0 .323-.12.544-.359.221-.237.389-.464.504-.681M8 0C3.58 0 0 3.581 0 8a8 8 0 008 8 8 8 0 008-8c0-4.419-3.58-8-8-8" }))); };

var SvgIconInlineAdd = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14 9H9v5a1 1 0 11-2 0V9H2a1 1 0 110-2h5V2a1 1 0 112 0v5h5a1 1 0 110 2" }))); };

var SvgIconLaunch = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M0 4h9.464L7.465 6H1v8h11V8.535l1-1V15H0V4zm10-3h6v6l-1.834-2.752-5.459 5.459a.997.997 0 01-1.414 0 .999.999 0 010-1.414l5.459-5.459L10 1z" }))); };

var SvgIconLightBulb = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8.003 7.133c.12-.235.284-.383.497-.383.053-.044.25-.476.25-1.25s-.197-1.207-.28-1.257c-.023.05-.22.483-.22 1.257h-.5c0-.774-.197-1.207-.28-1.257-.023.05-.22.483-.22 1.257 0 .774.197 1.206.28 1.256h.005c.197 0 .353.146.468.377zM10.7 7.9C11.398 6.97 12 6.166 12 5c0-2.206-1.794-4-4-4S4 2.794 4 5c0 1.166.603 1.97 1.3 2.9.464.618.959 1.29 1.295 2.1H7.75V8.5c0-.774-.197-1.207-.28-1.257h-.005c-.484 0-.715-.875-.715-1.743 0-.87.232-1.75.75-1.75.215 0 .38.15.5.388.12-.238.285-.388.5-.388.519 0 .75.88.75 1.75 0 .871-.231 1.75-.75 1.75-.053.043-.25.476-.25 1.25V10h1.155c.336-.81.83-1.482 1.295-2.1zM13 5c0 3-3 4-3 7H6c0-3-3-4-3-7a5 5 0 015-5 5 5 0 015 5zm-4 9H6a1 1 0 001 1v.5a.5.5 0 00.5.5h1a.5.5 0 00.5-.5V15h1a1 1 0 00-1-1zm0-1.5a1 1 0 011 1H7a1 1 0 01-1-1h3z" }))); };

var SvgIconLink = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M6.979 10.435l.729-.729A.98.98 0 008 9a.991.991 0 00-.06-.308 2.135 2.135 0 01-.355-.277A1.99 1.99 0 017 7c0-.535.208-1.037.585-1.415l3-3C10.963 2.208 11.465 2 12 2s1.037.208 1.415.586c.377.377.585.88.585 1.413a1.99 1.99 0 01-.585 1.416L11.65 7.178c.225.574.35 1.188.35 1.821 0 .248-.036.489-.071.728l2.9-2.899A4.002 4.002 0 0012 0a3.99 3.99 0 00-2.829 1.172l-3 3a4.002 4.002 0 000 5.657c.246.247.522.44.808.607m2.85-4.264a4.002 4.002 0 010 5.657l-3 3A3.99 3.99 0 014 16a3.99 3.99 0 01-2.829-1.17 4.002 4.002 0 010-5.658l2.9-2.9C4.036 6.513 4 6.753 4 7c0 .635.125 1.248.35 1.822l-1.765 1.764A1.99 1.99 0 002 11.998c0 .535.208 1.037.585 1.415.378.377.88.585 1.415.585a1.99 1.99 0 001.415-.585l3-3C8.791 10.037 9 9.534 9 9c0-.534-.209-1.036-.585-1.414a2.028 2.028 0 00-.355-.276A1.01 1.01 0 018 7c0-.27.101-.514.292-.706l.729-.729c.286.168.562.362.808.607" }))); };

var SvgIconList = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M1 9h14V7H1v2zm0-6h14V1H1v2zm0 3h14V4H1v2zm0 9h14v-2H1v2zm0-3h14v-2H1v2z" }))); };

var SvgIconListHierarchical = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M4 9h12V7H4v2zM0 3h16V1H0v2zm4 3h12V4H4v2zm0 9h12v-2H4v2zm-4-3h16v-2H0v2z" }))); };

var SvgIconLocation = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8 2C6.346 2 5 3.346 5 5s1.346 3 3 3 3-1.346 3-3-1.346-3-3-3m0 14S3 7.757 3 5c0-2.757 2.243-5 5-5s5 2.243 5 5-5 11-5 11" }))); };

var SvgIconLockClosed = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M6 3.5c0-1.103.897-2 2-2s2 .897 2 2V7H6V3.5zM14 7h-2V4c0-2.206-1.794-4-4-4S4 1.794 4 4v3H2a1 1 0 00-1 1v7a1 1 0 001 1h12a1 1 0 001-1V8a1 1 0 00-1-1z" }))); };

var SvgIconLockOpen = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M15 8v7a1 1 0 01-1 1H2a1 1 0 01-1-1V8a1 1 0 011-1h8V3.5c0-1.103-.897-2-2-2s-2 .897-2 2V5H4V4c0-2.206 1.794-4 4-4s4 1.794 4 4v3h2a1 1 0 011 1" }))); };

var SvgIconMenu = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M16 13.5v-1a.5.5 0 00-.5-.5H.5a.5.5 0 00-.5.5v1a.5.5 0 00.5.5h15a.5.5 0 00.5-.5m0-10v-1a.5.5 0 00-.5-.5H.5a.5.5 0 00-.5.5v1a.5.5 0 00.5.5h15a.5.5 0 00.5-.5m0 5v-1a.5.5 0 00-.5-.5H.5a.5.5 0 00-.5.5v1a.5.5 0 00.5.5h15a.5.5 0 00.5-.5" }))); };

var SvgIconNote = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M3 9h10V8H3v1zm0 2h7v-1H3v1zM15 1H1v14h10.5a.5.5 0 00.5-.5V12h2.5a.5.5 0 00.5-.5V1zm1-1v11.586a1 1 0 01-.293.707l-3.414 3.414a1 1 0 01-.707.293H0V0h16zM3 7h6V6H3v1zm0-2h10V4H3v1z" }))); };

var SvgIconNotifications = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M15.5 12c-.9 0-2.9-1.2-3.5-5-.6-3.5-.9-4.8-3.5-5 .3-.2.5-.6.5-1 0-.6-.4-1-1-1S7 .4 7 1c0 .4.2.8.5 1-2.6.2-2.9 1.5-3.5 5-.6 3.8-2.6 5-3.5 5-.3 0-.5.2-.5.5s.2.5.5.5h15c.3 0 .5-.2.5-.5s-.2-.5-.5-.5M6 14h4c0 1.1-.9 2-2 2s-2-.9-2-2" }))); };

var SvgIconOptions = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M16 2c0-.4-.3-.8-.7-.8H6.8c-.5 0-.8.4-.8.8s.3.8.8.8h8.4c.5 0 .8-.4.8-.8zM0 1v2c0 .6.4 1 1 1h3c.6 0 1-.4 1-1V1c0-.6-.4-1-1-1H1C.4 0 0 .4 0 1zm16 13c0-.4-.3-.8-.7-.8H6.8c-.5 0-.8.4-.8.8s.3.8.8.8h8.4c.5 0 .8-.4.8-.8zm0-6c0-.4-.3-.8-.7-.8H6.8c-.5 0-.8.4-.8.8s.3.8.8.8h8.4c.5 0 .8-.4.8-.8zM0 7v2c0 .6.4 1 1 1h3c.6 0 1-.4 1-1V7c0-.6-.4-1-1-1H1c-.6 0-1 .4-1 1zm1 2h3V7H1v2zm-1 4v2c0 .6.4 1 1 1h3c.6 0 1-.4 1-1v-2c0-.6-.4-1-1-1H1c-.6 0-1 .4-1 1zm1 2h3v-2H1v2z" }))); };

var SvgIconOverflow = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M3 10a1.5 1.5 0 10-.001-3.001A1.5 1.5 0 003 10m5 0a1.5 1.5 0 10-.001-3.001A1.5 1.5 0 008 10m3.5-1.5a1.5 1.5 0 113.001.001A1.5 1.5 0 0111.5 8.5" }))); };

var SvgIconPause = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14 0c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H2c-1.1 0-2-.9-2-2V2C0 .9.9 0 2 0h12zM9 12h2V4H9v8zm-4 0h2V4H5v8z" }))); };

var SvgIconPixel = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M5 11V5h6v6H5zm8-2.5v-1h3.001v1H13zm-13 0v-1h3v1H0zM7.5 16v-3h1v3h-1zm0-13V-.001h1V3h-1zm6.51-.303L12.596 4.11l-.707-.707 1.414-1.414.707.707zM1.99 13.303l1.414-1.414.707.707-1.414 1.414-.707-.707zm9.9-.707l.706-.707 1.414 1.414-.707.707-1.414-1.414zm-9.9-9.9l.707-.706L4.11 3.404l-.707.707L1.99 2.697z" }))); };

var SvgIconPlay = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M6 12V4l6 4-6 4zm8-12H2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V2a2 2 0 00-2-2z" }))); };

var SvgIconPrivateMarketplace = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M10.014 6a2 2 0 01-2-2h4a2 2 0 01-2 2zm2-2h4a2 2 0 01-4 0zm-10 2a2 2 0 01-2-2h4a2 2 0 01-2 2zm4 0a2 2 0 01-2-2h4a2 2 0 01-2 2zm5 9V9h-2.5v3.081c.285.088.486.239.486.419s-.201.331-.486.419V15h2.5zm-3.5-2.928V9h-2.5v6h2.5v-2.072C7.212 12.842 7 12.686 7 12.5s.212-.342.514-.428zM10.014 7c.768 0 1.469-.29 2-.766a2.987 2.987 0 002 .766v9H2V6.999L2.014 7c.768 0 1.469-.29 2-.766a2.987 2.987 0 002 .766c.768 0 1.469-.29 2-.766a2.987 2.987 0 002 .766zM3 0h10l3.001 3H0l3-3z" }))); };

var SvgIconProcess = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M10.796 4.994l1.5-2.599a.918.918 0 00-1.588-.917l-1.5 2.598a.918.918 0 001.588.918zm-.58 6.344a.25.25 0 00-.433.25l1.5 2.598a.252.252 0 00.342.091.25.25 0 00.092-.34l-1.5-2.599zm1.374-1.555a.251.251 0 00-.342.091.25.25 0 00.092.341l2.598 1.5a.248.248 0 00.34-.09.25.25 0 00-.09-.342l-2.598-1.5zM8 .166A.833.833 0 007.167 1v3a.833.833 0 001.667 0V1A.833.833 0 008 .166zm7 7.584h-3a.249.249 0 00-.25.25c0 .14.11.25.25.25h3c.139 0 .25-.11.25-.25a.249.249 0 00-.25-.25zm-7 3.916a.333.333 0 00-.333.334v3a.334.334 0 00.667 0v-3A.334.334 0 008 11.666zM4.584 8A.585.585 0 004 7.416H1a.584.584 0 000 1.168h3A.585.585 0 004.584 8zm-2.98-2.924l2.599 1.5a.663.663 0 00.91-.244.665.665 0 00-.243-.91l-2.598-1.5a.666.666 0 10-.667 1.154zm9.082 1.373a.899.899 0 001.229.33l2.599-1.5a.9.9 0 10-.901-1.558l-2.598 1.5a.898.898 0 00-.33 1.228zM4.97 9.75a.5.5 0 00-.683-.184l-2.598 1.5a.501.501 0 10.5.867l2.598-1.5a.5.5 0 00.183-.683zm.18-8.188a.75.75 0 00-1.3.75l1.5 2.598a.75.75 0 101.3-.75l-1.5-2.598zm1.213 10.11l-1.5 2.597a.416.416 0 11-.722-.416l1.5-2.598a.417.417 0 11.722.417z" }))); };

var SvgIconProductAudiences = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M13 13.5H3v-.625c0-1.054.854-1.403 2.17-1.904 1.311-.499 1.731-.92 1.731-1.822 0-.541-.443-.439-.62-1.43-.073-.411-.401.001-.468-.938 0-.281.177-.316.265-.316.028 0 .047.004.047.004s-.118-.668-.156-1.094C5.92 4.845 6.18 3.5 8 3.5s2.079 1.345 2.031 1.875c-.038.426-.156 1.094-.156 1.094s.019-.004.048-.004c.087 0 .264.035.264.316-.067.939-.396.527-.468.938-.177.991-.62.889-.62 1.43 0 .902.42 1.323 1.731 1.822 1.316.501 2.17.85 2.17 1.904v.625zm3-2.125c0-1.054-.854-1.403-2.17-1.904-1.311-.499-1.731-.92-1.731-1.822 0-.541.443-.439.62-1.43.072-.411.401.001.468-.938 0-.281-.177-.316-.264-.316-.029 0-.048.004-.048.004s.118-.668.156-1.094C13.079 3.345 12.82 2 11 2c-1.237 0-1.75.619-1.938 1.183A3.055 3.055 0 008 3c-.433 0-.772.079-1.062.183C6.75 2.619 6.237 2 5 2 3.18 2 2.92 3.345 2.969 3.875c.038.426.156 1.094.156 1.094s-.019-.004-.047-.004c-.088 0-.266.035-.266.316.068.939.396.527.469.938.177.991.62.889.62 1.43 0 .902-.42 1.323-1.731 1.822-1.316.501-2.17.85-2.17 1.904V12h2.699a1.89 1.89 0 00-.199.875V14h11v-1.125c0-.344-.073-.629-.199-.875H16v-.625z" }))); };

var SvgIconProductBi = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M9 11.5a4.5 4.5 0 11-9-.001 4.5 4.5 0 019 0zM4 0a3 3 0 110 6 3 3 0 010-6zm0 1c-1.103 0-2 .897-2 2s.897 2 2 2 2-.897 2-2-.897-2-2-2zm4.5 4.75a3.25 3.25 0 116.5 0 3.25 3.25 0 01-6.5 0zM12.25 11a1.75 1.75 0 110 3.5 1.75 1.75 0 010-3.5zm0 1a.75.75 0 10.75.75.752.752 0 00-.75-.75z" }))); };

var SvgIconProductCampaigns = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M2 5h1a1.5 1.5 0 113 0h8v.5H5.908c-.206.581-.756 1-1.408 1a1.495 1.495 0 01-1.408-1H2V5zm6 6h6v.5H7.908c-.206.581-.756 1-1.408 1a1.495 1.495 0 01-1.408-1H2V11h3a1.5 1.5 0 113 0zm5-3h1v.5h-1.092c-.207.581-.756 1-1.408 1a1.496 1.496 0 01-1.408-1H2V8h8a1.5 1.5 0 113 0zm2 5c0 .551-.448 1-1 1H1V3c0-.551.449-1 1-1h13v11zM2 1a2 2 0 00-2 2v12h14a2 2 0 002-2V1H2z" }))); };

var SvgIconProductDashboard = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M15 9a6.979 6.979 0 00-2.042-4.936L9.952 8.572A2 2 0 118 7c.147 0 .29.018.428.048l4.509-3.006A6.98 6.98 0 008 2C4.14 2 1 5.14 1 9a6.98 6.98 0 002.585 5.416L5 13h6l1.415 1.415A6.976 6.976 0 0015 9m1 0c0 2.72-1.36 5.12-3.436 6.565L11 14H5l-1.58 1.554A7.987 7.987 0 010 9a8 8 0 0116 0M7.5 5A4.5 4.5 0 003 9.5c0 .987.327 1.893.866 2.634l-.576.576A5.97 5.97 0 012 9a6 6 0 019.406-4.937L9.371 5.419A4.455 4.455 0 007.5 5" }))); };

var SvgIconProductInsights = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M10 1.5c1.379 0 2.5 1.122 2.5 2.5S11.379 6.5 10 6.5A2.503 2.503 0 017.5 4c0-1.378 1.122-2.5 2.5-2.5zM2.25 7.75C3.215 7.75 4 8.535 4 9.5s-.785 1.75-1.75 1.75S.5 10.465.5 9.5s.785-1.75 1.75-1.75zm8.129 3.434a1.748 1.748 0 00-.923-.617l.434-3.578c.037.001.072.011.11.011.711 0 1.354-.257 1.868-.67l1.873 1.971A1.22 1.22 0 0013.5 9c0 .133.037.256.076.376l-3.197 1.808zm-5.973-1.076A2.22 2.22 0 004.5 9.5c0-.865-.494-1.607-1.21-1.984l.508-1.307c.068.012.13.041.202.041.174 0 .339-.036.489-.1l3.325 4.82a1.72 1.72 0 00-.317.398l-3.091-1.26zm4.987-3.17l-.434 3.566c-.263.006-.51.073-.733.184L4.898 5.862c.206-.214.331-.499.343-.815l1.861-.311a2.997 2.997 0 002.291 2.202zm5.357.812c-.242 0-.455.087-.646.206l-1.872-1.969C12.704 5.457 13 4.766 13 4a3 3 0 10-6 0c0 .083.018.161.024.242l-1.864.311A1.244 1.244 0 002.75 5c0 .438.239.806.582 1.029l-.508 1.304a2.217 2.217 0 00-.574-.083 2.25 2.25 0 100 4.5c.853 0 1.585-.479 1.967-1.179l3.09 1.259a1.709 1.709 0 00-.057.42 1.75 1.75 0 003.5 0c0-.224-.046-.436-.122-.633l3.19-1.803c.23.262.556.436.932.436a1.25 1.25 0 000-2.5z" }))); };

var SvgIconProductMediaPlans = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M4 7L2 5a.707.707 0 011-1l1 1 5-5H0v9h9V2L4 7zm6 2h6V3h-6v6zm1-1h4V4h-4v4zm-8 8h6v-6H3v6zm1-1h4v-4H4v4zm6 1h6v-6h-6v6zm1-1h4v-4h-4v4z" }))); };

var SvgIconRadioNone = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8 0a8 8 0 100 16A8 8 0 008 0m0 1c3.859 0 7 3.141 7 7s-3.141 7-7 7c-3.86 0-7-3.141-7-7s3.14-7 7-7" }))); };

var SvgIconRadioSelect = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M13 8A5 5 0 113 8a5 5 0 0110 0m-5 7c-3.86 0-7-3.141-7-7s3.14-7 7-7 7 3.141 7 7-3.14 7-7 7M8 0a8 8 0 100 16A8 8 0 008 0" }))); };

var SvgIconReload = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M10 6l2.232-2.232A5.966 5.966 0 008 2C4.691 2 2 4.691 2 8s2.691 6 6 6 6-2.691 6-6h2a8 8 0 11-8-8c2.21 0 4.205.899 5.651 2.349L16 0v6h-6z" }))); };

var SvgIconRemove = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M10.121 11.536l-2.12-2.122-2.122 2.122a1 1 0 11-1.415-1.415L6.586 8 4.464 5.879A1 1 0 015.88 4.465L8 6.585l2.121-2.12a.999.999 0 111.414 1.414l-2.12 2.12 2.12 2.122a1 1 0 11-1.414 1.415M8.001 0C3.58 0 0 3.58 0 8a8 8 0 008 8 8 8 0 008-8c0-4.42-3.58-8-8-8" }))); };

var SvgIconReport = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M6 0h9v16H1V4l5-4zM2 5v10h12V1H7v4H2zm2 8v-1h8v1H4zm1.707-5.707L7 8.586l4.293-4.293 1.414 1.414L7 11.414 4.293 8.707l1.414-1.414z" }))); };

var SvgIconResources = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M2 9l5.5 1.4V16L2 14.133V9zm12 0v5.133L8.5 16v-5.6L14 9zM8 7l6 1-6 1.5L2 8l6-1zM3.845 2.052l1.103 3.845L1.103 7 0 3.155l3.845-1.103zm10.561-1.014L16 6.396l-4.924-.868 3.33-4.49zM8 0a2.5 2.5 0 110 5 2.5 2.5 0 010-5z" }))); };

var SvgIconSave = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M11.586 0a1 1 0 01.7.286l3.421 3.421a1 1 0 01.293.707V15a1 1 0 01-1 1H1a1 1 0 01-1-1V1a1 1 0 011-1h10.586zM8 7a3 3 0 100 6 3 3 0 000-6zm3-5H2v3h9V2z" }))); };

var SvgIconSchedule = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M11 2H9V1a1 1 0 10-2 0v1H5V1a1 1 0 10-2 0v1H1a1 1 0 00-1 1v8a1 1 0 001 1h10a1 1 0 001-1V3a1 1 0 00-1-1m2 2v8a1 1 0 01-1 1H2a1 1 0 001 1h10a1 1 0 001-1V5a1 1 0 00-1-1m3 3v8a1 1 0 01-1 1H5a1 1 0 01-1-1h10a1 1 0 001-1V6a1 1 0 011 1" }))); };

var SvgIconSearch = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M6 10a4 4 0 110-8 4 4 0 010 8m9.293 3.293l-2.126-2.126a.999.999 0 00-.391-.242l-1.052-.35a1.013 1.013 0 01-.391-.242l-.623-.623a5.986 5.986 0 001.194-4.792C11.459 2.389 9.349.382 6.801.052a6.005 6.005 0 00-6.749 6.75c.33 2.547 2.337 4.657 4.866 5.103A5.993 5.993 0 009.71 10.71l.623.623a1 1 0 01.241.391l.352 1.052c.049.147.132.281.241.391l2.126 2.126a.999.999 0 001.414 0l.586-.586a.999.999 0 000-1.414" }))); };

var SvgIconSendToCampaigns = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14 0a2 2 0 012 2v12a2 2 0 01-2 2H2a2 2 0 01-2-2V4.828a2 2 0 01.586-1.414L3.414.586A2 2 0 014.828 0zm-1 2H6v3a1 1 0 01-1 1H2v7a1 1 0 001 1h10a1 1 0 001-1V3a1 1 0 00-1-1zM7.793 5.793A1 1 0 019.01 5.64l.102.07.094.083 2.5 2.5.042.044.008.01.03.036-.008-.009.025.03.026.037.024.038.021.037.016.03.015.03.023.053.011.03.01.03A1.071 1.071 0 0112 9l-.004.085-.007.064-.004.026-.005.026a.94.94 0 01-.051.17l-.023.052-.015.031-.016.03a.878.878 0 01-.095.141l-.073.082-2.5 2.5-.094.083a1 1 0 01-1.403-1.403l.083-.094.791-.793H5l-.117-.007A1 1 0 015 8h3.585l-.792-.793-.083-.094a1 1 0 01.083-1.32z" }))); };

var SvgIconSettings = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8 10.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5M14 9h2V7h-2c-1 0-1.379-.914-1.379-.914s-.379-.915.328-1.622l1.414-1.414-1.414-1.414-1.414 1.414c-.707.707-1.621.329-1.621.329S9 3 9 2V0H7v2c0 1-.914 1.379-.914 1.379s-.915.378-1.622-.329L3.05 1.636 1.636 3.05 3.05 4.464c.707.707.329 1.622.329 1.622S3 7 2 7H0v2h2c1 0 1.379.914 1.379.914s.378.915-.329 1.622L1.636 12.95l1.414 1.414 1.414-1.414c.707-.707 1.622-.329 1.622-.329S7 13 7 14v2h2v-2c0-1 .914-1.379.914-1.379s.915-.378 1.622.329l1.414 1.414 1.414-1.414-1.414-1.414c-.707-.707-.329-1.622-.329-1.622S13 9 14 9" }))); };

var SvgIconSidebar = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M10 14V2h4v12h-4zM0 16V0h16v16H0zm1-1h14V1H1v14z" }))); };

var SvgIconStar = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8 0L6.111 6.111H0l4.944 3.777L3.055 16 8 12.224 12.945 16l-1.89-6.112L16 6.111H9.888z" }))); };

var SvgIconStatusAlert = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M9 12a1 1 0 11-2 0 1 1 0 112 0zm-1.5-1.5S7 4.553 7 4a1 1 0 112 0c0 .553-.5 6.5-.5 6.5h-1zM11 0H5L0 5v6l5 5h6l5-5V5l-5-5z" }))); };

var SvgIconStatusBan = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8 14a5.97 5.97 0 01-3.473-1.113l8.36-8.36A5.968 5.968 0 0114 8a6 6 0 01-6 6M2 8a6 6 0 016-6c1.296 0 2.492.415 3.473 1.113l-8.36 8.36A5.968 5.968 0 012 8m6-8a8 8 0 100 16A8 8 0 008 0" }))); };

var SvgIconStatusDot = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("circle", { cx: 8, cy: 8, r: 6 }))); };

var SvgIconStatusDotOutline = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8 2a6 6 0 110 12A6 6 0 018 2zm0 2a4 4 0 100 8 4 4 0 000-8z" }))); };

var SvgIconStatusPending = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M10.354 11.354a.5.5 0 000-.707L8.5 8.793V5.5a.5.5 0 10-1 0v3.707l2.146 2.146a.497.497 0 00.707 0zM4 0S2 0 1 1 0 4 0 4l1 1 4-4-1-1zm7 1l4 4 1-1s0-2-1-3-3-1-3-1l-1 1zM8 2a7 7 0 100 14A7 7 0 008 2zm0 2c2.757 0 5 2.244 5 5 0 2.757-2.243 5-5 5-2.756 0-5-2.243-5-5 0-2.756 2.244-5 5-5z" }))); };

var SvgIconStatusWarning = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M9 12.998a1 1 0 11-2 0 1 1 0 112 0zm-1.5-1.5S7 5.551 7 4.998a1 1 0 112 0c0 .553-.5 6.5-.5 6.5h-1zm8.222.499h.009L9.732 1.605H9.72A1.993 1.993 0 008 .61c-.736 0-1.373.403-1.72.995h-.012L.269 11.997h.009A1.969 1.969 0 000 12.998a2 2 0 002 2h12a2 2 0 002-2c0-.367-.105-.706-.278-1.001z" }))); };

var SvgIconStop = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14 0a2 2 0 012 2v12a2 2 0 01-2 2H2a2 2 0 01-2-2V2a2 2 0 012-2h12zM5 11h6V5H5v6z" }))); };

var SvgIconTable = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M16 1v14H0V1h16zm-.999 3H11v10h4.001V4zM10 4H6v10h4V4zM5.001 4H1v10h4.001V4z" }))); };

var SvgIconTarget = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14.83 4.558L14.533 5H13.19c.512.883.81 1.907.81 3a6 6 0 11-6-6c1.094 0 2.117.298 3 .81V1.465l.445-.297.331-.22a8 8 0 103.277 3.278l-.223.332zM7.646 8.354a.498.498 0 00.707 0L12.707 4H14l2-2.98L15 1V0l-3 2v1.293L7.646 7.647a.5.5 0 000 .707zM6 8a2 2 0 002 2 1.994 1.994 0 001.99-1.892c.021-.02.05-.026.07-.047l1.623-1.623a4 4 0 11-2.122-2.121L7.94 5.94c-.02.02-.027.049-.047.07A1.996 1.996 0 006 8z" }))); };

var SvgIconThumbnail = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M6 10h4V6H6v4zm0-5h4V1H6v4zm5 5h4V6h-4v4zm0 5h4v-4h-4v4zm-5 0h4v-4H6v4zm-5 0h4v-4H1v4zm0-5h4V6H1v4zm0-5h4V1H1v4zm10 0h4V1h-4v4z" }))); };

var SvgIconTime = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M10.354 10.354a.5.5 0 000-.707L8.5 7.792V3.999a.5.5 0 10-1 0v4.208l2.146 2.146a.497.497 0 00.707 0M8 0A8.001 8.001 0 108 16.002 8.001 8.001 0 008 0m0 2c3.309 0 6 2.692 6 6 0 3.31-2.691 6-6 6s-6-2.69-6-6C2 4.692 4.69 2 8 2" }))); };

var SvgIconTools = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14.5 15a.5.5 0 110-1 .5.5 0 010 1zM9.299 7.3c.222.119.457.2.701.2.384 0 .768-.146 1.061-.439l4.5-4.5a1.502 1.502 0 000-2.122 1.502 1.502 0 00-2.122 0l-4.5 4.5c-.479.48-.544 1.194-.239 1.761L7.64 5.641C7.867 5.139 8 4.586 8 4a4 4 0 00-4-4c-.586 0-1.139.133-1.641.359L5 3v2H3L.359 2.359A3.984 3.984 0 000 4a4 4 0 004 4c.586 0 1.139-.133 1.64-.359l.865.864-3.5 3.5L3 12H2l-2 3 1 1 3-2-.001-.002L4 13l-.005-.005 3.5-3.5 6.091 6.091a1.415 1.415 0 002-2L9.299 7.3z" }))); };

var SvgIconTrash = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M7 2h2v-.987H7V2zm5.5 0H10V.75A.752.752 0 009.25 0h-2.5A.752.752 0 006 .75V2H3.5c-.55 0-1 .45-1 1v1h11V3c0-.55-.45-1-1-1zM4 7a1 1 0 012 0v6a1 1 0 01-2 0V7zm6 0a1 1 0 112 0v6a1 1 0 11-2 0V7zM7 7a1 1 0 012 0v6a1 1 0 01-2 0V7zm-4 8a1 1 0 001 1h8a1 1 0 001-1V4.5H3V15z" }))); };

var SvgIconTriangleDown = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14 4H2l6.001 10z" }))); };

var SvgIconTriangleRight = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M4 2v12l10-6.001z" }))); };

var SvgIconUnlink = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M7.408 2.936c.225.573.35 1.187.35 1.821 0 .248-.037.488-.072.728l2.9-2.899A1.989 1.989 0 0112 2c.534 0 1.036.208 1.414.586.378.378.586.88.586 1.414 0 .534-.208 1.037-.586 1.414l-2.9 2.9c.24-.035.48-.071.728-.071a4.97 4.97 0 011.82.35l1.766-1.764a4 4 0 10-5.657-5.658L7.408 2.936zm1.185 10.128a4.995 4.995 0 01-.35-1.821c0-.247.036-.488.07-.728l-2.898 2.9A1.99 1.99 0 014 14a1.988 1.988 0 01-1.414-.585A1.99 1.99 0 012 12c0-.534.209-1.036.586-1.414l2.899-2.9c-.24.035-.48.071-.727.071a4.98 4.98 0 01-1.822-.35L1.172 9.172a4 4 0 005.657 5.657l1.764-1.765zm5.5-2.296l-2.5-1a.255.255 0 00-.325.139.252.252 0 00.14.325l2.5 1a.25.25 0 00.325-.139.251.251 0 00-.14-.325zm-4.185.5a.252.252 0 00-.14.325l1 2.5a.251.251 0 00.326.139.251.251 0 00.139-.325l-1-2.5a.253.253 0 00-.325-.139zm1.27-.445a.25.25 0 10-.354.354l1.975 1.974a.246.246 0 00.353 0 .249.249 0 000-.353l-1.974-1.975zm-9.27-5.591l2.5 1a.25.25 0 00.325-.139.251.251 0 00-.14-.325l-2.5-1a.255.255 0 00-.325.139.252.252 0 00.14.325zM6 4.75a.251.251 0 00.232-.343l-1-2.5a.253.253 0 00-.325-.139.252.252 0 00-.14.325l1 2.5A.251.251 0 006 4.75zM2.849 3.202a.25.25 0 01.354-.353l1.975 1.974a.252.252 0 010 .354.249.249 0 01-.354 0L2.849 3.202z" }))); };

var SvgIconUpload = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M14 10v4H2v-4a1 1 0 10-2 0v4a2 2 0 002 2h12a2 2 0 002-2v-4a1 1 0 10-2 0zm-1-4h-3v5a1 1 0 01-1 1H7a1 1 0 01-1-1V6H3l5-6 5 6z" }))); };

var SvgIconUser = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M16 15v1H0v-1c0-1.687 1.367-2.245 3.473-3.046 2.097-.799 2.769-1.473 2.769-2.915 0-.866-.71-.703-.992-2.289-.116-.657-.643.002-.75-1.5 0-.598.5-.5.5-.5S4.811 3.682 4.75 3c-.077-.849.338-3 3.25-3s3.327 2.151 3.25 3c-.061.682-.25 1.75-.25 1.75s.5-.098.5.5c-.107 1.502-.634.843-.75 1.5-.282 1.586-.992 1.423-.992 2.289 0 1.442.672 2.116 2.769 2.915C14.633 12.755 16 13.313 16 15" }))); };

var SvgIconView = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M8 12c-3.303 0-5.832-2.8-6.766-4.002.57-.736 1.736-2.068 3.317-2.991A3.97 3.97 0 003.999 7c0 2.21 1.79 4 4 4 2.212 0 4-1.79 4-4 0-.727-.207-1.398-.546-1.984 1.574.923 2.744 2.252 3.315 2.986C13.835 9.205 11.315 12 7.998 12m1-7.5A1.5 1.5 0 119 7.5a1.5 1.5 0 010-3.001M8 3C3.2 3 0 8 0 8s3.2 5 8 5c4.801 0 8-5 8-5S12.8 3 8 3" }))); };

var SvgIconWarRoom = function (props) { return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "currentColor" }, props),
    React.createElement("path", { d: "M4 10.995a.965.965 0 00-.494.15 5.245 5.245 0 01.605-6.686C6.04 2.53 9.084 2.44 11.146 4.141l-.707.708c-1.569-1.218-3.827-1.124-5.268.317a4.002 4.002 0 005.658 5.658 3.998 3.998 0 00.48-5.069 3.752 3.752 0 01-5.784 4.715 3.25 3.25 0 010-4.596 3.24 3.24 0 014.207-.318L8.245 7.043a.984.984 0 00-.952.245.999.999 0 101.414 1.414.984.984 0 00.245-.952l2.194-2.194c.053.067.116.13.163.2-.046-.072-.111-.133-.163-.2l1.415-1.415c1.895 2.26 1.806 5.62-.318 7.743-2.015 2.015-5.146 2.207-7.392.605A.974.974 0 005 11.995a1 1 0 00-1-1m-1.303 2.303a7.249 7.249 0 010-10.253C5.409.333 9.73.226 12.578 2.71l-.724.725c-2.358-1.992-5.875-1.904-8.097.318-2.112 2.113-2.302 5.4-.605 7.744a.973.973 0 00-.152.5 1 1 0 001 1c.186 0 .35-.065.498-.153 2.346 1.696 5.632 1.507 7.745-.605 2.222-2.222 2.31-5.739.318-8.097l1.432-1.432c2.669 3.036 2.57 7.653-.311 10.562a.99.99 0 00-.682-.276 1 1 0 00-1 1c0 .176.058.333.137.476-2.975 1.774-6.879 1.388-9.44-1.173M13.993 2.71l.017-.017-.707-.707-.018.017C10.144-.773 5.348-.667 2.343 2.338a8 8 0 000 11.314 8 8 0 009.981 1.071.987.987 0 00.676.272 1 1 0 001-1 .966.966 0 00-.174-.53 7.99 7.99 0 00.167-10.756" }))); };

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".acl-Icon {\n  display: inline-block;\n  line-height: 1;\n  vertical-align: middle; }\n";
styleInject(css_248z);

/**
 * NOTE:
 * When adding new icons, add them to the _icons/svg folder and then run `npm run build:icons`.
 * This command will run the script to create the icon components among other things. Once it's complete,
 * it'll spit out a switch statement to console that can be pasted below.
 *
 * DOUBLE NOTE:
 * Make sure the icon file names are the same as the rest. E.g. `IconName.svg`.
 */
var IconSize;
(function (IconSize) {
    IconSize[IconSize["base"] = 16] = "base";
    IconSize[IconSize["medium"] = 24] = "medium";
    IconSize[IconSize["large"] = 32] = "large";
})(IconSize || (IconSize = {}));
var Icon = function (_a) {
    var _b = _a.size, size = _b === void 0 ? IconSize.base : _b, props = __rest(_a, ["size"]);
    var propsToPass = {
        className: "acl-Icon " + (props.className || '') + " acl-Icon-" + props.name,
        style: __assign(__assign({}, props.style), { fontSize: size, color: props.color })
    };
    var iconComponents = {
        ActivityLog: React.createElement(SvgIconActivityLog, __assign({}, propsToPass)),
        Add: React.createElement(SvgIconAdd, __assign({}, propsToPass)),
        Archived: React.createElement(SvgIconArchived, __assign({}, propsToPass)),
        ArrowDown: React.createElement(SvgIconArrowDown, __assign({}, propsToPass)),
        ArrowLeft: React.createElement(SvgIconArrowLeft, __assign({}, propsToPass)),
        ArrowOver: React.createElement(SvgIconArrowOver, __assign({}, propsToPass)),
        ArrowRight: React.createElement(SvgIconArrowRight, __assign({}, propsToPass)),
        ArrowUnder: React.createElement(SvgIconArrowUnder, __assign({}, propsToPass)),
        ArrowUp: React.createElement(SvgIconArrowUp, __assign({}, propsToPass)),
        Assign: React.createElement(SvgIconAssign, __assign({}, propsToPass)),
        Attachment: React.createElement(SvgIconAttachment, __assign({}, propsToPass)),
        AutoFix: React.createElement(SvgIconAutoFix, __assign({}, propsToPass)),
        Back: React.createElement(SvgIconBack, __assign({}, propsToPass)),
        Branch: React.createElement(SvgIconBranch, __assign({}, propsToPass)),
        Broadcaster: React.createElement(SvgIconBroadcaster, __assign({}, propsToPass)),
        Budget: React.createElement(SvgIconBudget, __assign({}, propsToPass)),
        Bullet: React.createElement(SvgIconBullet, __assign({}, propsToPass)),
        ChannelAmobeeSocial: React.createElement(SvgIconChannelAmobeeSocial, __assign({}, propsToPass)),
        ChannelAudio: React.createElement(SvgIconChannelAudio, __assign({}, propsToPass)),
        ChannelBanner: React.createElement(SvgIconChannelBanner, __assign({}, propsToPass)),
        ChannelDisplay: React.createElement(SvgIconChannelDisplay, __assign({}, propsToPass)),
        ChannelSocial: React.createElement(SvgIconChannelSocial, __assign({}, propsToPass)),
        ChannelVideo: React.createElement(SvgIconChannelVideo, __assign({}, propsToPass)),
        ChartBar: React.createElement(SvgIconChartBar, __assign({}, propsToPass)),
        ChartBarBi: React.createElement(SvgIconChartBarBi, __assign({}, propsToPass)),
        ChartColumn: React.createElement(SvgIconChartColumn, __assign({}, propsToPass)),
        ChartLine: React.createElement(SvgIconChartLine, __assign({}, propsToPass)),
        ChartPie: React.createElement(SvgIconChartPie, __assign({}, propsToPass)),
        Check: React.createElement(SvgIconCheck, __assign({}, propsToPass)),
        CheckboxCheck: React.createElement(SvgIconCheckboxCheck, __assign({}, propsToPass)),
        CheckboxIndeterminate: React.createElement(SvgIconCheckboxIndeterminate, __assign({}, propsToPass)),
        CheckboxNone: React.createElement(SvgIconCheckboxNone, __assign({}, propsToPass)),
        ChevronDown: React.createElement(SvgIconChevronDown, __assign({}, propsToPass)),
        ChevronLeft: React.createElement(SvgIconChevronLeft, __assign({}, propsToPass)),
        ChevronRight: React.createElement(SvgIconChevronRight, __assign({}, propsToPass)),
        ChevronUp: React.createElement(SvgIconChevronUp, __assign({}, propsToPass)),
        CloseLarge: React.createElement(SvgIconCloseLarge, __assign({}, propsToPass)),
        CloseSmall: React.createElement(SvgIconCloseSmall, __assign({}, propsToPass)),
        CloudUpload: React.createElement(SvgIconCloudUpload, __assign({}, propsToPass)),
        ColumnSettings: React.createElement(SvgIconColumnSettings, __assign({}, propsToPass)),
        Copy: React.createElement(SvgIconCopy, __assign({}, propsToPass)),
        Creatives: React.createElement(SvgIconCreatives, __assign({}, propsToPass)),
        CurrencyEuro: React.createElement(SvgIconCurrencyEuro, __assign({}, propsToPass)),
        CurrencyPound: React.createElement(SvgIconCurrencyPound, __assign({}, propsToPass)),
        CurrencyUsDollar: React.createElement(SvgIconCurrencyUsDollar, __assign({}, propsToPass)),
        CurrencyYen: React.createElement(SvgIconCurrencyYen, __assign({}, propsToPass)),
        Data: React.createElement(SvgIconData, __assign({}, propsToPass)),
        Date: React.createElement(SvgIconDate, __assign({}, propsToPass)),
        Deal: React.createElement(SvgIconDeal, __assign({}, propsToPass)),
        Delete: React.createElement(SvgIconDelete, __assign({}, propsToPass)),
        Details: React.createElement(SvgIconDetails, __assign({}, propsToPass)),
        DeviceDesktop: React.createElement(SvgIconDeviceDesktop, __assign({}, propsToPass)),
        DeviceLaptop: React.createElement(SvgIconDeviceLaptop, __assign({}, propsToPass)),
        DevicePhone: React.createElement(SvgIconDevicePhone, __assign({}, propsToPass)),
        DeviceTablet: React.createElement(SvgIconDeviceTablet, __assign({}, propsToPass)),
        Download: React.createElement(SvgIconDownload, __assign({}, propsToPass)),
        Draft: React.createElement(SvgIconDraft, __assign({}, propsToPass)),
        Edit: React.createElement(SvgIconEdit, __assign({}, propsToPass)),
        EditBulk: React.createElement(SvgIconEditBulk, __assign({}, propsToPass)),
        Education: React.createElement(SvgIconEducation, __assign({}, propsToPass)),
        Email: React.createElement(SvgIconEmail, __assign({}, propsToPass)),
        Export: React.createElement(SvgIconExport, __assign({}, propsToPass)),
        Fav: React.createElement(SvgIconFav, __assign({}, propsToPass)),
        Favorite: React.createElement(SvgIconFavorite, __assign({}, propsToPass)),
        Feed: React.createElement(SvgIconFeed, __assign({}, propsToPass)),
        Filter: React.createElement(SvgIconFilter, __assign({}, propsToPass)),
        Forward: React.createElement(SvgIconForward, __assign({}, propsToPass)),
        Frequency: React.createElement(SvgIconFrequency, __assign({}, propsToPass)),
        Globe: React.createElement(SvgIconGlobe, __assign({}, propsToPass)),
        Grip: React.createElement(SvgIconGrip, __assign({}, propsToPass)),
        Health: React.createElement(SvgIconHealth, __assign({}, propsToPass)),
        Help: React.createElement(SvgIconHelp, __assign({}, propsToPass)),
        Hold: React.createElement(SvgIconHold, __assign({}, propsToPass)),
        Import: React.createElement(SvgIconImport, __assign({}, propsToPass)),
        Info: React.createElement(SvgIconInfo, __assign({}, propsToPass)),
        InlineAdd: React.createElement(SvgIconInlineAdd, __assign({}, propsToPass)),
        Launch: React.createElement(SvgIconLaunch, __assign({}, propsToPass)),
        LightBulb: React.createElement(SvgIconLightBulb, __assign({}, propsToPass)),
        Link: React.createElement(SvgIconLink, __assign({}, propsToPass)),
        List: React.createElement(SvgIconList, __assign({}, propsToPass)),
        ListHierarchical: React.createElement(SvgIconListHierarchical, __assign({}, propsToPass)),
        Location: React.createElement(SvgIconLocation, __assign({}, propsToPass)),
        LockClosed: React.createElement(SvgIconLockClosed, __assign({}, propsToPass)),
        LockOpen: React.createElement(SvgIconLockOpen, __assign({}, propsToPass)),
        Menu: React.createElement(SvgIconMenu, __assign({}, propsToPass)),
        Note: React.createElement(SvgIconNote, __assign({}, propsToPass)),
        Notifications: React.createElement(SvgIconNotifications, __assign({}, propsToPass)),
        Options: React.createElement(SvgIconOptions, __assign({}, propsToPass)),
        Overflow: React.createElement(SvgIconOverflow, __assign({}, propsToPass)),
        Pause: React.createElement(SvgIconPause, __assign({}, propsToPass)),
        Pixel: React.createElement(SvgIconPixel, __assign({}, propsToPass)),
        Play: React.createElement(SvgIconPlay, __assign({}, propsToPass)),
        PrivateMarketplace: React.createElement(SvgIconPrivateMarketplace, __assign({}, propsToPass)),
        Process: React.createElement(SvgIconProcess, __assign({}, propsToPass)),
        ProductAudiences: React.createElement(SvgIconProductAudiences, __assign({}, propsToPass)),
        ProductBi: React.createElement(SvgIconProductBi, __assign({}, propsToPass)),
        ProductCampaigns: React.createElement(SvgIconProductCampaigns, __assign({}, propsToPass)),
        ProductDashboard: React.createElement(SvgIconProductDashboard, __assign({}, propsToPass)),
        ProductInsights: React.createElement(SvgIconProductInsights, __assign({}, propsToPass)),
        ProductMediaPlans: React.createElement(SvgIconProductMediaPlans, __assign({}, propsToPass)),
        RadioNone: React.createElement(SvgIconRadioNone, __assign({}, propsToPass)),
        RadioSelect: React.createElement(SvgIconRadioSelect, __assign({}, propsToPass)),
        Reload: React.createElement(SvgIconReload, __assign({}, propsToPass)),
        Remove: React.createElement(SvgIconRemove, __assign({}, propsToPass)),
        Report: React.createElement(SvgIconReport, __assign({}, propsToPass)),
        Resources: React.createElement(SvgIconResources, __assign({}, propsToPass)),
        Save: React.createElement(SvgIconSave, __assign({}, propsToPass)),
        Schedule: React.createElement(SvgIconSchedule, __assign({}, propsToPass)),
        Search: React.createElement(SvgIconSearch, __assign({}, propsToPass)),
        SendToCampaigns: React.createElement(SvgIconSendToCampaigns, __assign({}, propsToPass)),
        Settings: React.createElement(SvgIconSettings, __assign({}, propsToPass)),
        Sidebar: React.createElement(SvgIconSidebar, __assign({}, propsToPass)),
        Star: React.createElement(SvgIconStar, __assign({}, propsToPass)),
        StatusAlert: React.createElement(SvgIconStatusAlert, __assign({}, propsToPass)),
        StatusBan: React.createElement(SvgIconStatusBan, __assign({}, propsToPass)),
        StatusDot: React.createElement(SvgIconStatusDot, __assign({}, propsToPass)),
        StatusDotOutline: React.createElement(SvgIconStatusDotOutline, __assign({}, propsToPass)),
        StatusPending: React.createElement(SvgIconStatusPending, __assign({}, propsToPass)),
        StatusWarning: React.createElement(SvgIconStatusWarning, __assign({}, propsToPass)),
        Stop: React.createElement(SvgIconStop, __assign({}, propsToPass)),
        Table: React.createElement(SvgIconTable, __assign({}, propsToPass)),
        Target: React.createElement(SvgIconTarget, __assign({}, propsToPass)),
        Thumbnail: React.createElement(SvgIconThumbnail, __assign({}, propsToPass)),
        Time: React.createElement(SvgIconTime, __assign({}, propsToPass)),
        Tools: React.createElement(SvgIconTools, __assign({}, propsToPass)),
        Trash: React.createElement(SvgIconTrash, __assign({}, propsToPass)),
        TriangleDown: React.createElement(SvgIconTriangleDown, __assign({}, propsToPass)),
        TriangleRight: React.createElement(SvgIconTriangleRight, __assign({}, propsToPass)),
        Unlink: React.createElement(SvgIconUnlink, __assign({}, propsToPass)),
        Upload: React.createElement(SvgIconUpload, __assign({}, propsToPass)),
        User: React.createElement(SvgIconUser, __assign({}, propsToPass)),
        View: React.createElement(SvgIconView, __assign({}, propsToPass)),
        WarRoom: React.createElement(SvgIconWarRoom, __assign({}, propsToPass)),
    };
    if (iconComponents[props.name]) {
        return iconComponents[props.name];
    }
    if (props.name !== undefined) {
        console.error("Couldn't find the icon " + props.name + " in the icon list. If it was recently added, please run \"npm run build-icons\" and follow the instructions.");
    }
    return null;
};

var IconNames = ['ActivityLog', 'Add', 'Archived', 'ArrowDown', 'ArrowLeft', 'ArrowOver', 'ArrowRight', 'ArrowUnder', 'ArrowUp', 'Assign', 'Attachment', 'AutoFix', 'Back', 'Branch', 'Broadcaster', 'Budget', 'Bullet', 'ChannelAmobeeSocial', 'ChannelAudio', 'ChannelBanner', 'ChannelDisplay', 'ChannelSocial', 'ChannelVideo', 'ChartBar', 'ChartBarBi', 'ChartColumn', 'ChartLine', 'ChartPie', 'Check', 'CheckboxCheck', 'CheckboxIndeterminate', 'CheckboxNone', 'ChevronDown', 'ChevronLeft', 'ChevronRight', 'ChevronUp', 'CloseLarge', 'CloseSmall', 'CloudUpload', 'ColumnSettings', 'Copy', 'Creatives', 'CurrencyEuro', 'CurrencyPound', 'CurrencyUsDollar', 'CurrencyYen', 'Data', 'Date', 'Deal', 'Delete', 'Details', 'DeviceDesktop', 'DeviceLaptop', 'DevicePhone', 'DeviceTablet', 'Download', 'Draft', 'Edit', 'EditBulk', 'Education', 'Email', 'Export', 'Fav', 'Favorite', 'Feed', 'Filter', 'Forward', 'Frequency', 'Globe', 'Grip', 'Health', 'Help', 'Hold', 'Import', 'Info', 'InlineAdd', 'Launch', 'LightBulb', 'Link', 'List', 'ListHierarchical', 'Location', 'LockClosed', 'LockOpen', 'Menu', 'Note', 'Notifications', 'Options', 'Overflow', 'Pause', 'Pixel', 'Play', 'PrivateMarketplace', 'Process', 'ProductAudiences', 'ProductBi', 'ProductCampaigns', 'ProductDashboard', 'ProductInsights', 'ProductMediaPlans', 'RadioNone', 'RadioSelect', 'Reload', 'Remove', 'Report', 'Resources', 'Save', 'Schedule', 'Search', 'SendToCampaigns', 'Settings', 'Sidebar', 'Star', 'StatusAlert', 'StatusBan', 'StatusDot', 'StatusDotOutline', 'StatusPending', 'StatusWarning', 'Stop', 'Table', 'Target', 'Thumbnail', 'Time', 'Tools', 'Trash', 'TriangleDown', 'TriangleRight', 'Unlink', 'Upload', 'User', 'View', 'WarRoom'];

var css_248z$1 = ".acl-Accordion {\n  font-family: \"Roboto\", sans-serif; }\n  .acl-Accordion .acl-Accordion-title {\n    width: 100%;\n    font-weight: 600;\n    display: flex;\n    align-items: center;\n    font-size: 14px;\n    padding: 8px 12px 0;\n    flex-direction: row; }\n    .acl-Accordion .acl-Accordion-title .acl-Accordion-icon {\n      cursor: pointer;\n      display: inline-flex;\n      margin-right: 8px;\n      max-width: 13px; }\n      .acl-Accordion .acl-Accordion-title .acl-Accordion-icon > svg {\n        transition: all 0.3s ease;\n        width: 100%;\n        fill: #777777; }\n    .acl-Accordion .acl-Accordion-title .acl-Accordion-titleText {\n      cursor: pointer;\n      flex-grow: 1;\n      padding-right: 12px; }\n    .acl-Accordion .acl-Accordion-title .acl-Accordion-leftElement {\n      margin-right: 8px; }\n  .acl-Accordion .acl-Accordion-summary {\n    padding: 4px 12px;\n    color: #777777; }\n  .acl-Accordion .acl-Accordion-body {\n    transition: all 0.3s ease;\n    overflow: hidden;\n    opacity: 0.3;\n    max-height: 0; }\n    .acl-Accordion .acl-Accordion-body .acl-Accordion-content {\n      padding: 8px 12px; }\n  .acl-Accordion.open > .acl-Accordion-title {\n    color: #009ebd; }\n    .acl-Accordion.open > .acl-Accordion-title > .acl-Accordion-icon svg {\n      transform: rotate(90deg); }\n  .acl-Accordion.open > .acl-Accordion-body {\n    opacity: 1;\n    max-height: 100vh; }\n";
styleInject(css_248z$1);

var Accordion = function (_a) {
    var open = _a.open, title = _a.title, children = _a.children, summaryItems = _a.summaryItems, _b = _a.leftElementColor, leftElementColor = _b === void 0 ? ColorTheme.gray50 : _b, iconName = _a.iconName, number = _a.number, renderRightElement = _a.renderRightElement;
    if (iconName && number) {
        throw new Error('Using both `iconName` and `number` is not supported');
    }
    var _c = useState(open || false), isOpen = _c[0], setIsOpen = _c[1];
    var toggleAccordion = function () {
        setIsOpen(!isOpen);
    };
    var getRightElement = function () {
        var rightElement = safeInvoke(renderRightElement);
        if (!rightElement)
            return null;
        return React.createElement("div", { className: "acl-Accordion-rightElement" }, rightElement);
    };
    var renderLeftElement = function () {
        if (!number && !iconName)
            return null;
        if (number) {
            return (React.createElement("div", { className: "acl-Accordion-leftElement" },
                React.createElement("span", { style: { color: leftElementColor } }, number)));
        }
        if (iconName) {
            return (React.createElement("div", { className: "acl-Accordion-leftElement" },
                React.createElement(Icon, { name: iconName, color: leftElementColor })));
        }
    };
    return (React.createElement("div", { className: classNames('acl-Accordion', { open: isOpen }) },
        React.createElement("div", { className: "acl-Accordion-title" },
            React.createElement("span", { className: "acl-Accordion-icon", onClick: toggleAccordion },
                React.createElement(Icon, { name: "TriangleRight", color: ColorTheme.gray30 })),
            renderLeftElement(),
            React.createElement("span", { className: "acl-Accordion-titleText", onClick: toggleAccordion }, title),
            getRightElement()),
        !isOpen && (React.createElement("div", { className: "acl-Accordion-summary" }, summaryItems &&
            summaryItems.map(function (item, i) { return [
                i > 0 && ', ',
                item.label
            ]; }))),
        React.createElement("div", { className: "acl-Accordion-body" },
            React.createElement("div", { className: "acl-Accordion-content" }, children))));
};

var css_248z$2 = ".acl-Button {\n  display: inline-block;\n  box-sizing: border-box;\n  margin-bottom: 0;\n  padding-left: 16px;\n  padding-right: 16px;\n  padding-top: 8px;\n  padding-bottom: 8px;\n  height: calc(16px + (16px));\n  line-height: 16px;\n  font-family: \"Roboto\", sans-serif;\n  font-size: 14px;\n  font-weight: bold;\n  color: rgba(0, 0, 0, 0.5);\n  text-align: center;\n  text-shadow: none;\n  vertical-align: middle;\n  white-space: nowrap;\n  border: none;\n  border-radius: 4px;\n  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.3);\n  background-color: transparent;\n  background-image: none;\n  cursor: pointer;\n  user-select: none;\n  /** PRIMARY BUTTONS **/\n  /** STATIC BORDERLESS BUTTONS **/\n  /** BORDERLESS BUTTONS **/\n  /** UNDERLINE BUTTONS **/\n  /** TABLE BUTTONS **/\n  /** TOGGLE BUTTONS **/\n  /** DARK MODE **/\n  /** Compact Sizing **/ }\n  .acl-Button > span {\n    display: block;\n    width: 100%;\n    position: relative; }\n  .acl-Button.disabled {\n    cursor: not-allowed;\n    opacity: 0.3; }\n  .acl-Button:not(.disabled):hover, .acl-Button:not(.disabled).hover {\n    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.3); }\n    .acl-Button:not(.disabled):hover:not(:focus):not(.focus), .acl-Button:not(.disabled).hover:not(:focus):not(.focus) {\n      background-color: rgba(255, 255, 255, 0.3); }\n      .acl-Button:not(.disabled):hover:not(:focus):not(.focus):not(:active):not(.active), .acl-Button:not(.disabled).hover:not(:focus):not(.focus):not(:active):not(.active) {\n        transition: background-color ease-in 150ms; }\n  .acl-Button:not(.disabled):active, .acl-Button:not(.disabled).active {\n    background-color: rgba(0, 0, 0, 0.1);\n    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.3), inset 0 4px 1px 0 rgba(0, 0, 0, 0.05);\n    transition: background-color linear 0ms; }\n  .acl-Button:not(.disabled):focus, .acl-Button:not(.disabled).focus {\n    outline: 0; }\n    .acl-Button:not(.disabled):focus:not(:active):not(.active), .acl-Button:not(.disabled).focus:not(:active):not(.active) {\n      background-color: none; }\n      .acl-Button:not(.disabled):focus:not(:active):not(.active):not(.error), .acl-Button:not(.disabled).focus:not(:active):not(.active):not(.error) {\n        box-shadow: inset 0 0 0 1px #40b6ce; }\n  .acl-Button:not(.disabled).error {\n    box-shadow: inset 0 0 0 1px #e75556; }\n  .acl-Button.icon {\n    line-height: 16px;\n    padding-left: 8px;\n    padding-right: 8px;\n    padding-top: 8px;\n    padding-bottom: 8px; }\n    .acl-Button.icon > svg {\n      vertical-align: baseline !important; }\n  .acl-Button.iconText, .acl-Button.textIcon {\n    font-weight: normal;\n    color: rgba(0, 0, 0, 0.7);\n    padding-top: 8px;\n    padding-bottom: 8px; }\n    .acl-Button.iconText > span > svg, .acl-Button.textIcon > span > svg {\n      position: absolute;\n      margin: 0; }\n  .acl-Button.iconText {\n    padding-left: 12px;\n    padding-right: 12px; }\n    .acl-Button.iconText > span {\n      height: 16px;\n      padding-left: calc(12px + 16px); }\n      .acl-Button.iconText > span > svg {\n        left: 0; }\n  .acl-Button.textIcon {\n    padding-left: 12px;\n    padding-right: 12px;\n    text-align: left; }\n    .acl-Button.textIcon > span {\n      height: 16px;\n      padding-right: calc(12px + 16px); }\n      .acl-Button.textIcon > span > svg {\n        right: 0; }\n  .acl-Button.btnPrimary {\n    color: #ffffff;\n    background: #009ebd;\n    box-shadow: none;\n    outline: 0;\n    border-width: 0; }\n    .acl-Button.btnPrimary:not(.disabled):hover, .acl-Button.btnPrimary:not(.disabled).hover {\n      box-shadow: none;\n      background-color: #40b6ce; }\n      .acl-Button.btnPrimary:not(.disabled):hover:not(:focus):not(.focus), .acl-Button.btnPrimary:not(.disabled).hover:not(:focus):not(.focus) {\n        background-color: #40b6ce; }\n    .acl-Button.btnPrimary:not(.disabled):active, .acl-Button.btnPrimary:not(.disabled).active {\n      background-color: #00849e;\n      box-shadow: none; }\n    .acl-Button.btnPrimary:not(.disabled):focus:not(:active):not(.active), .acl-Button.btnPrimary:not(.disabled).focus:not(:active):not(.active) {\n      box-shadow: none; }\n  .acl-Button.btnStaticBorderless {\n    box-shadow: none; }\n    .acl-Button.btnStaticBorderless:not(.disabled):hover:not(:focus):not(.focus):not(:active):not(.active), .acl-Button.btnStaticBorderless:not(.disabled).hover:not(:focus):not(.focus):not(:active):not(.active) {\n      transition: background-color ease-in 150ms, box-shadow ease-in 150ms; }\n  .acl-Button.btnBorderless {\n    box-shadow: none;\n    background-color: transparent; }\n    .acl-Button.btnBorderless > svg {\n      opacity: 0.3;\n      transition: opacity ease-in 150ms; }\n    .acl-Button.btnBorderless:not(.disabled):hover, .acl-Button.btnBorderless:not(.disabled).hover {\n      box-shadow: none; }\n      .acl-Button.btnBorderless:not(.disabled):hover:not(:focus):not(.focus), .acl-Button.btnBorderless:not(.disabled).hover:not(:focus):not(.focus) {\n        background-color: transparent; }\n      .acl-Button.btnBorderless:not(.disabled):hover > svg, .acl-Button.btnBorderless:not(.disabled).hover > svg {\n        opacity: 1;\n        transition: opacity ease-in 150ms; }\n    .acl-Button.btnBorderless:not(.disabled):active, .acl-Button.btnBorderless:not(.disabled).active {\n      background-color: transparent;\n      box-shadow: none; }\n    .acl-Button.btnBorderless:not(.disabled):focus:not(:active):not(.active), .acl-Button.btnBorderless:not(.disabled).focus:not(:active):not(.active) {\n      box-shadow: none; }\n  .acl-Button.btnUnderline {\n    box-shadow: inset 0 -1px 0px 0px rgba(0, 0, 0, 0.3);\n    border-radius: 0; }\n    .acl-Button.btnUnderline:not(.disabled):hover, .acl-Button.btnUnderline:not(.disabled).hover, .acl-Button.btnUnderline:not(.disabled):active, .acl-Button.btnUnderline:not(.disabled).active, .acl-Button.btnUnderline:not(.disabled):focus, .acl-Button.btnUnderline:not(.disabled).focus, .acl-Button.btnUnderline:not(.disabled).error {\n      border-radius: 4px; }\n    .acl-Button.btnUnderline:not(.disabled):hover:not(:focus):not(.focus):not(:active):not(.active), .acl-Button.btnUnderline:not(.disabled).hover:not(:focus):not(.focus):not(:active):not(.active) {\n      transition: background-color ease-in 150ms, box-shadow ease-in 150ms, border-radius ease-in calc(150ms / 2); }\n  .acl-Button.btnTable {\n    box-shadow: inset 0 -1px 0px 0px rgba(0, 0, 0, 0.3);\n    border-radius: 0; }\n    .acl-Button.btnTable:not(.disabled):hover, .acl-Button.btnTable:not(.disabled).hover, .acl-Button.btnTable:not(.disabled):active, .acl-Button.btnTable:not(.disabled).active, .acl-Button.btnTable:not(.disabled):focus, .acl-Button.btnTable:not(.disabled).focus, .acl-Button.btnTable:not(.disabled).error {\n      border-radius: 0; }\n    .acl-Button.btnTable:not(.disabled):hover:not(:focus):not(.focus):not(:active):not(.active), .acl-Button.btnTable:not(.disabled).hover:not(:focus):not(.focus):not(:active):not(.active) {\n      transition: background-color ease-in 150ms, box-shadow ease-in 150ms, border-radius ease-in calc(150ms / 2); }\n  .acl-Button.btnToggle {\n    background-color: rgba(0, 0, 0, 0.05);\n    max-width: 176px;\n    white-space: nowrap;\n    overflow: hidden;\n    text-overflow: ellipsis; }\n    .acl-Button.btnToggle:not(.disabled):hover, .acl-Button.btnToggle:not(.disabled).hover:not(.toggled) {\n      background-color: #dff3f7;\n      color: #004f5f;\n      transition: background-color ease-in 150ms, box-shadow ease-in 150ms, border-radius ease-in calc(150ms / 2); }\n      .acl-Button.btnToggle:not(.disabled):hover > span > svg,\n      .acl-Button.btnToggle:not(.disabled):hover > svg, .acl-Button.btnToggle:not(.disabled).hover:not(.toggled) > span > svg,\n      .acl-Button.btnToggle:not(.disabled).hover:not(.toggled) > svg {\n        fill: #004f5f; }\n      .acl-Button.btnToggle:not(.disabled):hover:not(:active):not(.active):not(.error), .acl-Button.btnToggle:not(.disabled).hover:not(.toggled):not(:active):not(.active):not(.error) {\n        box-shadow: inset 0 0 0 1px #40b6ce; }\n      .acl-Button.btnToggle:not(.disabled):hover:not(:focus):not(.focus), .acl-Button.btnToggle:not(.disabled).hover:not(.toggled):not(:focus):not(.focus) {\n        background-color: #dff3f7;\n        color: #004f5f; }\n        .acl-Button.btnToggle:not(.disabled):hover:not(:focus):not(.focus) > span > svg,\n        .acl-Button.btnToggle:not(.disabled):hover:not(:focus):not(.focus) > svg, .acl-Button.btnToggle:not(.disabled).hover:not(.toggled):not(:focus):not(.focus) > span > svg,\n        .acl-Button.btnToggle:not(.disabled).hover:not(.toggled):not(:focus):not(.focus) > svg {\n          fill: #004f5f; }\n        .acl-Button.btnToggle:not(.disabled):hover:not(:focus):not(.focus):not(:active):not(.active) .acl-Button.btnToggle:not(.disabled):hover:not(:focus):not(.focus):not(.error), .acl-Button.btnToggle:not(.disabled).hover:not(.toggled):not(:focus):not(.focus):not(:active):not(.active) .acl-Button.btnToggle:not(.disabled):hover:not(:focus):not(.focus):not(.error), .acl-Button.btnToggle:not(.disabled):hover:not(:focus):not(.focus):not(:active):not(.active) .acl-Button.btnToggle:not(.disabled).hover:not(.toggled):not(:focus):not(.focus):not(.error), .acl-Button.btnToggle:not(.disabled).hover:not(.toggled):not(:focus):not(.focus):not(:active):not(.active) .acl-Button.btnToggle:not(.disabled).hover:not(.toggled):not(:focus):not(.focus):not(.error) {\n          box-shadow: inset 0 0 0 1px #40b6ce; }\n    .acl-Button.btnToggle.toggled, .acl-Button.btnToggle:active, .acl-Button.btnToggle.active {\n      background-color: #009ebd;\n      box-shadow: none;\n      color: #ffffff; }\n      .acl-Button.btnToggle.toggled > span > svg,\n      .acl-Button.btnToggle.toggled > svg, .acl-Button.btnToggle:active > span > svg,\n      .acl-Button.btnToggle:active > svg, .acl-Button.btnToggle.active > span > svg,\n      .acl-Button.btnToggle.active > svg {\n        fill: #ffffff; }\n      .acl-Button.btnToggle.toggled:hover:not(:focus):not(.focus), .acl-Button.btnToggle.toggled.hover:not(:focus):not(.focus), .acl-Button.btnToggle:active:hover:not(:focus):not(.focus), .acl-Button.btnToggle:active.hover:not(:focus):not(.focus), .acl-Button.btnToggle.active:hover:not(:focus):not(.focus), .acl-Button.btnToggle.active.hover:not(:focus):not(.focus) {\n        background-color: #009ebd;\n        color: #ffffff; }\n        .acl-Button.btnToggle.toggled:hover:not(:focus):not(.focus) > span > svg,\n        .acl-Button.btnToggle.toggled:hover:not(:focus):not(.focus) > svg, .acl-Button.btnToggle.toggled.hover:not(:focus):not(.focus) > span > svg,\n        .acl-Button.btnToggle.toggled.hover:not(:focus):not(.focus) > svg, .acl-Button.btnToggle:active:hover:not(:focus):not(.focus) > span > svg,\n        .acl-Button.btnToggle:active:hover:not(:focus):not(.focus) > svg, .acl-Button.btnToggle:active.hover:not(:focus):not(.focus) > span > svg,\n        .acl-Button.btnToggle:active.hover:not(:focus):not(.focus) > svg, .acl-Button.btnToggle.active:hover:not(:focus):not(.focus) > span > svg,\n        .acl-Button.btnToggle.active:hover:not(:focus):not(.focus) > svg, .acl-Button.btnToggle.active.hover:not(:focus):not(.focus) > span > svg,\n        .acl-Button.btnToggle.active.hover:not(:focus):not(.focus) > svg {\n          fill: #ffffff; }\n        .acl-Button.btnToggle.toggled:hover:not(:focus):not(.focus):not(.error), .acl-Button.btnToggle.toggled.hover:not(:focus):not(.focus):not(.error), .acl-Button.btnToggle:active:hover:not(:focus):not(.focus):not(.error), .acl-Button.btnToggle:active.hover:not(:focus):not(.focus):not(.error), .acl-Button.btnToggle.active:hover:not(:focus):not(.focus):not(.error), .acl-Button.btnToggle.active.hover:not(:focus):not(.focus):not(.error) {\n          box-shadow: none; }\n      .acl-Button.btnToggle.toggled.error, .acl-Button.btnToggle:active.error, .acl-Button.btnToggle.active.error {\n        box-shadow: inset 0 0 0 1px #e75556; }\n    .acl-Button.btnToggle:not(.error):not(:focus):not(.focus), .acl-Button.btnToggle:not(.error):focus:not(:active):not(.active) {\n      box-shadow: none; }\n  .acl-Button.dark {\n    color: rgba(255, 255, 255, 0.5);\n    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.3);\n    /** PRIMARY BUTTONS **/\n    /** STATIC BORDERLESS BUTTONS **/\n    /** BORDERLESS BUTTONS **/\n    /** UNDERLINE BUTTONS **/\n    /** TABLE BUTTONS **/ }\n    .acl-Button.dark > span > svg,\n    .acl-Button.dark > svg {\n      fill: rgba(255, 255, 255, 0.5); }\n    .acl-Button.dark:not(.disabled):hover, .acl-Button.dark:not(.disabled).hover {\n      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.3); }\n      .acl-Button.dark:not(.disabled):hover:not(:focus):not(.focus), .acl-Button.dark:not(.disabled).hover:not(:focus):not(.focus) {\n        background-color: rgba(255, 255, 255, 0.05); }\n    .acl-Button.dark:not(.disabled):active, .acl-Button.dark:not(.disabled).active {\n      background-color: rgba(0, 0, 0, 0.1);\n      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.3), inset 0 4px 2px 0 rgba(0, 0, 0, 0.05); }\n    .acl-Button.dark:not(.disabled).error {\n      box-shadow: inset 0 0 0 1px #e75556; }\n    .acl-Button.dark.iconText, .acl-Button.dark.textIcon {\n      color: rgba(255, 255, 255, 0.7); }\n    .acl-Button.dark.btnPrimary {\n      color: #ffffff;\n      box-shadow: none; }\n      .acl-Button.dark.btnPrimary:not(.disabled):hover, .acl-Button.dark.btnPrimary:not(.disabled).hover {\n        box-shadow: none;\n        background-color: #40b6ce; }\n        .acl-Button.dark.btnPrimary:not(.disabled):hover:not(:focus):not(.focus), .acl-Button.dark.btnPrimary:not(.disabled).hover:not(:focus):not(.focus) {\n          background-color: #40b6ce; }\n      .acl-Button.dark.btnPrimary:not(.disabled):active, .acl-Button.dark.btnPrimary:not(.disabled).active {\n        background-color: #00849e;\n        box-shadow: none; }\n      .acl-Button.dark.btnPrimary:not(.disabled):focus:not(:active):not(.active), .acl-Button.dark.btnPrimary:not(.disabled).focus:not(:active):not(.active) {\n        box-shadow: none; }\n    .acl-Button.dark.btnStaticBorderless {\n      box-shadow: none; }\n      .acl-Button.dark.btnStaticBorderless:not(.disabled):hover:not(:focus):not(.focus):not(:active):not(.active), .acl-Button.dark.btnStaticBorderless:not(.disabled).hover:not(:focus):not(.focus):not(:active):not(.active) {\n        transition: background-color ease-in 150ms, box-shadow ease-in 150ms; }\n    .acl-Button.dark.btnBorderless {\n      box-shadow: none;\n      background-color: transparent; }\n      .acl-Button.dark.btnBorderless > svg {\n        fill: rgba(255, 255, 255, 0.5);\n        opacity: 0.3;\n        transition: opacity ease-in 150ms; }\n      .acl-Button.dark.btnBorderless:not(.disabled):hover, .acl-Button.dark.btnBorderless:not(.disabled).hover {\n        box-shadow: none; }\n        .acl-Button.dark.btnBorderless:not(.disabled):hover:not(:focus):not(.focus), .acl-Button.dark.btnBorderless:not(.disabled).hover:not(:focus):not(.focus) {\n          background-color: transparent; }\n        .acl-Button.dark.btnBorderless:not(.disabled):hover > svg, .acl-Button.dark.btnBorderless:not(.disabled).hover > svg {\n          opacity: 1;\n          transition: opacity ease-in 150ms; }\n      .acl-Button.dark.btnBorderless:not(.disabled):active, .acl-Button.dark.btnBorderless:not(.disabled).active {\n        background-color: transparent;\n        box-shadow: none; }\n      .acl-Button.dark.btnBorderless:not(.disabled):focus:not(:active):not(.active), .acl-Button.dark.btnBorderless:not(.disabled).focus:not(:active):not(.active) {\n        box-shadow: none; }\n    .acl-Button.dark.btnUnderline {\n      box-shadow: inset 0 -1px 0px 0px rgba(255, 255, 255, 0.3);\n      border-radius: 0; }\n      .acl-Button.dark.btnUnderline:not(.disabled):hover, .acl-Button.dark.btnUnderline:not(.disabled).hover, .acl-Button.dark.btnUnderline:not(.disabled):active, .acl-Button.dark.btnUnderline:not(.disabled).active, .acl-Button.dark.btnUnderline:not(.disabled):focus, .acl-Button.dark.btnUnderline:not(.disabled).focus, .acl-Button.dark.btnUnderline:not(.disabled).error {\n        border-radius: 4px; }\n      .acl-Button.dark.btnUnderline:not(.disabled):hover:not(:focus):not(.focus):not(:active):not(.active), .acl-Button.dark.btnUnderline:not(.disabled).hover:not(:focus):not(.focus):not(:active):not(.active) {\n        transition: background-color ease-in 150ms, box-shadow ease-in 150ms, border-radius ease-in calc(150ms / 2); }\n    .acl-Button.dark.btnTable {\n      box-shadow: inset 0 -1px 0px 0px rgba(255, 255, 255, 0.3);\n      border-radius: 0; }\n      .acl-Button.dark.btnTable:not(.disabled):hover, .acl-Button.dark.btnTable:not(.disabled).hover, .acl-Button.dark.btnTable:not(.disabled):active, .acl-Button.dark.btnTable:not(.disabled).active, .acl-Button.dark.btnTable:not(.disabled):focus, .acl-Button.dark.btnTable:not(.disabled).focus, .acl-Button.dark.btnTable:not(.disabled).error {\n        border-radius: 0; }\n      .acl-Button.dark.btnTable:not(.disabled):hover:not(:focus):not(.focus):not(:active):not(.active), .acl-Button.dark.btnTable:not(.disabled).hover:not(:focus):not(.focus):not(:active):not(.active) {\n        transition: background-color ease-in 150ms, box-shadow ease-in 150ms, border-radius ease-in calc(150ms / 2); }\n  .acl-Button.compact {\n    height: calc(16px + (8px));\n    line-height: 16px;\n    padding-left: 12px;\n    padding-right: 12px;\n    padding-top: 4px;\n    padding-bottom: 4px; }\n    .acl-Button.compact.icon {\n      padding-left: 6px;\n      padding-right: 6px;\n      padding-top: 6px;\n      padding-bottom: 6px; }\n    .acl-Button.compact.iconText, .acl-Button.compact.textIcon {\n      padding-top: 4px;\n      padding-bottom: 4px; }\n    .acl-Button.compact.iconText {\n      padding-left: 8px;\n      padding-right: 8px; }\n      .acl-Button.compact.iconText > span {\n        padding-left: calc(8px + 16px); }\n        .acl-Button.compact.iconText > span > svg {\n          left: 0; }\n    .acl-Button.compact.textIcon {\n      padding-left: 8px;\n      padding-right: 8px; }\n      .acl-Button.compact.textIcon > span {\n        padding-right: calc(8px + 16px); }\n        .acl-Button.compact.textIcon > span > svg {\n          right: 0; }\n  .acl-Button.ultraCompact:not(.compact) {\n    padding: 0 0 0 0;\n    margin: 0 0 0 0;\n    height: 16px;\n    line-height: 16px; }\n";
styleInject(css_248z$2);

var ButtonFormat;
(function (ButtonFormat) {
    ButtonFormat["ICON"] = "icon";
    ButtonFormat["ICON_TEXT"] = "iconText";
    ButtonFormat["TEXT_ICON"] = "textIcon";
})(ButtonFormat || (ButtonFormat = {}));

var ButtonVariant;
(function (ButtonVariant) {
    ButtonVariant["PRIMARY"] = "primary";
    ButtonVariant["NEUTRAL"] = "neutral";
    ButtonVariant["STATIC_BORDERLESS"] = "static";
    ButtonVariant["BORDERLESS"] = "borderless";
    ButtonVariant["UNDERLINE"] = "underline";
    ButtonVariant["TABLE"] = "table";
    ButtonVariant["DROPDOWN"] = "dropdown";
})(ButtonVariant || (ButtonVariant = {}));

var Button = forwardRef(function (_a, ref) {
    var _b = _a.variant, variant = _b === void 0 ? ButtonVariant.NEUTRAL : _b, format = _a.format, _c = _a.focus, focus = _c === void 0 ? false : _c, _d = _a.active, active = _d === void 0 ? false : _d, _e = _a.compact, compact = _e === void 0 ? false : _e, _f = _a.ultraCompact, ultraCompact = _f === void 0 ? false : _f, _g = _a.error, error = _g === void 0 ? false : _g, _h = _a.dark, dark = _h === void 0 ? false : _h, _j = _a.disabled, disabled = _j === void 0 ? false : _j, style = _a.style, label = _a.label, icon = _a.icon, _k = _a.iconPosition, iconPosition = _k === void 0 ? 'left' : _k, _l = _a.type, type = _l === void 0 ? 'button' : _l, title = _a.title, _m = _a.allowPropagation, allowPropagation = _m === void 0 ? false : _m, htmlProps = __rest(_a, ["variant", "format", "focus", "active", "compact", "ultraCompact", "error", "dark", "disabled", "style", "label", "icon", "iconPosition", "type", "title", "allowPropagation"]);
    var getChildren = function () {
        if (label || icon) {
            return (React.createElement(React.Fragment, null,
                iconPosition === 'right' && label,
                icon && (React.createElement(Icon, { name: icon, color: ColorTheme.buttons.neutral, size: !compact && IconSize.base })),
                iconPosition !== 'right' && label));
        }
        return htmlProps.children;
    };
    var hasText = function (children) {
        if (!children)
            return false;
        return !!children.find(function (child) { return typeof child === 'string' && child.length; });
    };
    var isButtonFormat = function (formatToCheck) {
        if (format) {
            //Button format is manually set
            return format === formatToCheck;
        }
        var children = getChildren();
        switch (formatToCheck) {
            case ButtonFormat.ICON:
                if (icon && !label)
                    return true;
                if (label)
                    return false;
                if (!icon) {
                    return (typeof children !== 'string' &&
                        (!Array.isArray(children) || !hasText(children)));
                }
                break;
            case ButtonFormat.ICON_TEXT:
                if (icon && label) {
                    return iconPosition === 'left';
                }
                if (Array.isArray(children)) {
                    return (typeof children[0] === 'object' && hasText(children));
                }
                break;
            case ButtonFormat.TEXT_ICON:
                if (icon && label) {
                    return iconPosition === 'right';
                }
                if (Array.isArray(children)) {
                    return (typeof children[1] === 'object' && hasText(children));
                }
                break;
            default:
                return false;
        }
        return false;
    };
    var handleClick = function (e) {
        if (!disabled) {
            if (!allowPropagation) {
                e.stopPropagation();
            }
            safeInvoke(htmlProps.onClick, [e]);
        }
    };
    var handleBlur = function (e) {
        if (!disabled &&
            htmlProps.onBlur &&
            typeof htmlProps.onBlur === 'function') {
            htmlProps.onBlur(e);
        }
    };
    var cssClasses = classNames('acl-Button', htmlProps.className, {
        btnPrimary: variant === ButtonVariant.PRIMARY,
        btnStaticBorderless: variant === ButtonVariant.STATIC_BORDERLESS,
        btnBorderless: variant === ButtonVariant.BORDERLESS,
        btnUnderline: variant === ButtonVariant.UNDERLINE,
        btnTable: variant === ButtonVariant.TABLE,
        focus: focus,
        active: active,
        compact: compact,
        ultraCompact: ultraCompact && variant === ButtonVariant.BORDERLESS,
        error: error,
        dark: dark,
        disabled: disabled,
        icon: isButtonFormat(ButtonFormat.ICON),
        iconText: isButtonFormat(ButtonFormat.ICON_TEXT),
        textIcon: isButtonFormat(ButtonFormat.TEXT_ICON)
    });
    var buttonChildren = getChildren();
    return (React.createElement("button", { className: cssClasses, style: style, disabled: disabled, type: type, title: title, onClick: handleClick, onBlur: handleBlur, ref: ref }, isButtonFormat(ButtonFormat.ICON_TEXT) ||
        isButtonFormat(ButtonFormat.TEXT_ICON) ? (React.createElement("span", null, buttonChildren)) : (buttonChildren)));
});

var StepperButton = function (props) {
    var handleDec = function () {
        if (!props.decrementDisabled && props.onDecrement) {
            props.onDecrement();
        }
    };
    var handleInc = function () {
        if (!props.incrementDisabled && props.onIncrement) {
            props.onIncrement();
        }
    };
    return (React.createElement("div", { className: classNames('acl-stepperButton', props.className), style: props.style },
        React.createElement(Button, { variant: ButtonVariant.STATIC_BORDERLESS, ultraCompact: true, disabled: props.decrementDisabled, style: {
                marginRight: 12
            }, onClick: handleDec },
            React.createElement(Icon, { name: "ChevronLeft" })),
        React.createElement(Button, { variant: ButtonVariant.STATIC_BORDERLESS, ultraCompact: true, disabled: props.incrementDisabled, onClick: handleInc },
            React.createElement(Icon, { name: "ChevronRight" }))));
};

var Portal = function (_a) {
    var _b = _a.disabled, disabled = _b === void 0 ? false : _b, props = __rest(_a, ["disabled"]);
    var root = document.getElementById(props.portalRoot);
    if (!disabled) {
        return props.children
            ? createPortal(props.children, root || document.body)
            : null;
    }
    return React.createElement(React.Fragment, null, props.children);
};

var AmobeeLogo = function (props) { return (React.createElement("svg", __assign({ width: 132, height: 32, viewBox: "0 0 132 32", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, props),
    React.createElement("path", { d: "M2 7.5C2 6.67 2.67 6 3.5 6h21c.83 0 1.5.67 1.5 1.5v13c0 .83-.67 1.5-1.5 1.5H14a1.007 1.007 0 00-.83 1.57c.61.88 1.43 1.71 2.39 2.43H8.5c-.55 0-1 .45-1 1s.45 1 1 1h11c.55 0 1-.45 1-1 0-.44-.28-.81-.67-.94-1.33-.47-2.57-1.19-3.57-2.06h8.24c1.93 0 3.5-1.57 3.5-3.5v-13C28 5.57 26.43 4 24.5 4h-21C1.57 4 0 5.57 0 7.5v12c0 1.96.73 3.32 2 4v-16z", fill: "currentColor" }),
    React.createElement("path", { d: "M0 19.5c0 1.96.73 3.32 2 4 .68.37 1.35.5 2.09.5 2.72 0 6.58-1.91 11.52-5.71.44-.34.52-.96.18-1.4a.992.992 0 00-1.4-.18c-7.64 5.88-10.5 5.51-11.38 5.08C2.18 21.37 2 20.32 2 19.5H0z", fill: "url(#prefix__paint0_linear)" }),
    React.createElement("path", { d: "M98.936 18.832c0-1.304-1.416-1.408-2.088-1.408H93.6v2.832h3.296c.656 0 2.04-.12 2.04-1.424zm-5.336-4.4h2.68c.672 0 2.024-.096 2.024-1.336 0-1.248-1.248-1.368-1.936-1.368H93.6v2.704zm9.6 4.968c0 3.128-2.744 4.288-5.824 4.288H89.44V8.304h7.88c3.368 0 5.176 1.568 5.176 3.896 0 1.36-.496 2.568-2.36 3.448 2.184.744 3.064 1.864 3.064 3.752zM83.752 16c0-2.296-1.768-4.16-3.944-4.16-2.168 0-3.936 1.864-3.936 4.16 0 2.288 1.768 4.152 3.936 4.152 2.176 0 3.944-1.864 3.944-4.152zm4.352 0c0 4.416-3.712 8-8.296 8-4.576 0-8.288-3.584-8.288-8 0-4.424 3.712-8 8.288-8 4.584 0 8.296 3.576 8.296 8zm20.92 1.624h7.384v-3.368h-7.384v-2.328h8.152V8.304h-12.352v15.384h12.464v-3.624h-8.264v-2.44zm14.712 2.44v-2.44h7.384v-3.368h-7.384v-2.328h8.152V8.304h-12.352v15.384H132v-3.624h-8.264zm-77.448-2.416l-1.72-4.376-1.736 4.376h3.456zm.376-9.456l6.544 15.496H48.64l-1.128-2.744h-5.928l-1.104 2.744H36l6.544-15.496h4.12zm19.072.112h4.504v15.384H66v-8.84s-3.456 5.312-3.536 5.424a.48.48 0 01-.416.208.512.512 0 01-.432-.208l-3.528-5.376v8.792h-4.2V8.304H58.4l3.664 5.952 3.672-5.952z", fill: "currentColor" }),
    React.createElement("defs", null,
        React.createElement("linearGradient", { id: "prefix__paint0_linear", x1: 1.781, y1: 23.212, x2: 14.612, y2: 14.077, gradientUnits: "userSpaceOnUse" },
            React.createElement("stop", { offset: 0.025, stopColor: "currentColor" }),
            React.createElement("stop", { offset: 0.917, stopOpacity: 0, stopColor: "currentColor" }))))); };

var usePagination = function (_a) {
    var totalCount = _a.totalCount, _b = _a.pageSize, pageSize = _b === void 0 ? 5 : _b, _c = _a.siblingCount, siblingCount = _c === void 0 ? 1 : _c, _d = _a.currentPage, currentPage = _d === void 0 ? 1 : _d;
    var range = function (start, end) {
        var length = end - start + 1;
        return Array.from({ length: length }, function (_, idx) { return idx + start; });
    };
    var paginationRange = useMemo(function () {
        var totalPageCount = Math.ceil(totalCount / pageSize);
        var totalPageNumbers = siblingCount + 5;
        if (totalPageNumbers >= totalPageCount) {
            return range(1, totalPageCount);
        }
        var leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
        var rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);
        var shouldShowLeftDots = leftSiblingIndex > 2;
        var shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;
        var firstPageIndex = 1;
        var lastPageIndex = totalPageCount;
        if (!shouldShowLeftDots && shouldShowRightDots) {
            var leftItemCount = 3 + 2 * siblingCount;
            var leftRange = range(1, leftItemCount);
            return __spreadArrays(leftRange, ['...', totalPageCount]);
        }
        if (shouldShowLeftDots && !shouldShowRightDots) {
            var rightItemCount = 3 + 2 * siblingCount;
            var rightRange = range(totalPageCount - rightItemCount + 1, totalPageCount);
            return __spreadArrays([firstPageIndex, '...'], rightRange);
        }
        if (!shouldShowLeftDots && !shouldShowRightDots) {
            return range(1, totalPageCount);
        }
        if (shouldShowLeftDots && shouldShowRightDots) {
            var middleRange = range(leftSiblingIndex, rightSiblingIndex);
            return __spreadArrays([
                firstPageIndex,
                '...'
            ], middleRange, [
                '...',
                lastPageIndex
            ]);
        }
    }, [totalCount, pageSize, siblingCount, currentPage]);
    return paginationRange;
};

var RNDContext = createDndContext(HTML5Backend);
function DragAndDrop(_a) {
    var children = _a.children;
    var manager = useRef(RNDContext);
    return (React.createElement(DndProvider, { manager: manager.current.dragDropManager }, children));
}

var css_248z$3 = ".acl-Tooltip-wrapper {\n  display: inline-block; }\n\n.acl-Tooltip {\n  max-width: 300px;\n  padding: 10px 0;\n  z-index: 100; }\n  .acl-Tooltip .tooltipText {\n    position: relative;\n    border-radius: 3px;\n    background: #303030;\n    padding: 4px 8px;\n    text-align: center;\n    color: #ffffff;\n    font-size: 12px;\n    line-height: 16px;\n    overflow-wrap: break-word;\n    z-index: 2;\n    white-space: pre-line; }\n  .acl-Tooltip .tooltipArrow {\n    position: absolute;\n    width: 12px;\n    height: 12px; }\n    .acl-Tooltip .tooltipArrow:after {\n      z-index: 1;\n      content: '';\n      display: block;\n      position: absolute;\n      background: #303030;\n      width: 12px;\n      height: 12px;\n      transform: rotate(45deg); }\n  .acl-Tooltip[data-popper-placement^='top'] .tooltipArrow:after {\n    top: -7px; }\n  .acl-Tooltip[data-popper-placement^='bottom'] .tooltipArrow {\n    bottom: calc(100% - 16px); }\n    .acl-Tooltip[data-popper-placement^='bottom'] .tooltipArrow:after {\n      top: auto; }\n  .acl-Tooltip.noArrow {\n    padding: 6px 0; }\n";
styleInject(css_248z$3);

var Tooltip = function (_a) {
    var _b = _a.delay, delay = _b === void 0 ? 300 : _b, _c = _a.arrow, arrow = _c === void 0 ? true : _c, props = __rest(_a, ["delay", "arrow"]);
    var _d = useState(props.visible || false), visible = _d[0], setVisible = _d[1];
    var _e = useState(null), popperElement = _e[0], setPopperElement = _e[1];
    var triggerRef = useRef(null);
    var _f = useState(null), arrowRef = _f[0], setArrowRef = _f[1];
    var duration = 300;
    var defaultStyle = {
        opacity: 1,
        transition: "opacity " + duration + "ms cubic-bezier(0.19, 1, 0.22, 1)"
    };
    var transitionStyles = {
        entering: { opacity: 0 },
        entered: { opacity: 1 },
        exiting: { opacity: 1 },
        exited: { opacity: 0 },
        unmounted: { opacity: 0 }
    };
    var popperProps = {
        placement: props.placement || 'bottom',
        strategy: 'fixed',
        modifiers: [
            {
                name: 'arrow',
                options: {
                    element: arrowRef
                }
            }
        ]
    };
    var _g = usePopper(triggerRef.current, popperElement, popperProps), styles = _g.styles, attributes = _g.attributes;
    var cssClasses = classNames('acl-Tooltip', {
        noArrow: !arrow
    });
    function handleClick() {
        var triggerCheck = function () {
            if (triggerRef.current) {
                var triggerHtml = triggerRef.current.innerHTML;
                if (!triggerHtml) {
                    setVisible(false);
                }
            }
        };
        // eslint-disable-next-line no-invalid-this
        setTimeout(triggerCheck.bind(this), 0);
    }
    useEffect(function () {
        if (props.visible && !props.disabled && props.trigger) {
            setVisible(props.visible);
        }
    }, [props.visible, props.disabled]);
    useEffect(function () {
        document.addEventListener('click', handleClick, false);
        return function () {
            document.removeEventListener('click', handleClick, false);
        };
    }, [visible, triggerRef]);
    return (React.createElement("div", { ref: triggerRef, className: "acl-Tooltip-wrapper", style: props.style, onMouseOver: function () {
            setVisible(true);
            safeInvoke(props.onToggleTooltip, [true]);
        }, onMouseLeave: function () {
            setVisible(false);
            safeInvoke(props.onToggleTooltip, [false]);
        } },
        props.trigger(),
        React.createElement(Portal, null, visible && !props.disabled && (React.createElement(Transition, { timeout: delay, in: visible, appear: true, mountOnEnter: true, unmountOnExit: true }, function (state) { return (React.createElement("div", __assign({ className: cssClasses, "data-testid": "tooltip-test", style: __assign({ zIndex: 1000 }, styles.popper), ref: setPopperElement }, attributes.popper),
            React.createElement("div", { style: __assign(__assign({}, defaultStyle), transitionStyles[state]) },
                React.createElement("div", { className: "tooltipText" }, props.text),
                arrow && (React.createElement("div", { ref: setArrowRef, style: styles.arrow, className: "tooltipArrow" }))))); })))));
};

var css_248z$4 = ".acl-Button.acl-ToggleButton {\n  background-color: rgba(0, 0, 0, 0.05);\n  max-width: 176px;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis; }\n  .acl-Button.acl-ToggleButton:not(.disabled):hover:not(.toggled) {\n    background-color: #dff3f7;\n    color: #004f5f;\n    transition: background-color ease-in 150ms, box-shadow ease-in 150ms, border-radius ease-in calc(150ms / 2); }\n    .acl-Button.acl-ToggleButton:not(.disabled):hover:not(.toggled) > span > svg,\n    .acl-Button.acl-ToggleButton:not(.disabled):hover:not(.toggled) > svg {\n      fill: #004f5f; }\n    .acl-Button.acl-ToggleButton:not(.disabled):hover:not(.toggled):not(:active):not(.active):not(.error) {\n      box-shadow: inset 0 0 0 1px #40b6ce; }\n    .acl-Button.acl-ToggleButton:not(.disabled):hover:not(.toggled):not(:focus):not(.focus) {\n      background-color: #dff3f7;\n      color: #004f5f; }\n      .acl-Button.acl-ToggleButton:not(.disabled):hover:not(.toggled):not(:focus):not(.focus) > span > svg,\n      .acl-Button.acl-ToggleButton:not(.disabled):hover:not(.toggled):not(:focus):not(.focus) > svg {\n        fill: #004f5f; }\n      .acl-Button.acl-ToggleButton:not(.disabled):hover:not(.toggled):not(:focus):not(.focus):not(:active):not(.active) .acl-Button.acl-ToggleButton:not(.disabled):hover:not(.toggled):not(:focus):not(.focus):not(.error) {\n        box-shadow: inset 0 0 0 1px #40b6ce; }\n  .acl-Button.acl-ToggleButton.toggled, .acl-Button.acl-ToggleButton:active, .acl-Button.acl-ToggleButton.active {\n    background-color: #009ebd;\n    box-shadow: none;\n    color: #ffffff; }\n    .acl-Button.acl-ToggleButton.toggled > span > svg,\n    .acl-Button.acl-ToggleButton.toggled > svg, .acl-Button.acl-ToggleButton:active > span > svg,\n    .acl-Button.acl-ToggleButton:active > svg, .acl-Button.acl-ToggleButton.active > span > svg,\n    .acl-Button.acl-ToggleButton.active > svg {\n      fill: #ffffff; }\n    .acl-Button.acl-ToggleButton.toggled:hover:not(:focus):not(.focus), .acl-Button.acl-ToggleButton:active:hover:not(:focus):not(.focus), .acl-Button.acl-ToggleButton.active:hover:not(:focus):not(.focus) {\n      background-color: #009ebd;\n      color: #ffffff; }\n      .acl-Button.acl-ToggleButton.toggled:hover:not(:focus):not(.focus) > span > svg,\n      .acl-Button.acl-ToggleButton.toggled:hover:not(:focus):not(.focus) > svg, .acl-Button.acl-ToggleButton:active:hover:not(:focus):not(.focus) > span > svg,\n      .acl-Button.acl-ToggleButton:active:hover:not(:focus):not(.focus) > svg, .acl-Button.acl-ToggleButton.active:hover:not(:focus):not(.focus) > span > svg,\n      .acl-Button.acl-ToggleButton.active:hover:not(:focus):not(.focus) > svg {\n        fill: #ffffff; }\n      .acl-Button.acl-ToggleButton.toggled:hover:not(:focus):not(.focus):not(.error), .acl-Button.acl-ToggleButton:active:hover:not(:focus):not(.focus):not(.error), .acl-Button.acl-ToggleButton.active:hover:not(:focus):not(.focus):not(.error) {\n        box-shadow: none; }\n    .acl-Button.acl-ToggleButton.toggled.error, .acl-Button.acl-ToggleButton:active.error, .acl-Button.acl-ToggleButton.active.error {\n      box-shadow: inset 0 0 0 1px #e75556; }\n  .acl-Button.acl-ToggleButton:not(.error):not(:focus):not(.focus), .acl-Button.acl-ToggleButton:not(.error):focus:not(:active):not(.active) {\n    box-shadow: none; }\n";
styleInject(css_248z$4);

var ToggleButton = function (_a) {
    var _b = _a.compact, compact = _b === void 0 ? false : _b, _c = _a.ultraCompact, ultraCompact = _c === void 0 ? false : _c, _d = _a.error, error = _d === void 0 ? false : _d, _e = _a.dark, dark = _e === void 0 ? false : _e, _f = _a.disabled, disabled = _f === void 0 ? false : _f, _g = _a.iconPosition, iconPosition = _g === void 0 ? 'left' : _g, _h = _a.initialOn, initialOn = _h === void 0 ? false : _h, _j = _a.allowPropagation, allowPropagation = _j === void 0 ? false : _j, props = __rest(_a, ["compact", "ultraCompact", "error", "dark", "disabled", "iconPosition", "initialOn", "allowPropagation"]);
    var _k = useState(initialOn), toggled = _k[0], toggleButton = _k[1];
    var _l = useState(false), tooltip = _l[0], setTooltip = _l[1];
    var buttonRef = useRef(null);
    var handleClick = function () {
        if (!disabled) {
            toggleButton(!toggled);
            if (props.onToggle) {
                props.onToggle(!toggled);
            }
        }
    };
    var tooltipText = function () {
        if (props.label)
            return props.label;
        return React.Children.toArray(props.children)
            .filter(function (child) { return typeof child === 'string'; })
            .toString();
    };
    useEffect(function () {
        if (buttonRef.current) {
            if (ColorTheme.buttons.toggleMaxWidth &&
                buttonRef.current.scrollWidth ===
                    Number(ColorTheme.buttons.toggleMaxWidth.replace('px', ''))) {
                setTooltip(true);
            }
            else {
                setTooltip(false);
            }
        }
    }, [window.innerWidth, props.children]);
    useEffect(function () {
        if (typeof props.active === 'boolean') {
            toggleButton(props.active);
            if (props.onToggle) {
                props.onToggle(props.active);
            }
        }
    }, [props.active]);
    return (React.createElement(Tooltip, { trigger: function () { return (React.createElement("div", { ref: buttonRef },
            React.createElement(Button, { ultraCompact: ultraCompact, onClick: handleClick, className: classNames('acl-ToggleButton', props.className, { toggled: toggled }), compact: compact, error: error, dark: dark, disabled: disabled, style: props.style, icon: props.icon, iconPosition: iconPosition, label: props.label, allowPropagation: allowPropagation }, props.children))); }, text: tooltipText(), arrow: false, placement: "top", disabled: !tooltip }));
};

var BannerMessageVariant;
(function (BannerMessageVariant) {
    BannerMessageVariant["INFO"] = "info";
    BannerMessageVariant["ERROR"] = "error";
    BannerMessageVariant["WARNING"] = "warning";
    BannerMessageVariant["SUCCESS"] = "success";
})(BannerMessageVariant || (BannerMessageVariant = {}));

var css_248z$5 = ".acl-BannerMessage {\n  display: block;\n  position: relative;\n  -webkit-user-select: none;\n  -moz-user-select: none;\n  -ms-user-select: none;\n  user-select: none;\n  border-radius: 4px;\n  font-family: \"Roboto\", sans-serif;\n  font-size: 14px;\n  line-height: 16px;\n  padding-top: 8px;\n  padding-bottom: 8px;\n  padding-right: 12px;\n  padding-left: calc(12px + 16px + calc((calc(16px + 8px * 2) - 16px) / 2)); }\n  .acl-BannerMessage.hideIcon {\n    padding-left: 12px; }\n  .acl-BannerMessage.dismissible {\n    padding-right: calc(12px + 16px + calc((calc(16px + 8px * 2) - 16px) / 2)); }\n  .acl-BannerMessage.info {\n    color: #004f5f;\n    background-color: #dff3f7; }\n  .acl-BannerMessage.error {\n    color: #e75556;\n    background-color: #fceaea; }\n  .acl-BannerMessage.warning {\n    color: #caa900;\n    background-color: #f6f0d5; }\n  .acl-BannerMessage.success {\n    color: #52a145;\n    background-color: #e9f3e8; }\n  .acl-BannerMessage svg.icon {\n    position: absolute;\n    top: calc((calc(16px + 8px * 2) - 16px) / 2);\n    left: 12px; }\n  .acl-BannerMessage .close {\n    position: absolute;\n    top: 0;\n    right: calc(12px - 8px);\n    margin: 0; }\n";
styleInject(css_248z$5);

var BannerMessage = function (_a) {
    var hideIcon = _a.hideIcon, dismissible = _a.dismissible, _b = _a.variant, variant = _b === void 0 ? BannerMessageVariant.INFO : _b, timeout = _a.timeout, onClose = _a.onClose, htmlProps = __rest(_a, ["hideIcon", "dismissible", "variant", "timeout", "onClose"]);
    var cssClasses = classNames('acl-BannerMessage', htmlProps.className, {
        info: variant === BannerMessageVariant.INFO,
        warning: variant === BannerMessageVariant.WARNING,
        error: variant === BannerMessageVariant.ERROR,
        success: variant === BannerMessageVariant.SUCCESS,
        hideIcon: hideIcon,
        dismissible: dismissible
    });
    useEffect(function () {
        if (timeout) {
            window.setTimeout(function () {
                safeInvoke(onClose);
            }, timeout);
        }
    });
    var getIcon = function () {
        switch (variant) {
            case BannerMessageVariant.WARNING:
                return 'StatusWarning';
            case BannerMessageVariant.ERROR:
                return 'StatusAlert';
            case BannerMessageVariant.SUCCESS:
                return 'Check';
            default:
                return 'Info';
        }
    };
    var getColor = function () {
        switch (variant) {
            case BannerMessageVariant.WARNING:
                return ColorTheme.yellow30;
            case BannerMessageVariant.ERROR:
                return ColorTheme.red50;
            case BannerMessageVariant.SUCCESS:
                return ColorTheme.green50;
            default:
                return ColorTheme.amobee50;
        }
    };
    return (React.createElement("div", { className: cssClasses, style: htmlProps.style },
        !hideIcon && (React.createElement(Icon, { className: "icon", name: getIcon(), color: getColor(), size: IconSize.base })),
        htmlProps.children,
        dismissible && (React.createElement(Button, { className: "close", variant: ButtonVariant.BORDERLESS, onClick: function () { return safeInvoke(onClose); } },
            React.createElement(Icon, { name: "CloseSmall", size: IconSize.base })))));
};

var css_248z$6 = ".acl-Breadcrumbs {\n  display: block;\n  position: relative;\n  white-space: nowrap;\n  width: 100%;\n  overflow: hidden;\n  text-rendering: initial;\n  -webkit-font-smoothing: initial; }\n  .acl-Breadcrumbs .acl-BreadcrumbScroller {\n    padding: 8px 8px 4px; }\n  .acl-Breadcrumbs .acl-Breadcrumb-divider {\n    margin: 0 8px; }\n\n.acl-Breadcrumb {\n  padding: 0;\n  margin: 0;\n  display: inline-block;\n  position: relative;\n  color: rgba(255, 255, 255, 0.5); }\n  .acl-Breadcrumb label {\n    color: rgba(255, 255, 255, 0.3);\n    display: block;\n    text-transform: uppercase;\n    font-size: 12px;\n    line-height: 16px;\n    padding-left: 8px; }\n  .acl-Breadcrumb .acl-Breadcrumb-button {\n    display: block;\n    padding: 4px 8px;\n    border-radius: 4px;\n    text-align: center;\n    position: relative; }\n    .acl-Breadcrumb .acl-Breadcrumb-button .acl-Icon {\n      margin-left: 8px;\n      transform: rotate(0deg);\n      transition: transform 0.35s cubic-bezier(0.68, -0.55, 0.265, 1.55); }\n    .acl-Breadcrumb .acl-Breadcrumb-button:hover {\n      background: rgba(255, 255, 255, 0.05);\n      transition: background 0.15s ease-in-out; }\n      .acl-Breadcrumb .acl-Breadcrumb-button:hover.has-dropdown {\n        cursor: pointer; }\n        .acl-Breadcrumb .acl-Breadcrumb-button:hover.has-dropdown .acl-Icon {\n          transform: rotate(90deg); }\n    .acl-Breadcrumb .acl-Breadcrumb-button.dropdown-open {\n      background: rgba(0, 0, 0, 0.1); }\n      .acl-Breadcrumb .acl-Breadcrumb-button.dropdown-open .acl-Icon {\n        transform: rotate(90deg); }\n    .acl-Breadcrumb .acl-Breadcrumb-button:hover, .acl-Breadcrumb .acl-Breadcrumb-button.dropdown-open {\n      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.3); }\n    .acl-Breadcrumb .acl-Breadcrumb-button .acl-Breadcrumb-tooltip {\n      display: inline-block;\n      vertical-align: middle; }\n    .acl-Breadcrumb .acl-Breadcrumb-button .acl-Breadcrumb-anchor {\n      display: inline-block;\n      max-width: 270px;\n      white-space: nowrap;\n      overflow: hidden;\n      text-overflow: ellipsis;\n      vertical-align: middle;\n      color: rgba(255, 255, 255, 0.5);\n      text-decoration: none; }\n      .acl-Breadcrumb .acl-Breadcrumb-button .acl-Breadcrumb-anchor:hover[href] {\n        text-decoration: underline; }\n";
styleInject(css_248z$6);

var css_248z$7 = ".acl-FormControl {\n  padding: 8px 12px;\n  display: inline-block; }\n  .acl-FormControl.compact {\n    padding: 4px 8px; }\n  .acl-FormControl.fluid {\n    display: block; }\n    .acl-FormControl.fluid .acl-Tooltip-wrapper {\n      display: block; }\n  .acl-FormControl.table {\n    padding: 2px 0px; }\n";
styleInject(css_248z$7);

var FormControl = function (_a) {
    var _b = _a.fluid, fluid = _b === void 0 ? true : _b, props = __rest(_a, ["fluid"]);
    var getClassNames = function () {
        return classNames('acl-FormControl', props.className, {
            fluid: fluid,
            compact: props.compact,
            table: props.table
        });
    };
    return (React.createElement("div", { className: getClassNames(), style: props.style }, props.children));
};

var css_248z$8 = ".acl-Input--default, .acl-Input--borderless, .acl-Input--underlined, .acl-Input--table {\n  border-radius: 4px;\n  border: none;\n  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3);\n  padding: 7px 12px;\n  color: #474747;\n  display: flex;\n  align-items: center;\n  width: 100%; }\n  .acl-Input--default input, .acl-Input--borderless input, .acl-Input--underlined input, .acl-Input--table input {\n    min-width: 0;\n    background-color: transparent;\n    font-size: 14px;\n    line-height: 16px;\n    border: none;\n    flex: 1 1 auto;\n    color: #474747; }\n    .acl-Input--default input::placeholder, .acl-Input--borderless input::placeholder, .acl-Input--underlined input::placeholder, .acl-Input--table input::placeholder {\n      font-style: italic;\n      color: rgba(0, 0, 0, 0.3);\n      text-align: left; }\n    .acl-Input--default input:focus, .acl-Input--borderless input:focus, .acl-Input--underlined input:focus, .acl-Input--table input:focus {\n      outline: none; }\n  .dark.acl-Input--default, .dark.acl-Input--borderless, .dark.acl-Input--underlined, .dark.acl-Input--table {\n    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.3);\n    color: rgba(255, 255, 255, 0.7); }\n    .dark.acl-Input--default input, .dark.acl-Input--borderless input, .dark.acl-Input--underlined input, .dark.acl-Input--table input {\n      color: rgba(255, 255, 255, 0.7); }\n      .dark.acl-Input--default input::placeholder, .dark.acl-Input--borderless input::placeholder, .dark.acl-Input--underlined input::placeholder, .dark.acl-Input--table input::placeholder {\n        color: rgba(255, 255, 255, 0.3); }\n    .dark.acl-Input--default .acl-Input-leftElement, .dark.acl-Input--borderless .acl-Input-leftElement, .dark.acl-Input--underlined .acl-Input-leftElement, .dark.acl-Input--table .acl-Input-leftElement,\n    .dark.acl-Input--default .acl-Input-rightElement,\n    .dark.acl-Input--borderless .acl-Input-rightElement,\n    .dark.acl-Input--underlined .acl-Input-rightElement,\n    .dark.acl-Input--table .acl-Input-rightElement {\n      color: rgba(255, 255, 255, 0.5); }\n\n.acl-Input--compact {\n  padding: 3px 8px; }\n\n.acl-Input--default:hover, .acl-Input--borderless:hover, .acl-Input--underlined:hover, .acl-Input--table:hover {\n  background-color: rgba(255, 255, 255, 0.3);\n  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3), inset 0 4px 2px rgba(0, 0, 0, 0.05);\n  border-radius: 4px; }\n  .dark.acl-Input--default:hover, .dark.acl-Input--borderless:hover, .dark.acl-Input--underlined:hover, .dark.acl-Input--table:hover {\n    background-color: rgba(0, 0, 0, 0.1);\n    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.3), inset 0 4px 2px rgba(0, 0, 0, 0.05); }\n\n.acl-Input--default:focus-within, .acl-Input--borderless:focus-within, .acl-Input--underlined:focus-within, .acl-Input--table:focus-within {\n  background-color: #ffffff;\n  box-shadow: 0 0 0 1px #40b6ce, inset 0 4px 2px rgba(0, 0, 0, 0.1);\n  border-radius: 4px; }\n  .dark.acl-Input--default:focus-within, .dark.acl-Input--borderless:focus-within, .dark.acl-Input--underlined:focus-within, .dark.acl-Input--table:focus-within {\n    background-color: rgba(0, 0, 0, 0.3);\n    box-shadow: 0 0 0 1px #40b6ce, inset 0 4px 2px rgba(0, 0, 0, 0.1); }\n\n.acl-Input--right-placeholder input[type=\"text\"]::placeholder {\n  text-align: right; }\n\n.acl-Input--right-placeholder input[type=\"text\"]:-ms-input-placeholder {\n  text-align: right; }\n\n.acl-Input--borderless {\n  box-shadow: none; }\n  .acl-Input--borderless.dark {\n    box-shadow: none; }\n\n.acl-Input--underlined {\n  border-radius: 0;\n  box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.3); }\n  .acl-Input--underlined.dark {\n    box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.3); }\n  .acl-Input--underlined.error {\n    border-radius: 4px; }\n\n.acl-Input--table {\n  border-radius: 0;\n  box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.1); }\n  .acl-Input--table:hover, .acl-Input--table:focus-within {\n    border-radius: 0; }\n  .acl-Input--table.dark {\n    box-shadow: 0 1px 0 0 rgba(255, 255, 255, 0.1); }\n  .acl-Input--table.error {\n    border-radius: 0; }\n\n.acl-Input--error,\n.acl-Input--error.dark {\n  box-shadow: 0 0 0 1px #e75556; }\n\n.acl-Input--disabled {\n  opacity: 0.3;\n  pointer-events: none; }\n\n.acl-Input-wrapper {\n  position: relative;\n  overflow: hidden; }\n  .acl-Input-wrapper .acl-Input-leftElement,\n  .acl-Input-wrapper .acl-Input-rightElement {\n    color: rgba(0, 0, 0, 0.5);\n    display: flex;\n    align-items: center; }\n  .acl-Input-wrapper.dark .leftElement,\n  .acl-Input-wrapper.dark .rightElement {\n    color: rgba(255, 255, 255, 0.5); }\n\n.acl-Input-leftElement {\n  margin-right: 8px;\n  flex-wrap: wrap;\n  flex: 0 0 auto; }\n\n.acl-Input-rightElement {\n  margin-left: 8px;\n  transition: all 0.15s;\n  flex: 0 0 auto; }\n";
styleInject(css_248z$8);

var Input = forwardRef(function (_a, ref) {
    var _b = _a.variant, variant = _b === void 0 ? 'default' : _b, compact = _a.compact, error = _a.error, dark = _a.dark, disabled = _a.disabled, leftElement = _a.leftElement, leftElementAsString = _a.leftElementAsString, rightElement = _a.rightElement, initialValue = _a.initialValue, validator = _a.validator, _c = _a.showRightElement, showRightElement = _c === void 0 ? 'whenFilledOnHover' : _c, _d = _a.rightAlignInput, rightAlignInput = _d === void 0 ? false : _d, _e = _a.rightAlignPlaceholder, rightAlignPlaceholder = _e === void 0 ? false : _e, htmlProps = __rest(_a, ["variant", "compact", "error", "dark", "disabled", "leftElement", "leftElementAsString", "rightElement", "initialValue", "validator", "showRightElement", "rightAlignInput", "rightAlignPlaceholder"]);
    var inputRef = useRef(null);
    var classes = classNames("acl-Input--" + variant, 'acl-Input-wrapper', {
        'acl-Input--compact': compact,
        'acl-Input--error': error,
        dark: dark,
        'acl-Input--disabled': disabled,
        'acl-Input--right-placeholder': rightAlignPlaceholder
    });
    var uncontrolled = initialValue !== undefined ||
        typeof htmlProps.onChange !== 'function' ||
        typeof htmlProps.value !== 'string';
    var formControlProps = partitionFormControlProps(htmlProps).formControlProps;
    var inputProps = partitionHtmlInputProps(htmlProps).inputProps;
    var _f = useState(false), hovered = _f[0], setHovered = _f[1];
    var _g = useState(false), filled = _g[0], setFilled = _g[1];
    var _h = useState(initialValue || ''), val = _h[0], setVal = _h[1];
    useEffect(function () {
        if (inputProps.value && inputProps.value.length) {
            setFilled(true);
        }
        else {
            setFilled(false);
        }
    }, [inputProps.value]);
    var handleChange = function (e) {
        // Set filled state
        if (e.target.value && e.target.value.length) {
            setFilled(true);
        }
        else {
            setFilled(false);
        }
        if (uncontrolled) {
            setVal(e.target.value);
        }
        if (htmlProps.onChange) {
            htmlProps.onChange(e);
        }
    };
    var handleBlur = function (e) {
        if (htmlProps.onBlur && typeof htmlProps.onBlur === 'function') {
            htmlProps.onBlur(e);
        }
    };
    var handleKeyDown = function (e) {
        if (!e.metaKey &&
            typeof validator === 'function' &&
            !validator(e.key, e.target.value)) {
            e.preventDefault();
        }
        if (typeof htmlProps.onKeyDown === 'function') {
            htmlProps.onKeyDown(e);
        }
    };
    var shouldShowRightElement = function () {
        if (showRightElement === 'always' || !showRightElement) {
            return true;
        }
        if (showRightElement === 'onHover') {
            return hovered;
        }
        if (showRightElement === 'whenFilled') {
            return filled;
        }
        if (showRightElement === 'whenFilledOnHover') {
            return filled && hovered;
        }
        return null;
    };
    var renderLeftElement = function () {
        if (!leftElement)
            return null;
        if (typeof leftElement === 'string') {
            if (leftElementAsString) {
                return (React.createElement("div", { style: { minWidth: 16 }, className: "acl-Input-leftElement" }, leftElement.toString()));
            }
            return (React.createElement(Icon, { name: leftElement, className: "acl-Input-leftElement" }));
        }
        if (typeof leftElement === 'object') {
            return React.cloneElement(leftElement, {
                className: 'acl-Input-leftElement'
            });
        }
        return null;
    };
    return (React.createElement(FormControl, __assign({}, formControlProps),
        React.createElement("div", { ref: ref, className: classes, onMouseOver: function () { return setHovered(true); }, onMouseOut: function () { return setHovered(false); }, onClick: function () {
                return inputRef && inputRef.current && inputRef.current.focus();
            } },
            renderLeftElement(),
            React.createElement("input", __assign({ type: "text" }, inputProps, { disabled: disabled, onChange: handleChange, onBlur: handleBlur, onKeyDown: handleKeyDown, value: uncontrolled ? val : inputProps.value, ref: inputRef, style: {
                    textAlign: rightAlignInput ? 'right' : 'left'
                } })),
            (rightElement || htmlProps.children) && (React.createElement("div", { className: "acl-Input-rightElement", style: {
                    visibility: shouldShowRightElement()
                        ? 'visible'
                        : 'hidden'
                } }, rightElement || htmlProps.children)))));
});

var InputVariant;
(function (InputVariant) {
    InputVariant["DEFAULT"] = "default";
    InputVariant["BORDERLESS"] = "borderless";
    InputVariant["UNDERLINED"] = "underlined";
    InputVariant["TABLE"] = "table";
})(InputVariant || (InputVariant = {}));

/**
 * Returns a string with special characters escaped with backslashes
 * Special characters:  \ - ^ $ * + ? . ( ) | [ ] { }
 * @param str
 */
var addSlashes = function (str) {
    return str
        .trim()
        .replace(/\\/g, '\\\\')
        .replace(/[-^$*+?.()|[\]{}]/g, '\\$&');
};

var HighlightMatch = function (_a) {
    var _b = _a.children, children = _b === void 0 ? '' : _b, _c = _a.match, match = _c === void 0 ? '' : _c;
    var MATCH_EXP = new RegExp("(" + addSlashes(match) + ")", 'ig');
    var matchParts = children.split(MATCH_EXP);
    return !match.length ? (React.createElement("span", null, children)) : (React.createElement("span", null, matchParts
        .filter(function (part) { return part; })
        .map(function (part, i) {
        return match.trim().length >= 1 && MATCH_EXP.test(part) ? (React.createElement("strong", { key: i }, part)) : (React.createElement("span", { key: i }, part));
    })));
};

var css_248z$9 = ".acl-MenuItem {\n  max-width: 100%;\n  padding: 8px 12px;\n  cursor: pointer;\n  display: flex;\n  align-items: flex-start; }\n  .acl-MenuItem:hover, .acl-MenuItem.acl-BasicMenu--active {\n    color: #ffffff;\n    background-color: #009ebd; }\n    .acl-MenuItem:hover .acl-MenuItem-subLabel, .acl-MenuItem.acl-BasicMenu--active .acl-MenuItem-subLabel {\n      color: #ffffff; }\n    .acl-MenuItem:hover.acl-MenuItem--selected svg, .acl-MenuItem.acl-BasicMenu--active.acl-MenuItem--selected svg {\n      color: #ffffff; }\n    .acl-MenuItem:hover button, .acl-MenuItem.acl-BasicMenu--active button {\n      color: rgba(255, 255, 255, 0.6); }\n      .acl-MenuItem:hover button:hover, .acl-MenuItem.acl-BasicMenu--active button:hover {\n        color: #ffffff; }\n  .acl-MenuItem.acl-BasicMenu--disabled {\n    cursor: not-allowed;\n    opacity: 0.3; }\n    .acl-MenuItem.acl-BasicMenu--disabled.acl-MenuItem--selected svg {\n      color: #009ebd; }\n    .acl-MenuItem.acl-BasicMenu--disabled:hover {\n      color: inherit;\n      background-color: inherit; }\n      .acl-MenuItem.acl-BasicMenu--disabled:hover.acl-MenuItem--selected svg {\n        color: #009ebd; }\n  .acl-MenuItem.acl-BasicMenu--hasDivider {\n    border-bottom: 1px solid #e2e2e2;\n    padding-bottom: 16px;\n    margin-bottom: 8px; }\n  .acl-MenuItem.acl-MenuItem-emptyText {\n    font-style: italic;\n    color: #474747;\n    opacity: unset;\n    cursor: unset; }\n  .acl-MenuItem .acl-MenuItem-leftElement {\n    display: inline-flex;\n    margin-right: 8px;\n    flex: 1 1 auto; }\n    .acl-MenuItem .acl-MenuItem-leftElement > span {\n      display: inline-flex; }\n    .acl-MenuItem .acl-MenuItem-leftElement .hideLeftElement {\n      visibility: hidden;\n      margin-right: -8px; }\n  .acl-MenuItem .acl-MenuItem-rightElement {\n    flex: 1 1 auto;\n    align-items: center;\n    margin-left: 4px; }\n  .acl-MenuItem .acl-BasicMenu--label {\n    flex: 0 1 auto;\n    text-overflow: ellipsis;\n    white-space: nowrap;\n    overflow: hidden;\n    width: 100%; }\n  .acl-MenuItem .acl-MenuItem-subLabel {\n    color: rgba(0, 0, 0, 0.5);\n    margin-top: 6px; }\n  .acl-MenuItem .acl-MenuItem-rightIcon {\n    margin-right: 12px; }\n";
styleInject(css_248z$9);

var MenuItem = function (props) {
    if (!props.label) {
        return null;
    }
    var _a = useState(false), tooltip = _a[0], setTooltip = _a[1];
    var menuItemRef = useRef(null);
    var handleClick = function (event) {
        if (props.disabled)
            return null;
        if (typeof props.onClick === 'function') {
            props.onClick({ props: props, event: event });
        }
        if (typeof props.onSelection === 'function') {
            props.onSelection({ props: props, event: event });
        }
    };
    var controlArea = useMemo(function () { return makeRenderer(props.renderMenuItemControls, props); }, [props.renderMenuItemControls]);
    useEffect(function () {
        if (menuItemRef.current) {
            if (menuItemRef.current.scrollWidth >
                menuItemRef.current.clientWidth) {
                setTooltip(false);
            }
            else {
                setTooltip(true);
            }
        }
    }, [window.innerWidth, props]);
    var renderTooltipTrigger = function () {
        return (React.createElement("div", { className: classNames('acl-MenuItem', props.className, {
                'acl-BasicMenu--disabled': props.disabled,
                'acl-BasicMenu--active': props.active,
                'acl-BasicMenu--hasDivider': props.hasDivider
            }), onClick: handleClick, onMouseOver: props.onMouseOver },
            props.leftElement && (React.createElement("div", { className: "acl-MenuItem-leftElement" }, props.leftElement)),
            React.createElement("div", { className: "acl-BasicMenu--label", ref: menuItemRef },
                props.label,
                props.subLabel && (React.createElement("div", { className: "acl-MenuItem-subLabel" }, props.subLabel))),
            typeof props.renderMenuItemControls === 'function' ? (controlArea('renderMenuItemControls', props)) : (React.createElement(React.Fragment, null, props.rightIcon && (React.createElement("div", { className: "acl-MenuItem-rightIcon" },
                React.createElement(Tooltip, { disabled: !props.rightIconText, text: props.rightIconText, placement: "bottom", trigger: function () { return (React.createElement(Icon, { name: props.rightIcon, color: props.rightIconColor })); } }))))),
            props.rightElement && (React.createElement("div", { className: "acl-MenuItem-rightElement" }, props.rightElement))));
    };
    return (React.createElement(Tooltip, { trigger: renderTooltipTrigger, text: props.label, placement: "bottom", disabled: tooltip, style: { display: 'block' } }));
};

var css_248z$a = ".acl-Spinner {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center; }\n  .acl-Spinner.acl-Spinner--determinate svg {\n    fill: none; }\n    .acl-Spinner.acl-Spinner--determinate svg path {\n      transition: stroke-dashoffset 0.25s cubic-bezier(0.495, -0.17, 0, 1.005); }\n  .acl-Spinner.acl-Spinner--indeterminate svg {\n    transform-origin: center;\n    animation: 1s linear infinite spin; }\n\n@keyframes spin {\n  0% {\n    transform: rotate(0); }\n  100% {\n    transform: rotate(360deg); } }\n";
styleInject(css_248z$a);

var Spinner = function (_a) {
    var _b = _a.size, size = _b === void 0 ? 'large' : _b, _c = _a.indeterminateLoadingText, indeterminateLoadingText = _c === void 0 ? 'Loading...' : _c, className = _a.className, style = _a.style, props = __rest(_a, ["size", "indeterminateLoadingText", "className", "style"]);
    var R = size === 'small' ? 8 : 16;
    var CIRCUMFERENCE = R * 2;
    var STROKE_WIDTH = size === 'small' ? 2 : 4;
    var renderDeterminate = function () {
        // Shoutout to blueprint from whom this is largely stolen
        // https://github.com/palantir/blueprint/blob/develop/packages/core/src/components/spinner/spinner.tsx
        var SPINNER_TRACK = "M 50,50 m 0,-" + R + " a " + R + "," + R + " 0 1 1 0," + R * 2 + " a " + R + "," + R + " 0 1 1 0,-" + R * 2;
        var PATH_LENGTH = 280;
        var strokeOffset = PATH_LENGTH - PATH_LENGTH * (props.progress / 100);
        var getViewBox = function () {
            var radius = R + STROKE_WIDTH / 2;
            var viewBoxX = (50 - radius).toFixed(2);
            var viewBoxWidth = (radius * 2).toFixed(2);
            return viewBoxX + " " + viewBoxX + " " + viewBoxWidth + " " + viewBoxWidth;
        };
        return (React.createElement("svg", { viewBox: getViewBox(), width: CIRCUMFERENCE, height: CIRCUMFERENCE, xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("path", { d: SPINNER_TRACK, pathLength: PATH_LENGTH, stroke: ColorTheme.transparent(ColorTheme.black, 0.1), strokeWidth: STROKE_WIDTH }),
            React.createElement("path", { d: SPINNER_TRACK, pathLength: PATH_LENGTH, strokeDasharray: PATH_LENGTH + " " + PATH_LENGTH, strokeDashoffset: strokeOffset, strokeWidth: STROKE_WIDTH, stroke: ColorTheme.amobee40 })));
    };
    var renderIndeterminate = function () {
        return (React.createElement("svg", { width: CIRCUMFERENCE, height: CIRCUMFERENCE, viewBox: "0 0 " + CIRCUMFERENCE + " " + CIRCUMFERENCE },
            React.createElement("defs", null,
                React.createElement("linearGradient", { id: "a", x1: "50%", x2: "50%", y1: "0%", y2: "100%" },
                    React.createElement("stop", { offset: "0%", stopColor: "#009EBD", stopOpacity: ".05" }),
                    React.createElement("stop", { offset: "100%", stopColor: "#009EBD", stopOpacity: ".5" })),
                React.createElement("linearGradient", { id: "b", x1: "50%", x2: "50%", y1: "0%", y2: "100%" },
                    React.createElement("stop", { offset: "0%", stopColor: "#009EBD" }),
                    React.createElement("stop", { offset: "100%", stopColor: "#009EBD", stopOpacity: ".5" }))),
            React.createElement("g", { fill: "none", fillRule: "evenodd", style: {
                    transform: size === 'large' ? 'scale(2)' : undefined
                } },
                React.createElement("path", { stroke: "url(#a)", strokeWidth: "2", d: "M8,15 C11.8659932,15 15,11.8659932 15,8 C15,4.13400675 11.8659932,1 8,1" }),
                React.createElement("path", { stroke: "url(#b)", strokeWidth: "2", d: "M1,15 C4.86599325,15 8,11.8659932 8,8 C8,4.13400675 4.86599325,1 1,1", transform: "matrix(-1 0 0 1 9 0)" }))));
    };
    return (React.createElement("div", { className: "acl-Spinner acl-Spinner--" + (props.progress ? 'determinate' : 'indeterminate') + " " + (className ? className : ''), style: __assign({ flexDirection: size === 'small' ? 'row' : 'column' }, style) },
        props.progress ? renderDeterminate() : renderIndeterminate(),
        props.showText && (React.createElement("span", { className: "acl-Spinner-text", style: {
                marginLeft: size === 'small' ? 4 : 0,
                marginTop: size === 'small' ? 0 : 4
            } }, props.progress && typeof props.progress === 'number'
            ? props.progress + "%"
            : indeterminateLoadingText))));
};

var css_248z$b = ".acl-BasicMenu {\n  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 16px 0 rgba(0, 0, 0, 0.3);\n  display: inline-flex;\n  flex-direction: column;\n  background: #ffffff;\n  z-index: 100; }\n  .acl-BasicMenu.acl-BasicMenu--dark {\n    background-color: #303030;\n    color: rgba(255, 255, 255, 0.7);\n    box-shadow: inset 0 0 0 1px #777777; }\n    .acl-BasicMenu.acl-BasicMenu--dark.acl-BasicMenu--searchable .acl-BasicMenu-search {\n      border-bottom: 1px solid #474747; }\n  .acl-BasicMenu.acl-BasicMenu--searchable .acl-BasicMenu-search {\n    border-bottom: 1px solid rgba(0, 0, 0, 0.1); }\n    .acl-BasicMenu.acl-BasicMenu--searchable .acl-BasicMenu-search .acl-Input--borderless:hover, .acl-BasicMenu.acl-BasicMenu--searchable .acl-BasicMenu-search .acl-Input--borderless:focus-within {\n      box-shadow: none; }\n  .acl-BasicMenu.acl-BasicMenu--keyboardNavigation .acl-MenuItem:hover:not(.acl-BasicMenu--active) {\n    color: initial;\n    background: initial; }\n  .acl-BasicMenu .acl-BasicMenu-Items {\n    overflow-x: hidden;\n    overflow-y: auto; }\n    .acl-BasicMenu .acl-BasicMenu-Items.acl-BasicMenu-Items--scroll {\n      overflow-y: scroll; }\n";
styleInject(css_248z$b);

var css_248z$c = ".acl-MenuCategory {\n  max-width: 100%;\n  font-size: 13px;\n  font-weight: bold;\n  color: rgba(0, 0, 0, 0.5);\n  background: #f2f2f2;\n  padding: 4px 12px; }\n";
styleInject(css_248z$c);

var MenuCategory = function (props) {
    if (!props.category) {
        return null;
    }
    return React.createElement("div", { className: 'acl-MenuCategory' }, props.category);
};

var BasicMenu = /** @class */ (function (_super) {
    __extends(BasicMenu, _super);
    function BasicMenu(props) {
        var _this = _super.call(this, props) || this;
        _this.rowHeight = 32; // Expected height for standard menu item
        _this.rowWithSubLabelHeight = 54; // Expected height for menu item with subLabel
        _this.categoryHeight = 24; // Expected height for category
        var resultsLength = props.items
            ? props.items.length
            : props.children
                ? React.Children.toArray(props.children).length
                : 0;
        var items = BasicMenu.getCategorizedItems(props.items, props.categoryAccessor);
        if (!items) {
            items =
                BasicMenu.getCategorizedItems(_this.getItemsFromChildren(), props.categoryAccessor) || [];
        }
        var maxHeight = _this.generateMenuMaxHeight(items, 0);
        _this.state = {
            itemSource: props.items ? 'items' : 'children',
            initialItems: items || [],
            filteredItems: items || [],
            displayedItems: _this.getInitialDisplayedItems(items || []),
            initialItemsWidth: null,
            keyNavIndex: null,
            searchTerm: '',
            resultsLength: resultsLength,
            maxRows: props.maxResultRows,
            minWidth: props.minWidth + "px",
            maxWidth: props.maxWidth + "px",
            infiniteScroll: props.infiniteScroll,
            menuItemHeight: _this.getMenuItemHeight(items),
            menuMaxHeight: maxHeight,
            menuStyle: {
                maxHeight: maxHeight + "px"
            }
        };
        _this.setScrollableMenuRef = _this.setScrollableMenuRef.bind(_this);
        _this.loadMoreRef = React.createRef();
        _this.handleOnInputKey = _this.handleOnInputKey.bind(_this);
        _this.handleItemSelect = _this.handleItemSelect.bind(_this);
        _this.handleItemClick = _this.handleItemClick.bind(_this);
        _this.filterMenuItems = _this.filterMenuItems.bind(_this);
        _this.setFilteredAndDisplayedMenuItems = _this.setFilteredAndDisplayedMenuItems.bind(_this);
        _this.generateMenuMaxHeight = _this.generateMenuMaxHeight.bind(_this);
        _this.setNavIndex = _this.setNavIndex.bind(_this);
        _this.setSearchTerm = _this.setSearchTerm.bind(_this);
        _this.handleMenuScroll = _this.handleMenuScroll.bind(_this);
        _this.appendDisplayedNextItems = _this.appendDisplayedNextItems.bind(_this);
        return _this;
    }
    BasicMenu.getCategorizedItems = function (items, categoryAccessor) {
        if (!items) {
            return null;
        }
        if (categoryAccessor) {
            var categorizedItems_1 = [];
            var categories = Array.from(new Set(items
                .filter(function (item) { return item[categoryAccessor]; })
                .map(function (item) { return item[categoryAccessor]; }))).sort();
            categories.forEach(function (cat) {
                categorizedItems_1.push({ category: cat });
                categorizedItems_1 = __spreadArrays(categorizedItems_1, items.filter(function (item) { return item[categoryAccessor] === cat; }));
            });
            var noCategory = items.filter(function (item) {
                return typeof item[categoryAccessor] !== 'string' &&
                    typeof item[categoryAccessor] !== 'number';
            });
            if (noCategory.length) {
                categorizedItems_1.push({ category: 'Other' });
                categorizedItems_1 = __spreadArrays(categorizedItems_1, noCategory);
            }
            return categorizedItems_1;
        }
        return items;
    };
    BasicMenu.prototype.getItemsFromChildren = function () {
        var items = [];
        if (this.props.children) {
            React.Children.forEach(React.Children.toArray(this.props.children), function (child) {
                items.push(child.props);
            });
        }
        return items;
    };
    BasicMenu.prototype.componentDidMount = function () {
        // we will call this function when the component mounts
        if (typeof this.props.infiniteScrollCallback === 'function') {
            this.initInfiniteScroll();
        }
    };
    BasicMenu.prototype.initInfiniteScroll = function () {
        var _this = this;
        var suppressInitialCall = true;
        var observer = new IntersectionObserver(function (entry) {
            // fix this later: When the no of items is lesser than the max-height of the menu, it doesn't trigger infiniteScrollCallback function
            if (entry[0].isIntersecting && !suppressInitialCall) {
                _this.props.infiniteScrollCallback();
            }
            /* This is probably a bit hacky. For unknown reasons, this _always_
        fires on initial load, when the menu isn't even open. This suppresses
        the initial callback
        */
            suppressInitialCall = false;
        }, 
        // A threshold of 1.0 means that when 100% of the target is visible within the element specified by the root option, the callback is invoked.
        { root: null, threshold: 1.0, rootMargin: '0px' });
        // intersection observer is observing a div at the end of the menu which is the target
        if (this.loadMoreRef) {
            observer.observe(this.loadMoreRef.current);
        }
    };
    BasicMenu.prototype.componentDidUpdate = function () {
        if (this.scrollableMenuRef &&
            this.scrollableMenuRef.offsetWidth &&
            this.scrollableMenuRef.offsetWidth !== this.state.initialItemsWidth) {
            this.setState({
                initialItemsWidth: this.scrollableMenuRef.offsetWidth
            });
        }
        var newItems = this.state.itemSource === 'items'
            ? this.props.items
            : this.getItemsFromChildren();
        var categorized = BasicMenu.getCategorizedItems(newItems, this.props.categoryAccessor);
        var currentItems = Array.isArray(this.state.initialItems)
            ? __spreadArrays(this.state.initialItems).sort()
            : [];
        var incomingItems = __spreadArrays(categorized).sort() || [];
        if (!isEqual(currentItems, incomingItems)) {
            var items = categorized || [];
            var itemHeight = this.getMenuItemHeight(items);
            var maxHeight = this.generateMenuMaxHeight(items, 0);
            this.setState({
                initialItems: items,
                filteredItems: items,
                displayedItems: this.getInitialDisplayedItems(items),
                menuItemHeight: itemHeight,
                menuMaxHeight: maxHeight,
                menuStyle: {
                    maxHeight: maxHeight + "px"
                }
            });
        }
    };
    BasicMenu.prototype.render = function () {
        var _this = this;
        if (React.Children.count(this.props.children) === 0 &&
            !this.props.items &&
            !this.props.emptyText) {
            return null;
        }
        return (React.createElement("div", { ref: this.props.menuRef, className: classNames('acl-BasicMenu', this.props.className, {
                'acl-BasicMenu--dark': this.props.dark,
                'acl-BasicMenu--keyboardNavigation': !!this.state
                    .keyNavIndex,
                'acl-BasicMenu--searchable': this.props.searchable
            }), style: this.menuStyles() },
            this.renderSearch(),
            React.createElement("div", { ref: function (el) { return _this.setScrollableMenuRef(el); }, className: classNames('acl-BasicMenu-Items', {
                    'acl-BasicMenu-Items--scroll': this.state.filteredItems.length > this.state.maxRows
                }), style: this.state.menuStyle },
                React.createElement("div", null,
                    this.renderItems(),
                    React.createElement("div", { ref: this.loadMoreRef })))));
    };
    BasicMenu.prototype.getMenuItemHeight = function (items) {
        var itemWithSubLabel = items.find(function (item) { return item && !!item.subLabel; });
        return itemWithSubLabel ? this.rowWithSubLabelHeight : this.rowHeight;
    };
    BasicMenu.prototype.menuStyles = function () {
        if (this.props.width) {
            return __assign({ width: this.props.width }, this.props.style);
        }
        return __assign({ width: this.state.initialItemsWidth, minWidth: this.state.minWidth, maxWidth: this.state.maxWidth }, this.props.style);
    };
    BasicMenu.prototype.getInitialDisplayedItems = function (filtered) {
        var lastIndex = this.getLastMenuItemIndex(filtered); // Return maxRows * 3 for scroll buffer
        return filtered.slice(0, lastIndex + 1);
    };
    BasicMenu.prototype.appendDisplayedNextItems = function () {
        var startIndex = this.state.displayedItems.length;
        var itemsToAppend = this.state.filteredItems.slice(startIndex, startIndex + this.state.maxRows);
        this.setState(function (prevState) { return ({
            displayedItems: __spreadArrays(prevState.displayedItems).concat(itemsToAppend)
        }); });
    };
    BasicMenu.prototype.setScrollableMenuRef = function (element) {
        this.scrollableMenuRef = element;
    };
    BasicMenu.prototype.renderSearch = function () {
        var _this = this;
        return (this.props.searchable && (React.createElement("div", { className: "acl-BasicMenu-search" },
            React.createElement(Input, { variant: InputVariant.UNDERLINED, compact: true, dark: this.props.dark, leftElement: "Search", placeholder: "Search", onKeyUp: function (e) {
                    _this.handleOnInputKey(e, true);
                } }))));
    };
    BasicMenu.prototype.renderEmptyState = function () {
        if (this.props.renderEmptyMenuItemWithIconRef && this.props.emptyText) {
            return (React.createElement(MenuItem, { className: "acl-MenuItem-emptyText", disabled: true, label: this.props.emptyText, leftElement: this.props.renderEmptyMenuItemWithIconRef }));
        }
        else if (!this.props.renderEmptyMenuItemWithIconRef &&
            this.props.emptyText) {
            return (React.createElement(MenuItem, { className: "acl-MenuItem-emptyText", disabled: true, label: this.props.emptyText }));
        }
        return null;
    };
    BasicMenu.prototype.renderItems = function () {
        var _this = this;
        if (!this.state.initialItems || !this.state.initialItems.length) {
            return this.renderEmptyState();
        }
        var firstItem = this.state.displayedItems && this.state.displayedItems[0];
        if (!firstItem ||
            (!firstItem.category && !firstItem.label && this.props.children)) {
            // Return raw children if not standard menu items or categories:
            return this.props.children;
        }
        if (this.props.searchable) {
            // If no results
            if (!this.state.displayedItems.length) {
                return this.renderEmptyState();
            }
            return this.state.displayedItems.map(function (item, i) {
                return item.category ? (React.createElement(MenuCategory, __assign({}, item, { key: i }))) : (React.createElement(MenuItem, __assign({}, item, { key: i, label: React.createElement(HighlightMatch, { match: _this.state.searchTerm }, item[_this.props.itemLabel]), active: i === _this.state.keyNavIndex, onClick: function (_a) {
                        var event = _a.event;
                        return _this.handleItemClick(item, event);
                    } })));
            });
        }
        return this.state.displayedItems.map(function (item, i) {
            return item.category ? (React.createElement(MenuCategory, __assign({}, item, { key: i }))) : (React.createElement(MenuItem, __assign({}, item, { key: i, label: item[_this.props.itemLabel], active: i === _this.state.keyNavIndex, onClick: function (_a) {
                    var event = _a.event;
                    return _this.handleItemClick(item, event);
                } })));
        });
    };
    BasicMenu.prototype.handleMenuScroll = function (e) {
        var target = e.target;
        var displayedMenuItems = this.state.displayedItems.filter(function (item) { return !item.category; });
        var fi = // First item of second to last page (trigger point to append another page)
         displayedMenuItems[displayedMenuItems.length - this.state.maxRows * 2];
        var fiIndex = indexOf(this.state.displayedItems, fi);
        var fiPosition = this.getItemPos(this.state.displayedItems, fiIndex);
        var atScrollEnd = target.offsetHeight + target.scrollTop >= target.scrollHeight;
        if (target.scrollTop >= fiPosition || atScrollEnd) {
            this.appendDisplayedNextItems();
        }
        if (this.props.onScroll) {
            this.props.onScroll(e);
        }
    };
    BasicMenu.prototype.setSearchTerm = function (searchStr) {
        this.setState({ searchTerm: searchStr });
    };
    BasicMenu.prototype.setNavIndex = function (index) {
        var scrollTop = this.scrollableMenuRef.scrollTop;
        var itemHeight = this.state.menuItemHeight;
        var itemPos = this.getItemPos(this.state.displayedItems, index);
        var bottom = scrollTop + this.state.menuMaxHeight;
        var distFromItemBottom = bottom - itemPos - itemHeight;
        var distFromItemTop = scrollTop - itemPos;
        var itemAbove = distFromItemTop >= itemHeight;
        var itemPartialAbove = distFromItemTop < itemHeight && distFromItemTop > 0;
        var itemBelow = distFromItemBottom < 0 && distFromItemBottom <= -itemHeight;
        var itemPartialBelow = distFromItemBottom < 0 && distFromItemBottom > -itemHeight;
        this.setState({ keyNavIndex: index });
        if (index !== null && index !== undefined) {
            if (index === 0 || index === 1) {
                this.scrollableMenuRef.scrollTop = 0;
            }
            else if (itemPartialBelow) {
                this.scrollableMenuRef.scrollTop =
                    this.scrollableMenuRef.scrollTop - distFromItemBottom;
            }
            else if (itemBelow || itemAbove || itemPartialAbove) {
                this.scrollableMenuRef.scrollTop =
                    itemPos < itemHeight ? 0 : itemPos;
            }
        }
    };
    BasicMenu.prototype.getItemPos = function (items, index) {
        var numCategories = items.filter(function (item, idx) { return idx < index && item.category; }).length;
        var numItems = index - numCategories;
        return (numCategories * this.categoryHeight +
            numItems * this.state.menuItemHeight);
    };
    // eslint-disable-next-line class-methods-use-this
    BasicMenu.prototype.getLastMenuItemIndex = function (items) {
        // this will return the last index of the filteredIItems
        return items.length - 1;
    };
    BasicMenu.prototype.generateMenuMaxHeight = function (items, startIndex) {
        var endIndex = this.getLastMenuItemIndex(items);
        var numVisibleCategories = items.filter(function (item, index) {
            return index >= startIndex && index <= endIndex && item.category;
        }).length;
        return (this.props.maxResultRows * this.getMenuItemHeight(items) +
            numVisibleCategories * this.categoryHeight);
    };
    BasicMenu.prototype.setFilteredAndDisplayedMenuItems = function (items) {
        var _this = this;
        var maxHeight = this.generateMenuMaxHeight(items, 0);
        this.setState(function () { return ({
            filteredItems: items,
            displayedItems: _this.getInitialDisplayedItems(items),
            menuMaxHeight: maxHeight,
            menuStyle: {
                maxHeight: maxHeight + "px"
            }
        }); });
    };
    BasicMenu.prototype.filterMenuItems = function (searchStr) {
        var _this = this;
        var expression = new RegExp(addSlashes(searchStr), 'ig');
        var filteredMenuItems = this.state.initialItems.filter(function (item) {
            return (expression.test(item[_this.props.itemLabel]) ||
                !!item.category);
        });
        if (filteredMenuItems.length > 0) {
            var hasCategories = !!filteredMenuItems.find(function (item) { return item.category; });
            if (hasCategories) {
                var filteredWithoutEmptyCategories = filteredMenuItems.filter(function (item, index) {
                    return (!item.category || // Not a category
                        (index !== filteredMenuItems.length - 1 && // A category and not last item in the list
                            !filteredMenuItems[index + 1].category) // A category with an item under it (not empty)
                    );
                });
                this.setFilteredAndDisplayedMenuItems(filteredWithoutEmptyCategories);
            }
            else {
                this.setFilteredAndDisplayedMenuItems(filteredMenuItems);
            }
        }
        else {
            this.setFilteredAndDisplayedMenuItems(this.state.initialItems);
        }
    };
    BasicMenu.prototype.handleItemClick = function (itemProps, event // making params match handleItemSelect so they can be used interchangeably
    ) {
        if (typeof itemProps.onClick === 'function') {
            itemProps.onClick({ props: itemProps, event: event });
        }
        this.handleItemSelect(itemProps, event);
    };
    BasicMenu.prototype.handleItemSelect = function (itemProps, event, persistHighlightOnSelect) {
        if (typeof itemProps.onSelection === 'function') {
            itemProps.onSelection({
                props: itemProps,
                event: event
            });
        }
        if (typeof this.props.onSelection === 'function') {
            this.props.onSelection({
                props: itemProps,
                event: event
            });
        }
        if (!persistHighlightOnSelect) {
            this.setNavIndex(null);
        }
    };
    BasicMenu.prototype.handleOnInputKey = function (e, localSearch, persistHighlightOnSelect) {
        var _this = this;
        var setNextItem = function (startingIndex, direction) {
            var dir = direction || 'down';
            for (var i = startingIndex; dir === 'down' ? i <= _this.state.displayedItems.length : i >= 0; dir === 'down' ? i++ : i--) {
                // Find the next item that is not a category beginning with startingIndex
                if (!_this.state.displayedItems[i]) {
                    setNextItem(0);
                    break;
                }
                if (!_this.state.displayedItems[i].category) {
                    _this.setNavIndex(i);
                    break;
                }
                else if (dir === 'up' && i === 0) {
                    _this.setNavIndex(null);
                    break;
                }
            }
        };
        switch (e.key) {
            case 'ArrowDown':
                setNextItem(!this.state.keyNavIndex && this.state.keyNavIndex !== 0
                    ? 0
                    : this.state.keyNavIndex + 1);
                break;
            case 'ArrowUp':
                e.currentTarget.selectionStart = get(e, 'currentTarget.value.length');
                e.currentTarget.selectionEnd = get(e, 'currentTarget.value.length');
                if (this.state.keyNavIndex - 1 >= 0) {
                    setNextItem(this.state.keyNavIndex - 1, 'up');
                }
                break;
            case 'Enter':
                if (this.state.keyNavIndex !== null) {
                    this.handleItemSelect(this.state.displayedItems[this.state.keyNavIndex], e, persistHighlightOnSelect);
                }
                break;
            default:
                if (e.key === 'Backspace') {
                    this.setNavIndex(null);
                }
                if (localSearch) {
                    this.setSearchTerm(e.currentTarget.value);
                    this.filterMenuItems(e.currentTarget.value);
                    if (e.currentTarget.value !== this.state.searchTerm) {
                        this.setNavIndex(null);
                    }
                }
                else if (this.state.keyNavIndex) {
                    this.setNavIndex(null);
                }
        }
    };
    BasicMenu.defaultProps = {
        itemLabel: 'label',
        error: false,
        disabled: false,
        debounceTimeMs: 0,
        loadMoreOffset: 72,
        maxResultRows: 15,
        minWidth: 200,
        maxWidth: 560,
        infiniteScroll: 'client'
    };
    BasicMenu.Item = MenuItem;
    BasicMenu.Category = MenuCategory;
    BasicMenu.propTypes = {
        children: function (props, propName) {
            var prop = props[propName];
            var error;
            React.Children.forEach(prop, function (child) {
                if (child &&
                    child.type !== MenuItem &&
                    child.type !== MenuCategory &&
                    child.type !== Spinner) {
                    error = new Error('Children should be of type `MenuItem`, `MenuCategory` or `Spinner`');
                }
            });
            return error;
        }
    };
    return BasicMenu;
}(Component));

var css_248z$d = ".acl-Dimmer {\n  position: fixed;\n  top: 0;\n  left: 0;\n  height: 100vh;\n  width: 100vw;\n  background: rgba(0, 0, 0, 0.5);\n  z-index: 1; }\n";
styleInject(css_248z$d);

var transitionStyles = {
    entering: { opacity: 0 },
    entered: { opacity: 1 },
    exiting: { opacity: 1 },
    exited: { opacity: 0 },
    unmounted: { opacity: 0 }
};
var Dimmer = function (_a) {
    var _b = _a.visible, visible = _b === void 0 ? true : _b, _c = _a.fadeInDuration, fadeInDuration = _c === void 0 ? 400 : _c, props = __rest(_a, ["visible", "fadeInDuration"]);
    var defaultStyle = {
        opacity: 0,
        transition: "opacity " + fadeInDuration + "ms cubic-bezier(0.19, 1, 0.22, 1)"
    };
    return (React.createElement(Transition, { timeout: 0, in: visible, appear: true, mountOnEnter: true, unmountOnExit: true }, function (state) { return (React.createElement("div", { className: "acl-Dimmer", style: __assign(__assign(__assign({}, defaultStyle), transitionStyles[state]), { zIndex: props.zIndex }), onClick: function (e) { return props.onClick && props.onClick(e); } })); }));
};

var css_248z$e = ".acl-SlideoutMenu-itemWrapper {\n  list-style-type: none; }\n  .acl-SlideoutMenu-itemWrapper:not(:first-child) {\n    margin-top: 8px; }\n\n.acl-SlideoutMenu-link {\n  color: rgba(255, 255, 255, 0.7);\n  text-decoration: none;\n  padding: 8px 16px;\n  min-height: 40px;\n  display: flex;\n  align-items: flex-start;\n  border-radius: 3px; }\n  .acl-SlideoutMenu-link.active {\n    background-color: #000000;\n    color: #ffffff; }\n    .acl-SlideoutMenu-link.active:hover {\n      pointer-events: none; }\n  .acl-SlideoutMenu-link:hover {\n    cursor: pointer;\n    background-color: #474747; }\n  .acl-SlideoutMenu-link:hover, .acl-SlideoutMenu-link:focus, .acl-SlideoutMenu-link:active {\n    text-decoration: none;\n    color: rgba(255, 255, 255, 0.7); }\n\n.acl-SlideoutMenu-icon {\n  line-height: 0;\n  height: 24px;\n  flex: 0 0 24px;\n  overflow: hidden; }\n\n.acl-SlideoutMenu-label {\n  align-self: center;\n  margin-left: 12px; }\n";
styleInject(css_248z$e);

// TODO: figure out best way to set active item
var SlideoutMenuItem = function (props) { return (React.createElement("li", { className: "acl-SlideoutMenu-itemWrapper" },
    React.createElement("a", __assign({}, props.linkProps, { title: props.title || props.label, className: classNames('acl-SlideoutMenu-link', {
            active: props.active
        }) }),
        props.icon && (React.createElement(Icon, { name: props.icon, className: "acl-SlideoutMenu-icon" })),
        React.createElement("span", { className: "acl-SlideoutMenu-label" }, props.label)))); };

var css_248z$f = "@import-normalize;\n* {\n  box-sizing: border-box; }\n\nbody {\n  font-family: \"Roboto\", sans-serif;\n  font-size: 14px;\n  line-height: 16px;\n  color: #474747;\n  margin: 0; }\n\n.acl-SlideoutMenu {\n  position: absolute;\n  top: 0;\n  left: -100%;\n  height: 100%;\n  padding: 8px 0;\n  background-color: #303030;\n  overflow: hidden;\n  display: flex;\n  flex-direction: column;\n  border-right: 1px solid rgba(255, 255, 255, 0.3);\n  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.3);\n  z-index: 100;\n  transition-property: left;\n  transition-timing-function: cubic-bezier(0.19, 1, 0.22, 1); }\n  .acl-SlideoutMenu.open {\n    left: 0; }\n  .acl-SlideoutMenu ul {\n    flex: 1 1 auto;\n    margin: 0;\n    padding: 0 8px;\n    overflow: hidden;\n    overflow-y: auto; }\n";
styleInject(css_248z$f);

var TRANSITION_DURATION = 400;
var DEFAULT_MENU_WIDTH = 216;
var SlideoutMenu = function (_a) {
    var 
    // Defaults
    _b = _a.open, 
    // Defaults
    open = _b === void 0 ? false : _b, _c = _a.width, width = _c === void 0 ? DEFAULT_MENU_WIDTH : _c, _d = _a.closeOnDimmerClick, closeOnDimmerClick = _d === void 0 ? true : _d, _e = _a.closeOnSelection, closeOnSelection = _e === void 0 ? true : _e, _f = _a.withDimmer, withDimmer = _f === void 0 ? true : _f, _g = _a.dimmerZIndex, dimmerZIndex = _g === void 0 ? 1 : _g, props = __rest(_a, ["open", "width", "closeOnDimmerClick", "closeOnSelection", "withDimmer", "dimmerZIndex"]);
    var handleToggle = function (action) {
        if (props.toggle) {
            props.toggle(action);
        }
    };
    if (!props.items || !props.items.length)
        return null;
    return (React.createElement(Portal, { portalRoot: props.portalRoot },
        React.createElement("nav", { className: classNames('acl-SlideoutMenu', props.className, {
                open: open
            }), style: {
                width: width,
                transitionDuration: TRANSITION_DURATION + "ms"
            } },
            React.createElement("ul", null, props.items.map(function (item, i) { return (React.createElement(SlideoutMenuItem, __assign({}, item, { key: i, linkProps: __assign({ onClick: function () {
                        return closeOnSelection && handleToggle('CLOSE');
                    } }, item.linkProps) }))); }))),
        withDimmer && (React.createElement(Dimmer, { visible: open, fadeInDuration: TRANSITION_DURATION, zIndex: dimmerZIndex, onClick: function () { return closeOnDimmerClick && handleToggle('CLOSE'); } }))));
};

var css_248z$g = ".acl-CollapsibleNav {\n  position: absolute;\n  top: 0;\n  left: 0;\n  height: 100%;\n  width: 216px;\n  padding: 8px 0;\n  background-color: #303030;\n  display: flex;\n  flex-direction: column;\n  transition: all 0.25s ease-in-out; }\n  .acl-CollapsibleNav.acl-CollapsibleNav--collapsed .acl-CollapsibleNavItem-link .acl-CollapsibleNavItem-label {\n    opacity: 0;\n    transition: opacity 0.25s ease-in-out; }\n  .acl-CollapsibleNav ul {\n    flex: 1 1 auto;\n    margin: 0;\n    padding: 0;\n    overflow: hidden;\n    overflow-y: auto; }\n  .acl-CollapsibleNav .acl-CollapsibleNav-footer {\n    flex: 0 1 auto;\n    display: flex;\n    justify-content: flex-end;\n    padding: 0 8px; }\n    .acl-CollapsibleNav .acl-CollapsibleNav-footer .acl-CollapsibleNav-CollapseToggle {\n      width: 40px; }\n      .acl-CollapsibleNav .acl-CollapsibleNav-footer .acl-CollapsibleNav-CollapseToggle .acl-Icon {\n        transition: transform 0.25s 0.25s cubic-bezier(0.68, -0.55, 0.265, 1.55); }\n\n.acl-CollapsibleNavItem {\n  list-style-type: none;\n  padding: 0 8px; }\n  .acl-CollapsibleNavItem:not(:first-child) {\n    margin-top: 8px; }\n  .acl-CollapsibleNavItem .acl-CollapsibleNavItem-link {\n    color: rgba(255, 255, 255, 0.7);\n    text-decoration: none;\n    display: flex;\n    border-radius: 3px;\n    align-items: center;\n    padding: 8px 12px;\n    min-height: 36px;\n    white-space: nowrap;\n    overflow: hidden; }\n    .acl-CollapsibleNavItem .acl-CollapsibleNavItem-link.active {\n      background: white;\n      color: rgba(0, 0, 0, 0.7); }\n      .acl-CollapsibleNavItem .acl-CollapsibleNavItem-link.active:hover {\n        cursor: default; }\n    .acl-CollapsibleNavItem .acl-CollapsibleNavItem-link:not(.active):hover, .acl-CollapsibleNavItem .acl-CollapsibleNavItem-link:not(.active).acl-CollapsibleNavItem-link--dropdown-open {\n      cursor: pointer;\n      background-color: #474747; }\n    .acl-CollapsibleNavItem .acl-CollapsibleNavItem-link:not(.active):hover, .acl-CollapsibleNavItem .acl-CollapsibleNavItem-link:not(.active):focus, .acl-CollapsibleNavItem .acl-CollapsibleNavItem-link:not(.active):active {\n      text-decoration: none;\n      color: rgba(255, 255, 255, 0.7); }\n  .acl-CollapsibleNavItem .acl-CollapsibleNavItem-icon {\n    line-height: 0;\n    overflow: hidden;\n    flex: 0 0 auto; }\n  .acl-CollapsibleNavItem .acl-CollapsibleNavItem-label {\n    align-self: center;\n    margin-left: 12px;\n    text-overflow: ellipsis;\n    overflow: hidden;\n    flex: 1 1 auto; }\n";
styleInject(css_248z$g);

var DEFAULT_WIDTH = 216;
var COLLAPSED_WIDTH = 56;
var CollapsibleNav = function (_a) {
    var _b = _a.width, width = _b === void 0 ? DEFAULT_WIDTH : _b, props = __rest(_a, ["width"]);
    var _c = useState(props.initialCollapsed || false), collapsed = _c[0], setCollapsed = _c[1];
    return (React.createElement("nav", { className: classNames('acl-CollapsibleNav', {
            'acl-CollapsibleNav--collapsed': collapsed
        }), style: {
            width: collapsed ? COLLAPSED_WIDTH : width
        } },
        React.createElement("ul", null, props.children),
        React.createElement("div", { className: "acl-CollapsibleNav-footer" },
            React.createElement(Button, { variant: ButtonVariant.STATIC_BORDERLESS, dark: true, onClick: function () { return setCollapsed(!collapsed); }, className: "acl-CollapsibleNav-CollapseToggle" },
                React.createElement(Icon, { size: 16, name: "ChevronLeft", style: {
                        transform: collapsed ? 'rotate(180deg)' : null
                    } })))));
};

var CollapsibleNavItem = function (props) {
    var _a = useState(false), open = _a[0], setOpen = _a[1];
    var menuRef = useRef(null);
    var _b = useState(null), navRef = _b[0], setNavRef = _b[1];
    var _c = useState(null), popperRef = _c[0], setPopperRef = _c[1];
    var popperProps = {
        placement: 'right-start',
        strategy: 'fixed',
        modifiers: [
            {
                name: 'preventOverflow',
                options: {
                    mainAxis: false
                }
            }
        ]
    };
    var _d = usePopper(navRef, popperRef, popperProps), styles = _d.styles, attributes = _d.attributes, update = _d.update;
    var cssClasses = classNames('acl-CollapsibleNavItem-link', {
        active: props.active,
        'acl-CollapsibleNavItem-link--dropdown-open': open
    });
    return (React.createElement("li", { ref: setNavRef, className: "acl-CollapsibleNavItem", onMouseMove: update, onMouseOver: function () { return setOpen(true); }, onMouseLeave: function () { return setOpen(false); } },
        React.createElement("a", __assign({}, props.linkProps, { title: props.title || props.label, className: cssClasses }),
            props.icon && (React.createElement(Icon, { name: props.icon, className: "acl-CollapsibleNavItem-icon" })),
            React.createElement("span", { className: "acl-CollapsibleNavItem-label" }, props.label),
            props.childItems && props.childItems.length && (React.createElement(Icon, { name: "ChevronRight" }))),
        React.createElement(Portal, null,
            React.createElement("div", __assign({}, attributes.popper, { style: __assign({ zIndex: 100 }, styles.popper), ref: setPopperRef }), props.childItems && props.childItems.length && open && (React.createElement(BasicMenu, { menuRef: menuRef, items: props.childItems, dark: true }))))));
};

var FormNavItem = function (_a) {
    var _b = _a.status, status = _b === void 0 ? 'NONE' : _b, leftElementIcon = _a.leftElementIcon, leftElementText = _a.leftElementText, props = __rest(_a, ["status", "leftElementIcon", "leftElementText"]);
    var renderLeftElement = function () {
        if (!leftElementIcon && !leftElementText)
            return null;
        if (leftElementIcon && typeof leftElementIcon === 'string') {
            return (React.createElement(Icon, { className: "acl-FormNavItem-leftElement", name: leftElementIcon, color: ColorTheme.amobeePrimaryColor }));
        }
        if (leftElementText && typeof leftElementText === 'string') {
            return (React.createElement("span", { className: "acl-FormNavItem-leftElement", style: { color: ColorTheme.amobeePrimaryColor } }, leftElementText));
        }
    };
    return (React.createElement("li", { className: classNames('acl-FormNavItem', {
            'acl-FormNavItem--active': props.active
        }), onClick: function () {
            return props.onClick && props.onClick(__assign(__assign({}, props), { status: status }));
        } },
        renderLeftElement(),
        React.createElement("span", { className: "acl-FormNavItem-title" }, props.title),
        status !== 'NONE' && (React.createElement(Icon, { className: "acl-FormNavItem-status", name: status === 'ERROR' ? 'StatusAlert' : 'Check', color: status === 'ERROR' ? '#E02A2B' : '#52a145' }))));
};

var css_248z$h = ".acl-FormNav {\n  position: absolute;\n  top: 0;\n  left: 0;\n  height: 100%;\n  width: 220px;\n  background-color: rgba(0, 0, 0, 0.05);\n  padding: 0;\n  display: flex;\n  flex-direction: column; }\n  .acl-FormNav ul {\n    flex: 1 1 auto;\n    margin: 0;\n    padding: 8px;\n    overflow: hidden;\n    overflow-y: auto; }\n\n.acl-FormNavItem {\n  display: flex;\n  border-radius: 3px;\n  align-items: center;\n  padding: 8px 12px;\n  height: 36px;\n  white-space: nowrap;\n  overflow: hidden;\n  color: rgba(0, 0, 0, 0.7); }\n  .acl-FormNavItem:hover {\n    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.3);\n    background-color: rgba(255, 255, 255, 0.3);\n    cursor: pointer;\n    transition: all 0.1s ease-in; }\n  .acl-FormNavItem:not(:first-child) {\n    margin-top: 8px; }\n  .acl-FormNavItem.acl-FormNavItem--active {\n    background-color: rgba(0, 0, 0, 0.1);\n    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.3);\n    pointer-events: none; }\n  .acl-FormNavItem .acl-FormNavItem-title {\n    flex: 1 1 auto; }\n  .acl-FormNavItem .acl-FormNavItem-status {\n    flex: 0 0 auto; }\n  .acl-FormNavItem .acl-FormNavItem-leftElement {\n    width: 16px;\n    height: 16px;\n    margin-right: 8px;\n    text-align: center;\n    font-weight: bold; }\n";
styleInject(css_248z$h);

var FormNav = function (props) { return (React.createElement("nav", { className: "acl-FormNav " + (props.className || ''), style: props.style },
    React.createElement("ul", null, props.items.map(function (item) { return (React.createElement(FormNavItem, __assign({}, item, { active: props.activeItem === item.title, key: item.title }))); })))); };

var StepsType;
(function (StepsType) {
    StepsType["ICON"] = "ICON";
    StepsType["NUMBER"] = "NUMBER";
    StepsType["FILL"] = "FILL";
})(StepsType || (StepsType = {}));
var StepsOrientation;
(function (StepsOrientation) {
    StepsOrientation["HORIZONTAL"] = "HORIZONTAL";
    StepsOrientation["VERTICAL"] = "VERTICAL";
})(StepsOrientation || (StepsOrientation = {}));

var css_248z$i = ".acl-Steps {\n  position: relative;\n  display: flex; }\n  .acl-Steps .acl-Step {\n    flex: 1;\n    position: relative; }\n    .acl-Steps .acl-Step .acl-Step-content {\n      display: flex;\n      align-items: center;\n      flex-direction: column;\n      position: relative;\n      z-index: 1; }\n      .acl-Steps .acl-Step .acl-Step-content .acl-Step-circle {\n        height: 32px;\n        width: 32px;\n        background: white;\n        border: 1px solid #ababab;\n        border-radius: 32px;\n        display: flex;\n        justify-content: center;\n        align-items: center;\n        color: white;\n        font-size: 20px;\n        transition: all .25s ease-in-out; }\n      .acl-Steps .acl-Step .acl-Step-content .acl-StepLabel {\n        max-width: 200px;\n        display: flex;\n        flex-direction: column;\n        align-items: center;\n        font-size: 12px; }\n        .acl-Steps .acl-Step .acl-Step-content .acl-StepLabel .acl-StepLabel-warningContent {\n          color: #caa900;\n          font-size: 12px; }\n          .acl-Steps .acl-Step .acl-Step-content .acl-StepLabel .acl-StepLabel-warningContent > * {\n            color: #caa900;\n            font-size: 12px; }\n  .acl-Steps .acl-Steps-track {\n    z-index: 0;\n    height: 2px;\n    background: #e2e2e2;\n    width: 100%;\n    position: absolute;\n    left: -50%;\n    right: 50%;\n    top: 16px; }\n    .acl-Steps .acl-Steps-track .acl-Steps-trackProgress {\n      position: absolute;\n      left: 0;\n      top: 0;\n      height: 100%;\n      background: #278917;\n      border-radius: 2px;\n      transition: all .25s ease-in-out; }\n  .acl-Steps.acl-Steps--vertical .acl-Step {\n    display: flex;\n    align-items: center; }\n    .acl-Steps.acl-Steps--vertical .acl-Step .acl-Step-content {\n      flex-direction: row; }\n      .acl-Steps.acl-Steps--vertical .acl-Step .acl-Step-content .acl-StepLabel {\n        margin-left: 8px; }\n  .acl-Steps.acl-Steps--vertical .acl-Steps-track {\n    top: -50%;\n    bottom: 50%;\n    left: 16px;\n    transform: translateX(-50%);\n    width: 2px;\n    height: 100%; }\n    .acl-Steps.acl-Steps--vertical .acl-Steps-track .acl-Steps-trackProgress {\n      width: 100%;\n      height: unset; }\n  .acl-Steps.acl-Steps--number .acl-Step--complete .acl-Steps-trackProgress, .acl-Steps.acl-Steps--icon .acl-Step--complete .acl-Steps-trackProgress {\n    background: #278917; }\n  .acl-Steps.acl-Steps--number .acl-Step--complete .acl-Step-circle, .acl-Steps.acl-Steps--icon .acl-Step--complete .acl-Step-circle {\n    background: #278917;\n    border-color: #278917; }\n  .acl-Steps.acl-Steps--number .acl-Step--warning .acl-Step-circle, .acl-Steps.acl-Steps--icon .acl-Step--warning .acl-Step-circle {\n    background: #caa900;\n    border-color: #caa900; }\n  .acl-Steps.acl-Steps--fill .acl-Step.acl-Step--complete .acl-Steps-trackProgress {\n    background: #00849e; }\n  .acl-Steps.acl-Steps--fill .acl-Step.acl-Step--complete .acl-Step-circle {\n    background: #00849e;\n    border-color: #00849e; }\n  .acl-Steps.acl-Steps--fill .acl-Step.acl-Step--complete.acl-Step--warning .acl-Step-circle {\n    background: #caa900;\n    border-color: #caa900; }\n\nacl-ngx-step {\n  flex: 1; }\n\nacl-ngx-steps {\n  height: 100%;\n  width: 100%; }\n";
styleInject(css_248z$i);

var Steps = function (_a) {
    var _b = _a.type, type = _b === void 0 ? StepsType.ICON : _b, _c = _a.orientation, orientation = _c === void 0 ? StepsOrientation.HORIZONTAL : _c, props = __rest(_a, ["type", "orientation"]);
    var isStepComplete = function (index) {
        return index + 1 <= props.completedStep;
    };
    var getStyles = function () {
        if (orientation !== StepsOrientation.VERTICAL)
            return null;
        return {
            flexDirection: 'column',
            height: '100%',
            display: 'inline-flex'
        };
    };
    return (React.createElement("div", { className: classNames('acl-Steps', "acl-Steps--" + orientation.toLowerCase(), "acl-Steps--" + type.toLowerCase()), style: getStyles() }, React.Children.map(props.children, function (child, idx) {
        var newStepProps = __assign({ complete: isStepComplete(idx), type: child.props.type || type, number: idx + 1, lastStep: idx + 1 >= React.Children.count(props.children), orientation: orientation }, child.props);
        return React.cloneElement(child, newStepProps);
    })));
};

var Step = function (props) {
    var renderStepContent = function () {
        if (!props.complete)
            return null;
        var getWarningIcon = function () {
            if (props.warning) {
                return props.warningIcon || 'StatusWarning';
            }
            return 'Check';
        };
        switch (props.type) {
            case 'NUMBER':
                return props.number;
            case 'ICON':
                return React.createElement(Icon, { name: getWarningIcon() });
            default:
                return null;
        }
    };
    var maybeRenderWarningContent = function () {
        if (typeof props.renderWarningContent !== 'function' ||
            !props.warning ||
            !props.complete) {
            return null;
        }
        return (React.createElement("span", { className: "acl-StepLabel-warningContent" }, props.renderWarningContent()));
    };
    var getProgressStyle = function () {
        if (props.orientation === StepsOrientation.HORIZONTAL) {
            return {
                width: props.complete ? '100%' : '0%'
            };
        }
        if (props.orientation === StepsOrientation.VERTICAL) {
            return {
                height: props.complete ? '100%' : '0%'
            };
        }
    };
    var maybeRenderTrack = function () {
        if (props.number === 1)
            return null;
        return (React.createElement("div", { className: "acl-Steps-track" },
            React.createElement("div", { className: "acl-Steps-trackProgress", style: getProgressStyle() })));
    };
    return (React.createElement("div", { className: classNames('acl-Step', props.className, {
            'acl-Step--complete': props.complete,
            'acl-Step--warning': props.warning
        }), onClick: function () { return safeInvoke(props.onClick, [props]); }, style: props.style },
        maybeRenderTrack(),
        React.createElement("div", { className: "acl-Step-content" },
            React.createElement("div", { className: "acl-Step-circle" }, renderStepContent()),
            React.createElement("div", { className: "acl-StepLabel" },
                maybeRenderWarningContent(),
                React.createElement("span", { style: {
                        color: props.complete ? 'black' : ColorTheme.gray30
                    } }, props.label)))));
};

var Breadcrumb = function (_a) {
    var _b = _a.dropdownEnabled, dropdownEnabled = _b === void 0 ? true : _b, _c = _a.dropdownDarkThemed, dropdownDarkThemed = _c === void 0 ? true : _c, props = __rest(_a, ["dropdownEnabled", "dropdownDarkThemed"]);
    var _d = useState(false), open = _d[0], setOpen = _d[1];
    var _e = useState(false), tooltip = _e[0], setTooltip = _e[1];
    var _f = useState(null), menuRef = _f[0], setMenuRef = _f[1];
    var _g = useState(null), crumbRef = _g[0], setCrumbRef = _g[1];
    var linkRef = useRef(null);
    var popperProps = {
        placement: 'bottom-start',
        strategy: 'fixed',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 4]
                }
            }
        ]
    };
    var _h = usePopper(crumbRef, menuRef, popperProps), styles = _h.styles, attributes = _h.attributes, update = _h.update;
    var handleCrumbClick = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!dropdownEnabled ||
                        !props.dropdownItems ||
                        !props.dropdownItems.length) {
                        return [2 /*return*/];
                    }
                    if (props.onDropdownOpen) {
                        props.onDropdownOpen({
                            title: props.title,
                            label: props.label,
                            link: props.link
                        });
                    }
                    setOpen(!open);
                    return [4 /*yield*/, update()];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    var handleAnchorClick = function (e) {
        e.stopPropagation();
        if (props.onClick) {
            props.onClick({
                title: props.title,
                label: props.label,
                link: props.link
            });
        }
    };
    var handleDropdownSelection = function (selection) {
        if (props.onDropdownSelection &&
            typeof props.onDropdownSelection === 'function') {
            props.onDropdownSelection(selection);
        }
        setOpen(false);
    };
    useDetectOutsideClick([{ current: menuRef }, { current: crumbRef }], function () {
        setOpen(false);
    });
    useEffect(function () {
        if (linkRef.current) {
            if (linkRef.current.scrollWidth > linkRef.current.clientWidth) {
                setTooltip(true);
            }
            else {
                setTooltip(false);
            }
        }
    }, [window.innerWidth, props.title]);
    return (React.createElement("div", { className: "acl-Breadcrumb", key: props.title },
        React.createElement("label", null, props.label),
        React.createElement("div", { ref: setCrumbRef, role: "button", onClick: handleCrumbClick, className: classNames('acl-Breadcrumb-button', {
                'dropdown-open': open,
                'has-dropdown': dropdownEnabled &&
                    props.dropdownItems &&
                    props.dropdownItems.length
            }) },
            React.createElement(Tooltip, { className: "acl-Breadcrumb-tooltip", arrow: true, trigger: function () { return (React.createElement("a", { className: "acl-Breadcrumb-anchor", onClick: handleAnchorClick, href: props.link, ref: linkRef }, props.title)); }, text: props.title, disabled: !tooltip || !!open }),
            React.createElement(Icon, { name: "ChevronRight" })),
        React.createElement(Portal, null,
            React.createElement("div", __assign({}, attributes.popper, { style: __assign({ zIndex: 1000 }, styles.popper), ref: setMenuRef }), dropdownEnabled && open && (React.createElement(BasicMenu, { searchable: props.dropdownSearchable, itemLabel: props.dropdownItemLabel, minWidth: props.dropdownMinWidth, maxWidth: props.dropdownMaxWidth, maxResultRows: props.dropdownMaxResultRows, infiniteScroll: props.dropdownInfiniteScroll, onScroll: props.onDropdownScroll, onQueryChange: props.onDropdownQueryChange, onSelection: handleDropdownSelection, dark: dropdownDarkThemed }, props.dropdownItems &&
                props.dropdownItems.map(function (item, idx) { return (React.createElement(MenuItem, __assign({}, item, { key: idx }))); })))))));
};

var css_248z$j = ".acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll:before, .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll.acl-BreadcrumbScroller--fade-left:before, .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll.acl-BreadcrumbScroller--fade-both:before {\n  position: absolute;\n  left: 0;\n  top: 0;\n  height: 100%;\n  width: 125px;\n  background: linear-gradient(to right, #ffffff, transparent);\n  content: '';\n  z-index: 1;\n  transition: left 10ms ease-in; }\n\n.acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll:after, .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll.acl-BreadcrumbScroller--fade-right:after, .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll.acl-BreadcrumbScroller--fade-both:after {\n  position: absolute;\n  right: 0;\n  top: 0;\n  height: 100%;\n  width: 125px;\n  background: linear-gradient(to left, #ffffff, transparent);\n  content: '';\n  z-index: 1;\n  transition: right 10ms ease-in; }\n\n.acl-BreadcrumbScroller {\n  display: block;\n  position: relative;\n  white-space: nowrap;\n  overflow: hidden; }\n  .acl-BreadcrumbScroller .acl-BreadcrumbScroller--wrapper {\n    display: block;\n    overflow: hidden; }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll .acl-BreadcrumbScroller--back-button,\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll .acl-BreadcrumbScroller--forward-button {\n    display: flex;\n    flex-direction: column;\n    justify-content: center;\n    align-items: center;\n    opacity: 0;\n    position: absolute;\n    background: #ffffff;\n    height: 100%;\n    width: 1px;\n    transition: width 0ms linear 150ms, opacity 150ms ease-in;\n    top: 0;\n    z-index: 2;\n    overflow: hidden;\n    cursor: pointer; }\n    .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll .acl-BreadcrumbScroller--back-button .acl-Icon,\n    .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll .acl-BreadcrumbScroller--forward-button .acl-Icon {\n      display: block;\n      position: relative; }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll .acl-BreadcrumbScroller--back-button {\n    border-right: 1px solid rgba(0, 0, 0, 0.3);\n    left: 0; }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll .acl-BreadcrumbScroller--forward-button {\n    border-left: 1px solid rgba(0, 0, 0, 0.3);\n    right: 0; }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll:before {\n    left: -125px;\n    transition: left 150ms ease-in; }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll:after {\n    right: -125px;\n    transition: right 150ms ease-in; }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll.acl-BreadcrumbScroller--fade-left:hover .acl-BreadcrumbScroller--back-button {\n    width: 25px;\n    opacity: 1;\n    transition: opacity 150ms ease-in; }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--scroll.acl-BreadcrumbScroller--fade-right:hover .acl-BreadcrumbScroller--forward-button {\n    width: 25px;\n    opacity: 1;\n    transition: opacity 150ms ease-in; }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--dark.acl-BreadcrumbScroller--scroll .acl-BreadcrumbScroller--back-button,\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--dark.acl-BreadcrumbScroller--scroll .acl-BreadcrumbScroller--forward-button {\n    background: #303030;\n    color: rgba(255, 255, 255, 0.3); }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--dark.acl-BreadcrumbScroller--scroll .acl-BreadcrumbScroller--back-button {\n    border-right: 1px solid rgba(255, 255, 255, 0.3); }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--dark.acl-BreadcrumbScroller--scroll .acl-BreadcrumbScroller--forward-button {\n    border-left: 1px solid rgba(255, 255, 255, 0.3); }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--dark.acl-BreadcrumbScroller--scroll:before {\n    background: linear-gradient(to right, #303030, transparent); }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--dark.acl-BreadcrumbScroller--scroll:after {\n    background: linear-gradient(to left, #303030, transparent); }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--dark.acl-BreadcrumbScroller--scroll.acl-BreadcrumbScroller--fade-left:before {\n    background: linear-gradient(to right, #303030, transparent); }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--dark.acl-BreadcrumbScroller--scroll.acl-BreadcrumbScroller--fade-right:after {\n    background: linear-gradient(to left, #303030, transparent); }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--dark.acl-BreadcrumbScroller--scroll.acl-BreadcrumbScroller--fade-both:before {\n    background: linear-gradient(to right, #303030, transparent); }\n  .acl-BreadcrumbScroller.acl-BreadcrumbScroller--dark.acl-BreadcrumbScroller--scroll.acl-BreadcrumbScroller--fade-both:after {\n    background: linear-gradient(to left, #303030, transparent); }\n";
styleInject(css_248z$j);

var BreadcrumbScroller = function (_a) {
    var _b = _a.enabled, enabled = _b === void 0 ? true : _b, _c = _a.dark, dark = _c === void 0 ? false : _c, props = __rest(_a, ["enabled", "dark"]);
    var _d = useState('none'), fade = _d[0], setFade = _d[1];
    var navRef = useRef(null);
    var scrollToBeginning = function () {
        if (navRef.current) {
            navRef.current.scroll({
                left: 0,
                behavior: 'smooth'
            });
        }
    };
    var scrollToEnd = function () {
        if (navRef.current) {
            navRef.current.scroll({
                left: navRef.current.scrollWidth,
                behavior: 'smooth'
            });
        }
    };
    var handleNavScroll = function (e) {
        var target = e.target;
        if (target.scrollWidth > target.clientWidth) {
            if (target.scrollLeft > 0 &&
                target.scrollLeft + target.clientWidth < target.scrollWidth) {
                setFade('both');
                return;
            }
            if (navRef.current.scrollLeft > 0) {
                setFade('left');
                return;
            }
            if (target.scrollLeft + target.clientWidth < target.scrollWidth) {
                setFade('right');
            }
        }
    };
    var checkFadeRequired = function () {
        if (navRef.current) {
            // If there's no need to scroll, remove the fade.
            if (navRef.current.clientWidth === navRef.current.scrollWidth) {
                setFade('none');
            }
        }
    };
    var handleResize = function () {
        checkFadeRequired();
        scrollToEnd();
    };
    // Scroll to end if breadcrumbs change
    useEffect(function () {
        if (enabled) {
            checkFadeRequired();
            scrollToEnd();
        }
    }, [props.children]);
    useEffect(function () {
        if (enabled && navRef.current) {
            navRef.current.addEventListener('scroll', handleNavScroll);
            return function () {
                return navRef.current.removeEventListener('scroll', handleNavScroll);
            };
        }
    }, []);
    var renderBreadcrumbScrollerWrapper = function () { return (React.createElement("div", { className: "acl-BreadcrumbScroller--wrapper", ref: navRef }, props.children)); };
    return (React.createElement("div", { className: classNames('acl-BreadcrumbScroller', "acl-BreadcrumbScroller--fade-" + fade, {
            'acl-BreadcrumbScroller--scroll': enabled,
            'acl-BreadcrumbScroller--dark': dark
        }) },
        enabled && (React.createElement("div", { className: "acl-BreadcrumbScroller--back-button", onClick: scrollToBeginning },
            React.createElement(Icon, { name: "ChevronLeft" }))),
        enabled ? (React.createElement(ResizeObserver, { onResize: debounce(function () {
                handleResize();
            }, 100) }, renderBreadcrumbScrollerWrapper())) : (renderBreadcrumbScrollerWrapper()),
        enabled && (React.createElement("div", { className: "acl-BreadcrumbScroller--forward-button", onClick: scrollToEnd },
            React.createElement(Icon, { name: "ChevronRight" })))));
};

var Breadcrumbs = function (props) {
    return (React.createElement("div", { className: classNames('acl-Breadcrumbs', props.className), style: props.style },
        React.createElement(BreadcrumbScroller, { enabled: true, dark: true }, props.breadcrumbs.map(function (b) { return (React.createElement(Breadcrumb, __assign({ key: b.label }, b))); }))));
};

var css_248z$k = ".acl-TextBreadcrumbs {\n  display: block;\n  position: relative;\n  white-space: nowrap;\n  height: 32px;\n  line-height: 32px;\n  text-rendering: initial;\n  -webkit-font-smoothing: initial; }\n  .acl-TextBreadcrumbs .acl-BreadcrumbScroller {\n    padding: 0 12px; }\n    .acl-TextBreadcrumbs .acl-BreadcrumbScroller .acl-TextBreadcrumb.acl-Link {\n      color: #474747; }\n    .acl-TextBreadcrumbs .acl-BreadcrumbScroller .acl-Icon {\n      margin: 0 4px; }\n\n.acl-Toolbar.bulk .acl-TextBreadcrumbs .acl-TextBreadcrumb.acl-Link {\n  color: #004f5f !important; }\n\n.acl-Toolbar .acl-TextBreadcrumbs .acl-TextBreadcrumb.acl-Link {\n  color: #474747;\n  font-weight: bold; }\n";
styleInject(css_248z$k);

var css_248z$l = ".acl-Link {\n  color: #009ebd;\n  text-decoration: none;\n  font-size: 14px;\n  line-height: 16px; }\n  .acl-Link.acl-Link--dark {\n    color: white; }\n  .acl-Link:hover {\n    text-decoration: underline;\n    cursor: pointer; }\n";
styleInject(css_248z$l);

var Link = forwardRef(
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function (_a, ref) {
    var children = _a.children, dark = _a.dark, className = _a.className, props = __rest(_a, ["children", "dark", "className"]);
    return (React.createElement("a", __assign({ className: classNames('acl-Link', className, {
            'acl-Link--dark': dark
        }) }, props), children));
});

var TextBreadcrumb = function (props) {
    var handleClick = function (event) {
        safeInvoke(props.onClick, [{ props: props, event: event }]);
    };
    return (React.createElement(React.Fragment, null,
        props.index > 0 && React.createElement(Icon, { name: "ChevronRight" }),
        React.createElement(Link, { className: "acl-TextBreadcrumb", href: props.href, onClick: handleClick },
            props.label,
            !!props.count && " (" + props.count + ")")));
};

var TextBreadcrumbs = function (_a) {
    var _b = _a.enableScrollButtons, enableScrollButtons = _b === void 0 ? false : _b, props = __rest(_a, ["enableScrollButtons"]);
    var mapBreadcrumbs = function () {
        if (!props.children) {
            return;
        }
        var mapped = function (children) {
            if (children) {
                return React.Children.map(children, function (crumb, idx) {
                    return renderBreadcrumb(crumb, idx);
                });
            }
        };
        if (isReactFragment(props.children)) {
            var children = props.children;
            return mapped(children && children.props && children.props.children);
        }
        return mapped(props.children);
    };
    var renderBreadcrumb = function (node, idx) {
        if (node.type !== TextBreadcrumb) {
            console.error(new Error('Children should be of type `TextBreadcrumb`'));
            return;
        }
        var breadcrumbProps = node.props;
        return React.createElement(TextBreadcrumb, __assign({ index: idx }, breadcrumbProps));
    };
    return (React.createElement("nav", { className: "acl-TextBreadcrumbs" },
        React.createElement(BreadcrumbScroller, { enabled: enableScrollButtons }, mapBreadcrumbs())));
};

var css_248z$m = ".acl-CallToAction {\n  vertical-align: middle;\n  max-width: 500px;\n  margin: auto;\n  text-align: center; }\n  .acl-CallToAction .acl-CallToAction--svgContainer {\n    height: 75%;\n    width: 75%;\n    margin: auto; }\n    .acl-CallToAction .acl-CallToAction--svgContainer svg {\n      height: 50%;\n      width: 50%; }\n  .acl-CallToAction .acl-CallToAction--title {\n    font-weight: bold;\n    margin-top: 16px; }\n  .acl-CallToAction .acl-CallToAction--description {\n    margin-top: 8px; }\n  .acl-CallToAction .acl-CallToAction--button {\n    margin-top: 16px; }\n";
styleInject(css_248z$m);

var EmptyChart = function () {
    return (React.createElement("svg", { width: "150px", height: "150px", viewBox: "0 0 150 150", version: "1.1" },
        React.createElement("title", null, "Empty Chart"),
        React.createElement("desc", null, "Created with Sketch."),
        React.createElement("defs", null,
            React.createElement("rect", { id: "path-1", x: "35", y: "35", width: "80", height: "80", rx: "2" }),
            React.createElement("filter", { x: "-9.4%", y: "-9.4%", width: "118.8%", height: "118.8%", filterUnits: "objectBoundingBox", id: "filter-2" },
                React.createElement("feOffset", { dx: "0", dy: "0", in: "SourceAlpha", result: "shadowOffsetOuter1" }),
                React.createElement("feGaussianBlur", { stdDeviation: "2.5", in: "shadowOffsetOuter1", result: "shadowBlurOuter1" }),
                React.createElement("feColorMatrix", { values: "0 0 0 0 0.851958786   0 0 0 0 0.846471704   0 0 0 0 0.846471704  0 0 0 0.5 0", type: "matrix", in: "shadowBlurOuter1" }))),
        React.createElement("g", { id: "Null-states", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" },
            React.createElement("g", { id: "no-data-avaliable---sidebar---chart", transform: "translate(-1051.000000, -801.000000)" },
                React.createElement("g", { id: "Group", transform: "translate(1051.000000, 801.000000)" },
                    React.createElement("circle", { id: "Oval", fill: "#F1F1F1", cx: "75", cy: "75", r: "75" }),
                    React.createElement("g", { id: "Rectangle" },
                        React.createElement("use", { fill: "black", fillOpacity: "1", filter: "url(#filter-2)", xlinkHref: "#path-1" }),
                        React.createElement("use", { fill: "#FFFFFF", fillRule: "evenodd", xlinkHref: "#path-1" })),
                    React.createElement("rect", { id: "Rectangle", fill: "#C6C6C6", x: "62", y: "88", width: "6", height: "15", rx: "1" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#C6C6C6", x: "52", y: "82", width: "6", height: "21", rx: "1" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#C6C6C6", x: "72", y: "76", width: "6", height: "27", rx: "1" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#C6C6C6", x: "82", y: "85", width: "6", height: "18", rx: "1" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#C6C6C6", x: "92", y: "69", width: "6", height: "34", rx: "1" }),
                    React.createElement("polyline", { id: "Path-4", stroke: "#C6C6C6", points: "55 66 64 69 75 57 85 62 95 48" }),
                    React.createElement("circle", { id: "Oval", fill: "#ABABAB", cx: "64", cy: "69", r: "2" }),
                    React.createElement("circle", { id: "Oval", fill: "#ABABAB", cx: "55", cy: "66", r: "2" }),
                    React.createElement("circle", { id: "Oval", fill: "#ABABAB", cx: "75", cy: "57", r: "2" }),
                    React.createElement("circle", { id: "Oval", fill: "#ABABAB", cx: "95", cy: "48", r: "2" }),
                    React.createElement("circle", { id: "Oval", fill: "#ABABAB", cx: "85", cy: "62", r: "2" }))))));
};

var EmptyDashboard = function () { return (React.createElement("svg", { width: "324px", height: "324px", viewBox: "0 0 324 324", version: "1.1" },
    React.createElement("title", null, "Empty Dashboard"),
    React.createElement("defs", null,
        React.createElement("rect", { id: "path-1", x: "52", y: "77", width: "217", height: "173", rx: "4" }),
        React.createElement("filter", { x: "-4.6%", y: "-4.6%", width: "109.2%", height: "111.6%", filterUnits: "objectBoundingBox", id: "filter-2" },
            React.createElement("feMorphology", { radius: "0.5", operator: "dilate", in: "SourceAlpha", result: "shadowSpreadOuter1" }),
            React.createElement("feOffset", { dx: "0", dy: "2", in: "shadowSpreadOuter1", result: "shadowOffsetOuter1" }),
            React.createElement("feGaussianBlur", { stdDeviation: "2.5", in: "shadowOffsetOuter1", result: "shadowBlurOuter1" }),
            React.createElement("feComposite", { in: "shadowBlurOuter1", in2: "SourceAlpha", operator: "out", result: "shadowBlurOuter1" }),
            React.createElement("feColorMatrix", { values: "0 0 0 0 0.88627451   0 0 0 0 0.88627451   0 0 0 0 0.88627451  0 0 0 1 0", type: "matrix", in: "shadowBlurOuter1" }))),
    React.createElement("g", { id: "Null-states", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" },
        React.createElement("g", { id: "Dashboard", transform: "translate(-558.000000, -203.000000)" },
            React.createElement("g", { id: "Group-2", transform: "translate(558.000000, 203.000000)" },
                React.createElement("circle", { id: "Oval", fill: "#DFF3F7", opacity: "0.219610305", cx: "162", cy: "162", r: "162" }),
                React.createElement("g", { id: "Rectangle" },
                    React.createElement("use", { fill: "black", fillOpacity: "1", filter: "url(#filter-2)" }),
                    React.createElement("rect", { stroke: "#009EBD", strokeWidth: "1", strokeLinejoin: "round", fill: "#FFFFFF", fillRule: "evenodd", x: "52.5", y: "77.5", width: "216", height: "172", rx: "4" })),
                React.createElement("path", { d: "M266,77.5 L55,77.5 C54.3096441,77.5 53.6846441,77.779822 53.232233,78.232233 C52.779822,78.6846441 52.5,79.3096441 52.5,80 L52.5,93.5 L268.5,93.5 L268.5,80 C268.5,79.3096441 268.220178,78.6846441 267.767767,78.232233 C267.315356,77.779822 266.690356,77.5 266,77.5 Z", id: "Rectangle", stroke: "#009EBD", fill: "#DFF3F7" }),
                React.createElement("rect", { id: "Rectangle", fill: "#FFFFFF", x: "57", y: "81", width: "207", height: "9", rx: "1" }),
                React.createElement("circle", { id: "Oval", stroke: "#40B6CE", cx: "183.5", cy: "39.5", r: "3" }),
                React.createElement("circle", { id: "Oval", stroke: "#BFE7EF", cx: "12.5", cy: "268.5", r: "3" }),
                React.createElement("circle", { id: "Oval", stroke: "#40B6CE", cx: "316.5", cy: "93.5", r: "3" }),
                React.createElement("circle", { id: "Oval", stroke: "#BFE7EF", cx: "301.5", cy: "187.5", r: "3" }),
                React.createElement("g", { id: "Group-3", transform: "translate(221.000000, 312.000000)", fill: "#009EC1" },
                    React.createElement("rect", { id: "Rectangle", x: "0", y: "3", width: "6", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", transform: "translate(3.000000, 3.500000) rotate(90.000000) translate(-3.000000, -3.500000) ", x: "0", y: "3", width: "6", height: "1", rx: "0.5" })),
                React.createElement("g", { id: "Group-3", transform: "translate(90.000000, 19.000000)", fill: "#009EC1" },
                    React.createElement("rect", { id: "Rectangle", x: "0", y: "3", width: "6", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", transform: "translate(3.000000, 3.500000) rotate(90.000000) translate(-3.000000, -3.500000) ", x: "0", y: "3", width: "6", height: "1", rx: "0.5" })),
                React.createElement("g", { id: "Group-3", transform: "translate(6.000000, 167.000000)", fill: "#009EC1" },
                    React.createElement("rect", { id: "Rectangle", x: "0", y: "3", width: "6", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", transform: "translate(3.000000, 3.500000) rotate(90.000000) translate(-3.000000, -3.500000) ", x: "0", y: "3", width: "6", height: "1", rx: "0.5" })),
                React.createElement("path", { d: "M257.917801,28.2272681 C257.79158,28.2483048 257.673386,28.3174928 257.593133,28.429846 L254.536323,32.7093809 C254.475751,32.7941813 254.443189,32.8957885 254.443189,33 C254.443189,33.1380712 254.499154,33.2630712 254.589636,33.3535534 C254.680118,33.4440356 254.805118,33.5 254.943189,33.5 L261.056811,33.5 C261.161022,33.5 261.262629,33.4674384 261.34743,33.4068667 C261.459783,33.3266145 261.528971,33.2084196 261.550008,33.0821995 C261.571044,32.9559794 261.54393,32.821734 261.463677,32.7093809 L257.917801,28.2272681 Z", id: "Triangle", stroke: "#BFE7EF", transform: "translate(258.000000, 30.500000) rotate(-150.000000) translate(-258.000000, -30.500000) " }),
                React.createElement("path", { d: "M122.917801,293.227268 C122.79158,293.248305 122.673386,293.317493 122.593133,293.429846 L119.536323,297.709381 C119.475751,297.794181 119.443189,297.895789 119.443189,298 C119.443189,298.138071 119.499154,298.263071 119.589636,298.353553 C119.680118,298.444036 119.805118,298.5 119.943189,298.5 L126.056811,298.5 C126.161022,298.5 126.262629,298.467438 126.34743,298.406867 C126.459783,298.326614 126.528971,298.20842 126.550008,298.082199 C126.571044,297.955979 126.54393,297.821734 126.463677,297.709381 L122.917801,293.227268 Z", id: "Triangle", stroke: "#80CFDE", transform: "translate(123.000000, 295.500000) rotate(-150.000000) translate(-123.000000, -295.500000) " }),
                React.createElement("path", { d: "M19.9178005,70.2272681 C19.7915804,70.2483048 19.6733855,70.3174928 19.5931333,70.429846 L16.5363226,74.7093809 C16.4757509,74.7941813 16.4431893,74.8957885 16.4431893,75 C16.4431893,75.1380712 16.4991537,75.2630712 16.5896359,75.3535534 C16.6801181,75.4440356 16.8051181,75.5 16.9431893,75.5 L23.0568107,75.5 C23.1610222,75.5 23.2626294,75.4674384 23.3474298,75.4068667 C23.4597829,75.3266145 23.528971,75.2084196 23.5500076,75.0821995 C23.5710443,74.9559794 23.5439297,74.821734 23.4636774,74.7093809 L19.9178005,70.2272681 Z", id: "Triangle", stroke: "#80CFDE", transform: "translate(20.000000, 72.500000) rotate(-150.000000) translate(-20.000000, -72.500000) " }),
                React.createElement("path", { d: "M297.917801,272.227268 C297.79158,272.248305 297.673386,272.317493 297.593133,272.429846 L294.536323,276.709381 C294.475751,276.794181 294.443189,276.895789 294.443189,277 C294.443189,277.138071 294.499154,277.263071 294.589636,277.353553 C294.680118,277.444036 294.805118,277.5 294.943189,277.5 L301.056811,277.5 C301.161022,277.5 301.262629,277.467438 301.34743,277.406867 C301.459783,277.326614 301.528971,277.20842 301.550008,277.082199 C301.571044,276.955979 301.54393,276.821734 301.463677,276.709381 L297.917801,272.227268 Z", id: "Triangle", stroke: "#80CFDE", transform: "translate(298.000000, 274.500000) rotate(-150.000000) translate(-298.000000, -274.500000) " }),
                React.createElement("rect", { id: "Rectangle", fill: "#DFF3F7", x: "84", y: "136", width: "8", height: "26", rx: "1" }),
                React.createElement("rect", { id: "Rectangle", fill: "#DFF3F7", x: "108", y: "112", width: "8", height: "50", rx: "1" }),
                React.createElement("rect", { id: "Rectangle", fill: "#DFF3F7", x: "120", y: "118", width: "8", height: "44", rx: "1" }),
                React.createElement("rect", { id: "Rectangle", fill: "#DFF3F7", x: "132", y: "140", width: "8", height: "22", rx: "1" }),
                React.createElement("rect", { id: "Rectangle", fill: "#DFF3F7", x: "96", y: "121", width: "8", height: "41", rx: "1" }),
                React.createElement("path", { d: "M84,152 L92,152 L92,161 C92,161.552285 91.5522847,162 91,162 L85,162 C84.4477153,162 84,161.552285 84,161 L84,152 L84,152 Z", id: "Rectangle", fill: "#40B6CE" }),
                React.createElement("path", { d: "M108,138 L116,138 L116,161 C116,161.552285 115.552285,162 115,162 L109,162 C108.447715,162 108,161.552285 108,161 L108,138 L108,138 Z", id: "Rectangle", fill: "#40B6CE" }),
                React.createElement("path", { d: "M120,144 L128,144 L128,161 C128,161.552285 127.552285,162 127,162 L121,162 C120.447715,162 120,161.552285 120,161 L120,144 L120,144 Z", id: "Rectangle", fill: "#40B6CE" }),
                React.createElement("path", { d: "M132,154 L140,154 L140,161 C140,161.552285 139.552285,162 139,162 L133,162 C132.447715,162 132,161.552285 132,161 L132,154 L132,154 Z", id: "Rectangle", fill: "#40B6CE" }),
                React.createElement("path", { d: "M96,145 L104,145 L104,161 C104,161.552285 103.552285,162 103,162 L97,162 C96.4477153,162 96,161.552285 96,161 L96,145 L96,145 Z", id: "Rectangle", fill: "#40B6CE" }),
                React.createElement("path", { d: "M112,185 C98.745166,185 88,195.745166 88,209 C88,222.254834 98.745166,233 112,233 C125.254834,233 136,222.254834 136,209 L112,209 L112,185 Z", id: "Path", fill: "#DFF3F7" }),
                React.createElement("path", { d: "M114,207 L114,183 L114.396884,183.003215 C127.468663,183.215154 138,193.877714 138,207 L138,207 L114,207 Z", id: "Combined-Shape", fill: "#40B6CE" }),
                React.createElement("circle", { id: "Oval", stroke: "#DFF3F7", strokeWidth: "7", cx: "209", cy: "136", r: "22" }),
                React.createElement("path", { d: "M190,146.024237 C193.636085,153.143898 200.896496,158 209.259939,158 C221.266643,158 231,147.99159 231,135.64559 C231,135.092132 230.98044,134.543372 230.941991,134", id: "Path", stroke: "#40B6CE", strokeWidth: "7" }),
                React.createElement("path", { d: "M231,135 C231,123.40202 221.59798,114 210,114", id: "Path", stroke: "#BFE7EF", strokeWidth: "7" }),
                React.createElement("rect", { id: "Rectangle", fill: "#40B6CE", x: "183", y: "188", width: "53", height: "6", rx: "1" }),
                React.createElement("rect", { id: "Rectangle", fill: "#DFF3F7", x: "183", y: "197", width: "20", height: "6", rx: "1" }),
                React.createElement("rect", { id: "Rectangle", fill: "#40B6CE", x: "183", y: "205", width: "41", height: "6", rx: "1" }),
                React.createElement("rect", { id: "Rectangle", fill: "#40B6CE", x: "183", y: "224", width: "47", height: "6", rx: "1" }),
                React.createElement("rect", { id: "Rectangle", fill: "#DFF3F7", x: "183", y: "214", width: "33", height: "6", rx: "1" }),
                React.createElement("circle", { id: "Oval", fill: "#E75556", cx: "61.5", cy: "85.5", r: "1.5" }),
                React.createElement("circle", { id: "Oval", fill: "#CAA900", cx: "67.5", cy: "85.5", r: "1.5" }),
                React.createElement("circle", { id: "Oval", fill: "#52A145", cx: "73.5", cy: "85.5", r: "1.5" })))))); };

var EmptyData = function () { return (React.createElement("svg", { width: "150px", height: "150px", viewBox: "0 0 150 150", version: "1.1" },
    React.createElement("title", null, "Empty Data"),
    React.createElement("defs", null,
        React.createElement("rect", { id: "path-1", x: "2.27373675e-13", y: "0", width: "58", height: "74", rx: "2" }),
        React.createElement("filter", { x: "-12.9%", y: "-10.1%", width: "125.9%", height: "120.3%", filterUnits: "objectBoundingBox", id: "filter-2" },
            React.createElement("feOffset", { dx: "0", dy: "0", in: "SourceAlpha", result: "shadowOffsetOuter1" }),
            React.createElement("feGaussianBlur", { stdDeviation: "2.5", in: "shadowOffsetOuter1", result: "shadowBlurOuter1" }),
            React.createElement("feColorMatrix", { values: "0 0 0 0 0.851958786   0 0 0 0 0.846471704   0 0 0 0 0.846471704  0 0 0 0.5 0", type: "matrix", in: "shadowBlurOuter1" })),
        React.createElement("rect", { id: "path-3", x: "0", y: "0", width: "61", height: "78", rx: "2" }),
        React.createElement("filter", { x: "-13.1%", y: "-9.0%", width: "126.2%", height: "120.5%", filterUnits: "objectBoundingBox", id: "filter-4" },
            React.createElement("feOffset", { dx: "0", dy: "1", in: "SourceAlpha", result: "shadowOffsetOuter1" }),
            React.createElement("feGaussianBlur", { stdDeviation: "2.5", in: "shadowOffsetOuter1", result: "shadowBlurOuter1" }),
            React.createElement("feColorMatrix", { values: "0 0 0 0 0.851958786   0 0 0 0 0.846471704   0 0 0 0 0.846471704  0 0 0 0.702059659 0", type: "matrix", in: "shadowBlurOuter1" }))),
    React.createElement("g", { id: "Null-states", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" },
        React.createElement("g", { id: "no-data-avaliable---page-level---table-or-content", transform: "translate(-725.000000, -524.000000)" },
            React.createElement("g", { id: "Group", transform: "translate(725.000000, 524.000000)" },
                React.createElement("circle", { id: "Oval", fill: "#F1F1F1", cx: "75", cy: "75", r: "75" }),
                React.createElement("g", { id: "Group-2", transform: "translate(68.075171, 81.852630) rotate(-16.000000) translate(-68.075171, -81.852630) translate(39.075171, 44.852630)" },
                    React.createElement("g", { id: "Rectangle" },
                        React.createElement("use", { fill: "black", fillOpacity: "1", filter: "url(#filter-2)", xlinkHref: "#path-1" }),
                        React.createElement("use", { fill: "#FFFFFF", fillRule: "evenodd", xlinkHref: "#path-1" }))),
                React.createElement("g", { id: "Group-2", transform: "translate(83.500000, 76.000000) rotate(7.000000) translate(-83.500000, -76.000000) translate(53.000000, 37.000000)" },
                    React.createElement("g", { id: "Rectangle" },
                        React.createElement("use", { fill: "black", fillOpacity: "1", filter: "url(#filter-4)", xlinkHref: "#path-3" }),
                        React.createElement("use", { fill: "#FFFFFF", fillRule: "evenodd", xlinkHref: "#path-3" })),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "15", width: "24", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "23", width: "16", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "28", y: "23", width: "25", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "31", width: "35", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "39", width: "45", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "47", width: "29", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "55", width: "11", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "25", y: "55", width: "20", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "63", width: "13", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "26", y: "63", width: "24", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "42", y: "47", width: "11", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "36", y: "15", width: "17", height: "1", rx: "0.5" }))))))); };

var EmptySearchResults = function () { return (React.createElement("svg", { width: "150px", height: "150px", viewBox: "0 0 150 150", version: "1.1" },
    React.createElement("title", null, "Empty Search Results"),
    React.createElement("defs", null,
        React.createElement("rect", { id: "path-1", x: "0", y: "0", width: "61", height: "78", rx: "2" }),
        React.createElement("filter", { x: "-12.3%", y: "-9.6%", width: "124.6%", height: "119.2%", filterUnits: "objectBoundingBox", id: "filter-2" },
            React.createElement("feOffset", { dx: "0", dy: "0", in: "SourceAlpha", result: "shadowOffsetOuter1" }),
            React.createElement("feGaussianBlur", { stdDeviation: "2.5", in: "shadowOffsetOuter1", result: "shadowBlurOuter1" }),
            React.createElement("feColorMatrix", { values: "0 0 0 0 0.851958786   0 0 0 0 0.846471704   0 0 0 0 0.846471704  0 0 0 0.5 0", type: "matrix", in: "shadowBlurOuter1" })),
        React.createElement("circle", { id: "path-3", cx: "18", cy: "18", r: "18" }),
        React.createElement("filter", { x: "-19.4%", y: "-13.9%", width: "138.9%", height: "138.9%", filterUnits: "objectBoundingBox", id: "filter-4" },
            React.createElement("feOffset", { dx: "0", dy: "2", in: "SourceAlpha", result: "shadowOffsetOuter1" }),
            React.createElement("feGaussianBlur", { stdDeviation: "2", in: "shadowOffsetOuter1", result: "shadowBlurOuter1" }),
            React.createElement("feComposite", { in: "shadowBlurOuter1", in2: "SourceAlpha", operator: "out", result: "shadowBlurOuter1" }),
            React.createElement("feColorMatrix", { values: "0 0 0 0 0.88627451   0 0 0 0 0.88627451   0 0 0 0 0.88627451  0 0 0 1 0", type: "matrix", in: "shadowBlurOuter1" })),
        React.createElement("rect", { id: "path-5", x: "2.70710678", y: "0.707106781", width: "4", height: "12", rx: "1" }),
        React.createElement("filter", { x: "-175.0%", y: "-41.7%", width: "450.0%", height: "216.7%", filterUnits: "objectBoundingBox", id: "filter-6" },
            React.createElement("feOffset", { dx: "0", dy: "2", in: "SourceAlpha", result: "shadowOffsetOuter1" }),
            React.createElement("feGaussianBlur", { stdDeviation: "2", in: "shadowOffsetOuter1", result: "shadowBlurOuter1" }),
            React.createElement("feColorMatrix", { values: "0 0 0 0 0.88627451   0 0 0 0 0.88627451   0 0 0 0 0.88627451  0 0 0 1 0", type: "matrix", in: "shadowBlurOuter1" })),
        React.createElement("path", { d: "M1.63168601,5.5904501 L2.74875727,4.93272082 L2.74875727,4.93272082 L6.65254935,5.15811919 L7.5519391,5.61480081 C8.22341718,5.9557561 8.64644661,6.64499708 8.64644661,7.39807963 L8.64644661,19.9327208 C8.64644661,21.0372903 7.75101611,21.9327208 6.64644661,21.9327208 L2.64644661,21.9327208 C1.54187711,21.9327208 0.646446609,21.0372903 0.646446609,19.9327208 L0.646446609,7.31389459 C0.646446609,6.60546432 1.02121588,5.94989366 1.63168601,5.5904501 Z", id: "path-7" }),
        React.createElement("filter", { x: "-112.5%", y: "-29.4%", width: "275.0%", height: "182.4%", filterUnits: "objectBoundingBox", id: "filter-8" },
            React.createElement("feOffset", { dx: "-2", dy: "2", in: "SourceAlpha", result: "shadowOffsetOuter1" }),
            React.createElement("feGaussianBlur", { stdDeviation: "2", in: "shadowOffsetOuter1", result: "shadowBlurOuter1" }),
            React.createElement("feColorMatrix", { values: "0 0 0 0 0.88627451   0 0 0 0 0.88627451   0 0 0 0 0.88627451  0 0 0 1 0", type: "matrix", in: "shadowBlurOuter1" }))),
    React.createElement("g", { id: "Null-states", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" },
        React.createElement("g", { id: "Search-Result", transform: "translate(-645.000000, -412.000000)" },
            React.createElement("g", { id: "Group", transform: "translate(645.000000, 412.000000)" },
                React.createElement("circle", { id: "Oval", fill: "#F1F1F1", cx: "75", cy: "75", r: "75" }),
                React.createElement("g", { id: "Group-2", transform: "translate(45.000000, 36.000000)" },
                    React.createElement("g", { id: "Rectangle" },
                        React.createElement("use", { fill: "black", fillOpacity: "1", filter: "url(#filter-2)", xlinkHref: "#path-1" }),
                        React.createElement("use", { fill: "#FFFFFF", fillRule: "evenodd", xlinkHref: "#path-1" })),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "15", width: "24", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "23", width: "16", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "28", y: "23", width: "25", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "31", width: "35", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "39", width: "45", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "47", width: "29", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "55", width: "11", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "25", y: "55", width: "20", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "25", y: "55", width: "20", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "8", y: "63", width: "13", height: "1", rx: "0.5" }),
                    React.createElement("circle", { id: "Oval", fill: "#FFFFFF", cx: "32.5", cy: "66.5", r: "9.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "42", y: "47", width: "11", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#ABABAB", x: "36", y: "15", width: "17", height: "1", rx: "0.5" })),
                React.createElement("g", { id: "Group-3", transform: "translate(68.000000, 74.000000)" },
                    React.createElement("g", { id: "Oval" },
                        React.createElement("use", { fill: "black", fillOpacity: "1", filter: "url(#filter-4)", xlinkHref: "#path-3" }),
                        React.createElement("circle", { stroke: "#919191", strokeWidth: "2", strokeLinejoin: "miter", fill: "#FFFFFF", fillRule: "evenodd", cx: "18", cy: "18", r: "17" })),
                    React.createElement("g", { id: "Group-4", transform: "translate(37.232233, 37.939340) rotate(-45.000000) translate(-37.232233, -37.939340) translate(32.732233, 26.939340)" },
                        React.createElement("g", { id: "Rectangle" },
                            React.createElement("use", { fill: "black", fillOpacity: "1", filter: "url(#filter-6)", xlinkHref: "#path-5" }),
                            React.createElement("use", { fill: "#919191", fillRule: "evenodd", xlinkHref: "#path-5" })),
                        React.createElement("g", { id: "Rectangle" },
                            React.createElement("use", { fill: "black", fillOpacity: "1", filter: "url(#filter-8)", xlinkHref: "#path-7" }),
                            React.createElement("use", { fill: "#919191", fillRule: "evenodd", xlinkHref: "#path-7" }))),
                    React.createElement("rect", { id: "Rectangle", fill: "#919191", x: "9", y: "9", width: "16", height: "2", rx: "1" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#919191", x: "1", y: "17", width: "25", height: "2", rx: "1" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#919191", x: "10", y: "25", width: "18", height: "2", rx: "1" }))))))); };

var EmptyTable = function () { return (React.createElement("svg", { width: "176px", height: "151px", viewBox: "0 0 176 151", version: "1.1" },
    React.createElement("title", null, "Empty Table"),
    React.createElement("g", { id: "Null-states", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" },
        React.createElement("g", { id: "no-content-created", transform: "translate(-714.000000, -404.000000)" },
            React.createElement("g", { id: "Group-4", transform: "translate(714.000000, 404.000000)" },
                React.createElement("circle", { id: "Oval", fill: "#DFF3F7", opacity: "0.300130208", cx: "85", cy: "75", r: "75" }),
                React.createElement("circle", { id: "Oval", stroke: "#009EBF", cx: "27.5", cy: "17.5", r: "3" }),
                React.createElement("circle", { id: "Oval", stroke: "#009EBF", cx: "172.5", cy: "71.5", r: "3" }),
                React.createElement("g", { id: "Group-3", transform: "translate(0.000000, 86.000000)", fill: "#009EC1" },
                    React.createElement("rect", { id: "Rectangle", x: "0", y: "3", width: "6", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", transform: "translate(3.000000, 3.500000) rotate(90.000000) translate(-3.000000, -3.500000) ", x: "0", y: "3", width: "6", height: "1", rx: "0.5" })),
                React.createElement("g", { id: "Group-3", transform: "translate(117.000000, 144.000000)", fill: "#009EC1" },
                    React.createElement("rect", { id: "Rectangle", x: "0", y: "3", width: "6", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", transform: "translate(3.000000, 3.500000) rotate(90.000000) translate(-3.000000, -3.500000) ", x: "0", y: "3", width: "6", height: "1", rx: "0.5" })),
                React.createElement("path", { d: "M113.917801,11.2272681 C113.79158,11.2483048 113.673386,11.3174928 113.593133,11.429846 L110.536323,15.7093809 C110.475751,15.7941813 110.443189,15.8957885 110.443189,16 C110.443189,16.1380712 110.499154,16.2630712 110.589636,16.3535534 C110.680118,16.4440356 110.805118,16.5 110.943189,16.5 L117.056811,16.5 C117.161022,16.5 117.262629,16.4674384 117.34743,16.4068667 C117.459783,16.3266145 117.528971,16.2084196 117.550008,16.0821995 C117.571044,15.9559794 117.54393,15.821734 117.463677,15.7093809 L113.917801,11.2272681 Z", id: "Triangle", stroke: "#009EBF", transform: "translate(114.000000, 13.500000) rotate(-150.000000) translate(-114.000000, -13.500000) " }),
                React.createElement("path", { d: "M40.9178005,136.227268 C40.7915804,136.248305 40.6733855,136.317493 40.5931333,136.429846 L37.5363226,140.709381 C37.4757509,140.794181 37.4431893,140.895789 37.4431893,141 C37.4431893,141.138071 37.4991537,141.263071 37.5896359,141.353553 C37.6801181,141.444036 37.8051181,141.5 37.9431893,141.5 L44.0568107,141.5 C44.1610222,141.5 44.2626294,141.467438 44.3474298,141.406867 C44.4597829,141.326614 44.528971,141.20842 44.5500076,141.082199 C44.5710443,140.955979 44.5439297,140.821734 44.4636774,140.709381 L40.9178005,136.227268 Z", id: "Triangle", stroke: "#009EBF", transform: "translate(41.000000, 138.500000) rotate(-150.000000) translate(-41.000000, -138.500000) " }),
                React.createElement("g", { id: "Group-2", transform: "translate(39.000000, 28.000000)" },
                    React.createElement("rect", { id: "Rectangle", stroke: "#009EBD", fill: "#FFFFFF", transform: "translate(38.698463, 46.289899) rotate(-20.000000) translate(-38.698463, -46.289899) ", x: "13.1984631", y: "7.78989928", width: "51", height: "77", rx: "2" }),
                    React.createElement("rect", { id: "Rectangle", stroke: "#009EBD", fill: "#FFFFFF", transform: "translate(53.880757, 44.586097) rotate(23.000000) translate(-53.880757, -44.586097) ", x: "27.3807573", y: "13.0860967", width: "53", height: "63", rx: "2" }),
                    React.createElement("rect", { id: "Rectangle", stroke: "#009EBD", fill: "#FFFFFF", x: "18.5", y: "16.5", width: "60", height: "77", rx: "2" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#009EBF", x: "26", y: "31", width: "6", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#009EBF", x: "36", y: "31", width: "35", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#009EBF", x: "26", y: "40", width: "6", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#009EBF", x: "36", y: "40", width: "35", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#009EBF", x: "26", y: "49", width: "6", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#009EBF", x: "36", y: "49", width: "35", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#009EBF", x: "26", y: "58", width: "6", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#009EBF", x: "36", y: "58", width: "35", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#009EBF", x: "26", y: "67", width: "6", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#009EBF", x: "36", y: "67", width: "35", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#009EBF", x: "26", y: "76", width: "6", height: "1", rx: "0.5" }),
                    React.createElement("rect", { id: "Rectangle", fill: "#009EBF", x: "36", y: "76", width: "35", height: "1", rx: "0.5" }))))))); };

var PageNotFound = function () { return (React.createElement("svg", { width: "150px", height: "150px", viewBox: "0 0 150 150", version: "1.1" },
    React.createElement("title", null, "Page Not Found"),
    React.createElement("defs", null,
        React.createElement("rect", { id: "path-1", x: "25", y: "41", width: "101", height: "69", rx: "4" }),
        React.createElement("filter", { x: "-9.9%", y: "-11.6%", width: "119.8%", height: "129.0%", filterUnits: "objectBoundingBox", id: "filter-2" },
            React.createElement("feMorphology", { radius: "0.5", operator: "dilate", in: "SourceAlpha", result: "shadowSpreadOuter1" }),
            React.createElement("feOffset", { dx: "0", dy: "2", in: "shadowSpreadOuter1", result: "shadowOffsetOuter1" }),
            React.createElement("feGaussianBlur", { stdDeviation: "2.5", in: "shadowOffsetOuter1", result: "shadowBlurOuter1" }),
            React.createElement("feColorMatrix", { values: "0 0 0 0 0.88627451   0 0 0 0 0.88627451   0 0 0 0 0.88627451  0 0 0 1 0", type: "matrix", in: "shadowBlurOuter1" }))),
    React.createElement("g", { id: "Null-states", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" },
        React.createElement("g", { id: "page-not-found", transform: "translate(-645.000000, -333.000000)" },
            React.createElement("g", { id: "Group-2", transform: "translate(645.000000, 333.000000)" },
                React.createElement("circle", { id: "Oval", fill: "#F1F1F1", cx: "75", cy: "75", r: "75" }),
                React.createElement("path", { d: "M116,27.5 C113.656157,27.5 111.531247,28.3370615 109.989398,29.6982311 C108.469052,31.0404189 107.516684,32.8940095 107.501435,34.9477854 C107.498895,35.2898724 107.522434,35.6267923 107.570367,35.9570815 C107.309462,35.7388161 107.033805,35.5440806 106.746952,35.3744582 C105.809805,34.8203017 104.695364,34.5 103.5,34.5 C101.838297,34.5 100.332421,35.1190917 99.242946,36.124761 C98.1685392,37.1165212 97.5,38.485485 97.5,40 C97.5,42.0421734 98.7145614,43.8171889 100.500745,44.7643412 L100.500302,45.4999151 L127.205906,45.4953823 C128.401667,45.4416266 129.47583,44.9210122 130.250764,44.1116877 C131.024604,43.3035057 131.5,42.207266 131.5,41 C131.5,39.7573593 130.99632,38.6323593 130.181981,37.8180195 C129.367641,37.0036797 128.242641,36.5 127,36.5 L124.329693,36.5009451 C124.441392,36.0161369 124.5,35.5141541 124.5,35 C124.5,32.9349788 123.552864,31.0689263 122.031779,29.7168504 C120.488112,28.3447021 118.354513,27.5 116,27.5 Z", id: "Combined-Shape", stroke: "#919191", fill: "#FFFFFF" }),
                React.createElement("g", { id: "Rectangle" },
                    React.createElement("use", { fill: "black", fillOpacity: "1", filter: "url(#filter-2)" }),
                    React.createElement("use", { fill: "#FFFFFF", fillRule: "evenodd" })),
                React.createElement("path", { d: "M28,41 L123,41 C124.656854,41 126,42.3431458 126,44 L126,53 L126,53 L25,53 L25,44 C25,42.3431458 26.3431458,41 28,41 Z", id: "Rectangle", fill: "#C6C6C6" }),
                React.createElement("rect", { id: "Rectangle", fill: "#FFFFFF", x: "29", y: "44", width: "92", height: "6", rx: "1" }),
                React.createElement("circle", { id: "Oval", fill: "#E75556", cx: "32", cy: "47", r: "1" }),
                React.createElement("text", { id: "404", fontFamily: "Roboto-Bold, Roboto", fontSize: "20", fontWeight: "bold", fill: "#5E5E5E" },
                    React.createElement("tspan", { x: "57.2880859", y: "87" }, "404")),
                React.createElement("path", { d: "M43,96.375 C40.0222889,96.375 37.4030634,98.0594241 35.3079319,100.710609 C34.2085781,99.866646 32.9024365,99.375 31.5,99.375 C29.8278808,99.375 28.2933022,100.073894 27.0820293,101.234986 C25.8263305,102.438662 24.917031,104.137422 24.2152241,106.387048 C23.1446347,106.477966 22.1923866,107.077622 21.5054718,107.978504 C20.8018907,108.901243 20.375,110.140026 20.375,111.5 C20.375,112.820813 20.7777288,114.027171 21.4451927,114.944934 C22.0972163,115.841466 23.002945,116.462906 24.0286471,116.624995 L52.1874176,116.620461 C53.4162469,116.560818 54.5163709,115.916603 55.3120834,114.923473 C56.1274251,113.905845 56.625,112.522267 56.625,111 C56.625,109.435496 56.0994235,108.01734 55.2380127,106.992957 C54.4012517,105.997887 53.2456248,105.375721 51.5821656,105.034665 C51.3637921,102.587521 50.3236837,100.408237 48.7697283,98.8463602 C47.2435843,97.3124364 45.2208503,96.375 43,96.375 Z", id: "Combined-Shape", stroke: "#919191", strokeWidth: "0.75", fill: "#F1F1F1" }),
                React.createElement("circle", { id: "Oval", fill: "#CAA900", cx: "36", cy: "47", r: "1" }),
                React.createElement("circle", { id: "Oval", fill: "#52A145", cx: "40", cy: "47", r: "1" })))))); };

var NoPicture = function () {
    return (React.createElement("svg", { width: "150px", height: "150px", viewBox: "0 0 150 150", version: "1.1" },
        React.createElement("title", null, "No Picture"),
        React.createElement("defs", null,
            React.createElement("rect", { id: "path-1", x: "0", y: "0", width: "80", height: "80", rx: "2" }),
            React.createElement("filter", { x: "-10.0%", y: "-8.8%", width: "120.0%", height: "120.0%", filterUnits: "objectBoundingBox", id: "filter-2" },
                React.createElement("feOffset", { dx: "0", dy: "1", in: "SourceAlpha", result: "shadowOffsetOuter1" }),
                React.createElement("feGaussianBlur", { stdDeviation: "2.5", in: "shadowOffsetOuter1", result: "shadowBlurOuter1" }),
                React.createElement("feColorMatrix", { values: "0 0 0 0 0.851958786   0 0 0 0 0.851958786   0 0 0 0 0.851958786  0 0 0 0.702059659 0", type: "matrix", in: "shadowBlurOuter1" }))),
        React.createElement("g", { id: "Null-states", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" },
            React.createElement("g", { id: "Group-7" },
                React.createElement("circle", { id: "Oval", fill: "#F1F1F1", cx: "75", cy: "75", r: "75" }),
                React.createElement("g", { id: "Group-6", transform: "translate(35.000000, 35.000000)" },
                    React.createElement("g", { id: "Rectangle" },
                        React.createElement("use", { fill: "black", fillOpacity: "1", filter: "url(#filter-2)", xlinkHref: "#path-1" }),
                        React.createElement("use", { fill: "#FFFFFF", fillRule: "evenodd", xlinkHref: "#path-1" })),
                    React.createElement("circle", { id: "Oval", fill: "#D8D8D8", cx: "61", cy: "20", r: "7" }),
                    React.createElement("path", { d: "M6,65 L21.316466,45.6217485 C21.6589318,45.1884639 22.2878019,45.1148407 22.7210865,45.4573066 C22.7673305,45.4938576 22.8102523,45.5344258 22.8493504,45.5785373 L34.9508369,59.231753 C35.6834985,60.058361 36.9475362,60.1345197 37.7741443,59.4018582 C37.925971,59.267287 38.0564381,59.1103979 38.1610664,58.9365772 L53.5131152,33.4319743 C53.7979355,32.958798 54.4124131,32.8061051 54.8855895,33.0909254 C55.0269411,33.1760095 55.1450453,33.2947449 55.2293749,33.4365479 L74,65 L74,65 L6,65 Z", id: "Path-12", fill: "#D8D8D8", transform: "translate(40.000000, 48.500000) scale(-1, 1) translate(-40.000000, -48.500000) " }))))));
};



var CallToActions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    EmptyChart: EmptyChart,
    EmptyDashboard: EmptyDashboard,
    EmptyData: EmptyData,
    EmptySearchResults: EmptySearchResults,
    EmptyTable: EmptyTable,
    PageNotFound: PageNotFound,
    NoPicture: NoPicture
});

var getCtaImageComponent = function (ctaType) {
    return CallToActions[ctaType]();
};
var CallToAction = function (props) {
    return (React.createElement("div", { className: "acl-CallToAction" },
        React.createElement("div", { className: "acl-CallToAction--svgContainer" }, getCtaImageComponent(props.type)),
        React.createElement("div", { className: "acl-CallToAction--title" }, props.title),
        props.description && (React.createElement("div", { className: "acl-CallToAction--description" }, props.description)),
        props.showButton && typeof props.onButtonClick === 'function' && (React.createElement("div", { className: "acl-CallToAction--button" },
            React.createElement(Button, { variant: ButtonVariant.PRIMARY, onClick: props.onButtonClick }, props.buttonText)))));
};

var DropdownButton = function (_a) {
    var _b = _a.triggerElement, triggerElement = _b === void 0 ? {} : _b, _c = _a.searchable, searchable = _c === void 0 ? false : _c, _d = _a.closeOnItemSelect, closeOnItemSelect = _d === void 0 ? true : _d, _e = _a.closeOnClickOutside, closeOnClickOutside = _e === void 0 ? true : _e, _f = _a.dropdownOffset, dropdownOffset = _f === void 0 ? [0, 4] : _f, props = __rest(_a, ["triggerElement", "searchable", "closeOnItemSelect", "closeOnClickOutside", "dropdownOffset"]);
    var _g = useState(false), open = _g[0], setOpen = _g[1];
    var containerRef = useRef(null);
    var menuRef = useRef(null);
    var _h = useState(null), buttonRef = _h[0], setButtonRef = _h[1];
    var _j = useState(null), popperRef = _j[0], setPopperRef = _j[1];
    var popperProps = {
        placement: props.placement || 'bottom-start',
        strategy: 'fixed',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: dropdownOffset || [0, 4]
                }
            }
        ]
    };
    var _k = usePopper(buttonRef, popperRef, popperProps), styles = _k.styles, attributes = _k.attributes, update = _k.update;
    useDetectOutsideClick([containerRef, menuRef], function () {
        if (closeOnClickOutside) {
            setOpen(false);
        }
    });
    var handleOnSelection = function (selection) {
        if (props.onSelection) {
            props.onSelection(selection);
        }
        if (closeOnItemSelect) {
            setOpen(false);
        }
    };
    return (React.createElement("div", { style: __assign({ position: 'relative', display: 'inline-block' }, props.style), ref: containerRef, className: props.className || null },
        React.createElement(Button, __assign({ allowPropagation: true, variant: props.variant }, triggerElement.props, props.buttonProps, { active: open, ref: setButtonRef, dark: (props.buttonProps && props.buttonProps.dark) || props.dark, onClick: function () {
                setOpen(!open);
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                update();
            } })),
        React.createElement(Portal, null,
            React.createElement("div", __assign({}, attributes.popper, { style: __assign({ zIndex: 2000 }, styles.popper), ref: setPopperRef }), !props.disabled && open && (React.createElement(BasicMenu, { menuRef: menuRef, searchable: searchable, itemLabel: props.itemLabel, loading: props.loading, infiniteScroll: props.infiniteScroll, onScroll: props.onScroll, onQueryChange: props.onQueryChange, onSelection: handleOnSelection, items: props.menuItems, maxResultRows: props.maxResultRows, minWidth: props.minWidth, maxWidth: props.maxWidth, className: 'basicMenu ' + props.className, style: {
                    color: ColorTheme.black
                } }))))));
};

var css_248z$n = ".acl-chip {\n  border-radius: 4px;\n  background-color: #dff3f7;\n  color: rgba(0, 79, 95, 0.8);\n  max-width: 350px;\n  display: inline-flex;\n  position: relative;\n  font-size: 14px;\n  line-height: 16px;\n  padding: 4px 8px; }\n  .acl-chip:hover {\n    background-color: #bfe7ef; }\n  .acl-chip .chip-text {\n    white-space: nowrap;\n    overflow: hidden;\n    text-overflow: ellipsis;\n    max-width: 176px; }\n  .acl-chip .chip-label-container {\n    white-space: nowrap;\n    overflow: hidden;\n    text-overflow: ellipsis;\n    max-width: 100px;\n    padding-right: 8px;\n    margin-right: 8px;\n    box-shadow: 2px 0px 0px -1px rgba(0, 79, 95, 0.4); }\n    .acl-chip .chip-label-container span {\n      font-weight: bold; }\n  .acl-chip .close-chip {\n    position: absolute;\n    width: 48px;\n    height: 24px;\n    right: 0;\n    top: 0;\n    border-top-right-radius: 4px;\n    border-bottom-right-radius: 4px;\n    background: linear-gradient(to right, rgba(191, 231, 239, 0) 0%, #bfe7ef 40%); }\n    .acl-chip .close-chip a {\n      position: absolute;\n      top: 4px;\n      right: 6px;\n      height: 16px;\n      width: 16px;\n      cursor: pointer; }\n      .acl-chip .close-chip a .acl-Icon {\n        vertical-align: unset; }\n      .acl-chip .close-chip a svg {\n        opacity: 0.4; }\n        .acl-chip .close-chip a svg:hover {\n          opacity: 1; }\n    .acl-chip .close-chip .chip-dropdown-container .acl-Button {\n      margin: 0;\n      padding: 0;\n      line-height: inherit;\n      position: absolute;\n      height: 28px;\n      width: 16px;\n      top: 0;\n      right: 30px; }\n      .acl-chip .close-chip .chip-dropdown-container .acl-Button svg {\n        color: rgba(0, 79, 95, 0.8); }\n    .acl-chip .close-chip .chip-dropdown-container .acl-Button:not(.disabled).active, .acl-chip .close-chip .chip-dropdown-container .acl-Button:not(.disabled).focus, .acl-chip .close-chip .chip-dropdown-container .acl-Button:not(.disabled):focus, .acl-chip .close-chip .chip-dropdown-container .acl-Button:not(.disabled):active, .acl-chip .close-chip .chip-dropdown-container .acl-Button:not(.disabled):hover {\n      background-color: transparent;\n      box-shadow: none; }\n  .acl-chip span {\n    cursor: default; }\n";
styleInject(css_248z$n);

var Chip = function (_a) {
    var _b = _a.closable, closable = _b === void 0 ? true : _b, _c = _a.hideOnClose, hideOnClose = _c === void 0 ? true : _c, props = __rest(_a, ["closable", "hideOnClose"]);
    var _d = useState(true), shouldShow = _d[0], setShouldShow = _d[1];
    var _e = useState(false), shouldShowClose = _e[0], setShouldShowClose = _e[1];
    var onCloseClick = function (e) {
        e.stopPropagation();
        safeInvoke(props.onClose);
        if (hideOnClose) {
            setShouldShow(false);
        }
    };
    var renderDropdownMenu = function () {
        if (!props.menuItems || !Array.isArray(props.menuItems)) {
            return null;
        }
        return (React.createElement("div", { className: "chip-dropdown-container" },
            React.createElement(DropdownButton, { menuItems: props.menuItems, triggerElement: React.createElement(Button, { variant: ButtonVariant.STATIC_BORDERLESS },
                    React.createElement(Icon, { name: "ChevronDown" })), style: { position: 'unset' }, dropdownOffset: [0, 0] })));
    };
    var renderLabel = function () {
        if (!props.label)
            return null;
        return (React.createElement("div", { className: "chip-label-container" },
            React.createElement("span", null, props.label)));
    };
    var getCloseParentStyle = function () {
        if (props.menuItems && Array.isArray(props.menuItems)) {
            return {
                width: '100px'
            };
        }
        return {
            width: '48px'
        };
    };
    var renderCloseButton = function () {
        if (!shouldShowClose)
            return null;
        return (React.createElement("div", { className: "close-chip", style: getCloseParentStyle() },
            renderDropdownMenu(),
            React.createElement("a", { onClick: onCloseClick },
                React.createElement(Icon, { name: "Remove" }))));
    };
    var component = function () { return (React.createElement("div", { className: "acl-chip", onClick: function (e) { return safeInvoke(props.onClick, [e]); }, onMouseEnter: function () { return setShouldShowClose(true); }, onMouseLeave: function () { return setShouldShowClose(false); } },
        renderLabel(),
        React.createElement("span", { className: "chip-text" }, props.text || props.children),
        closable && renderCloseButton())); };
    return shouldShow ? component() : null;
};

var ChipGroup = function (_a) {
    var _b = _a.collapsedText, collapsedText = _b === void 0 ? 'selections' : _b, props = __rest(_a, ["collapsedText"]);
    var groupRef = useRef(null);
    var width = useRef(null);
    var originalGroupWidth = useRef(0);
    var _c = useState(false), collapsed = _c[0], setCollapsed = _c[1];
    var handleResize = function () {
        if (window.innerWidth !== width.current) {
            maybeCollapseGroup();
            width.current = window.innerWidth;
        }
    };
    useEffect(function () {
        window.addEventListener('resize', function () { return handleResize(); });
        return window.removeEventListener('resize', function () { return handleResize(); });
    }, []);
    useLayoutEffect(function () {
        width.current = window.innerWidth;
        if (groupRef.current) {
            originalGroupWidth.current = groupRef.current.scrollWidth;
        }
    }, []);
    useLayoutEffect(function () {
        if (props.disableCollapse)
            return undefined;
        setCollapsed(false);
        originalGroupWidth.current = groupRef.current.scrollWidth;
        setCollapsed(shouldCollapse());
    }, [props.chips.length, props.label]);
    useLayoutEffect(function () {
        if (!collapsed) {
            originalGroupWidth.current = groupRef.current.scrollWidth;
            setCollapsed(shouldCollapse());
        }
    }, [collapsed]);
    var isShrinking = function () {
        return window.innerWidth < width.current;
    };
    var shouldCollapse = function () {
        if (!groupRef.current)
            return false;
        return originalGroupWidth.current > groupRef.current.clientWidth;
    };
    var maybeCollapseGroup = function () {
        if (!groupRef.current)
            return;
        if (isShrinking() && !collapsed) {
            setCollapsed(shouldCollapse());
        }
        if (!isShrinking()) {
            // This will trigger the layoutEffect which will then decide whether to keep it collapsed
            setCollapsed(false);
        }
    };
    var renderCollapsedChip = function () {
        return (React.createElement(Chip, { closable: false },
            React.createElement(Link, { onClick: function () {
                    return safeInvoke(props.onCollapsedClick, [
                        { chips: props.chips, label: props.label }
                    ]);
                } },
                props.chips.length,
                " ",
                collapsedText)));
    };
    return (React.createElement("div", { className: "acl-InlineChipContainer-group", ref: groupRef },
        props.label && (React.createElement("strong", { className: "acl-InlineChipContainer-label" }, props.label)),
        React.createElement("div", { className: "acl-InlineChipContainer-chips" }, !collapsed || props.disableCollapse
            ? props.chips.map(function (c, idx) { return React.createElement(Chip, __assign({ key: idx }, c)); })
            : renderCollapsedChip())));
};

var css_248z$o = ".acl-InlineChipContainer {\n  white-space: nowrap;\n  display: flex;\n  overflow: hidden; }\n\n.acl-InlineChipContainer-group {\n  flex: 0 1 auto;\n  overflow: auto; }\n  .acl-InlineChipContainer-group:not(:last-child) {\n    margin-right: .5em; }\n  .acl-InlineChipContainer-group > * {\n    display: inline-block; }\n  .acl-InlineChipContainer-group .acl-InlineChipContainer-label {\n    margin-right: .5em; }\n  .acl-InlineChipContainer-group .acl-chip:not(:last-child) {\n    margin-right: .5em; }\n\nacl-ngx-chip-group {\n  display: flex;\n  overflow: hidden; }\n";
styleInject(css_248z$o);

var InlineChipContainer = function (props) {
    var renderGroupsFromProp = function () {
        if (!props.groups || !props.groups.length)
            return null;
        return props.groups.map(function (c, idx) { return (React.createElement(ChipGroup, __assign({}, c, { key: idx, onCollapsedClick: function (selection) {
                return safeInvoke(props.onCollapsedClick, [selection]);
            } }))); });
    };
    return (React.createElement("div", { className: "acl-InlineChipContainer" }, props.children ? props.children : renderGroupsFromProp()));
};

var FlyoutWidthSize;
(function (FlyoutWidthSize) {
    FlyoutWidthSize["small"] = "small";
    FlyoutWidthSize["medium"] = "medium";
    FlyoutWidthSize["large"] = "large";
})(FlyoutWidthSize || (FlyoutWidthSize = {}));

var FlyoutBody = function (props) { return (React.createElement("div", { className: "acl-Flyout-body" }, props.children)); };

var FlyoutFooter = function (props) { return (React.createElement("footer", { className: "acl-FlyoutFooter" },
    React.createElement("div", { className: "footer-content" }, props.children),
    React.createElement("div", { className: "footer-actions" },
        props.leftButton && React.createElement(Button, __assign({}, props.leftButton)),
        props.rightButton && React.createElement(Button, __assign({}, props.rightButton))))); };

var FlyoutHeader = function (props) {
    var descRowRef = useRef(null);
    var descRef = useRef(null);
    var _a = useState(false), showPopover = _a[0], setShowPopover = _a[1];
    // Logic to tell if the description text will be ellipsized. If so, we need to show a popover on hover.
    useEffect(function () {
        if (descRowRef.current && descRef.current) {
            setShowPopover(descRowRef.current.scrollHeight < descRef.current.scrollHeight);
        }
    }, [descRef, descRowRef, props.descriptionText]);
    var renderDescription = function () {
        if (typeof props.renderCustomDescription === 'function') {
            return React.createElement("small", null, props.renderCustomDescription());
        }
        return (props.descriptionText && (React.createElement(Tooltip, { disabled: !showPopover, trigger: function () { return (React.createElement("small", { ref: descRef }, props.descriptionText)); }, text: props.descriptionText })));
    };
    return (React.createElement("header", { className: "acl-FlyoutHeader" },
        React.createElement("div", { className: "left" },
            React.createElement("div", { className: "title-row", style: {
                    marginBottom: props.descriptionText &&
                        props.descriptionText.length
                        ? 8
                        : 20
                } },
                (props.title && (React.createElement("h1", { title: props.title }, props.title))) ||
                    (props.children && React.createElement("div", null, props.children)),
                props.onNext &&
                    props.onPrevious &&
                    props.showPagination && (React.createElement(StepperButton, { onDecrement: function () { return props.onPrevious(); }, onIncrement: function () { return props.onNext(); }, incrementDisabled: props.disableNext, decrementDisabled: props.disablePrevious }))),
            React.createElement("div", { className: "description-row", ref: descRowRef }, renderDescription())),
        React.createElement("div", { className: "right" },
            React.createElement(Button, { variant: ButtonVariant.STATIC_BORDERLESS, onClick: function () { return props.onClose(); } },
                React.createElement(Icon, { name: "CloseLarge" })))));
};

var css_248z$p = ".acl-Flyout {\n  position: fixed;\n  right: 0;\n  top: 0;\n  bottom: 0;\n  background-color: #ffffff;\n  z-index: 100;\n  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 16px 0 rgba(0, 0, 0, 0.3);\n  display: flex;\n  flex-direction: column; }\n\n.acl-FlyoutHeader {\n  padding: 24px 24px 0px;\n  display: flex;\n  border-bottom: 1px solid rgba(0, 0, 0, 0.3);\n  flex: 0 0 auto; }\n  .acl-FlyoutHeader .left {\n    flex: 1 1 auto;\n    overflow: hidden; }\n    .acl-FlyoutHeader .left .title-row {\n      display: flex; }\n      .acl-FlyoutHeader .left .title-row h1 {\n        padding: 0;\n        padding-right: 8px;\n        margin: 0;\n        font-size: 20px;\n        line-height: 28px;\n        color: rgba(0, 0, 0, 0.7);\n        font-weight: bold;\n        flex: 1 1 auto;\n        overflow: hidden;\n        text-overflow: ellipsis;\n        white-space: nowrap;\n        display: inline-block; }\n      .acl-FlyoutHeader .left .title-row div {\n        flex: 1 1 auto;\n        padding-right: 8px; }\n      .acl-FlyoutHeader .left .title-row .acl-stepperButton {\n        margin: 0 8px;\n        flex: 0 0 auto; }\n    .acl-FlyoutHeader .left .description-row small {\n      font-size: 12px;\n      line-height: 16px;\n      color: rgba(0, 0, 0, 0.5);\n      margin-bottom: 12px;\n      overflow: hidden;\n      text-overflow: ellipsis;\n      line-clamp: 2;\n      -webkit-line-clamp: 2;\n      display: -webkit-box;\n      -webkit-box-orient: vertical;\n      max-height: 40px; }\n  .acl-FlyoutHeader .right {\n    flex: 0 0 auto; }\n\n.acl-Flyout-body {\n  padding-left: 24px;\n  padding-right: 24px;\n  flex: 1 1 auto;\n  overflow: hidden;\n  overflow-y: auto;\n  padding-top: 12px; }\n\n.acl-FlyoutFooter {\n  padding: 24px;\n  display: flex;\n  flex: 0 0 36px;\n  align-items: center;\n  border-top: 1px solid rgba(0, 0, 0, 0.3); }\n  .acl-FlyoutFooter .footer-content {\n    flex: 1 1 auto;\n    margin-right: 16px; }\n  .acl-FlyoutFooter .footer-actions {\n    flex: 0 0 auto; }\n    .acl-FlyoutFooter .footer-actions :not(:first-child) {\n      margin-left: 8px; }\n\nacl-ngx-flyout-header {\n  display: flex;\n  flex: 0 0 auto; }\n  acl-ngx-flyout-header .acl-FlyoutHeader {\n    flex: unset;\n    width: 100%; }\n\nacl-ngx-flyout-body {\n  flex: 1 1 auto;\n  display: flex; }\n\nacl-ngx-flyout-footer {\n  display: flex;\n  flex: 0 0 36px; }\n  acl-ngx-flyout-footer .acl-FlyoutFooter {\n    flex: unset;\n    width: 100%; }\n";
styleInject(css_248z$p);

var Flyout = /** @class */ (function (_super) {
    __extends(Flyout, _super);
    function Flyout(props) {
        var _this = _super.call(this, props) || this;
        _this.style = {
            width: _this.width,
            minWidth: _this.minWidth,
            position: _this.props.pushContent ? 'relative' : 'fixed'
        };
        _this.defaultStyle = {
            opacity: 0,
            transform: _this.props.pushContent ? undefined : 'translateX(100%)',
            right: _this.props.pushContent ? '100%' : undefined,
            transition: 'all 400ms cubic-bezier(0.19, 1, 0.22, 1)'
        };
        return _this;
    }
    Object.defineProperty(Flyout.prototype, "transitionStyles", {
        get: function () {
            if (this.props.pushContent) {
                return {
                    entering: { opacity: 0, right: '-105%' },
                    entered: { opacity: 1, right: 0 },
                    exiting: { opacity: 1, right: '-105%' },
                    exited: { opacity: 0, right: '-105%' },
                    unmounted: { opacity: 0, right: '-105%' }
                };
            }
            return {
                entering: { opacity: 0, transform: 'translateX(105%)' },
                entered: { opacity: 1, transform: 'translateX(0)' },
                exiting: { opacity: 1, transform: 'translateX(105%)' },
                exited: { opacity: 0, transform: 'translateX(105%)' },
                unmounted: { opacity: 0, transform: 'translateX(105%)' }
            };
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Flyout.prototype, "width", {
        get: function () {
            if (this.props.large || this.props.size === FlyoutWidthSize.large) {
                return '60%';
            }
            else if (this.props.size === FlyoutWidthSize.small) {
                return '30%';
            }
            return '40%';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Flyout.prototype, "minWidth", {
        get: function () {
            if (this.props.large || this.props.size === FlyoutWidthSize.large) {
                return 800;
            }
            else if (this.props.size === FlyoutWidthSize.small) {
                return 400;
            }
            return 600;
        },
        enumerable: true,
        configurable: true
    });
    Flyout.prototype.render = function () {
        var _this = this;
        return (React.createElement(Portal, { portalRoot: this.props.portalRoot, disabled: !this.props.usePortal },
            React.createElement(Transition, { timeout: { enter: 0, exit: 400 }, in: this.props.open, appear: true, mountOnEnter: true, unmountOnExit: true }, function (state) { return (React.createElement("aside", { className: classNames('acl-Flyout', _this.props.className), style: __assign(__assign(__assign(__assign({}, _this.style), _this.defaultStyle), _this.transitionStyles[state]), _this.props.style) }, React.Children.map(_this.props.children, function (child) {
                // If the child is a flyout header, we clone it so we can add the onClose handler
                // this is so the developer doesn't have to do it for each flyout.
                // tslint:disable-next-line
                if (child.type === FlyoutHeader) {
                    return React.cloneElement(child, __assign(__assign({}, child.props), { onClose: _this.props.onClose }));
                }
                return child;
            }))); }),
            this.props.dimmer && (React.createElement(Dimmer, { visible: this.props.open, zIndex: 10, onClick: this.props.onClose }))));
    };
    Flyout.Header = FlyoutHeader;
    Flyout.Body = FlyoutBody;
    Flyout.Footer = FlyoutFooter;
    Flyout.defaultProps = {
        open: false,
        dimmer: true
    };
    return Flyout;
}(Component));

var css_248z$q = ".acl-Modal {\n  position: fixed;\n  top: 50%;\n  left: 50%;\n  border-radius: 3px;\n  transform: translate(-50%, -60%);\n  transition: transform 220ms ease-out, opacity 220ms ease-out;\n  background-color: #ffffff;\n  z-index: 100000;\n  width: 600px;\n  opacity: 0;\n  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 16px 0 rgba(0, 0, 0, 0.3);\n  padding: 32px; }\n  .acl-Modal.open {\n    opacity: 1;\n    transform: translate(-50%, -50%); }\n  .acl-Modal.full {\n    width: calc(100% - 32px); }\n  .acl-Modal.small {\n    width: 300px; }\n  .acl-Modal.medium {\n    width: 600px; }\n  .acl-Modal.large {\n    width: 900px; }\n  .acl-Modal .closeModal {\n    position: absolute;\n    right: 32px;\n    top: 32px;\n    z-index: 100;\n    padding: 0;\n    height: auto; }\n  .acl-Modal .modalTitle h3 {\n    margin: 0;\n    padding: 0 45px 0 0;\n    font-family: \"Roboto\", sans-serif;\n    font-size: 20px;\n    font-weight: bold;\n    line-height: 28px;\n    display: flex;\n    align-items: center; }\n    .acl-Modal .modalTitle h3 svg {\n      margin-right: 12px; }\n  .acl-Modal .modalTitle.scroll h3 {\n    padding: 0 45px 20px 0; }\n  .acl-Modal .modalBody {\n    padding-top: 0;\n    width: 100%;\n    margin-bottom: 32px; }\n    .acl-Modal .modalBody.scroll {\n      margin-top: 20px;\n      position: relative;\n      border-top: 1px solid rgba(0, 0, 0, 0.3);\n      border-bottom: 1px solid rgba(0, 0, 0, 0.3); }\n      .acl-Modal .modalBody.scroll:before, .acl-Modal .modalBody.scroll:after {\n        content: '';\n        height: 40px;\n        width: 100%;\n        position: absolute;\n        left: 0;\n        background: #ffffff; }\n      .acl-Modal .modalBody.scroll:before {\n        opacity: 0;\n        bottom: auto;\n        top: 0;\n        background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%); }\n      .acl-Modal .modalBody.scroll:after {\n        top: auto;\n        bottom: 0;\n        background: linear-gradient(0deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%); }\n      .acl-Modal .modalBody.scroll .modalBodyWrapper {\n        overflow-y: scroll;\n        overflow-x: hidden;\n        width: 100%;\n        max-height: 336px;\n        padding: 0 16px 0 0; }\n        .acl-Modal .modalBody.scroll .modalBodyWrapper:after {\n          content: '';\n          display: block;\n          padding-bottom: 32px; }\n    .acl-Modal .modalBody.scrolling:before {\n      opacity: 1; }\n  .acl-Modal .modalFooter {\n    text-align: right; }\n    .acl-Modal .modalFooter button {\n      margin-left: 16px; }\n";
styleInject(css_248z$q);

var ModalBody = function (props) {
    var _a = React.useState(false), scrolling = _a[0], setScroll = _a[1];
    var onScroll = function (e) {
        setScroll(Number(e.target.scrollTop) > 10);
    };
    var cssClasses = classNames('modalBody', {
        scroll: props.scroll,
        scrolling: scrolling
    });
    return (React.createElement("div", { className: cssClasses, onScroll: onScroll },
        React.createElement("div", { className: "modalBodyWrapper" }, props.children)));
};

var ModalFooter = function (props) { return (React.createElement("div", { className: "modalFooter" }, props.children)); };

var ModalHeader = function (props) { return (React.createElement("div", { className: "modalTitle" }, props.children && React.createElement("h3", null, props.children))); };

var ModalWidth;
(function (ModalWidth) {
    ModalWidth["SMALL"] = "small";
    ModalWidth["MEDIUM"] = "medium";
    ModalWidth["LARGE"] = "large";
    ModalWidth["FULL"] = "full";
})(ModalWidth || (ModalWidth = {}));
var duration = 300;
var defaultStyle = {
    opacity: 1,
    transition: "opacity " + duration + "ms cubic-bezier(0.19, 1, 0.22, 1)"
};
var transitionStyles$1 = {
    entering: { opacity: 0 },
    entered: { opacity: 1 },
    exiting: { opacity: 1 },
    exited: { opacity: 0 },
    unmounted: { opacity: 0 }
};
var Modal = /** @class */ (function (_super) {
    __extends(Modal, _super);
    function Modal() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Modal.prototype.handleToggle = function (action) {
        safeInvoke(this.props.onToggle, [action]);
    };
    Modal.prototype.render = function () {
        var _this = this;
        var width = this.props.width;
        var cssClasses = classNames('acl-Modal', {
            open: this.props.open,
            small: width === ModalWidth.SMALL,
            medium: width === ModalWidth.MEDIUM,
            large: width === ModalWidth.LARGE,
            full: width === ModalWidth.FULL
        });
        return (React.createElement(Portal, null, this.props.open && (React.createElement(React.Fragment, null,
            React.createElement(Transition, { timeout: 0, in: this.props.open, appear: true, mountOnEnter: true, unmountOnExit: true }, function (state) { return (React.createElement("div", { className: cssClasses, style: __assign(__assign(__assign({}, defaultStyle), transitionStyles$1[state]), { zIndex: 10000 }) },
                _this.props.closeButton && (React.createElement(Button, { variant: ButtonVariant.BORDERLESS, className: "closeModal", onClick: function () {
                        return _this.handleToggle('CLOSE');
                    } },
                    React.createElement(Icon, { name: "CloseLarge", color: ColorTheme.gray50, size: IconSize.base }))),
                _this.props.children)); }),
            React.createElement(Dimmer, { visible: this.props.open, fadeInDuration: duration, onClick: function () { return _this.handleToggle('CLOSE'); } })))));
    };
    Modal.Header = ModalHeader;
    Modal.Body = ModalBody;
    Modal.Footer = ModalFooter;
    return Modal;
}(Component));

var css_248z$r = ".acl-Navbar {\n  display: flex;\n  position: relative;\n  padding: 8px;\n  background: #303030;\n  color: rgba(255, 255, 255, 0.7);\n  height: 52px;\n  align-items: stretch;\n  border-bottom: 2px solid #009ebd; }\n  .acl-Navbar .left {\n    color: white; }\n    .acl-Navbar .left .logoLink {\n      color: inherit;\n      text-decoration: none;\n      line-height: 1; }\n      .acl-Navbar .left .logoLink:active, .acl-Navbar .left .logoLink:hover, .acl-Navbar .left .logoLink:focus {\n        color: inherit;\n        text-decoration: none; }\n    .acl-Navbar .left .logo {\n      height: 32px;\n      display: inline-block;\n      width: auto;\n      margin-right: 12px; }\n    .acl-Navbar .left .productName {\n      font-size: 20px;\n      line-height: 28px;\n      text-transform: uppercase; }\n    .acl-Navbar .left .acl-Tag {\n      color: #ffffff;\n      border-radius: 100px;\n      padding: 0 8px;\n      font-size: 12px;\n      font-weight: bold;\n      margin-left: 8px; }\n  .acl-Navbar .left,\n  .acl-Navbar .center,\n  .acl-Navbar .right {\n    align-items: center;\n    display: flex; }\n  .acl-Navbar .center {\n    flex: 1 1 auto;\n    margin-left: 12px;\n    display: flex;\n    justify-content: center; }\n  .acl-Navbar .right > {\n    margin-left: 12px; }\n    .acl-Navbar .right > :not(:first-child) {\n      margin-left: 8px; }\n";
styleInject(css_248z$r);

var NavbarCenter = function (props) { return (React.createElement("div", { className: "center" }, props.children)); };

var NavbarRight = function (props) { return (React.createElement("div", { className: "right" }, props.children)); };

var Navbar = /** @class */ (function (_super) {
    __extends(Navbar, _super);
    function Navbar() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Navbar.prototype.render = function () {
        var _a = this.props.accentColor, accentColor = _a === void 0 ? ColorTheme.amobeePrimaryColor : _a;
        return (React.createElement("nav", { className: "acl-Navbar", style: {
                borderBottomColor: this.props.accentColor,
                zIndex: this.props.zIndex
            } },
            React.createElement("div", { className: "left" },
                this.getMenuButtonElement(),
                this.renderLogo(),
                React.createElement("div", { className: "productName", style: { color: accentColor } }, this.props.productName),
                this.props.tagText && (React.createElement("div", { className: "acl-Tag", style: { backgroundColor: accentColor } }, this.props.tagText))),
            this.props.children));
    };
    Navbar.prototype.getMenuButtonElement = function () {
        if (!this.props.menuButtonEl) {
            return null;
        }
        return React.cloneElement(this.props.menuButtonEl, {
            style: {
                marginRight: 12
            }
        });
    };
    Navbar.prototype.renderLogo = function () {
        var customLogo = safeInvoke(this.props.renderCustomLogo);
        if (!customLogo)
            return this.renderDefaultLogo();
        if (this.props.logoLink) {
            return (React.createElement("a", { className: "logoLink", href: this.props.logoLink }, customLogo));
        }
        return customLogo;
    };
    Navbar.prototype.renderDefaultLogo = function () {
        if (this.props.logoLink) {
            return (React.createElement("a", { className: "logoLink", href: this.props.logoLink },
                React.createElement(AmobeeLogo, { className: "logo" })));
        }
        return React.createElement(AmobeeLogo, { className: "logo" });
    };
    Navbar.Center = NavbarCenter;
    Navbar.Right = NavbarRight;
    Navbar.propTypes = {
        children: function (props, propName) {
            var prop = props[propName];
            React.Children.forEach(prop, function (child) {
                if (![NavbarCenter, NavbarRight].includes(child.type)) {
                    return new Error('Children must be of type `NavbarCenter` or `NavbarRight`');
                }
                return undefined;
            });
        }
    };
    return Navbar;
}(Component));

var css_248z$s = ".acl-PageHeader {\n  background: #303030;\n  color: #ffffff;\n  display: flex;\n  align-items: stretch; }\n";
styleInject(css_248z$s);

var css_248z$t = ".acl-PageHeaderSection {\n  display: flex;\n  flex-direction: column;\n  margin: 8px 0;\n  padding: 0 16px;\n  min-height: 44px; }\n  .acl-PageHeaderSection:first-child {\n    flex: 1 1 auto; }\n  .acl-PageHeaderSection:not(:first-child) {\n    border-left: 1px solid rgba(255, 255, 255, 0.1); }\n  .acl-PageHeaderSection label {\n    text-transform: uppercase;\n    font-size: 12px;\n    line-height: 16px;\n    color: rgba(255, 255, 255, 0.5);\n    white-space: nowrap; }\n";
styleInject(css_248z$t);

var PageHeader = function (props) { return (React.createElement("header", { className: "acl-PageHeader " + (props.className || ''), style: props.style }, props.children)); };

var PageHeaderSection = function (props) { return (React.createElement("section", { className: "acl-PageHeaderSection " + (props.className || ''), style: props.style },
    React.createElement("label", null, props.label),
    props.children)); };

var css_248z$u = ".acl-Pagination {\n  display: flex;\n  list-style-type: none; }\n  .acl-Pagination .acl-Pagination--item {\n    padding: 0 8px;\n    height: 32px;\n    text-align: center;\n    margin: auto 4px;\n    color: #000000;\n    display: flex;\n    box-sizing: border-box;\n    align-items: center;\n    border-radius: 3px;\n    min-width: 32px;\n    font-size: 14px;\n    justify-content: center; }\n    .acl-Pagination .acl-Pagination--item.acl-Pagination--item-dots:hover {\n      background-color: transparent;\n      cursor: default; }\n    .acl-Pagination .acl-Pagination--item:hover {\n      background-color: #D6F8F8;\n      cursor: pointer; }\n      .acl-Pagination .acl-Pagination--item:hover:last-of-type, .acl-Pagination .acl-Pagination--item:hover:first-of-type {\n        background-color: transparent; }\n    .acl-Pagination .acl-Pagination--item.selected {\n      background-color: #04B3B7;\n      color: #ffffff; }\n    .acl-Pagination .acl-Pagination--item .acl-Pagination--arrow::before {\n      position: relative;\n      content: '';\n      display: inline-block;\n      width: 6px;\n      height: 6px;\n      border-right: 0.12em solid rgba(0, 0, 0, 0.7);\n      border-top: 0.12em solid rgba(0, 0, 0, 0.7); }\n    .acl-Pagination .acl-Pagination--item .acl-Pagination--arrow.left {\n      transform: rotate(-135deg) translate(-50%); }\n    .acl-Pagination .acl-Pagination--item .acl-Pagination--arrow.right {\n      top: 3px;\n      transform: rotate(45deg); }\n    .acl-Pagination .acl-Pagination--item.disabled {\n      pointer-events: none; }\n      .acl-Pagination .acl-Pagination--item.disabled .acl-Pagination--arrow::before {\n        border-right: 0.12em solid rgba(0, 0, 0, 0.5);\n        border-top: 0.12em solid rgba(0, 0, 0, 0.5);\n        opacity: 0.3; }\n      .acl-Pagination .acl-Pagination--item.disabled:hover {\n        cursor: default; }\n  .acl-Pagination.dark .acl-Pagination--item {\n    color: #ffffff; }\n    .acl-Pagination.dark .acl-Pagination--item:hover {\n      background-color: #D6F8F899; }\n      .acl-Pagination.dark .acl-Pagination--item:hover:last-of-type, .acl-Pagination.dark .acl-Pagination--item:hover:first-of-type {\n        background-color: transparent; }\n    .acl-Pagination.dark .acl-Pagination--item.acl-Pagination--item-dots:hover {\n      color: #ffffff;\n      background-color: transparent; }\n    .acl-Pagination.dark .acl-Pagination--item.disabled .acl-Pagination--arrow::before {\n      border-right-color: rgba(255, 255, 255, 0.5);\n      border-top-color: rgba(255, 255, 255, 0.5); }\n  .acl-Pagination.dark .acl-Pagination--arrow::before {\n    border-right-color: rgba(255, 255, 255, 0.7);\n    border-top-color: rgba(255, 255, 255, 0.7); }\n";
styleInject(css_248z$u);

var Pagination = function (_a) {
    var _b;
    var _c = _a.siblingCount, siblingCount = _c === void 0 ? 1 : _c, _d = _a.pageSize, pageSize = _d === void 0 ? 1 : _d, totalCount = _a.totalCount, props = __rest(_a, ["siblingCount", "pageSize", "totalCount"]);
    var _e = useState(props.currentPage), currentPage = _e[0], setCurrentPage = _e[1];
    var paginationRange = usePagination({
        currentPage: currentPage,
        totalCount: totalCount,
        siblingCount: siblingCount,
        pageSize: pageSize
    });
    if (currentPage === 0 || paginationRange.length < 2) {
        return null;
    }
    var onNext = function () {
        pageChange(currentPage + 1);
    };
    var onPrevious = function () {
        pageChange(currentPage - 1);
    };
    var pageChange = function (value) {
        setCurrentPage(value);
        if (props.onPageChange)
            props.onPageChange(value);
    };
    var lastPage = paginationRange[paginationRange.length - 1];
    return (React.createElement("ul", { className: classNames('acl-Pagination', (_b = {
                dark: props.dark
            },
            _b[props.className] = props.className,
            _b)), style: props.style },
        React.createElement("li", { className: classNames('acl-Pagination--item', {
                disabled: currentPage === 1
            }), onClick: onPrevious },
            React.createElement("div", { className: "acl-Pagination--arrow left" })),
        paginationRange.map(function (pageNumber, index) {
            if (pageNumber === '...') {
                return (React.createElement("li", { className: "acl-Pagination--item acl-Pagination--item-dots", key: index }, "..."));
            }
            return (React.createElement("li", { className: classNames('acl-Pagination--item', {
                    selected: pageNumber === currentPage
                }), onClick: function () {
                    pageChange(pageNumber);
                }, key: index }, pageNumber));
        }),
        React.createElement("li", { className: classNames('acl-Pagination--item', {
                disabled: currentPage === lastPage
            }), onClick: onNext },
            React.createElement("div", { className: "acl-Pagination--arrow right" }))));
};

var css_248z$v = ".acl-Popover {\n  z-index: 100;\n  position: relative;\n  border-radius: 4px;\n  background: #fff;\n  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 16px 0 rgba(0, 0, 0, 0.3);\n  padding: 16px;\n  text-align: left;\n  width: auto;\n  max-width: 300px;\n  margin: 13px; }\n  .acl-Popover .popoverTitle {\n    display: flex;\n    position: relative;\n    margin-bottom: 12px;\n    align-items: center; }\n    .acl-Popover .popoverTitle h3 {\n      font-size: 14px;\n      line-height: 16px;\n      flex: 1 0 auto;\n      margin: 0;\n      max-width: 255px;\n      padding-right: 10px;\n      overflow: hidden;\n      text-overflow: ellipsis; }\n    .acl-Popover .popoverTitle .closePopover {\n      align-self: flex-start; }\n  .acl-Popover .popoverContent {\n    position: relative;\n    font-size: 12px;\n    line-height: 16px;\n    max-height: 236px;\n    overflow-x: hidden;\n    overflow-y: auto; }\n    .acl-Popover .popoverContent .popover-content-bottom-fade,\n    .acl-Popover .popoverContent .popover-content-top-fade {\n      position: sticky;\n      width: 100%;\n      height: 15px;\n      background: linear-gradient(0deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);\n      bottom: 0; }\n    .acl-Popover .popoverContent .popover-content-top-fade {\n      background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);\n      top: 0;\n      bottom: auto;\n      opacity: 0; }\n    .acl-Popover .popoverContent.scrolling .popover-content-top-fade {\n      opacity: 1; }\n\n.acl-Popover-Wrapper[data-popper-placement=\"top\" i] .acl-Popover .popoverArrow {\n  position: absolute;\n  background-color: #ffffff;\n  width: 12px;\n  height: 12px;\n  transform: rotate(-45deg);\n  border: 1px rgba(0, 0, 0, 0.3) solid;\n  bottom: -6px;\n  left: 50%;\n  margin-left: -6px;\n  border-top: none;\n  border-right: none; }\n\n.acl-Popover-Wrapper[data-popper-placement=\"top-start\" i] .acl-Popover .popoverArrow {\n  position: absolute;\n  background-color: #ffffff;\n  width: 12px;\n  height: 12px;\n  transform: rotate(-45deg);\n  border: 1px rgba(0, 0, 0, 0.3) solid;\n  bottom: -6px;\n  left: 10px;\n  border-top: none;\n  border-right: none; }\n\n.acl-Popover-Wrapper[data-popper-placement=\"top-end\" i] .acl-Popover .popoverArrow {\n  position: absolute;\n  background-color: #ffffff;\n  width: 12px;\n  height: 12px;\n  transform: rotate(-45deg);\n  border: 1px rgba(0, 0, 0, 0.3) solid;\n  bottom: -6px;\n  right: 10px;\n  border-top: none;\n  border-right: none; }\n\n.acl-Popover-Wrapper[data-popper-placement=\"right\" i] .acl-Popover .popoverArrow {\n  position: absolute;\n  background-color: #ffffff;\n  width: 12px;\n  height: 12px;\n  transform: rotate(-45deg);\n  border: 1px rgba(0, 0, 0, 0.3) solid;\n  left: -6px;\n  top: 50%;\n  margin-top: -6px;\n  border-right: none;\n  border-bottom: none; }\n\n.acl-Popover-Wrapper[data-popper-placement=\"right-start\" i] .acl-Popover .popoverArrow {\n  position: absolute;\n  background-color: #ffffff;\n  width: 12px;\n  height: 12px;\n  transform: rotate(-45deg);\n  border: 1px rgba(0, 0, 0, 0.3) solid;\n  left: -6px;\n  top: 10px;\n  border-top: none;\n  border-right: none; }\n\n.acl-Popover-Wrapper[data-popper-placement=\"right-end\" i] .acl-Popover .popoverArrow {\n  position: absolute;\n  background-color: #ffffff;\n  width: 12px;\n  height: 12px;\n  transform: rotate(-45deg);\n  border: 1px rgba(0, 0, 0, 0.3) solid;\n  left: -6px;\n  bottom: 10px;\n  border-right: none;\n  border-bottom: none; }\n\n.acl-Popover-Wrapper[data-popper-placement=\"bottom\" i] .acl-Popover .popoverArrow {\n  position: absolute;\n  background-color: #ffffff;\n  width: 12px;\n  height: 12px;\n  transform: rotate(-45deg);\n  border: 1px rgba(0, 0, 0, 0.3) solid;\n  top: -6px;\n  left: 50%;\n  margin-left: -6px;\n  border-left: none;\n  border-bottom: none; }\n\n.acl-Popover-Wrapper[data-popper-placement=\"bottom-start\" i] .acl-Popover .popoverArrow {\n  position: absolute;\n  background-color: #ffffff;\n  width: 12px;\n  height: 12px;\n  transform: rotate(-45deg);\n  border: 1px rgba(0, 0, 0, 0.3) solid;\n  top: -6px;\n  left: 10px;\n  border-left: none;\n  border-bottom: none; }\n\n.acl-Popover-Wrapper[data-popper-placement=\"bottom-end\" i] .acl-Popover .popoverArrow {\n  position: absolute;\n  background-color: #ffffff;\n  width: 12px;\n  height: 12px;\n  transform: rotate(-45deg);\n  border: 1px rgba(0, 0, 0, 0.3) solid;\n  top: -6px;\n  right: 10px;\n  border-left: none;\n  border-bottom: none; }\n\n.acl-Popover-Wrapper[data-popper-placement=\"left\" i] .acl-Popover .popoverArrow {\n  position: absolute;\n  background-color: #ffffff;\n  width: 12px;\n  height: 12px;\n  transform: rotate(-45deg);\n  border: 1px rgba(0, 0, 0, 0.3) solid;\n  right: -6px;\n  top: 50%;\n  margin-top: -6px;\n  border-top: none;\n  border-left: none; }\n\n.acl-Popover-Wrapper[data-popper-placement=\"left-start\" i] .acl-Popover .popoverArrow {\n  position: absolute;\n  background-color: #ffffff;\n  width: 12px;\n  height: 12px;\n  transform: rotate(-45deg);\n  border: 1px rgba(0, 0, 0, 0.3) solid;\n  right: -6px;\n  top: 10px;\n  border-top: none;\n  border-left: none; }\n\n.acl-Popover-Wrapper[data-popper-placement=\"left-end\" i] .acl-Popover .popoverArrow {\n  position: absolute;\n  background-color: #ffffff;\n  width: 12px;\n  height: 12px;\n  transform: rotate(-45deg);\n  border: 1px rgba(0, 0, 0, 0.3) solid;\n  right: -6px;\n  bottom: 10px;\n  border-top: none;\n  border-left: none; }\n";
styleInject(css_248z$v);

var Popover = function (_a) {
    var _b = _a.openOn, openOn = _b === void 0 ? 'click' : _b, props = __rest(_a, ["openOn"]);
    var _c = useState(props.open || false), open = _c[0], setOpen = _c[1];
    var _d = useState(true), trigger = _d[0], setTrigger = _d[1];
    var _e = useState(0), scrolling = _e[0], setScroll = _e[1];
    var _f = useState(false), overflow = _f[0], setOverflow = _f[1];
    var _g = useState(false), shouldRenderFadeBottom = _g[0], setShouldRenderFadeBottom = _g[1];
    var _h = useState(false), shouldRenderFadeTop = _h[0], setShouldRenderFadeTop = _h[1];
    var popoverRef = useRef(null);
    var _j = useState(null), arrowRef = _j[0], setArrowRef = _j[1];
    var popoverBodyRef = useRef(null);
    var _k = useState(null), triggerRef = _k[0], setTriggerRef = _k[1];
    var _l = useState(null), popoverOuterRef = _l[0], setPopoverOuterRef = _l[1];
    var isMouseOnPopoverRef = useRef(false);
    var isMouseOnTriggerRef = useRef(false);
    var duration = 300;
    var defaultStyle = {
        opacity: 1,
        transition: "opacity " + duration + "ms cubic-bezier(0.19, 1, 0.22, 1)"
    };
    var transitionStyles = {
        entering: { opacity: 0 },
        entered: { opacity: 1 },
        exiting: { opacity: 1 },
        exited: { opacity: 0 },
        unmounted: { opacity: 0 }
    };
    var popperProps = {
        placement: props.placement || 'right',
        strategy: 'fixed',
        modifiers: [
            {
                name: 'arrow',
                options: {
                    element: arrowRef
                }
            }
        ]
    };
    var _m = usePopper(triggerRef, popoverOuterRef, popperProps), styles = _m.styles, attributes = _m.attributes, update = _m.update;
    var handleScroll = function () {
        if (open)
            setOpen(false);
    };
    var toggle = function (show) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (openOn === 'hover') {
                        setOpen(show);
                    }
                    else {
                        setOpen(!open);
                    }
                    return [4 /*yield*/, update()];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    function handleClick() {
        var triggerCheck = function () {
            if (triggerRef !== null && !props.disabled) {
                var triggerHtml = triggerRef.innerHTML;
                if (!triggerHtml) {
                    setTrigger(false);
                }
            }
        };
        // eslint-disable-next-line no-invalid-this
        setTimeout(triggerCheck.bind(this), 0);
    }
    useDetectContentOverflow(popoverBodyRef, function () {
        return setShouldRenderFadeBottom(true);
    });
    if (props.closeOnOutsideClick && openOn === 'click') {
        useDetectOutsideClick([{ current: triggerRef }, popoverRef], function () {
            setOpen(false);
        });
    }
    var onScroll = function (e) {
        var top = e.target.scrollTop;
        setOverflow(top >= e.target.scrollHeight - 240);
        setScroll(top);
        setShouldRenderFadeTop(top >= 1 ? true : false);
    };
    var bodyClasses = classNames('popoverContent', {
        scrolling: scrolling > 1,
        bottom: overflow
    });
    useEffect(function () {
        if (open && !props.placement) {
            setOverflow(!(popoverBodyRef.current &&
                popoverBodyRef.current.scrollHeight >
                    popoverBodyRef.current.clientHeight));
        }
        document.addEventListener('scroll', handleScroll, true);
        document.addEventListener('click', handleClick, true);
        return function () {
            document.removeEventListener('scroll', handleScroll, true);
            document.removeEventListener('click', handleClick, true);
        };
    }, [triggerRef]);
    var handleTriggerClick = function () {
        if (openOn === 'click') {
            void toggle();
        }
    };
    var renderFadeTop = function () {
        if (!shouldRenderFadeTop)
            return null;
        return React.createElement("div", { className: "popover-content-top-fade" });
    };
    var renderFadeBottom = function () {
        if (!shouldRenderFadeBottom)
            return null;
        return React.createElement("div", { className: "popover-content-bottom-fade" });
    };
    var maybeClosePopover = function () {
        if (openOn === 'hover') {
            setTimeout(function () {
                if (!isMouseOnPopoverRef.current &&
                    !isMouseOnTriggerRef.current) {
                    void toggle(false);
                }
            }, 100);
        }
    };
    var handleMouseEnterTrigger = function () {
        isMouseOnTriggerRef.current = true;
        if (openOn === 'hover' && !open) {
            void toggle(true);
        }
    };
    var handleMouseLeaveTrigger = function () {
        isMouseOnTriggerRef.current = false;
        maybeClosePopover();
    };
    var handleMouseEnterPopover = function () {
        isMouseOnPopoverRef.current = true;
    };
    var handleMouseLeavePopover = function () {
        isMouseOnPopoverRef.current = false;
        maybeClosePopover();
    };
    var handleOnCloseClick = function () {
        isMouseOnTriggerRef.current = false;
        isMouseOnPopoverRef.current = false;
        setOpen(false);
    };
    return (React.createElement(React.Fragment, null, trigger && (React.createElement("div", { style: { display: 'inline-block' } },
        React.createElement("div", { "data-cy": "popover-trigger-wrapper", onClick: handleTriggerClick, onMouseOver: handleMouseEnterTrigger, onMouseOut: handleMouseLeaveTrigger, ref: setTriggerRef }, props.renderTrigger()),
        React.createElement(Portal, null,
            React.createElement("div", __assign({ onMouseOver: handleMouseEnterPopover, onMouseOut: handleMouseLeavePopover, style: __assign({ zIndex: 1000 }, styles.popper), ref: setPopoverOuterRef }, attributes.popper, { className: "acl-Popover-Wrapper" }), open && !props.disabled && (React.createElement(Transition, { timeout: duration, in: open, appear: true, mountOnEnter: true, unmountOnExit: true }, function (state) { return (React.createElement("div", { className: "acl-Popover", style: __assign(__assign({}, defaultStyle), transitionStyles[state]), ref: popoverRef },
                React.createElement("div", { className: "popoverArrow", ref: setArrowRef }),
                props.title && (React.createElement("div", { className: "popoverTitle" },
                    React.createElement("h3", { title: props.title }, props.title),
                    React.createElement(Button, { ultraCompact: true, variant: ButtonVariant.BORDERLESS, className: "closePopover", onClick: function () {
                            return handleOnCloseClick();
                        } },
                        React.createElement(Icon, { name: "CloseSmall", color: ColorTheme.gray80, size: IconSize.base })))),
                React.createElement("div", { className: bodyClasses, onScroll: onScroll, ref: popoverBodyRef },
                    renderFadeTop(),
                    React.createElement("div", null, props.children),
                    renderFadeBottom()))); }))))))));
};

var css_248z$w = ".acl-Tabs {\n  display: flex;\n  flex-direction: row;\n  flex-wrap: nowrap; }\n  .acl-Tabs .tabNav {\n    display: flex;\n    width: 100%; }\n  .acl-Tabs .tabPanes {\n    display: flex;\n    width: 100%; }\n  .acl-Tabs.stretch {\n    display: flex;\n    flex-wrap: wrap;\n    flex-direction: row;\n    align-content: stretch;\n    justify-content: space-between; }\n    .acl-Tabs.stretch .acl-Tab {\n      flex-direction: column;\n      flex-basis: 100%;\n      flex: 1; }\n  .acl-Tabs.fit {\n    display: flex;\n    flex-wrap: wrap;\n    flex-direction: row;\n    justify-content: flex-start; }\n    .acl-Tabs.fit .acl-Tab {\n      text-align: left; }\n  .acl-Tabs.left {\n    justify-content: flex-start;\n    border-right: 1px solid rgba(0, 0, 0, 0.1); }\n    .acl-Tabs.left .tabNav {\n      display: inline-flex;\n      flex-direction: column;\n      flex-basis: 0;\n      width: auto;\n      max-width: 150px; }\n      .acl-Tabs.left .tabNav .acl-Tab {\n        display: block;\n        text-align: left;\n        border-bottom: none;\n        border-right: 4px solid transparent; }\n        .acl-Tabs.left .tabNav .acl-Tab.active {\n          border-color: #009ebd; }\n    .acl-Tabs.left .tabPane {\n      display: inline-flex;\n      flex-basis: 1;\n      border: none; }\n  .acl-Tabs.right {\n    flex-direction: row-reverse;\n    border-left: 1px solid rgba(0, 0, 0, 0.1); }\n    .acl-Tabs.right .tabNav {\n      display: inline-flex;\n      flex-direction: column;\n      flex-basis: 0;\n      width: auto;\n      max-width: 150px; }\n      .acl-Tabs.right .tabNav .acl-Tab {\n        display: block;\n        text-align: left;\n        border-bottom: none;\n        border-left: 4px solid transparent; }\n        .acl-Tabs.right .tabNav .acl-Tab.active {\n          border-color: #009ebd; }\n    .acl-Tabs.right .tabPane {\n      display: inline-flex;\n      flex-basis: 1;\n      border: none;\n      order: 1; }\n  .acl-Tabs.dark .acl-Tab, .acl-Tabs.dark .acl-Tab:not(.active):hover,\n  .acl-Tabs.dark .tabPane,\n  .acl-Tabs.dark .tabPane:not(.active):hover {\n    color: #ffffff; }\n  .acl-Tabs.dark .tabPane {\n    border-top-color: rgba(255, 255, 255, 0.1); }\n  .acl-Tabs .tabPane {\n    width: 100%;\n    flex-basis: auto;\n    padding: 16px;\n    border-top: 4px solid transparent;\n    margin-top: -4px; }\n\n.acl-Tab,\na.acl-Tab,\na:not([href]).acl-Tab {\n  border-bottom: 4px solid transparent;\n  height: 32px;\n  padding: 8px;\n  text-align: center;\n  font-size: 14px;\n  line-height: 16px;\n  font-weight: bold;\n  cursor: pointer;\n  color: rgba(0, 0, 0, 0.3);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis; }\n  .acl-Tab.active,\n  a.acl-Tab.active,\n  a:not([href]).acl-Tab.active {\n    border-color: #009ebd;\n    color: #009ebd; }\n  .acl-Tab:not(.active):hover,\n  a.acl-Tab:not(.active):hover,\n  a:not([href]).acl-Tab:not(.active):hover {\n    color: rgba(0, 0, 0, 0.3); }\n  .acl-Tab .icon,\n  a.acl-Tab .icon,\n  a:not([href]).acl-Tab .icon {\n    margin-right: 8px;\n    position: relative;\n    top: -1px; }\n";
styleInject(css_248z$w);

var Tab = function (props) { return (React.createElement("a", { onClick: function () { return props.onTabClick(); }, className: classNames('acl-Tab', { active: props.active }), title: props.title },
    props.icon && React.createElement(Icon, { name: props.icon, className: "icon" }),
    props.title)); };

var alignTabs;
(function (alignTabs) {
    alignTabs["STRETCH"] = "stretch";
    alignTabs["FIT"] = "fit";
    alignTabs["LEFT"] = "left";
    alignTabs["RIGHT"] = "right";
})(alignTabs || (alignTabs = {}));
var Tabs = /** @class */ (function (_super) {
    __extends(Tabs, _super);
    function Tabs(props) {
        var _this = _super.call(this, props) || this;
        var activeTab = props.children.find(function (item) { return item.props.active; });
        _this.state = {
            activeTab: (activeTab && activeTab.props.id) || null
        };
        return _this;
    }
    Tabs.prototype.setActiveTab = function (tab) {
        this.setState({
            activeTab: tab.id
        });
        if (this.props.onTabChange) {
            this.props.onTabChange(tab);
        }
    };
    Tabs.prototype.render = function () {
        var _this = this;
        var cssClasses = classNames('acl-Tabs', {
            dark: this.props.dark,
            stretch: this.props.align === alignTabs.STRETCH,
            fit: this.props.align === alignTabs.FIT,
            left: this.props.align === alignTabs.LEFT,
            right: this.props.align === alignTabs.RIGHT
        });
        var tabPanes = React.Children.map(this.props.children, function (child) {
            return child.props.id === _this.state.activeTab && (React.createElement("div", { className: "tabPane" }, child.props.children));
        });
        return (React.createElement("div", { className: cssClasses },
            React.createElement("div", { className: "tabNav" }, this.renderTabs()),
            !this.props.navOnly && (React.createElement("div", { className: "tabPanes" }, tabPanes))));
    };
    Tabs.prototype.renderTabs = function () {
        var _this = this;
        return React.Children.map(this.props.children, function (child) {
            var childProps = child.props;
            return (React.createElement(Tab, { id: childProps.id, icon: childProps.icon, active: childProps.id === _this.state.activeTab, title: childProps.title, onTabClick: function () {
                    _this.setActiveTab(childProps);
                } }));
        });
    };
    return Tabs;
}(Component));

var css_248z$x = "@keyframes fadeAnimation {\n  0% {\n    opacity: 1; }\n  50% {\n    opacity: 0.3; }\n  100% {\n    opacity: 1; } }\n\n@-o-keyframes fadeAnimation {\n  0% {\n    opacity: 1; }\n  50% {\n    opacity: 0.3; }\n  100% {\n    opacity: 1; } }\n\n@-moz-keyframes fadeAnimation {\n  0% {\n    opacity: 1; }\n  50% {\n    opacity: 0.3; }\n  100% {\n    opacity: 1; } }\n\n@-webkit-keyframes fadeAnimation {\n  0% {\n    opacity: 1; }\n  50% {\n    opacity: 0.3; }\n  100% {\n    opacity: 1; } }\n\n.acl-SkeletonLoader {\n  position: relative;\n  overflow: hidden; }\n  .acl-SkeletonLoader.fade:after {\n    position: absolute;\n    bottom: 0;\n    left: 0;\n    right: 0;\n    width: 100%;\n    content: \"\";\n    background: linear-gradient(180deg, rgba(255, 255, 255, 0.21) 0%, rgba(255, 255, 255, 0.91) 43.49%, #ffffff 100%);\n    height: 50%; }\n  .acl-SkeletonLoader.fade.dark:after {\n    background: linear-gradient(180deg, rgba(48, 48, 48, 0.21) 0%, rgba(48, 48, 48, 0.91) 43.49%, #303030 100%); }\n  .acl-SkeletonLoader .acl-SkeletonLoader--row {\n    display: flex; }\n    .acl-SkeletonLoader .acl-SkeletonLoader--row .acl-SkeletonLoader--col {\n      background: #f1f1f1;\n      height: 16px;\n      min-width: 16px; }\n      .acl-SkeletonLoader .acl-SkeletonLoader--row .acl-SkeletonLoader--col.col-small {\n        width: 16px;\n        flex: 0 0 1; }\n      .acl-SkeletonLoader .acl-SkeletonLoader--row .acl-SkeletonLoader--col.col-medium {\n        flex: 1 0 0; }\n  .acl-SkeletonLoader.animated {\n    -webkit-animation: fadeAnimation 1.3s infinite ease-in-out;\n    -moz-animation: fadeAnimation 1.3s infinite ease-in-out;\n    -o-animation: fadeAnimation 1.3s infinite ease-in-out;\n    animation: fadeAnimation 1.3s infinite ease-in-out; }\n  .acl-SkeletonLoader.dark .acl-SkeletonLoader--col {\n    background: #5E5E5E; }\n";
styleInject(css_248z$x);

var SkeletonLoader = function (_a) {
    var _b = _a.rowCount, rowCount = _b === void 0 ? 1 : _b, _c = _a.columnCount, columnCount = _c === void 0 ? 1 : _c, _d = _a.columnMargin, columnMargin = _d === void 0 ? 10 : _d, _e = _a.borderRadius, borderRadius = _e === void 0 ? 2 : _e, props = __rest(_a, ["rowCount", "columnCount", "columnMargin", "borderRadius"]);
    var loaderClasses = classNames('acl-SkeletonLoader', {
        dark: props.dark,
        animated: props.animated,
        fade: props.fadeBottom
    }, "" + (props.className || ''));
    return (React.createElement("div", { className: loaderClasses, style: __assign({}, props.style) }, __spreadArrays(Array(rowCount)).map(function (row, rowIndex) { return (React.createElement("div", { className: "acl-SkeletonLoader--row", style: { height: props.rowHeight }, key: "row_" + rowIndex },
        props.includeBullet && (React.createElement("div", { className: "acl-SkeletonLoader--col col-small", style: {
                height: props.columnHeight,
                margin: columnMargin,
                borderRadius: borderRadius
            } }, "\u00A0")),
        __spreadArrays(Array(columnCount)).map(function (col, colIndex) { return (React.createElement("div", { className: "acl-SkeletonLoader--col col-medium", style: {
                height: props.columnHeight,
                margin: columnMargin,
                borderRadius: borderRadius
            }, key: "col_" + colIndex }, "\u00A0")); }))); })));
};

var css_248z$y = ".acl-DataTable {\n  background-color: #ffffff;\n  width: 100%;\n  height: 100%;\n  overflow: hidden;\n  position: relative;\n  display: flex;\n  flex-direction: column; }\n  .acl-DataTable .acl-DataTable-container {\n    display: flex;\n    overflow-y: auto;\n    overflow-x: hidden; }\n    .acl-DataTable .acl-DataTable-container .table {\n      flex-grow: 1;\n      flex: 1 1 auto;\n      background: #ffffff;\n      height: 100%;\n      transition: all 0.3s ease-in-out;\n      overflow: auto; }\n      .acl-DataTable .acl-DataTable-container .table.acl-DataTable-stickyHeaders .thead,\n      .acl-DataTable .acl-DataTable-container .table.acl-DataTable-stickyHeaders .tfoot {\n        position: sticky;\n        z-index: 1;\n        align-self: flex-start; }\n      .acl-DataTable .acl-DataTable-container .table.acl-DataTable-stickyHeaders .thead {\n        top: 0; }\n      .acl-DataTable .acl-DataTable-container .table.acl-DataTable-stickyHeaders .tfoot {\n        bottom: 0; }\n      .acl-DataTable .acl-DataTable-container .table.acl-DataTable-stickyHeaders .tbody {\n        position: relative;\n        z-index: 0; }\n      .acl-DataTable .acl-DataTable-container .table.acl-DataTable-stickyHeaders [data-sticky-td] {\n        position: sticky; }\n      .acl-DataTable .acl-DataTable-container .table.acl-DataTable-resizeable .resizer {\n        display: block !important; }\n      .acl-DataTable .acl-DataTable-container .table .thead {\n        width: fit-content;\n        min-width: 100%; }\n        .acl-DataTable .acl-DataTable-container .table .thead .tr .th {\n          cursor: default;\n          white-space: nowrap;\n          text-overflow: ellipsis;\n          word-break: break-all;\n          background-color: #f1f1f1;\n          padding: 0px 12px 0px 12px;\n          text-align: left;\n          border-right: 1px solid rgba(0, 0, 0, 0.1);\n          border-image: linear-gradient(to bottom, transparent 13%, rgba(0, 0, 0, 0.1) 13%);\n          border-image-slice: 1;\n          color: #777777;\n          user-select: none;\n          font-weight: bold;\n          position: relative;\n          display: flex;\n          align-items: center;\n          min-height: 32px; }\n          .acl-DataTable .acl-DataTable-container .table .thead .tr .th .treeControls {\n            display: flex;\n            align-items: center; }\n            .acl-DataTable .acl-DataTable-container .table .thead .tr .th .treeControls .treeExpandAll {\n              padding: 0 8px 0 0; }\n            .acl-DataTable .acl-DataTable-container .table .thead .tr .th .treeControls .treeSelectAll .acl-FormControl {\n              padding: 0 8px 0 0; }\n          .acl-DataTable .acl-DataTable-container .table .thead .tr .th .name {\n            flex: 1 1 auto; }\n          .acl-DataTable .acl-DataTable-container .table .thead .tr .th .sortIndicator {\n            width: 16px;\n            height: 16px; }\n            .acl-DataTable .acl-DataTable-container .table .thead .tr .th .sortIndicator svg {\n              vertical-align: 0; }\n          .acl-DataTable .acl-DataTable-container .table .thead .tr .th .resizer {\n            display: none;\n            background: transparent;\n            width: 3px;\n            height: 100%;\n            position: absolute;\n            right: 0;\n            top: 0;\n            -webkit-transform: translateX(50%);\n            -ms-transform: translateX(50%);\n            transform: translateX(50%);\n            z-index: 1; }\n          .acl-DataTable .acl-DataTable-container .table .thead .tr .th.sortable:hover {\n            cursor: pointer; }\n          .acl-DataTable .acl-DataTable-container .table .thead .tr .th.disabled {\n            pointer-events: none; }\n            .acl-DataTable .acl-DataTable-container .table .thead .tr .th.disabled > span {\n              opacity: 0.5; }\n          .acl-DataTable .acl-DataTable-container .table .thead .tr .th.selectColumn {\n            padding: 0;\n            border-right: none; }\n        .acl-DataTable .acl-DataTable-container .table .thead .tr .th:last-of-type {\n          border: none; }\n        .acl-DataTable .acl-DataTable-container .table .thead .tr.summary {\n          box-shadow: inset 0 -1px 0 0 rgba(0, 0, 0, 0.1); }\n          .acl-DataTable .acl-DataTable-container .table .thead .tr.summary .th {\n            border: none;\n            color: #ffffff;\n            background-color: #777777;\n            padding-top: 8px;\n            padding-left: 12px; }\n      .acl-DataTable .acl-DataTable-container .table .tbody {\n        min-width: 100%; }\n        .acl-DataTable .acl-DataTable-container .table .tbody .tr {\n          position: relative;\n          min-height: 32px; }\n          .acl-DataTable .acl-DataTable-container .table .tbody .tr.dropTarget {\n            border-bottom: 1px solid #009ebd; }\n          .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable) {\n            border-bottom: 1px solid rgba(0, 0, 0, 0.1); }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable).dropTarget {\n              border-bottom: 1px solid #009ebd; }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable).dragging {\n              background-color: #ffffff; }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable):hover .td, .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable).hover .td {\n              background-color: #bfe7ef; }\n              .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable):hover .td .includeExcludeCell .includeExcludeButtons, .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable).hover .td .includeExcludeCell .includeExcludeButtons {\n                display: block;\n                background: linear-gradient(to right, transparent 0%, #bfe7ef 30px); }\n                .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable):hover .td .includeExcludeCell .includeExcludeButtons Button.includeButton, .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable):hover .td .includeExcludeCell .includeExcludeButtons Button.includeButton.included, .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable).hover .td .includeExcludeCell .includeExcludeButtons Button.includeButton, .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable).hover .td .includeExcludeCell .includeExcludeButtons Button.includeButton.included {\n                  visibility: visible; }\n                .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable):hover .td .includeExcludeCell .includeExcludeButtons Button.excludeButton, .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable):hover .td .includeExcludeCell .includeExcludeButtons Button.excludeButton.excluded, .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable).hover .td .includeExcludeCell .includeExcludeButtons Button.excludeButton, .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable).hover .td .includeExcludeCell .includeExcludeButtons Button.excludeButton.excluded {\n                  visibility: visible; }\n              .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable):hover .td.dragging, .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable).hover .td.dragging {\n                background-color: #ffffff; }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable):hover.dragging, .acl-DataTable .acl-DataTable-container .table .tbody .tr:not(.editable).hover.dragging {\n              background-color: #ffffff; }\n          .acl-DataTable .acl-DataTable-container .table .tbody .tr.isRowPreview .td {\n            background-color: #ffffff !important; }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr.isRowPreview .td.dragging {\n              background-color: #ffffff;\n              opacity: 0.15; }\n          .acl-DataTable .acl-DataTable-container .table .tbody .tr .td {\n            display: flex;\n            align-items: center;\n            word-break: break-all;\n            padding: 0 12px;\n            background: #ffffff; }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.isRowActionColumn {\n              position: relative;\n              align-items: stretch; }\n              .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.isRowActionColumn .cell {\n                display: flex;\n                align-items: center; }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.dragging {\n              background-color: #ffffff;\n              opacity: 0.15; }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .overlay {\n              position: absolute;\n              top: 0px;\n              left: 0px;\n              background-color: rgba(0, 0, 0, 0.6);\n              width: 100%;\n              height: 100%;\n              z-index: 2; }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.hasSubRows {\n              background-color: #f1f1f1; }\n              .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.hasSubRows .rowActionButtonsContainer {\n                background: linear-gradient(to right, transparent 0%, #bfe7ef 30px); }\n                .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.hasSubRows .rowActionButtonsContainer .rowActionButton {\n                  color: rgba(0, 0, 0, 0.7); }\n              .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.hasSubRows .includeExcludeCell .includeExcludeButtons {\n                background: linear-gradient(to right, transparent 0%, #f1f1f1 30px); }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .rowActionCell .rowActionText {\n              display: -webkit-box;\n              -webkit-line-clamp: 2;\n              -webkit-box-orient: vertical;\n              text-overflow: ellipsis;\n              overflow: hidden; }\n              .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .rowActionCell .rowActionText a {\n                color: #004f5f;\n                text-decoration: none; }\n                .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .rowActionCell .rowActionText a:hover {\n                  text-decoration: underline; }\n              .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .rowActionCell .rowActionText.childRowText {\n                margin-left: 30px; }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .treeSelectionCell {\n              display: flex;\n              align-items: center; }\n              .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .treeSelectionCell .treeExpander {\n                width: 16px;\n                min-width: 16px;\n                margin-right: 8px; }\n              .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .treeSelectionCell .acl-FormControl {\n                margin-right: 8px;\n                padding: 0; }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .includeExcludeCell {\n              position: relative;\n              display: flex;\n              align-items: center;\n              width: 100%;\n              height: 100%; }\n              .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .includeExcludeCell .includeExcludeButtons {\n                display: none;\n                position: absolute;\n                top: 0;\n                right: 0;\n                height: 100%;\n                padding: 0 0 0 20px;\n                background: linear-gradient(to right, transparent 0%, #ffffff 30px); }\n                .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .includeExcludeCell .includeExcludeButtons.included, .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .includeExcludeCell .includeExcludeButtons.excluded {\n                  display: block; }\n                .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .includeExcludeCell .includeExcludeButtons > * {\n                  position: relative;\n                  top: calc(50% - 8px);\n                  margin: 0 0 0 12px; }\n                  .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .includeExcludeCell .includeExcludeButtons > *:not(:first-child) {\n                    margin: 0 0 0 12px; }\n                .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .includeExcludeCell .includeExcludeButtons Button {\n                  visibility: hidden; }\n                  .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .includeExcludeCell .includeExcludeButtons Button.includeButton.included {\n                    visibility: visible; }\n                    .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .includeExcludeCell .includeExcludeButtons Button.includeButton.included svg {\n                      color: #52a145 !important; }\n                  .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .includeExcludeCell .includeExcludeButtons Button.excludeButton.excluded {\n                    visibility: visible; }\n                    .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .includeExcludeCell .includeExcludeButtons Button.excludeButton.excluded svg {\n                      color: #e75556 !important; }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.isEditable {\n              padding: 0; }\n              .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.isEditable > div {\n                flex: 1;\n                display: flex;\n                align-self: stretch;\n                justify-content: center;\n                box-shadow: inset -1px -1px 0px rgba(0, 0, 0, 0.1);\n                border-radius: 0px; }\n              .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.isEditable .acl-FormControl {\n                width: 100%;\n                padding: 0; }\n              .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.isEditable .acl-Input--default {\n                box-shadow: none;\n                border-radius: 0px; }\n                .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.isEditable .acl-Input--default:hover {\n                  box-shadow: inset -1px -1px rgba(0, 0, 0, 0.3), inset 1px 1px rgba(0, 0, 0, 0.3); }\n                .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.isEditable .acl-Input--default.acl-Input--disabled {\n                  opacity: 1;\n                  pointer-events: all; }\n                  .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.isEditable .acl-Input--default.acl-Input--disabled input {\n                    cursor: not-allowed; }\n                  .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.isEditable .acl-Input--default.acl-Input--disabled:not(.acl-Input--error):hover {\n                    box-shadow: inset -1px -1px 0px rgba(0, 0, 0, 0.1); }\n                .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.isEditable .acl-Input--default:focus-within {\n                  box-shadow: inset 0 0 0 2px #009ebd; }\n                .acl-DataTable .acl-DataTable-container .table .tbody .tr .td.isEditable .acl-Input--default.acl-Input--error {\n                  box-shadow: inset 0 0 0 2px #e75556; }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr .td .dragCell {\n              flex: 1;\n              display: flex;\n              align-self: stretch;\n              justify-content: center; }\n          .acl-DataTable .acl-DataTable-container .table .tbody .tr .rowActionButtonsContainer {\n            display: flex;\n            height: 100%;\n            position: absolute;\n            top: 0;\n            right: 0;\n            background: linear-gradient(to right, transparent 0%, #bfe7ef 30px);\n            padding-left: 30px;\n            align-items: center; }\n            .acl-DataTable .acl-DataTable-container .table .tbody .tr .rowActionButtonsContainer .rowActionButton {\n              color: #004f5f;\n              margin: 0 8px; }\n          .acl-DataTable .acl-DataTable-container .table .tbody .tr .overlay {\n            position: absolute;\n            top: 0px;\n            left: 0px;\n            background-color: rgba(255, 255, 255, 0.5);\n            width: 100%;\n            height: 100%;\n            z-index: 4; }\n        .acl-DataTable .acl-DataTable-container .table .tbody .acl-DataTable-CallToAction-wrapper {\n          padding: 10%; }\n          .acl-DataTable .acl-DataTable-container .table .tbody .acl-DataTable-CallToAction-wrapper .acl-CallToAction {\n            max-width: 30%; }\n    .acl-DataTable .acl-DataTable-container .acl-DataTable-filter-container {\n      box-shadow: inset 1px 0 0 0 rgba(0, 0, 0, 0.3);\n      border-top: 1px solid rgba(0, 0, 0, 0.3);\n      border-bottom: 1px solid rgba(0, 0, 0, 0.3); }\n  .acl-DataTable .footer {\n    flex: 0 0 50px;\n    position: relative;\n    display: flex;\n    padding: 0 16px;\n    font-size: 14px;\n    line-height: 20px; }\n    .acl-DataTable .footer .pagination {\n      flex: 1 1 auto;\n      display: flex;\n      align-items: center; }\n    .acl-DataTable .footer .divider {\n      height: 20px;\n      width: 1px;\n      background: rgba(0, 0, 0, 0.1);\n      display: inline-block;\n      margin: 0 16px; }\n";
styleInject(css_248z$y);

var CellType;
(function (CellType) {
    CellType[CellType["DEFAULT"] = 0] = "DEFAULT";
    CellType[CellType["LINK"] = 1] = "LINK";
    CellType[CellType["ICON"] = 2] = "ICON";
    CellType[CellType["MULTILINE_TEXT"] = 3] = "MULTILINE_TEXT";
    CellType[CellType["SWITCH"] = 4] = "SWITCH";
    CellType[CellType["ROW_ACTION"] = 5] = "ROW_ACTION";
    CellType[CellType["TREE_SELECT"] = 6] = "TREE_SELECT";
    CellType[CellType["EDIT_TEXT"] = 7] = "EDIT_TEXT";
    CellType[CellType["DRAGGABLE"] = 8] = "DRAGGABLE";
})(CellType || (CellType = {}));

var DefaultCell = function (props) {
    if (props.column.highlightMatch) {
        return (React.createElement(HighlightMatch, { match: props.tableProps.searchQuery }, props.value || null));
    }
    return props.value || null;
};

var LinkCell = function (props) {
    var handleLinkClick = function (event) {
        event.preventDefault();
        props.column.onLinkClick(props.row);
    };
    if (props.column.onLinkClick) {
        if (props.column.isActive && props.column.isActive(props.row)) {
            return (React.createElement(Link, { onClick: handleLinkClick, style: { color: 'inherit' } },
                React.createElement(HighlightMatch, { match: props.tableProps.searchQuery }, props.value || null)));
        }
        if (props.column.highlightMatch) {
            return (React.createElement(HighlightMatch, { match: props.tableProps.searchQuery }, props.value || null));
        }
        return props.value || null;
    }
    if (props.column.highlightMatch) {
        return (React.createElement(HighlightMatch, { match: props.tableProps.searchQuery }, props.value || null));
    }
    return props.value || null;
};

var css_248z$z = ".acl-Checkbox {\n  position: relative;\n  display: block; }\n  .acl-Checkbox label {\n    display: flex;\n    align-items: center;\n    user-select: none; }\n    .acl-Checkbox label:hover {\n      cursor: pointer; }\n    .acl-Checkbox label input[type='checkbox'] {\n      position: absolute;\n      top: 0;\n      left: 0;\n      z-index: -1;\n      opacity: 0; }\n    .acl-Checkbox label .acl-styled-checkbox {\n      display: inline-block;\n      position: relative;\n      transition: all 150ms;\n      border-radius: 3px;\n      line-height: 1em;\n      font-size: 16px;\n      color: #009ebd;\n      width: 1em;\n      height: 1em; }\n      .acl-Checkbox label .acl-styled-checkbox:before {\n        content: '';\n        position: absolute;\n        top: 0;\n        left: 0;\n        bottom: 0;\n        right: 0;\n        border-radius: 3px;\n        z-index: 1;\n        border: 1px solid #40b6ce;\n        opacity: 0;\n        transition: opacity 0.25s; }\n    .acl-Checkbox label .acl-checkbox-label {\n      margin-left: 8px; }\n  .acl-Checkbox.fluid label {\n    display: flex; }\n  .acl-Checkbox.disabled {\n    opacity: 0.3;\n    pointer-events: none; }\n  .acl-Checkbox.error .acl-styled-checkbox:before {\n    border-color: #e75556;\n    opacity: 1; }\n  .acl-Checkbox:focus .acl-styled-checkbox:before, .acl-Checkbox:focus-within .acl-styled-checkbox:before {\n    opacity: 1; }\n  .acl-Checkbox.dark {\n    color: rgba(255, 255, 255, 0.7); }\n    .acl-Checkbox.dark.disabled {\n      opacity: 0.5;\n      color: #ffffff; }\n";
styleInject(css_248z$z);

var Checkbox = function (_a) {
    var error = _a.error, fluid = _a.fluid, dark = _a.dark, color = _a.color, borderColor = _a.borderColor, disabled = _a.disabled, indeterminate = _a.indeterminate, label = _a.label, compact = _a.compact, style = _a.style, inputRef = _a.inputRef, table = _a.table, htmlProps = __rest(_a, ["error", "fluid", "dark", "color", "borderColor", "disabled", "indeterminate", "label", "compact", "style", "inputRef", "table"]);
    var formControlProps = { disabled: disabled, fluid: fluid, compact: compact, table: table };
    var inputProps = partitionHtmlInputProps(htmlProps).inputProps;
    var _b = useState(false), checked = _b[0], setChecked = _b[1];
    var uncontrolled = htmlProps.checked !== undefined;
    useEffect(function () {
        if (htmlProps.checked !== undefined && htmlProps.checked !== checked) {
            setChecked(htmlProps.checked);
        }
    }, [htmlProps.checked]);
    var getEmptyColor = function () {
        if (dark) {
            return ColorTheme.transparent(ColorTheme.black, 0.7);
        }
        if (error) {
            return ColorTheme.red40;
        }
        if (borderColor) {
            return borderColor;
        }
        return ColorTheme.gray70;
    };
    var getCheckIcon = function () {
        if (indeterminate) {
            return React.createElement(SvgIconCheckboxIndeterminate, null);
        }
        if (htmlProps.checked) {
            if (color) {
                return React.createElement(SvgIconCheckboxCheck, { style: { color: color } });
            }
            return React.createElement(SvgIconCheckboxCheck, null);
        }
        return React.createElement(SvgIconCheckboxNone, { style: { color: getEmptyColor() } });
    };
    var cssClasses = classNames('acl-Checkbox', htmlProps.className, {
        error: error,
        dark: dark,
        disabled: disabled,
        required: htmlProps.required
    });
    var handleChange = function (e) {
        if (disabled)
            return;
        if (uncontrolled) {
            setChecked(e.target.checked);
        }
        if (htmlProps.onChange && typeof htmlProps.onChange === 'function') {
            htmlProps.onChange(e);
        }
        if (htmlProps.onBlur && typeof htmlProps.onBlur === 'function') {
            htmlProps.onBlur();
        }
    };
    return (React.createElement(FormControl, __assign({}, formControlProps),
        React.createElement("div", { className: cssClasses, style: style },
            React.createElement("label", null,
                React.createElement("input", __assign({ type: "checkbox" }, inputProps, { ref: inputRef, onChange: handleChange, disabled: disabled, style: { padding: 0, margin: 0 }, checked: uncontrolled ? checked : htmlProps.checked })),
                React.createElement("div", { className: "acl-styled-checkbox", role: "checkbox", "aria-checked": indeterminate || htmlProps.checked }, getCheckIcon()),
                label && (React.createElement("span", { className: "acl-checkbox-label" }, label))))));
};

var css_248z$A = ".acl-FormGroup {\n  padding: 24px;\n  display: inline-block; }\n  .acl-FormGroup.fluid {\n    display: block; }\n  .acl-FormGroup > *:not(:last-child) {\n    margin-bottom: 8px; }\n  .acl-FormGroup .acl-form-group-label-container {\n    display: flex;\n    align-items: center; }\n    .acl-FormGroup .acl-form-group-label-container label {\n      flex: 1;\n      text-transform: uppercase;\n      font-weight: bold; }\n    .acl-FormGroup .acl-form-group-label-container .acl-FormGroup-labelContent {\n      display: flex;\n      justify-content: flex-end;\n      align-items: center; }\n  .acl-FormGroup .acl-form-group-helptext-container p {\n    margin: 0;\n    font-size: 12px;\n    line-height: 16px; }\n  .acl-FormGroup .acl-form-group-error-container {\n    color: #e75556; }\n    .acl-FormGroup .acl-form-group-error-container ul {\n      list-style-type: none;\n      padding: 0;\n      margin: 0; }\n      .acl-FormGroup .acl-form-group-error-container ul li {\n        margin: 0;\n        padding: 0;\n        font-size: 12px;\n        line-height: 16px; }\n";
styleInject(css_248z$A);

/*
This component will be updated as more form elements are created.
 */
var FormGroup = function (_a) {
    var errors = _a.errors, dark = _a.dark, _b = _a.fluid, fluid = _b === void 0 ? true : _b, disabled = _a.disabled, label = _a.label, helpText = _a.helpText, style = _a.style, className = _a.className, children = _a.children, renderExtraLabelContent = _a.renderExtraLabelContent, htmlProps = __rest(_a, ["errors", "dark", "fluid", "disabled", "label", "helpText", "style", "className", "children", "renderExtraLabelContent"]);
    var classes = classNames('acl-FormGroup', className, {
        error: errors && errors.length,
        fluid: fluid,
        dark: dark,
        disabled: disabled
    });
    return (React.createElement("div", __assign({ className: classes, style: style }, htmlProps),
        React.createElement("div", { className: "acl-form-group-label-container" },
            React.createElement("label", null, label),
            typeof renderExtraLabelContent === 'function' && (React.createElement("div", { className: "acl-FormGroup-labelContent" }, renderExtraLabelContent()))),
        React.createElement("div", { className: "acl-form-group-container" }, children),
        helpText && (React.createElement("div", { className: "acl-form-group-helptext-container" },
            React.createElement("p", null, helpText))),
        errors && errors.length && (React.createElement("div", { className: "acl-form-group-error-container" },
            React.createElement("ul", null, errors.map(function (err, i) { return (React.createElement("li", { key: i }, err.message)); }))))));
};

var css_248z$B = ".acl-Radio {\n  position: relative;\n  display: block; }\n  .acl-Radio label {\n    display: flex;\n    align-items: center;\n    user-select: none; }\n    .acl-Radio label:hover {\n      cursor: pointer; }\n    .acl-Radio label input[type='radio'] {\n      position: absolute;\n      top: 0;\n      left: 0;\n      z-index: -1;\n      opacity: 0; }\n  .acl-Radio.fluid label {\n    display: flex; }\n  .acl-Radio:focus .styledRadio:before, .acl-Radio:focus-within .styledRadio:before {\n    opacity: 1; }\n\n.acl-Radio--disabled {\n  opacity: 0.3;\n  pointer-events: none; }\n\n.acl-Radio--dark {\n  color: rgba(255, 255, 255, 0.7); }\n  .acl-Radio--dark.disabled {\n    opacity: 0.5;\n    color: #ffffff; }\n\n.acl-Radio-styledRadio {\n  display: inline-block;\n  position: relative;\n  transition: all 150ms;\n  overflow: hidden;\n  line-height: 1em;\n  font-size: 16px;\n  color: #009ebd;\n  width: 1em;\n  height: 1em; }\n  .acl-Radio-styledRadio:before {\n    content: '';\n    position: absolute;\n    top: 0;\n    left: 0;\n    bottom: 0;\n    right: 0;\n    border-radius: 3px;\n    z-index: 1;\n    border: 1px solid #40b6ce;\n    opacity: 0;\n    transition: opacity 0.25s; }\n\n.acl-Radio-label {\n  margin-left: 8px; }\n";
styleInject(css_248z$B);

var Radio = function (_a) {
    var error = _a.error, fluid = _a.fluid, dark = _a.dark, disabled = _a.disabled, compact = _a.compact, children = _a.children, initialValue = _a.initialValue, style = _a.style, inputRef = _a.inputRef, htmlProps = __rest(_a, ["error", "fluid", "dark", "disabled", "compact", "children", "initialValue", "style", "inputRef"]);
    var uncontrolled = initialValue !== undefined;
    var formControlProps = { disabled: disabled, fluid: fluid, compact: compact };
    var inputProps = partitionHtmlInputProps(htmlProps).inputProps;
    var _b = useState(initialValue || false), chk = _b[0], setChk = _b[1];
    var getEmptyColor = function () {
        if (dark) {
            return ColorTheme.transparent(ColorTheme.black, 0.7);
        }
        if (error) {
            return ColorTheme.red40;
        }
        return ColorTheme.gray70;
    };
    var isChecked = function () { return (uncontrolled ? chk : inputProps.checked); };
    var getRadioIcon = function () {
        return isChecked() ? (React.createElement(SvgIconRadioSelect, { style: { color: ColorTheme.amobeePrimaryColor } })) : (React.createElement(SvgIconRadioNone, { style: { color: getEmptyColor() } }));
    };
    var cssClasses = classNames('acl-Radio', htmlProps.className, {
        'acl-Radio--dark': dark,
        'acl-Radio--disabled': disabled,
        'acl-Radio--required': htmlProps.required
    });
    var handleChange = function (e) {
        if (uncontrolled) {
            setChk(e.target.checked);
        }
        if (!disabled) {
            if (htmlProps.onChange &&
                typeof htmlProps.onChange === 'function') {
                htmlProps.onChange(e);
            }
            if (htmlProps.onBlur && typeof htmlProps.onBlur === 'function') {
                htmlProps.onBlur();
            }
        }
    };
    return (React.createElement(FormControl, __assign({}, formControlProps, { className: htmlProps.className, style: style }),
        React.createElement("div", { className: cssClasses },
            React.createElement("label", null,
                React.createElement("input", __assign({ type: "radio" }, inputProps, { ref: inputRef, onChange: handleChange, disabled: disabled, style: { padding: 0, margin: 0 }, checked: isChecked() })),
                React.createElement("div", { className: "acl-Radio-styledRadio", role: "radio", "aria-checked": isChecked() }, getRadioIcon()),
                React.createElement("span", { className: "acl-Radio-label" }, children || htmlProps.value)))));
};

var RadioGroup = function (props) {
    var renderChild = function (child, i) {
        var childProps = __assign(__assign({}, child.props), { key: i, checked: props.selectedValue === child.props.value, onChange: function (e) { return props.onChange && props.onChange(e); }, dark: props.dark, style: {
                display: props.inline ? 'inline-block' : 'block'
            } });
        return React.cloneElement(child, childProps);
    };
    return React.createElement(FormControl, null, props.children.map(renderChild));
};

var css_248z$C = ".acl-Select {\n  position: relative; }\n  .acl-Select select {\n    display: none; }\n  .acl-Select .acl-Button {\n    width: 100%; }\n  .acl-Select.acl-Select.acl-Select--placeholder .acl-Button {\n    font-style: italic; }\n  .acl-Select.acl-Select .acl-Button span {\n    overflow: hidden;\n    text-overflow: ellipsis; }\n  .acl-Select.acl-Select--placeholder .acl-Button.textIcon {\n    font-style: italic; }\n    .acl-Select.acl-Select--placeholder .acl-Button.textIcon span {\n      overflow: hidden;\n      text-overflow: ellipsis; }\n  .acl-Select .acl-MenuItem--placeholder {\n    font-style: italic; }\n  .acl-Select .acl-MenuItem.selected {\n    font-weight: bold; }\n";
styleInject(css_248z$C);

var Select = function (_a) {
    var initialValue = _a.initialValue, compact = _a.compact, error = _a.error, dark = _a.dark, _b = _a.fluid, fluid = _b === void 0 ? true : _b, disabled = _a.disabled, _c = _a.placeholder, placeholder = _c === void 0 ? '' : _c, style = _a.style, className = _a.className, menuWidth = _a.menuWidth, hideWithCss = _a.hideWithCss, _d = _a.usePortal, usePortal = _d === void 0 ? true : _d, props = __rest(_a, ["initialValue", "compact", "error", "dark", "fluid", "disabled", "placeholder", "style", "className", "menuWidth", "hideWithCss", "usePortal"]);
    var ref = useRef(null);
    var menuRef = useRef(null);
    // These use useState as opposed to useRef because Popper needs a re-render when they change
    var _e = useState(null), dropRef = _e[0], setDropRef = _e[1];
    var _f = useState(null), buttonRef = _f[0], setButtonRef = _f[1];
    var CONTROLLED = props.value !== undefined;
    var _g = useState(false), tooltip = _g[0], setTooltip = _g[1];
    var _h = useState(initialValue || null), val = _h[0], setVal = _h[1];
    var _j = useState(false), open = _j[0], setOpen = _j[1];
    var formControlProps = { disabled: disabled, fluid: fluid, compact: compact, style: style, className: className };
    // Close when tab is pressed
    useHandleKeyPress('Tab', function () {
        setOpen(false);
    }, buttonRef, undefined, 'keydown');
    var popperProps = {
        placement: 'bottom-start',
        strategy: 'fixed',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 4]
                }
            }
        ]
    };
    var _k = usePopper(buttonRef, dropRef, popperProps), styles = _k.styles, attributes = _k.attributes, update = _k.update;
    if (CONTROLLED && typeof props.onChange !== 'function') {
        throw new Error('If using the component in controlled mode, an onChange handler is required.');
    }
    var handleSelectClick = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    buttonRef.focus();
                    setOpen(!open);
                    return [4 /*yield*/, update()];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    var handleChange = function (item) {
        if (CONTROLLED) {
            safeInvoke(props.onChange, [item]);
        }
        else {
            setVal(item);
            safeInvoke(props.onChange, [item]);
        }
        setOpen(false);
    };
    var handleBlur = function (e) {
        safeInvoke(props.onBlur, [e]);
    };
    var getDisplayValue = useMemo(function () { return function () {
        if (hideWithCss) {
            return open ? 'block' : 'none';
        }
        return 'block';
    }; }, [hideWithCss, open]);
    useDetectOutsideClick(ref, function () {
        setOpen(false);
    });
    useEffect(function () {
        if (buttonRef) {
            setTooltip(buttonRef.clientWidth - 20 < buttonRef.children[0].scrollWidth);
        }
    }, [window.innerWidth, val, props.value]);
    var getButtonLabel = useMemo(function () { return function () {
        if (CONTROLLED) {
            if (props.value) {
                return props.value.label || props.value.value;
            }
        }
        if (hasDisplayTextValue()) {
            return val.label || val.value;
        }
        return placeholder;
    }; }, [CONTROLLED, props.value, val, placeholder]);
    var hasDisplayTextValue = function () {
        if (!val)
            return false;
        return (!!(val.label && val.label.length) ||
            !!(val.value && val.value.length));
    };
    return (React.createElement(FormControl, __assign({}, formControlProps),
        React.createElement("div", { ref: ref, className: classNames("acl-Select", 'acl-Select-wrapper', {
                'acl-Select--compact': compact,
                'acl-Select--error': error,
                dark: dark,
                'acl-Select--disabled': disabled,
                'acl-Select--placeholder': !hasDisplayTextValue()
            }) },
            React.createElement(Tooltip, { trigger: function () { return (React.createElement(Button, { className: classNames({
                        active: open
                    }), label: getButtonLabel(), compact: compact, error: error, disabled: disabled, dark: dark, variant: ButtonVariant.UNDERLINE, format: ButtonFormat.TEXT_ICON, iconPosition: "right", icon: "ChevronDown", ref: setButtonRef, onClick: handleSelectClick, onBlur: handleBlur })); }, text: getButtonLabel(), arrow: true, disabled: !tooltip }),
            React.createElement(Portal, { disabled: !usePortal },
                React.createElement("div", __assign({}, attributes.popper, { style: __assign({ zIndex: 2000 }, styles.popper), ref: setDropRef }), (hideWithCss || (!hideWithCss && open)) && (React.createElement(BasicMenu, { ref: menuRef, width: "100%", dark: dark, style: {
                        width: menuWidth,
                        display: getDisplayValue()
                    } },
                    !props.irreversible && (React.createElement(MenuItem, { key: "nullValue", label: placeholder || 'Select', onClick: function () {
                            return handleChange({
                                label: placeholder,
                                value: null
                            });
                        } })),
                    React.Children.map(props.children, function (c, idx) {
                        return (React.createElement(MenuItem, { label: c.props.label ||
                                c.props.value, disabled: c.props.disabled, onClick: function () {
                                return handleChange(c.props);
                            }, key: idx }));
                    }))))))));
};

var Option = function (props) {
    return React.createElement(BasicMenu.Item, { label: props.label, value: props.value });
};

var css_248z$D = ".acl-Switch {\n  display: inline-block; }\n  .acl-Switch input[type='checkbox'] {\n    display: inline-block;\n    position: absolute;\n    width: 0;\n    height: 0; }\n    .acl-Switch input[type='checkbox']:checked + .acl-Switch-track {\n      box-shadow: inset 0 0 0 1px #00849e;\n      background-color: #009ebd; }\n      .acl-Switch input[type='checkbox']:checked + .acl-Switch-track:after {\n        left: calc(32px / 2);\n        box-shadow: inset 0 0 0 1px #00849e; }\n  .acl-Switch .acl-Switch-track {\n    transition: all 200ms ease-in-out;\n    display: inline-block;\n    position: relative;\n    height: 16px;\n    width: 32px;\n    border-radius: 32px;\n    cursor: pointer;\n    background-color: #e2e2e2;\n    color: transparent;\n    box-shadow: inset 0 0 0 1px #ababab; }\n    .acl-Switch .acl-Switch-track:after {\n      transition: all 200ms ease-in-out;\n      content: ' ';\n      position: absolute;\n      height: 16px;\n      width: 16px;\n      border-radius: 50%;\n      background-color: #ffffff;\n      top: 0px;\n      left: 0px;\n      right: auto;\n      box-shadow: inset 0 0 0 1px #ababab; }\n  .acl-Switch.dark .acl-Switch-track {\n    background-color: rgba(255, 255, 255, 0.1);\n    box-shadow: inset 0 0 0 1px #777777; }\n  .acl-Switch.disabled {\n    opacity: 0.3; }\n    .acl-Switch.disabled .acl-Switch-track {\n      cursor: not-allowed; }\n";
styleInject(css_248z$D);

var Switch = function (_a) {
    var _b = _a.checked, checked = _b === void 0 ? false : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.dark, dark = _d === void 0 ? false : _d, onChange = _a.onChange, onBlur = _a.onBlur;
    return (React.createElement("div", { className: classNames('acl-Switch', { disabled: disabled, dark: dark }) },
        React.createElement("label", null,
            React.createElement("input", { type: "checkbox", role: "switch", checked: checked, disabled: disabled, onBlur: function () {
                    return !disabled && typeof onBlur === 'function'
                        ? onBlur()
                        : undefined;
                }, onChange: function (e) {
                    return !disabled && typeof onChange === 'function'
                        ? onChange(e)
                        : undefined;
                } }),
            React.createElement("div", { className: "acl-Switch-track", role: "checkbox" }))));
};

var css_248z$E = ".acl-FormSection {\n  padding: 24px 0;\n  margin: 0 24px;\n  border-bottom: 1px solid rgba(0, 0, 0, 0.05); }\n  .acl-FormSection h2 {\n    margin: 0;\n    font-size: 24px;\n    line-height: 32px;\n    font-weight: bold; }\n  .acl-FormSection p {\n    margin: 16px 0 0;\n    font-size: 14px;\n    line-height: 16px;\n    color: rgba(0, 0, 0, 0.5); }\n";
styleInject(css_248z$E);

var FormSection = function (props) {
    return props.title ? (React.createElement("section", { className: "acl-FormSection " + (props.className || ''), style: __assign({}, props.style) },
        React.createElement("h2", null, props.title),
        props.description && React.createElement("p", null, props.description))) : null;
};

var Slider = function (_a) {
    var _b = _a.initialValue, initialValue = _b === void 0 ? [0] : _b, _c = _a.stepSize, stepSize = _c === void 0 ? 1 : _c, props = __rest(_a, ["initialValue", "stepSize"]);
    var CONTROLLED = props.value !== undefined;
    var _d = useState(initialValue), values = _d[0], setValues = _d[1];
    if (CONTROLLED && typeof props.onChange !== 'function') {
        throw new Error('An `onChange` function must be provided if `value` is set.');
    }
    var IS_RANGE = !!(values && values.length > 1) ||
        !!(props.value && props.value.length > 1);
    var handleChange = function (newValues) {
        safeInvoke(props.onChange, [newValues]);
    };
    var handleDrag = function (newValues) {
        if (!CONTROLLED) {
            if (IS_RANGE) {
                var one = newValues[0], two = newValues[1];
                // @ts-ignore -- already confirmed that two is defined
                if (one < two) {
                    setValues(newValues);
                }
            }
            else {
                setValues(newValues);
            }
        }
        else {
            handleChange(newValues);
        }
    };
    var _e = useRanger({
        values: CONTROLLED ? props.value : values,
        onChange: handleChange,
        onDrag: handleDrag,
        min: props.min,
        max: props.max,
        stepSize: stepSize,
        tickSize: props.tickSize,
        ticks: props.ticks
    }), getTrackProps = _e.getTrackProps, handles = _e.handles, ticks = _e.ticks, segments = _e.segments;
    var formControlProps = partitionFormControlProps(props);
    var getSegmentBg = function (index) {
        if (IS_RANGE) {
            return index === 0 || index + 1 === segments.length
                ? ColorTheme.transparent(ColorTheme.black, 0.05)
                : ColorTheme.amobee50;
        }
        return index + 1 === segments.length
            ? ColorTheme.transparent(ColorTheme.black, 0.05)
            : ColorTheme.amobee50;
    };
    return (React.createElement(FormControl, __assign({}, formControlProps),
        React.createElement("div", { className: "acl-Slider" },
            React.createElement("div", __assign({ className: "acl-Slider-track" }, getTrackProps({
                style: {
                    display: 'inline-block',
                    height: 4,
                    width: '90%',
                    margin: '0 5%'
                }
            })),
                segments.map(function (segment, idx) {
                    return (React.createElement("div", __assign({ className: "acl-Slider-segment" }, segment.getSegmentProps({
                        style: {
                            backgroundColor: getSegmentBg(idx),
                            height: '100%',
                            borderRadius: 2
                        }
                    }), { key: idx })));
                }),
                ticks.map(function (tick) {
                    return (React.createElement("div", __assign({ className: "acl-Slider-tick" }, tick.getTickProps({
                        style: {
                            top: 16,
                            fontSize: 12,
                            lineHeight: '14px'
                        }
                    })),
                        React.createElement("span", { style: {
                                position: 'absolute',
                                transform: 'translateX(-50%)',
                                color: ColorTheme.gray50
                            } }, safeInvoke(props.tickFormatter, [
                            tick.value
                        ]) || tick.value)));
                }),
                handles.map(function (range) {
                    return (React.createElement("div", __assign({ className: "acl-Slider-handle" }, range.getHandleProps({
                        style: {
                            width: 20,
                            height: 20,
                            background: ColorTheme.amobee50,
                            borderRadius: 20,
                            border: "1px solid " + ColorTheme.white
                        }
                    }))));
                })))));
};

var SwitchCell = function (cell) {
    var _a = useState(!!cell.value), checked = _a[0], setChecked = _a[1];
    var handleChange = function (e) {
        setChecked(e.target.checked);
        cell.column.onSwitchClick(cell.row, setChecked);
    };
    React.useEffect(function () {
        if (checked !== cell.value) {
            setChecked(!!cell.value);
        }
    }, [cell.value]);
    if (cell.column.onSwitchClick) {
        return (React.createElement(Switch, { checked: checked, onChange: handleChange, disabled: cell.column.isDisabled
                ? cell.column.isDisabled(cell.row)
                : false }));
    }
    // tslint:disable-next-line: no-unsafe-any
    return cell.value || null;
};

var TreeSelectCell = function (props) {
    var handleExpandClick = function () {
        var isExpanded = !!props.row.isExpanded;
        props.onToggleRowExpand(props.row, !isExpanded);
    };
    var renderExpandButton = function () {
        return (React.createElement("div", { className: "treeExpander" }, props.row.original.hasSubRows && (React.createElement(Button, __assign({}, props.row.getToggleRowExpandedProps(), { onClick: handleExpandClick, className: "hover", ultraCompact: true, icon: props.row.isExpanded
                ? 'TriangleDown'
                : 'TriangleRight', variant: ButtonVariant.BORDERLESS })))));
    };
    var renderText = function () {
        if (props.column.highlightMatch) {
            return (React.createElement(HighlightMatch, { match: props.tableProps.searchQuery }, props.value || null));
        }
        return props.value || null;
    };
    return (React.createElement("div", { className: "treeSelectionCell", style: {
            paddingLeft: props.row.depth * 24 + "px"
        } },
        renderExpandButton(),
        props.tableProps.isSelectionEnabled && (React.createElement(Checkbox, __assign({}, props.row.getToggleRowSelectedProps(), { checked: !!props.row.isSelected, indeterminate: !!props.row.isSomeSelected }))),
        renderText()));
};

var RowActionCell = function (props) {
    var _a = useState(false), hovered = _a[0], setHovered = _a[1];
    var getTooltipText = function (actionButton, actionCell) {
        if (typeof actionButton.tooltipText === 'function') {
            return actionButton.tooltipText(actionCell.row);
        }
        return actionButton.tooltipText;
    };
    var rowActionButton = function (actionButton, actionCell) {
        if (!actionButton.isHidden || !actionButton.isHidden(actionCell.row)) {
            return (React.createElement(Tooltip, { key: actionButton.iconName, onToggleTooltip: function (visible) {
                    //@ts-ignore
                    safeInvoke(props.onToggleTooltip, [visible]);
                }, trigger: function () { return (React.createElement(Button, { variant: ButtonVariant.BORDERLESS, ultraCompact: true, className: "rowActionButton", key: actionButton.iconName, title: actionButton.titleText, onClick: function () {
                        return actionButton.onClick(actionCell.row);
                    } },
                    React.createElement(Icon, { name: actionButton.iconName, size: IconSize.base }))); }, text: getTooltipText(actionButton, actionCell), disabled: !actionButton.tooltipText, arrow: true }));
        }
        return null;
    };
    var handleDropdownItemSelection = useMemo(function () { return function (selection, dropdownCell, onClickFn) {
        var selectedMenuItemProps = {
            props: selection.props,
            event: selection.event,
            row: dropdownCell.row
        };
        safeInvoke(onClickFn, [selectedMenuItemProps]);
    }; }, []);
    var rowActionDropdownButton = useMemo(function () { return function (actionButton, actionCell) {
        if (actionButton.isHidden &&
            actionButton.isHidden(actionCell.row)) {
            return null;
        }
        var tempItems = cloneDeep(actionButton.items);
        var dropdownOptions = tempItems.filter(function (item) {
            if (!item.isHidden || !item.isHidden(actionCell.row)) {
                if (item.onClick) {
                    var tempClick_1 = item.onClick;
                    item.onClick = function (clickedItem) {
                        return handleDropdownItemSelection(clickedItem, actionCell, tempClick_1);
                    };
                }
                return item;
            }
            return null;
        });
        return (React.createElement(DropdownButton, { className: "rowActionButton", key: "DropdownButton", triggerElement: React.createElement(Button, { variant: ButtonVariant.BORDERLESS, ultraCompact: true },
                React.createElement(Icon, { name: "ChevronDown" })), menuItems: dropdownOptions, closeOnItemSelect: true }));
    }; }, []);
    return (React.createElement("div", __assign({}, props.getCellProps({
        style: { display: 'flex', alignItems: 'center' }
    }), { className: 'rowActionCell', onMouseEnter: function () { return setHovered(true); }, onMouseLeave: function () { return setHovered(false); } }),
        React.createElement("div", { className: classNames('rowActionText', {
                childRowText: props.row.depth > 0
            }), title: props.value },
            React.createElement(LinkCell, __assign({}, props))),
        hovered &&
            props.column.rowActionButtons &&
            props.column.rowActionButtons.length > 0 && (React.createElement("div", { className: 'rowActionButtonsContainer' }, props.column.rowActionButtons.map(function (actionButton) {
            if (actionButton.buttonType === 'button') {
                return rowActionButton(actionButton, props);
            }
            if (actionButton.buttonType === 'dropdown') {
                return rowActionDropdownButton(actionButton, props);
            }
            return null;
        })))));
};

var IncludeExcludeCellWrapper = function (props) {
    return (React.createElement("div", { className: 'includeExcludeCell' },
        React.createElement("div", { className: "includeExcludeCellContent" }, props.children),
        React.createElement("div", { className: classNames('includeExcludeButtons', {
                included: props.row.state.included,
                excluded: props.row.state.excluded
            }) },
            React.createElement(Button, { title: "Include", className: classNames('includeButton hover', {
                    included: props.row.state.included
                }), icon: "Check", variant: ButtonVariant.BORDERLESS, ultraCompact: true, onClick: function () {
                    safeInvoke(props.onInclude, [
                        props.row,
                        !props.row.state.included
                    ]);
                } }),
            React.createElement(Button, { title: "Exclude", className: classNames('excludeButton hover', {
                    excluded: props.row.state.excluded
                }), icon: "StatusBan", variant: ButtonVariant.BORDERLESS, ultraCompact: true, onClick: function () {
                    safeInvoke(props.onExclude, [
                        props.row,
                        !props.row.state.excluded
                    ]);
                } }))));
};

var getCellClassNames = function (cellToStyle, tableProps) {
    return classNames('td', {
        selectColumn: cellToStyle.column.id === 'selectionColumn',
        isRowActionColumn: cellToStyle.column.isRowActionColumn,
        hasSubRows: cellToStyle.row.original && cellToStyle.row.original.hasSubRows,
        isEditable: tableProps.useEditableStyles,
        dragging: cellToStyle.isDragging
    });
};
var CellFactory = function (props) {
    var cell = __assign(__assign({ tableProps: props.tableProps }, props.cell), props.cellModel);
    var style = {
        display: 'flex',
        alignItems: props.cell.column.isRowActionColumn ? 'stretch' : 'center'
    };
    // Fix cell flex at 60px for selection and expand columns
    if (cell.column.id === 'selectionColumn' ||
        cell.column.id === 'expandColumn' ||
        cell.column.id === 'dragColumn') {
        style.flex = '0 0 48px';
    }
    var commonCellProps = __assign({ className: getCellClassNames(cell, props.tableProps) }, cell.getCellProps({
        style: style
    }));
    var getCell = function () {
        if (!cell.column.isRowActionColumn) {
            switch (cell.column.cellType) {
                case CellType.DEFAULT:
                    return React.createElement(DefaultCell, __assign({}, cell, props));
                case CellType.LINK:
                    return React.createElement(LinkCell, __assign({}, cell, props));
                case CellType.SWITCH:
                    return React.createElement(SwitchCell, __assign({}, cell));
                case CellType.TREE_SELECT:
                    return React.createElement(TreeSelectCell, __assign({}, cell, props));
                case CellType.ROW_ACTION:
                    return React.createElement(RowActionCell, __assign({}, cell, props));
                case CellType.EDIT_TEXT:
                    return React.createElement(TextCell, __assign({}, cell, props));
                case CellType.DRAGGABLE:
                    return React.createElement(DragCell, __assign({}, cell, props));
                default:
                    return cell.render('Cell', props);
            }
        }
        else {
            return React.createElement(RowActionCell, __assign({}, cell, props));
        }
    };
    var getWrappedCell = function () {
        if (cell.column.isRowIncludeExcludeColumn) {
            return (React.createElement(IncludeExcludeCellWrapper, __assign({}, cell), getCell()));
        }
        return getCell();
    };
    return React.createElement("div", __assign({}, commonCellProps), getWrappedCell());
};

var HeaderCell = function (props) {
    var getHeaderStyle = useMemo(function () {
        var style = { display: 'flex', cursor: 'default' };
        if (props.isSorted) {
            style.backgroundImage =
                'linear-gradient(to bottom, transparent, rgba(0,0,0,0.1)';
        }
        // Fix selection and expand columns at 60px
        if (props.id === 'selectionColumn' ||
            props.id === 'expandColumn' ||
            props.id === 'dragColumn') {
            style.flex = '0 0 48px';
        }
        return style;
    }, [props.isSorted, props.canSort]);
    var getSortIndicator = function () {
        if (!props.canSort || !props.isSorted)
            return null;
        return (React.createElement("span", { className: "sortIndicator" },
            React.createElement(Icon, { name: props.isSortedDesc ? 'ArrowDown' : 'ArrowUp' })));
    };
    var getExpandButton = function () {
        var buttonProps = __assign(__assign({}, props.getToggleAllRowsExpandedProps()), { className: 'hover', ultraCompact: true, icon: props.isExpanded ? 'TriangleDown' : 'TriangleRight', variant: ButtonVariant.BORDERLESS });
        if (typeof props.toggleAllRowsExpand === 'function') {
            return (React.createElement(Button, __assign({}, buttonProps, { onClick: function () {
                    props.toggleAllRowsExpand(!props.isExpanded);
                } })));
        }
        return React.createElement(Button, __assign({}, buttonProps));
    };
    var getTreeControls = function () {
        return (React.createElement("div", { className: "treeControls" },
            React.createElement("div", { className: "treeExpandAll", onClick: function (e) {
                    e.stopPropagation();
                } }, getExpandButton()),
            props.tableProps.isSelectionEnabled && (React.createElement("div", { className: "treeSelectAll", onClick: function (e) {
                    e.stopPropagation();
                } },
                React.createElement(Checkbox, __assign({}, props.getToggleAllRowsSelectedProps()))))));
    };
    return (React.createElement("div", __assign({ className: classNames('th', {
            selectColumn: props.id === 'selectionColumn',
            isRowActionColumn: props.isRowActionColumn,
            sortable: props.canSort,
            disabled: props.disabled
        }) }, props.getHeaderProps({
        style: getHeaderStyle
    })),
        props.cellType === CellType.TREE_SELECT && getTreeControls(),
        React.createElement("div", __assign({}, props.getSortByToggleProps(), { style: {
                display: 'flex',
                width: '100%',
                cursor: 'pointer',
                height: '100%',
                alignItems: 'center'
            } }),
            React.createElement("span", { className: "name" }, props.render('Header')),
            getSortIndicator()),
        React.createElement("div", __assign({}, props.getResizerProps(), { className: "resizer " + (props.isResizing ? 'isResizing' : '') }))));
};

/**
 * @summary Component used for editing text within a table cell.
 */
var TextCell = function (_a) {
    var initialValue = _a.value, row = _a.row, cell = _a.cell, props = __rest(_a, ["value", "row", "cell"]);
    // We need to keep and update the state of the cell normally
    var _b = React.useState(initialValue), value = _b[0], setValue = _b[1];
    var _c = React.useState(false), hasError = _c[0], setHasError = _c[1];
    var _d = React.useState(''), errorMessage = _d[0], setErrorMessage = _d[1];
    var _e = React.useState(safeInvoke(cell.column.isDisabled, [__assign({}, row)])), isDisabled = _e[0], setIsDisabled = _e[1];
    var onChange = function (e) {
        cell.tableStateUpdateRef.current = true;
        setValue(e.currentTarget.value);
    };
    // We'll only update the external data when the input is blurred
    var onBlur = function () {
        safeInvoke(cell.column.updateData, [
            {
                rowIndex: row.index,
                columnId: props.column.id,
                value: value,
                setHasError: setHasError,
                setErrorMessage: setErrorMessage,
                setIsDisabled: setIsDisabled
            }
        ]);
    };
    //If the initialValue is changed external, sync it up with our state
    React.useEffect(function () {
        setValue(initialValue);
        if (hasError) {
            if (initialValue !== value) {
                setHasError(false);
                setErrorMessage('');
            }
        }
    }, [initialValue]);
    var renderInput = function () {
        return (React.createElement(Input, { disabled: isDisabled, error: hasError, variant: InputVariant.DEFAULT, value: value, onChange: onChange, onBlur: onBlur }));
    };
    return (React.createElement(React.Fragment, null, hasError && errorMessage ? (React.createElement(Popover, { openOn: 'hover', defaultPosition: { x: 'RIGHT', y: 'CENTER' }, renderTrigger: renderInput }, errorMessage)) : (renderInput())));
};

var DragCell = function (props) {
    return (React.createElement("div", { className: "dragCell", ref: props.dragRef, onMouseDown: function () { return props.updateRowFromCellEvent(); }, onMouseUp: function () { return props.updateRowFromCellEvent(); } },
        React.createElement(Icon, { name: "Grip", style: { cursor: 'grab', alignSelf: 'center' } })));
};

var SummaryRow = function (props) {
    var getCellClassNames = function (column, summaryRowProps) {
        return classNames({
            selectColumn: column.id === 'selectionColumn',
            isRowActionColumn: column.isRowActionColumn,
            noSelect: !summaryRowProps.isSelectionEnabled
        });
    };
    var getHeaderClassNames = function (header, summaryRowProps) {
        return classNames('th', getCellClassNames(header, summaryRowProps), {
            sortable: header.canSort
        });
    };
    return (React.createElement("div", { className: "summary tr", style: { width: props.width, display: 'flex' } }, props.headers.map(function (column) {
        if (column.totalLeft === 0) {
            // TODO: add sorted styles when column is sorted
            return (React.createElement("div", __assign({}, column.getHeaderProps(), { className: getHeaderClassNames(column, props), key: column.id }),
                React.createElement("span", null, "Totals")));
        }
        if (column.getSummaryValue) {
            return (React.createElement("div", __assign({}, column.getHeaderProps(), { className: getHeaderClassNames(column, props), key: column.id }),
                React.createElement("span", null, column.getSummaryValue(props.rows))));
        }
        return (React.createElement("div", __assign({}, column.getHeaderProps(), { className: getHeaderClassNames(column, props), key: column.id })));
    })));
};

var DND_ITEM_TYPE = 'row';
var DataTableRow = function (_a) {
    var row = _a.row, index = _a.index, moveRow = _a.moveRow, rowProps = _a.rowProps, tableProps = _a.tableProps, toggleRowExpand = _a.toggleRowExpand, tableStateUpdateRef = _a.tableStateUpdateRef;
    var dropRef = React.useRef(null);
    var dragRef = React.useRef(null);
    var _b = React.useState(null), rowHoverIndex = _b[0], setRowHoverIndex = _b[1];
    var _c = React.useState(false), isDragPreview = _c[0], setIsDragPreview = _c[1];
    var isDraggingRef = React.useRef(false);
    var _d = useDrop({
        accept: DND_ITEM_TYPE,
        collect: function (monitor) { return ({
            isOver: !!monitor.isOver()
        }); },
        drop: function (item, monitor) {
            var dragIndex = item.index;
            var hoverIndex = index;
            setIsDragPreview(false);
            // Time to actually perform the action
            moveRow(dragIndex, hoverIndex);
            item.index = hoverIndex;
        }
    }), isOver = _d[0].isOver, drop = _d[1];
    var _e = useDrag({
        item: { type: DND_ITEM_TYPE, index: index },
        collect: function (monitor) {
            return {
                isDragging: monitor.isDragging()
            };
        }
    }), isDragging = _e[0].isDragging, drag = _e[1], preview = _e[2];
    var updateRowStyleOnDragStart = function () {
        setIsDragPreview(!isDragPreview);
    };
    if (isDragging !== isDraggingRef.current) {
        isDraggingRef.current = isDragging;
        setIsDragPreview(isDraggingRef.current);
    }
    preview(drop(dropRef, { originalIndex: index }));
    drag(dragRef, {});
    return isDragging ? (React.createElement(React.Fragment, { key: index },
        React.createElement("div", __assign({ ref: dropRef, className: classNames('tr', {
                hover: row.state.hover && !tableProps.useEditableStyles,
                editable: tableProps.useEditableStyles,
                dropTarget: isOver,
                dragging: isDragging,
                isRowPreview: isDragPreview
            }) }, rowProps),
            row.cells.map(function (cell, i) {
                var cellProps = __assign(__assign({}, cell), { onToggleTooltip: function (visible) {
                        row.setState({ hover: visible }); // If a tooltip is currently visible, keep the row hover toggled
                    }, onExclude: tableProps.onRowExclude, onInclude: tableProps.onRowInclude, onToggleRowExpand: toggleRowExpand, tableStateUpdateRef: tableStateUpdateRef });
                return (React.createElement(CellFactory, { dragRef: dragRef, key: rowProps.key + '_' + i, cell: cellProps, tableProps: tableProps, updateRowFromCellEvent: updateRowStyleOnDragStart }));
            }),
            React.createElement("div", { className: "overlay" })),
        row.state.loading && (React.createElement("div", { className: "tr loading-row" },
            React.createElement(SkeletonLoader, { animated: true, rowCount: 5, columnCount: 4 }))))) : (React.createElement(React.Fragment, { key: index },
        React.createElement("div", __assign({ ref: dropRef, className: classNames('tr', {
                hover: row.state.hover &&
                    !tableProps.useEditableStyles &&
                    !isDragging,
                editable: tableProps.useEditableStyles,
                dropTarget: isOver,
                dragging: isDragging,
                isRowPreview: isDragPreview
            }) }, rowProps), row.cells.map(function (cell, i) {
            var cellProps = __assign(__assign({}, cell), { onToggleTooltip: function (visible) {
                    row.setState({ hover: visible }); // If a tooltip is currently visible, keep the row hover toggled
                }, onExclude: tableProps.onRowExclude, onInclude: tableProps.onRowInclude, onToggleRowExpand: toggleRowExpand, tableStateUpdateRef: tableStateUpdateRef });
            return (React.createElement(CellFactory, { dragRef: dragRef, key: rowProps.key + '_' + i, cell: cellProps, tableProps: tableProps, updateRowFromCellEvent: updateRowStyleOnDragStart }));
        })),
        row.state.loading && (React.createElement("div", { className: "tr loading-row" },
            React.createElement(SkeletonLoader, { animated: true, rowCount: 5, columnCount: 4 })))));
};

var addRowActionButtons = function (columns) {
    var rowActionColumn = columns.find(function (column) { return column.isRowActionColumn; });
    if (rowActionColumn) {
        rowActionColumn.minWidth = 192;
        rowActionColumn.width = 300;
    }
};
var buildTableOptions = function (tableProps) {
    addRowActionButtons(tableProps.columns);
    var tableOptions = {
        data: tableProps.data,
        columns: tableProps.columns,
        disableMultiSort: true,
        disableSortBy: tableProps.disableSorting || false,
        manualSortBy: typeof tableProps.onFetchData === 'function',
        initialState: {},
        uniqueIdAccessor: tableProps.uniqueIdAccessor,
        disableSortRemove: tableProps.disableSortRemove || false,
        useEditableStyles: tableProps.useEditableStyles || false
    };
    if (tableProps.hiddenColumns && tableProps.hiddenColumns.length) {
        tableOptions.initialState.hiddenColumns = tableProps.hiddenColumns;
    }
    if (tableProps.isPaginationEnabled) {
        var pageSize = tableProps.pageSize || 20;
        var pageCount = tableProps.pageCount || tableProps.data.length / pageSize;
        tableOptions.initialState.pageIndex = 0;
        tableOptions.initialState.pageSize = pageSize;
        tableOptions.pageCount = pageCount;
        tableOptions.manualPagination = true;
    }
    if (tableProps.updateData && typeof tableProps.updateData === 'function') {
        tableOptions.updateData = tableProps.updateData;
    }
    return tableOptions;
};
var anyColumnsAreTreeSelectType = function (allColumns) {
    return allColumns.some(function (c) { return c.cellType === CellType.TREE_SELECT; });
};

var DataTable = function (props) {
    // eslint-disable-next-line
    var _a = useState(''), query = _a[0], setQuery = _a[1];
    var pagedDataRequested = useRef(false);
    var defaultTableOptions = buildTableOptions(props);
    defaultTableOptions.defaultColumn = {
        minWidth: 30,
        width: 150
    };
    // non table option defaults
    var isPaginationEnabled = props.isPaginationEnabled || false;
    useMemo(function () {
        if (props.defaultSort) {
            defaultTableOptions.initialState.sortBy = [props.defaultSort];
        }
    }, [defaultTableOptions.initialState, props.defaultSort]);
    useMemo(function () {
        defaultTableOptions.initialState.selectedRowIds = props.initialSelectedRowIds
            ? props.initialSelectedRowIds
            : {};
    }, [defaultTableOptions.initialState, props.initialSelectedRowIds]);
    useMemo(function () {
        defaultTableOptions.initialState.expanded = props.initialExpandedRowIds
            ? props.initialExpandedRowIds
            : {};
    }, [defaultTableOptions.initialState, props.initialExpandedRowIds]);
    defaultTableOptions.getRowId = React.useCallback(function (row) {
        if (Object.prototype.hasOwnProperty.call(row, props.uniqueIdAccessor)) {
            return row[props.uniqueIdAccessor];
        }
        if (row.original &&
            Object.prototype.hasOwnProperty.call(row, props.uniqueIdAccessor)) {
            return row.original[props.uniqueIdAccessor];
        }
    }, []);
    var autoResetExpandedRef = React.useRef(true);
    var autoResetSelectedRowsRef = React.useRef(true);
    defaultTableOptions.autoResetExpanded = autoResetExpandedRef.current;
    defaultTableOptions.autoResetSelectedRows =
        autoResetSelectedRowsRef.current;
    var cancellablePromise = useCancellablePromise().cancellablePromise;
    var selectionColumn = {
        id: 'selectionColumn',
        accessor: 'selectionColumn',
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        sticky: props.enableStickyColumns ? 'left' : undefined,
        disableSortBy: true,
        Header: function (_a) {
            var getToggleAllRowsSelectedProps = _a.getToggleAllRowsSelectedProps, column = _a.column;
            return (React.createElement("div", __assign({}, column.getHeaderProps({
                style: {
                    display: 'flex',
                    justifyContent: 'center',
                    flex: '0 0 60px'
                }
            })),
                React.createElement(Checkbox, __assign({}, getToggleAllRowsSelectedProps(), { fluid: false }))));
        },
        Cell: function (_a) {
            var row = _a.row;
            return (React.createElement("div", null,
                React.createElement(Checkbox, __assign({}, row.getToggleRowSelectedProps()))));
        }
    };
    var _b = useTable(defaultTableOptions, useSortBy, useFlexLayout, useExpanded, usePagination$1, useResizeColumns, useRowSelect, useRowState, useSticky, function (hooks) {
        if (props.isSelectionEnabled &&
            !anyColumnsAreTreeSelectType(defaultTableOptions.columns)) {
            hooks.visibleColumns.push(function (columns) { return __spreadArrays([
                selectionColumn
            ], columns); });
        }
        if (props.isRowExpandEnabled) {
            hooks.visibleColumns.push(function (columns) { return __spreadArrays([
                {
                    id: 'expandColumn',
                    accessor: function () { return 'expandColumn'; },
                    width: 60,
                    minWidth: 60,
                    maxWidth: 60,
                    disableSortBy: true,
                    sticky: props.enableStickyColumns ? 'left' : undefined,
                    Header: function () { return (React.createElement("span", null, isAllRowsExpanded ? (React.createElement("div", { style: { flexBasis: 60 }, onClick: function () {
                            return toggleAllRowsExpand(false);
                        } },
                        React.createElement(Icon, { name: "TriangleDown" }))) : (React.createElement("div", { style: { flexBasis: 60 }, onClick: function () {
                            return toggleAllRowsExpand(true);
                        } },
                        React.createElement(Icon, { name: "TriangleRight" }))))); },
                    Cell: function (_a) {
                        var row = _a.row;
                        return row.canExpand || row.original.hasSubRows ? (React.createElement("span", null, row.isExpanded ? (React.createElement("div", { onClick: function () {
                                return toggleRowExpand(row, false);
                            } },
                            React.createElement(Icon, { name: "TriangleDown" }))) : (React.createElement("div", { onClick: function () {
                                return toggleRowExpand(row, true);
                            } },
                            React.createElement(Icon, { name: "TriangleRight" }))))) : null;
                    }
                }
            ], columns); });
        }
        if (props.enableDraggableRows) {
            hooks.visibleColumns.push(function (columns) { return __spreadArrays([
                {
                    id: 'dragColumn',
                    cellType: CellType.DRAGGABLE,
                    sticky: 'left',
                    width: 48,
                    minWidth: 48,
                    maxWidth: 48,
                    canSort: false,
                    accessor: function () { return 'dragColumn'; }
                }
            ], columns); });
        }
    }), getTableProps = _b.getTableProps, getTableBodyProps = _b.getTableBodyProps, getToggleAllRowsSelectedProps = _b.getToggleAllRowsSelectedProps, getToggleAllRowsExpandedProps = _b.getToggleAllRowsExpandedProps, rows = _b.rows, prepareRow = _b.prepareRow, selectedFlatRows = _b.selectedFlatRows, canNextPage = _b.canNextPage, gotoPage = _b.gotoPage, nextPage = _b.nextPage, toggleAllRowsExpanded = _b.toggleAllRowsExpanded, isAllRowsExpanded = _b.isAllRowsExpanded, headerGroups = _b.headerGroups, headers = _b.headers, _c = _b.state, pageIndex = _c.pageIndex, pageSize = _c.pageSize, sortBy = _c.sortBy, selectedRowIds = _c.selectedRowIds;
    var tableStateUpdateRef = useRef(false);
    var forcedPageIndex = useRef(false);
    var isInitialLoad = useRef(true);
    var tableWrapper = useRef(null);
    var isExpandAllRowsInProgress = useRef(false);
    var isRowExpandInProgress = useRef(false);
    var moveRow = function (dragIndex, hoverIndex) {
        if (typeof props.onDrop === 'function') {
            var dataCopy = __spreadArrays(props.data);
            var tempDragRow = dataCopy[dragIndex];
            dataCopy.splice(dragIndex, 1);
            dataCopy.splice(hoverIndex, 0, tempDragRow);
            // set our state ref, the assumption is the app will update their data prop.
            // we want to avoid certain state side effects when that happens.
            tableStateUpdateRef.current = true;
            props.onDrop(dataCopy);
        }
    };
    var toggleAllRowsExpand = function (expand) {
        if (expand) {
            if (typeof props.onFetchAllSubRows === 'function') {
                isExpandAllRowsInProgress.current = true;
                props.onFetchAllSubRows(rows);
            }
        }
        else {
            toggleAllRowsExpanded(false);
            isExpandAllRowsInProgress.current = false;
        }
    };
    var fetchSubRowData = useCallback(function (row) {
        return cancellablePromise(props.fetchSubRows({
            row: row,
            fetchParams: {
                pageIndex: pageIndex,
                pageSize: pageSize,
                sortBy: sortBy
            }
        }));
    }, []);
    var toggleRowExpand = function (row, expand) { return __awaiter(void 0, void 0, void 0, function () {
        var fetchSubRows;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(expand && typeof props.fetchSubRows === 'function')) return [3 /*break*/, 2];
                    isRowExpandInProgress.current = true;
                    autoResetSelectedRowsRef.current = false;
                    fetchSubRows = function (parentRow) {
                        return fetchSubRowData(parentRow);
                    };
                    return [4 /*yield*/, fetchSubRows(row)];
                case 1:
                    _a.sent();
                    row.toggleRowExpanded(expand);
                    isRowExpandInProgress.current = false;
                    autoResetSelectedRowsRef.current = true;
                    return [3 /*break*/, 3];
                case 2:
                    row.toggleRowExpanded(expand);
                    isRowExpandInProgress.current = false;
                    _a.label = 3;
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var throttled = useRef(debounce(function (options) {
        props.onFetchData(options);
    }, 100));
    var internalNextPage = function () {
        pagedDataRequested.current = true;
        isInitialLoad.current = false; // since this is triggered by pagination we are no longer on initial load
        forcedPageIndex.current = false;
        nextPage();
    };
    var getVisibleRowCount = function () {
        var rowCount = props.totalRows || rows.length;
        rows.forEach(function (r) {
            if (r.isExpanded && r.subRows && r.subRows.length > 0) {
                rowCount = rowCount + r.subRows.length;
            }
        });
        return rowCount;
    };
    useEffect(function () {
        if (props.onRowSelectionChange) {
            props.onRowSelectionChange({
                selectedVisibleRows: selectedFlatRows,
                allSelectedRowIds: selectedRowIds
            });
        }
    }, [selectedRowIds]);
    useEffect(function () {
        // if its the first load, we dont need to fetch any data as it should have been provided
        if (isInitialLoad.current) {
            return;
        }
        if (forcedPageIndex.current) {
            forcedPageIndex.current = false;
            return;
        }
        tableStateUpdateRef.current = true;
        autoResetExpandedRef.current = false;
        autoResetSelectedRowsRef.current = false;
        if (typeof props.onFetchData === 'function') {
            throttled.current({ pageIndex: pageIndex, pageSize: pageSize, sortBy: sortBy });
        }
    }, [pageIndex, pageSize, sortBy]);
    useEffect(function () {
        pagedDataRequested.current = false;
    }, [rows]);
    //reset the page index to 0 when sorting changes
    useEffect(function () {
        gotoPage(0);
    }, [gotoPage, sortBy]);
    /* reset the page index to 0 when the table data updates due to something
    other than internal table state changes
    */
    useEffect(function () {
        if (!tableStateUpdateRef.current) {
            if (!isInitialLoad.current && pageIndex > 0) {
                forcedPageIndex.current = true;
            }
            if (!isRowExpandInProgress.current &&
                !isExpandAllRowsInProgress.current) {
                gotoPage(0);
            }
        }
    }, [props.data, gotoPage]);
    // clear our ref when the data is loaded, after we perform any side effects
    useEffect(function () {
        tableStateUpdateRef.current = false;
        isInitialLoad.current = false;
        autoResetExpandedRef.current = false;
        if (isExpandAllRowsInProgress.current) {
            toggleAllRowsExpanded(isExpandAllRowsInProgress.current);
            isExpandAllRowsInProgress.current = false;
        }
        autoResetSelectedRowsRef.current = true;
    }, [props.data]);
    useEffect(function () {
        setQuery(props.searchQuery);
    }, [props.searchQuery]);
    var setRowState = function (row) {
        var rowState = {
            included: props.includedRowIds !== undefined &&
                !!props.includedRowIds[row.id],
            excluded: props.excludedRowIds !== undefined &&
                !!props.excludedRowIds[row.id],
            loading: props.loadingRowIds !== undefined &&
                !!props.loadingRowIds[row.id]
        };
        if (row.state.included !== rowState.included ||
            row.state.excluded !== rowState.excluded ||
            row.state.loading !== rowState.loading) {
            row.setState(rowState);
        }
    };
    var prepareRowPropsFromOptions = function (row, rowOptions, rowProps) {
        if (rowOptions.customStyles && isArray(rowOptions.customStyles)) {
            rowOptions.customStyles.forEach(function (customStyle) {
                var accessor = row.original[customStyle.accessor];
                if (accessor &&
                    (!customStyle.predicate ||
                        (customStyle.predicate &&
                            customStyle.predicate(accessor)))) {
                    Object.keys(customStyle.styles).forEach(function (key) {
                        rowProps.style[key] = customStyle.styles[key];
                    });
                }
            });
        }
    };
    var renderTableBody = function () {
        if (props.loading) {
            return (React.createElement(SkeletonLoader, { includeBullet: true, fadeBottom: true, animated: true, rowCount: 10, columnCount: 5 }));
        }
        if (rows && rows.length > 0) {
            return rows.map(function (row, idx) {
                prepareRow(row);
                setRowState(row);
                var rowProps = row.getRowProps();
                if (props.rowOptions) {
                    prepareRowPropsFromOptions(row, props.rowOptions, rowProps);
                }
                return (React.createElement(DataTableRow, { index: idx, row: row, moveRow: moveRow, rowProps: rowProps, tableProps: props, toggleRowExpand: toggleRowExpand, tableStateUpdateRef: tableStateUpdateRef }));
            });
        }
        if (props.showCallToAction && props.callToActionProps) {
            return (React.createElement("div", { className: "acl-DataTable-CallToAction-wrapper" },
                React.createElement(CallToAction, __assign({ showButton: props.showCallToAction }, props.callToActionProps))));
        }
        return null;
    };
    var renderHeaderCell = function (col) {
        var treeProps = {};
        if (col.cellType === CellType.TREE_SELECT) {
            treeProps = {
                getToggleAllRowsSelectedProps: getToggleAllRowsSelectedProps,
                getToggleAllRowsExpandedProps: getToggleAllRowsExpandedProps,
                isExpanded: !!isAllRowsExpanded,
                toggleAllRowsExpand: typeof props.onFetchAllSubRows === 'function'
                    ? toggleAllRowsExpand
                    : undefined,
                tableProps: props
            };
        }
        return (React.createElement(HeaderCell, __assign({ disabled: rows.length < 1, key: col.id }, col, treeProps)));
    };
    return (React.createElement("div", { className: "acl-DataTable" },
        React.createElement("div", { className: "acl-DataTable--toolbar-area" }, props.children),
        React.createElement("div", { className: "acl-DataTable-container" },
            React.createElement("div", __assign({ className: classNames('table', {
                    'acl-DataTable-stickyHeaders': props.stickyHeader,
                    'acl-DataTable-resizeable': props.enableColumnResize
                }) }, getTableProps({
                style: {
                    display: 'flex',
                    flexDirection: 'column',
                    width: props.tableWidth,
                    overflow: rows.length < 1 && 'hidden'
                }
            }), { ref: tableWrapper }),
                React.createElement("div", { className: "thead" },
                    headerGroups.map(function (group) { return (React.createElement("div", __assign({}, group.getHeaderGroupProps(), { className: "tr" }), group.headers.map(function (col) {
                        return renderHeaderCell(col);
                    }))); }),
                    props.showSummaryRow && (React.createElement(SummaryRow, { headers: headers, rows: rows, isSelectionEnabled: props.isSelectionEnabled }))),
                React.createElement("div", __assign({ className: "tbody" }, getTableBodyProps({
                    style: {
                        flex: '1 1 auto',
                        width: rows.length > 0 && 'fit-content'
                    }
                })),
                    React.createElement(DragAndDrop, null, renderTableBody()))),
            React.createElement("div", { className: "acl-DataTable-filter-container" }, safeInvoke$1(props.renderDataFilter))),
        rows && rows.length > 0 && isPaginationEnabled && (React.createElement("div", { className: 'footer' },
            React.createElement("div", { className: 'pagination' },
                React.createElement("span", null,
                    React.createElement("strong", null,
                        "Showing 1 - ",
                        rows.length),
                    " of",
                    ' ',
                    getVisibleRowCount(),
                    " results"),
                React.createElement("span", { className: 'divider' }),
                React.createElement("span", null,
                    React.createElement(Button, { variant: ButtonVariant.BORDERLESS, ultraCompact: true, onClick: internalNextPage, disabled: !canNextPage || pagedDataRequested.current }, "Show more")))))));
};

var css_248z$F = ".acl-DataFilter {\n  position: relative;\n  align-items: center;\n  width: 0;\n  transition: width 0.3s ease; }\n  .acl-DataFilter.open {\n    width: 300px; }\n    .acl-DataFilter.open .acl-DataFilter-Panel {\n      right: 0; }\n  .acl-DataFilter .acl-DataFilter-Panel {\n    position: relative;\n    transform: none;\n    left: auto;\n    right: -300px;\n    width: 300px;\n    bottom: 0;\n    overflow: hidden;\n    transition: right 0.3s ease; }\n    .acl-DataFilter .acl-DataFilter-Panel .acl-DataFilter--filters {\n      padding: 0 12px 12px 12px;\n      width: 300px; }\n      .acl-DataFilter .acl-DataFilter-Panel .acl-DataFilter--filters .acl-Accordion {\n        box-shadow: inset 0 -1px 0 0 #ababab;\n        padding-bottom: 8px;\n        padding-top: 8px;\n        padding-left: 0;\n        padding-right: 0; }\n        .acl-DataFilter .acl-DataFilter-Panel .acl-DataFilter--filters .acl-Accordion .acl-Accordion--Content {\n          padding-left: 8px;\n          padding-right: 8px; }\n        .acl-DataFilter .acl-DataFilter-Panel .acl-DataFilter--filters .acl-Accordion .acl-Accordion--Title {\n          padding-left: 8px;\n          padding-right: 8px; }\n        .acl-DataFilter .acl-DataFilter-Panel .acl-DataFilter--filters .acl-Accordion .acl-ToggleButton {\n          margin: 4px; }\n";
styleInject(css_248z$F);

var DataFilter = function (props) {
    return (React.createElement("div", { className: classNames('acl-DataFilter', {
            open: props.open
        }) },
        React.createElement("div", { className: "acl-DataFilter-Panel" },
            React.createElement("div", { className: "acl-DataFilter--header" }, safeInvoke(props.renderPanelHeader)),
            React.createElement("div", { className: "acl-DataFilter--filters" }, props.children))));
};

var css_248z$G = ".acl-DataFilterSelect {\n  width: 300px;\n  padding: 12px;\n  border-bottom: 1px solid #e2e2e2;\n  position: relative; }\n  .acl-DataFilterSelect .acl-DataFilterSelect-Banner {\n    position: absolute;\n    opacity: 0;\n    transition: left 300ms ease, right 300ms ease, opacity 250ms ease;\n    left: calc(300px / 2);\n    right: -300px; }\n    .acl-DataFilterSelect .acl-DataFilterSelect-Banner.open {\n      opacity: 1;\n      z-index: 10;\n      right: 12px;\n      left: 12px; }\n  .acl-DataFilterSelect .acl-DataFilterSelect-Form {\n    opacity: 1;\n    transition: opacity 0.3s ease; }\n    .acl-DataFilterSelect .acl-DataFilterSelect-Form.hidden {\n      opacity: 0; }\n  .acl-DataFilterSelect .acl-FormControl {\n    width: 230px;\n    margin-right: 8px;\n    display: inline-block;\n    padding: 0; }\n    .acl-DataFilterSelect .acl-FormControl .acl-Input--underlined {\n      border-radius: 0;\n      box-shadow: inset 0 -1px 0 0 #ababab; }\n      .acl-DataFilterSelect .acl-FormControl .acl-Input--underlined:hover {\n        box-shadow: inset 0 -1px 0 0 #ababab; }\n";
styleInject(css_248z$G);

var FilterSelect = function (props) {
    var _a = useState((props.initialSelectedItem && props.initialSelectedItem.label) || ''), inputText = _a[0], setInputText = _a[1];
    var _b = useState({
        visible: false,
        successText: 'Applied filters saved',
        errorText: 'An error occurred',
        type: BannerMessageVariant.SUCCESS
    }), banner = _b[0], setBanner = _b[1];
    var _c = useState(false), savePending = _c[0], setSavePending = _c[1];
    var renderDeleteButton = function (item) {
        if (props.hideDelete ||
            !props.onDelete ||
            typeof props.onDelete !== 'function') {
            return null;
        }
        return (React.createElement(Button, { ultraCompact: true, variant: ButtonVariant.BORDERLESS, icon: "Trash", onClick: function () { return safeInvoke(props.onDelete, [item]); } }));
    };
    var getMenuItems = function () {
        return props.filterSelectItems.map(function (item) { return (__assign(__assign({}, item), { renderMenuItemControls: function () { return renderDeleteButton(item); } })); });
    };
    var handleSave = function () { return __awaiter(void 0, void 0, void 0, function () {
        var successText_1, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setSavePending(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, new Promise(function (resolve, reject) {
                            props.onSave({ resolve: resolve, reject: reject }, inputText);
                        })];
                case 2:
                    successText_1 = _a.sent();
                    setBanner(function (prevState) { return (__assign(__assign({}, prevState), { successText: successText_1, visible: true, type: BannerMessageVariant.SUCCESS })); });
                    return [3 /*break*/, 5];
                case 3:
                    err_1 = _a.sent();
                    setBanner(function (prevState) { return (__assign(__assign({}, prevState), { errorText: err_1, type: BannerMessageVariant.ERROR, visible: true })); });
                    return [3 /*break*/, 5];
                case 4:
                    setSavePending(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var handleSelection = function (selection) {
        setInputText(selection.props.label);
        safeInvoke(props.onSelect, [selection.props]);
    };
    var RightEl = function () {
        if (savePending) {
            return React.createElement(Spinner, { size: "small" });
        }
        return (React.createElement(Button, { variant: ButtonVariant.BORDERLESS, ultraCompact: true, onClick: handleSave },
            React.createElement(Icon, { name: "Save" })));
    };
    return (React.createElement("div", { className: "acl-DataFilterSelect" },
        React.createElement("div", { className: classNames('acl-DataFilterSelect-Banner', {
                open: banner.visible
            }) }, banner.visible && (React.createElement(BannerMessage, { variant: banner.type, dismissible: true, timeout: 10000, onClose: function () {
                return setBanner(function (prevState) { return (__assign(__assign({}, prevState), { visible: false })); });
            } }, banner.type === BannerMessageVariant.SUCCESS
            ? banner.successText
            : banner.errorText))),
        React.createElement("div", { className: classNames('acl-DataFilterSelect-Form', {
                hidden: banner.visible
            }) },
            React.createElement(Input, { variant: InputVariant.UNDERLINED, value: inputText, onChange: function (e) { return setInputText(e.target.value); }, rightElement: React.createElement(RightEl, null), showRightElement: "whenFilled", placeholder: "Unnamed Filter", disabled: savePending }),
            props.filterSelectItems && (React.createElement(DropdownButton, { onSelection: handleSelection, triggerElement: React.createElement(Button, { variant: ButtonVariant.STATIC_BORDERLESS, disabled: savePending },
                    React.createElement(Icon, { name: "ChevronDown" })), menuItems: useMemo(getMenuItems, [
                    props.filterSelectItems,
                    props.onDelete
                ]) })))));
};

var css_248z$H = ".acl-Toolbar {\n  display: flex;\n  width: 100%;\n  height: 52px;\n  background-color: #ffffff;\n  padding: 8px;\n  align-items: stretch; }\n  .acl-Toolbar.bulk {\n    background-color: #dff3f7;\n    color: rgba(0, 0, 0, 0.7); }\n  .acl-Toolbar acl-ngx-table-toolbar-options {\n    display: flex;\n    width: 100%;\n    padding: 0;\n    margin: 0;\n    align-items: stretch; }\n  .acl-Toolbar .acl-DataFilter {\n    display: inline-flex; }\n  .acl-Toolbar .toolbarSection {\n    display: inline-flex;\n    align-items: center;\n    padding: 0 8px; }\n    .acl-Toolbar .toolbarSection:not(:first-child) {\n      border-left: 1px solid #e2e2e2; }\n    .acl-Toolbar .toolbarSection.push-right {\n      margin-left: auto;\n      border-left: none; }\n      .acl-Toolbar .toolbarSection.push-right:not(:last-child) {\n        border-right: 1px solid #e2e2e2; }\n    .acl-Toolbar .toolbarSection span {\n      margin: auto; }\n    .acl-Toolbar .toolbarSection > *:not(:last-child) {\n      margin-right: 8px; }\n  .acl-Toolbar Button {\n    height: 32px;\n    padding: 8px; }\n    .acl-Toolbar Button.icon {\n      padding: 8px; }\n    .acl-Toolbar Button.ellipseButton {\n      vertical-align: middle; }\n  .acl-Toolbar .acl-datafilter-datepicker {\n    padding: 8px 12px; }\n  .acl-Toolbar .toolbarDivider {\n    display: inline-block;\n    width: 1px;\n    height: 32px;\n    background: #e2e2e2; }\n";
styleInject(css_248z$H);

var TableToolbar = function (props) {
    var selectedCount = props.selectedCount || 0;
    var cssClasses = classNames('acl-Toolbar', {
        bulk: selectedCount > 0
    });
    return (React.createElement("div", { className: cssClasses },
        selectedCount > 0 && (React.createElement("div", { className: 'toolbarSection' }, typeof props.onSelectedCountClick === 'function' &&
            !props.disableSelectedCountClick ? (React.createElement("strong", null,
            React.createElement(Link, { onClick: props.onSelectedCountClick, style: { color: ColorTheme.amobee70 } },
                selectedCount,
                " selected"))) : (React.createElement("strong", null,
            selectedCount,
            " selected")))),
        props.children));
};

var BulkOptions = function (_a) {
    var _b = _a.isBulkEditVisible, isBulkEditVisible = _b === void 0 ? true : _b, _c = _a.isBulkCopyVisible, isBulkCopyVisible = _c === void 0 ? true : _c, _d = _a.isBulkStartVisible, isBulkStartVisible = _d === void 0 ? true : _d, _e = _a.isBulkPauseVisible, isBulkPauseVisible = _e === void 0 ? true : _e, _f = _a.isBulkRemoveVisible, isBulkRemoveVisible = _f === void 0 ? true : _f, props = __rest(_a, ["isBulkEditVisible", "isBulkCopyVisible", "isBulkStartVisible", "isBulkPauseVisible", "isBulkRemoveVisible"]);
    if (props.hide) {
        return null;
    }
    var getToolbarButton = function (buttonIcon, clickFunction, isButtonDisabled, isButtonVisible) {
        if (isButtonDisabled === void 0) { isButtonDisabled = false; }
        if (isButtonVisible === void 0) { isButtonVisible = true; }
        if (isButtonVisible) {
            return (React.createElement(Button, { variant: ButtonVariant.STATIC_BORDERLESS, onClick: clickFunction, disabled: isButtonDisabled },
                React.createElement(Icon, { name: buttonIcon })));
        }
        return null;
    };
    var editButton = getToolbarButton('Edit', props.onBulkEditClick, props.isBulkEditDisabled, isBulkEditVisible);
    var copyButton = getToolbarButton('Copy', props.onBulkCopyClick, props.isBulkCopyDisabled, isBulkCopyVisible);
    var startButton = getToolbarButton('Play', props.onBulkStartClick, props.isBulkStartDisabled, isBulkStartVisible);
    var pauseButton = getToolbarButton('Pause', props.onBulkPauseClick, props.isBulkPauseDisabled, isBulkPauseVisible);
    var removeButton = getToolbarButton('Trash', props.onBulkRemoveClick, props.isBulkRemoveDisabled, isBulkRemoveVisible);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: 'toolbarSection' },
            editButton,
            copyButton,
            startButton,
            pauseButton,
            removeButton,
            props.moreOptions && props.moreOptions.length > 0 && (React.createElement(DropdownButton, { key: 'DropDownButton', triggerElement: React.createElement(Button, { variant: ButtonVariant.STATIC_BORDERLESS },
                    React.createElement(Icon, { name: "Overflow" })), menuItems: props.moreOptions, closeOnItemSelect: true }))),
        props.children));
};

var TableToolbarOptions = function (_a) {
    var _b = _a.isAddButtonVisible, isAddButtonVisible = _b === void 0 ? true : _b, _c = _a.isExportButtonVisible, isExportButtonVisible = _c === void 0 ? true : _c, _d = _a.isMoreOptionsVisible, isMoreOptionsVisible = _d === void 0 ? true : _d, _e = _a.isRefreshVisible, isRefreshVisible = _e === void 0 ? true : _e, props = __rest(_a, ["isAddButtonVisible", "isExportButtonVisible", "isMoreOptionsVisible", "isRefreshVisible"]);
    if (props.hide) {
        return null;
    }
    var renderAddButton = function () {
        if (!isAddButtonVisible || typeof props.onAddClick !== 'function') {
            return null;
        }
        var button = (React.createElement(Button, { variant: ButtonVariant.STATIC_BORDERLESS, onClick: function () { return props.onAddClick(); }, disabled: props.isAddButtonDisabled },
            React.createElement(Icon, { name: "Add" }),
            !!props.addButtonText && props.addButtonText));
        if (props.addButtonTooltip) {
            return (React.createElement(Tooltip, { trigger: function () { return button; }, delay: 300, text: props.addButtonTooltip }));
        }
        return button;
    };
    var exportButton = isExportButtonVisible && typeof props.onExportClick === 'function' ? (React.createElement(Button, { variant: ButtonVariant.STATIC_BORDERLESS, onClick: function () { return props.onExportClick(); }, disabled: props.isExportButtonDisabled },
        React.createElement(Icon, { name: "Download" }))) : null;
    var moreOptionsButton = isMoreOptionsVisible &&
        props.moreOptions &&
        props.moreOptions.length > 0 ? (React.createElement(DropdownButton, { key: 'DropDownButton', triggerElement: React.createElement(Button, { variant: ButtonVariant.STATIC_BORDERLESS, disabled: props.isMoreOptionsDisabled },
            React.createElement(Icon, { name: "Overflow" })), menuItems: props.moreOptions, closeOnItemSelect: true })) : null;
    var renderRefreshIcon = function () {
        if (!isRefreshVisible)
            return null;
        return (React.createElement(Button, { style: { transform: 'scaleX(-1)' }, variant: ButtonVariant.STATIC_BORDERLESS, onClick: props.onRefreshClick },
            React.createElement(Icon, { name: "Reload" })));
    };
    return (React.createElement(React.Fragment, null,
        (isAddButtonVisible ||
            isExportButtonVisible ||
            isMoreOptionsVisible) && (React.createElement("div", { className: 'toolbarSection' },
            renderAddButton(),
            exportButton,
            moreOptionsButton)),
        typeof props.onRefreshClick === 'function' && isRefreshVisible && (React.createElement("div", { className: 'toolbarSection' }, renderRefreshIcon())),
        props.children));
};

var css_248z$I = ".acl-DataFilter-Toolbar {\n  position: relative;\n  display: flex;\n  align-items: center; }\n  .acl-DataFilter-Toolbar .acl-data-filter-options {\n    display: flex;\n    align-items: center; }\n  .acl-DataFilter-Toolbar .acl-DataFilter--button {\n    padding: 0 0 0 8px; }\n    .acl-DataFilter-Toolbar .acl-DataFilter--button .acl-Button:not(.disabled):focus:not(.error) {\n      box-shadow: none; }\n  .acl-DataFilter-Toolbar.open .acl-DataFilter--button .acl-Button:not(.disabled), .acl-DataFilter-Toolbar.open .acl-DataFilter--button .acl-Button:not(:focus):not(.focus), .acl-DataFilter-Toolbar.open .acl-DataFilter--button .acl-Button:not(:active):not(.active), .acl-DataFilter-Toolbar.open .acl-DataFilter--button .acl-Button:not(.error) {\n    background-color: rgba(0, 0, 0, 0.1);\n    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.3), inset 0 4px 1px 0 rgba(0, 0, 0, 0.05);\n    transition: background-color linear 0ms; }\n";
styleInject(css_248z$I);

var FilterToolbar = function (_a) {
    var _b = _a.activeFilterCount, activeFilterCount = _b === void 0 ? 0 : _b, _c = _a.filterButtonVariant, filterButtonVariant = _c === void 0 ? ButtonVariant.STATIC_BORDERLESS : _c, props = __rest(_a, ["activeFilterCount", "filterButtonVariant"]);
    var _d = useState(props.open !== undefined ? props.open : false), open = _d[0], setOpen = _d[1];
    // Allow open state to be controlled or uncontrolled
    var isOpen = function () {
        if (props.open !== undefined)
            return props.open;
        return open;
    };
    var toggleFilter = function () {
        if (props.onFilterToggle) {
            props.onFilterToggle(!open);
        }
        setOpen(!open);
    };
    var renderTypeahead = function () {
        if (!props.renderTypeahead ||
            typeof props.renderTypeahead !== 'function') {
            return null;
        }
        var typeahead = props.renderTypeahead();
        if (!typeahead)
            return null;
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "acl-datafilter-typeahead" }, typeahead),
            React.createElement("div", { className: "toolbarDivider" })));
    };
    var renderDatepicker = function () {
        if (typeof props.renderDatepicker !== 'function') {
            return null;
        }
        var datePicker = props.renderDatepicker();
        if (!datePicker)
            return null;
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "acl-datafilter-datepicker" }, datePicker),
            React.createElement("div", { className: "toolbarDivider" })));
    };
    return (React.createElement("div", { className: classNames('acl-DataFilter-Toolbar', {
            open: isOpen()
        }) },
        React.createElement("div", { className: "acl-data-filter-options" },
            renderTypeahead(),
            renderDatepicker()),
        React.createElement("div", { className: "acl-DataFilter--button" },
            React.createElement(Button, __assign({}, props, { allowPropagation: true, onClick: toggleFilter, variant: filterButtonVariant, style: {
                    color: activeFilterCount > 0
                        ? ColorTheme.amobee40
                        : ColorTheme.buttons.neutral
                } }),
                React.createElement(Icon, { name: "Filter", color: activeFilterCount > 0
                        ? ColorTheme.amobee40
                        : ColorTheme.buttons.neutral, size: IconSize.base }),
                "(",
                activeFilterCount.toString(),
                ")"))));
};

var getFill = function (index, colors) {
    if (!colors || index + 1 > colors.length) {
        return index % 2 === 0 ? ColorTheme.amobee50 : ColorTheme.green40;
    }
    return colors[index];
};
var renderBars = function (keys, colors, stackId, dataLabel, barSize) {
    if (!keys)
        return null;
    return keys.map(function (key, index) { return (React.createElement(Bar, { key: key.dataKey, stackId: stackId, dataKey: key.dataKey, fill: key.color || getFill(index, colors), label: dataLabel, barSize: barSize, yAxisId: key.axisId })); });
};
var renderLines = function (lineDataKeys, colors, dots, dataLabel, strokeWidth) {
    if (!lineDataKeys)
        return null;
    return lineDataKeys.map(function (key, index) { return (React.createElement(Line, { key: key.dataKey, dataKey: key.dataKey, stroke: key.color || getFill(index, colors), label: dataLabel, dot: dots, strokeWidth: strokeWidth, yAxisId: key.axisId })); });
};
var renderTooltip = function (onTooltipContent) {
    return (React.createElement(Tooltip$1, { cursor: { fill: 'transparent' }, content: onTooltipContent }));
};
var renderYAxes = function (yAxis) {
    if (!yAxis)
        return null;
    return yAxis.map(function (main, index) { return (React.createElement(YAxis, { tickLine: false, axisLine: false, dataKey: main.dataKey, ticks: main.ticks, domain: main.domain, angle: main.labelRotation, tick: main.tick, width: main.width, tickFormatter: main.onTickFormatter, label: {
            value: main.title,
            angle: main.titleRotation,
            position: main.titlePosition || null,
            dy: main.titleY,
            dx: main.titleX
        }, unit: main.unit, key: main.axisId, orientation: index === 0 ? 'left' : 'right', yAxisId: main.axisId })); });
};
var renderXAxes = function (xAxis) {
    if (!xAxis)
        return null;
    return xAxis.map(function (main, index) { return (React.createElement(XAxis, { tickLine: false, axisLine: { stroke: ColorTheme.gray10 }, dataKey: main.dataKey, ticks: main.ticks, domain: main.domain, angle: main.labelRotation, tick: main.tick, width: main.width, label: {
            value: main.title,
            angle: main.titleRotation,
            position: main.titlePosition || 'insideBottom'
        }, height: 50, unit: main.unit, tickFormatter: main.onTickFormatter, key: 'x' + index })); });
};
var renderLegend = function (vertAlignLegend, horizAlignLegend, legendHeight, legendStyle) {
    return (React.createElement(Legend, { verticalAlign: vertAlignLegend, align: horizAlignLegend, height: legendHeight, iconType: "rect", wrapperStyle: legendStyle }));
};

var BarChart = function (_a) {
    var data = _a.data, xAxis = _a.xAxis, yAxis = _a.yAxis, dataLabel = _a.dataLabel, enableLegend = _a.enableLegend, legendHeight = _a.legendHeight, legendStyle = _a.legendStyle, vertAlignLegend = _a.vertAlignLegend, horizAlignLegend = _a.horizAlignLegend, stackId = _a.stackId, barDataKeys = _a.barDataKeys, colors = _a.colors, onTooltipContent = _a.onTooltipContent, barSize = _a.barSize, props = __rest(_a, ["data", "xAxis", "yAxis", "dataLabel", "enableLegend", "legendHeight", "legendStyle", "vertAlignLegend", "horizAlignLegend", "stackId", "barDataKeys", "colors", "onTooltipContent", "barSize"]);
    if (!data)
        return null;
    return (React.createElement(ResponsiveContainer, null,
        React.createElement(BarChart$1, __assign({}, props, { data: data }),
            React.createElement(CartesianGrid, { vertical: false, stroke: ColorTheme.gray10 }),
            renderXAxes(xAxis),
            renderYAxes(yAxis),
            renderTooltip(onTooltipContent),
            enableLegend &&
                renderLegend(vertAlignLegend, horizAlignLegend, legendHeight, legendStyle),
            renderBars(barDataKeys, colors, stackId, dataLabel, barSize))));
};

var css_248z$J = ".recharts-legend-item-text {\n  font-size: 12px;\n  line-height: 16px;\n  text-transform: capitalize; }\n\n.recharts-cartesian-axis .recharts-label {\n  font-size: 12px;\n  line-height: 16px; }\n";
styleInject(css_248z$J);

var LineChart = function (_a) {
    var data = _a.data, xAxis = _a.xAxis, yAxis = _a.yAxis, dataLabel = _a.dataLabel, enableLegend = _a.enableLegend, legendHeight = _a.legendHeight, legendStyle = _a.legendStyle, vertAlignLegend = _a.vertAlignLegend, horizAlignLegend = _a.horizAlignLegend, dots = _a.dots, lineDataKeys = _a.lineDataKeys, colors = _a.colors, onTooltipContent = _a.onTooltipContent, strokeWidth = _a.strokeWidth, props = __rest(_a, ["data", "xAxis", "yAxis", "dataLabel", "enableLegend", "legendHeight", "legendStyle", "vertAlignLegend", "horizAlignLegend", "dots", "lineDataKeys", "colors", "onTooltipContent", "strokeWidth"]);
    if (!data)
        return null;
    return (React.createElement(ResponsiveContainer, null,
        React.createElement(LineChart$1, __assign({}, props, { data: data }),
            React.createElement(CartesianGrid, { vertical: false, stroke: ColorTheme.gray10 }),
            renderXAxes(xAxis),
            renderYAxes(yAxis),
            renderTooltip(onTooltipContent),
            enableLegend &&
                renderLegend(vertAlignLegend, horizAlignLegend, legendHeight, legendStyle),
            renderLines(lineDataKeys, colors, dots, dataLabel, strokeWidth))));
};

var BarLineChart = function (_a) {
    var data = _a.data, xAxis = _a.xAxis, yAxis = _a.yAxis, dataLabel = _a.dataLabel, enableLegend = _a.enableLegend, legendHeight = _a.legendHeight, legendStyle = _a.legendStyle, vertAlignLegend = _a.vertAlignLegend, horizAlignLegend = _a.horizAlignLegend, lineDataKeys = _a.lineDataKeys, barDataKeys = _a.barDataKeys, colors = _a.colors, dots = _a.dots, stackId = _a.stackId, onTooltipContent = _a.onTooltipContent, barSize = _a.barSize, strokeWidth = _a.strokeWidth, props = __rest(_a, ["data", "xAxis", "yAxis", "dataLabel", "enableLegend", "legendHeight", "legendStyle", "vertAlignLegend", "horizAlignLegend", "lineDataKeys", "barDataKeys", "colors", "dots", "stackId", "onTooltipContent", "barSize", "strokeWidth"]);
    if (!data)
        return null;
    return (React.createElement(ResponsiveContainer, null,
        React.createElement(ComposedChart, __assign({}, props, { data: data }),
            React.createElement(CartesianGrid, { vertical: false, stroke: ColorTheme.gray10 }),
            renderXAxes(xAxis),
            renderYAxes(yAxis),
            renderTooltip(onTooltipContent),
            enableLegend &&
                renderLegend(vertAlignLegend, horizAlignLegend, legendHeight, legendStyle),
            renderBars(barDataKeys, colors, stackId, dataLabel, barSize),
            renderLines(lineDataKeys, colors, dots, dataLabel, strokeWidth))));
};

var css_248z$K = ".acl-BulletChart {\n  display: flex;\n  justify-content: space-between;\n  margin: 10px 0; }\n  .acl-BulletChart .acl-BulletChart--title,\n  .acl-BulletChart .acl-BulletChart--value {\n    width: 10%;\n    padding: 0 10px; }\n  .acl-BulletChart .acl-BulletChart--title {\n    width: 30%;\n    text-overflow: ellipsis;\n    white-space: nowrap;\n    overflow: hidden; }\n  .acl-BulletChart .acl-BulletChart--chart {\n    width: 60%; }\n  .acl-BulletChart .recharts-surface {\n    height: 100% !important; }\n";
styleInject(css_248z$K);

var BulletChart = function (_a) {
    var bulletTitle = _a.bulletTitle, bulletValue = _a.bulletValue, color = _a.color, bulletProps = _a.bulletProps, props = __rest(_a, ["bulletTitle", "bulletValue", "color", "bulletProps"]);
    if (!bulletTitle)
        return null;
    var renderBulletValue = function () {
        var label;
        if (bulletProps[0].onTickFormatter) {
            var bv = bulletValue || 0;
            label = bulletProps[0].onTickFormatter(bv);
        }
        else {
            label = bulletValue.toString();
        }
        return React.createElement("div", { className: "acl-BulletChart--value" }, label);
    };
    return (React.createElement("div", { className: "acl-BulletChart" },
        React.createElement("div", { className: "acl-BulletChart--title", title: bulletTitle }, bulletTitle),
        React.createElement("div", { className: "acl-BulletChart--chart" },
            React.createElement(ResponsiveContainer, null,
                React.createElement(BarChart$1, __assign({}, props, { margin: { left: 0, right: 0, top: 0, bottom: 0 }, data: [{ title: bulletTitle, value: bulletValue }], layout: "vertical" }),
                    React.createElement(CartesianGrid, { vertical: false, horizontal: false }),
                    React.createElement(XAxis, { stroke: "transparent", type: "number", domain: bulletProps[0].domain, hide: true }),
                    React.createElement(YAxis, { dataKey: "title", type: "category", tick: null, unit: bulletProps[0].unit, tickLine: false, axisLine: false, domain: bulletProps[0].domain, hide: true }),
                    React.createElement(Tooltip$1, { cursor: { fill: 'transparent' }, wrapperStyle: { display: 'none' } }),
                    React.createElement(Bar, { dataKey: "value", background: { fill: 'rgba(0,0,0,0.1)' }, barSize: 10 },
                        React.createElement(Cell, { fill: color || ColorTheme.amobee50 }))))),
        renderBulletValue()));
};

var MultiAxisChart = function (_a) {
    var data = _a.data, lines = _a.lines, bars = _a.bars, xAxis = _a.xAxis, yAxis = _a.yAxis, dataLabel = _a.dataLabel, enableLegend = _a.enableLegend, legendHeight = _a.legendHeight, legendStyle = _a.legendStyle, vertAlignLegend = _a.vertAlignLegend, horizAlignLegend = _a.horizAlignLegend, dots = _a.dots, stackId = _a.stackId, colors = _a.colors, onTooltipContent = _a.onTooltipContent, barSize = _a.barSize, strokeWidth = _a.strokeWidth, props = __rest(_a, ["data", "lines", "bars", "xAxis", "yAxis", "dataLabel", "enableLegend", "legendHeight", "legendStyle", "vertAlignLegend", "horizAlignLegend", "dots", "stackId", "colors", "onTooltipContent", "barSize", "strokeWidth"]);
    if (!data)
        return null;
    return (React.createElement(ResponsiveContainer, null,
        React.createElement(ComposedChart, __assign({}, props, { data: data }),
            React.createElement(CartesianGrid, { vertical: false, stroke: ColorTheme.gray10 }),
            renderXAxes(xAxis),
            renderYAxes(yAxis),
            renderTooltip(onTooltipContent),
            enableLegend &&
                renderLegend(vertAlignLegend, horizAlignLegend, legendHeight, legendStyle),
            renderBars(bars, colors, stackId, dataLabel, barSize),
            renderLines(lines, colors, dots, dataLabel, strokeWidth))));
};

var DistributionBarChart = function (props) {
    var getLabelValue = function (data, key, index) {
        return (typeof props.legendFormatter === 'function'
            ? props.legendFormatter(data[key])
            : data[key]) + " " + Object.keys(data)[index];
    };
    return (React.createElement("div", { className: "acl-DistributionBarChart " + (props.className ? props.className : ''), style: props.style },
        React.createElement(ResponsiveContainer, { width: "100%", height: 44 },
            React.createElement(BarChart$1, { layout: "vertical", data: [props.data], barCategoryGap: 0, barGap: 0, margin: {
                    top: 20,
                    right: (Object.keys(props.data).length - 1) * 4,
                    bottom: 0,
                    left: 0
                } },
                React.createElement(YAxis, { type: "category", hide: true }),
                React.createElement(XAxis, { type: "number", hide: true }),
                Object.keys(props.data).map(function (key, idx) {
                    return (React.createElement(Bar, { key: idx, dataKey: key, stackId: "a", isAnimationActive: false, style: {
                            transform: "translateX(" + (idx > 0 ? idx * 4 + "px" : 0) + ")"
                        }, fill: getFill(idx, props.colors) },
                        React.createElement(LabelList, { position: "top", valueAccessor: function (data) {
                                return getLabelValue(data, key, idx);
                            } })));
                })))));
};

var DatepickerFooter = function (_a) {
    var _b = _a.enabled, enabled = _b === void 0 ? true : _b, _c = _a.clearButtonText, clearButtonText = _c === void 0 ? 'Clear' : _c, props = __rest(_a, ["enabled", "clearButtonText"]);
    var style = {
        wrapper: {
            padding: '16px 0 0',
            display: 'flex'
        },
        content: {
            flex: '1 1 auto',
            marginRight: 8
        }
    };
    if (!enabled && !props.showClearButton)
        return null;
    return (React.createElement("div", { className: "acl-DateRangePicker-row acl-DateRangePickerFooter", style: style.wrapper },
        React.createElement("div", { className: "acl-DateRangePickerFooter--content", style: style.content }, typeof props.renderChildren === 'function' &&
            props.renderChildren()),
        props.showClearButton && (React.createElement(Button, { variant: ButtonVariant.NEUTRAL, onClick: function () {
                return props.onClear ? props.onClear() : undefined;
            } }, clearButtonText))));
};

var months = Info.months();
var timestampToDateTimeWithTz = function (timestamp, timeZone) {
    if (!timestamp)
        return undefined;
    return DateTime.fromSeconds(timestamp, { zone: timeZone });
};
var dateTimeForOutput = function (date, timezone) {
    if (!date)
        return undefined;
    return date.setZone(timezone, { keepLocalTime: true }).toSeconds();
};
var stateToTimestamp = function (state, timezone) { return (__assign(__assign({}, state), { range: {
        start: dateTimeForOutput(state.range.start, timezone),
        end: dateTimeForOutput(state.range.end, timezone)
    }, hoveredTo: state.hoveredTo &&
        dateTimeForOutput(DateTime.fromJSDate(state.hoveredTo), timezone) })); };
var isRangeAllDay = function (range) {
    if (!range) {
        return true;
    }
    return (range.start.toFormat('HH:mm') === '00:00' &&
        range.end.toFormat('HH:mm') === '23:59');
};
var getTzString = function (timezone) {
    return DateTime.fromObject({ zone: timezone }).offsetNameShort;
};
var setDate = function (newDate, target, 
// If no target is provided, should the time be set to the start or end of the day?
defaultTime) {
    if (defaultTime === void 0) { defaultTime = 'start'; }
    if (!target) {
        return defaultTime === 'start'
            ? DateTime.fromJSDate(newDate).startOf('day')
            : DateTime.fromJSDate(newDate).endOf('day').set({ millisecond: 0 });
    }
    return target.set({
        day: newDate.getDate(),
        month: newDate.getMonth() + 1,
        year: newDate.getFullYear()
    });
};
var setTime = function (newTime, target) {
    return target.set({
        hour: newTime.getHours(),
        minute: newTime.getMinutes(),
        // Seconds and milliseconds are set to 0 since we can't change them in the UI anyway
        second: 0,
        millisecond: 0
    });
};
var startOfLocalDay = function (timezone) {
    return DateTime.fromObject({ zone: timezone }).startOf('day');
};
var endOfLocalDay = function (timezone) {
    return DateTime.fromObject({ zone: timezone })
        .endOf('day')
        .set({ millisecond: 0 }); // Set millisecond to 0 so we don't get a decimal for the seconds epoch output
};
var isSameDate = function (d1, d2) {
    if (!d1 || !d2)
        return false;
    return (DateTime.fromJSDate(d1).toISODate() ===
        DateTime.fromJSDate(d2).toISODate());
};
var dateTimeToJSDate = function (date) {
    if (!date)
        return undefined;
    return date.toJSDate();
};
var dateFormatStrings;
(function (dateFormatStrings) {
    dateFormatStrings["DATE"] = "D";
    dateFormatStrings["TIME"] = "t";
    dateFormatStrings["DATE_TZ"] = "D ZZZZ";
    dateFormatStrings["TIME_TZ"] = "t ZZZZ";
    dateFormatStrings["DATE_TIME"] = "D, t";
    dateFormatStrings["DATE_TIME_TZ"] = "D, t ZZZZ";
})(dateFormatStrings || (dateFormatStrings = {}));

var css_248z$L = ".acl-Input--default, .acl-Input--borderless, .acl-Input--underlined, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.bp3-timepicker-input, .acl-Input--table {\n  border-radius: 4px;\n  border: none;\n  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3);\n  padding: 7px 12px;\n  color: #474747;\n  display: flex;\n  align-items: center;\n  width: 100%; }\n  .acl-Input--default input, .acl-Input--borderless input, .acl-Input--underlined input, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.bp3-timepicker-input input, .acl-Input--table input {\n    min-width: 0;\n    background-color: transparent;\n    font-size: 14px;\n    line-height: 16px;\n    border: none;\n    flex: 1 1 auto;\n    color: #474747; }\n    .acl-Input--default input::placeholder, .acl-Input--borderless input::placeholder, .acl-Input--underlined input::placeholder, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.bp3-timepicker-input input::placeholder, .acl-Input--table input::placeholder {\n      font-style: italic;\n      color: rgba(0, 0, 0, 0.3);\n      text-align: left; }\n    .acl-Input--default input:focus, .acl-Input--borderless input:focus, .acl-Input--underlined input:focus, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.bp3-timepicker-input input:focus, .acl-Input--table input:focus {\n      outline: none; }\n  .dark.acl-Input--default, .dark.acl-Input--borderless, .dark.acl-Input--underlined, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.dark.bp3-timepicker-input, .dark.acl-Input--table {\n    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.3);\n    color: rgba(255, 255, 255, 0.7); }\n    .dark.acl-Input--default input, .dark.acl-Input--borderless input, .dark.acl-Input--underlined input, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.dark.bp3-timepicker-input input, .dark.acl-Input--table input {\n      color: rgba(255, 255, 255, 0.7); }\n      .dark.acl-Input--default input::placeholder, .dark.acl-Input--borderless input::placeholder, .dark.acl-Input--underlined input::placeholder, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.dark.bp3-timepicker-input input::placeholder, .dark.acl-Input--table input::placeholder {\n        color: rgba(255, 255, 255, 0.3); }\n    .dark.acl-Input--default .acl-Input-leftElement, .dark.acl-Input--borderless .acl-Input-leftElement, .dark.acl-Input--underlined .acl-Input-leftElement, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.dark.bp3-timepicker-input .acl-Input-leftElement, .dark.acl-Input--table .acl-Input-leftElement,\n    .dark.acl-Input--default .acl-Input-rightElement,\n    .dark.acl-Input--borderless .acl-Input-rightElement,\n    .dark.acl-Input--underlined .acl-Input-rightElement,\n    .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.dark.bp3-timepicker-input .acl-Input-rightElement,\n    .dark.acl-Input--table .acl-Input-rightElement {\n      color: rgba(255, 255, 255, 0.5); }\n\n.acl-Input--compact, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.bp3-timepicker-input {\n  padding: 3px 8px; }\n\n.acl-Input--default:hover, .acl-Input--borderless:hover, .acl-Input--underlined:hover, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.bp3-timepicker-input:hover, .acl-Input--table:hover {\n  background-color: rgba(255, 255, 255, 0.3);\n  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3), inset 0 4px 2px rgba(0, 0, 0, 0.05);\n  border-radius: 4px; }\n  .dark.acl-Input--default:hover, .dark.acl-Input--borderless:hover, .dark.acl-Input--underlined:hover, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.dark.bp3-timepicker-input:hover, .dark.acl-Input--table:hover {\n    background-color: rgba(0, 0, 0, 0.1);\n    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.3), inset 0 4px 2px rgba(0, 0, 0, 0.05); }\n\n.acl-Input--default:focus-within, .acl-Input--borderless:focus-within, .acl-Input--underlined:focus-within, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.bp3-timepicker-input:focus-within, .acl-Input--table:focus-within {\n  background-color: #ffffff;\n  box-shadow: 0 0 0 1px #40b6ce, inset 0 4px 2px rgba(0, 0, 0, 0.1);\n  border-radius: 4px; }\n  .dark.acl-Input--default:focus-within, .dark.acl-Input--borderless:focus-within, .dark.acl-Input--underlined:focus-within, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.dark.bp3-timepicker-input:focus-within, .dark.acl-Input--table:focus-within {\n    background-color: rgba(0, 0, 0, 0.3);\n    box-shadow: 0 0 0 1px #40b6ce, inset 0 4px 2px rgba(0, 0, 0, 0.1); }\n\n.acl-Input--right-placeholder input[type=\"text\"]::placeholder {\n  text-align: right; }\n\n.acl-Input--right-placeholder input[type=\"text\"]:-ms-input-placeholder {\n  text-align: right; }\n\n.acl-Input--borderless {\n  box-shadow: none; }\n  .acl-Input--borderless.dark {\n    box-shadow: none; }\n\n.acl-Input--underlined, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.bp3-timepicker-input {\n  border-radius: 0;\n  box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.3); }\n  .acl-Input--underlined.dark, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.dark.bp3-timepicker-input {\n    box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.3); }\n  .acl-Input--underlined.error, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.error.bp3-timepicker-input {\n    border-radius: 4px; }\n\n.acl-Input--table {\n  border-radius: 0;\n  box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.1); }\n  .acl-Input--table:hover, .acl-Input--table:focus-within {\n    border-radius: 0; }\n  .acl-Input--table.dark {\n    box-shadow: 0 1px 0 0 rgba(255, 255, 255, 0.1); }\n  .acl-Input--table.error {\n    border-radius: 0; }\n\n.acl-Input--error,\n.acl-Input--error.dark {\n  box-shadow: 0 0 0 1px #e75556; }\n\n.acl-Input--disabled, .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.bp3-timepicker-input[disabled] {\n  opacity: 0.3;\n  pointer-events: none; }\n\n.acl-Input-wrapper {\n  position: relative;\n  overflow: hidden; }\n  .acl-Input-wrapper .acl-Input-leftElement,\n  .acl-Input-wrapper .acl-Input-rightElement {\n    color: rgba(0, 0, 0, 0.5);\n    display: flex;\n    align-items: center; }\n  .acl-Input-wrapper.dark .leftElement,\n  .acl-Input-wrapper.dark .rightElement {\n    color: rgba(255, 255, 255, 0.5); }\n\n.acl-Input-leftElement {\n  margin-right: 8px;\n  flex-wrap: wrap;\n  flex: 0 0 auto; }\n\n.acl-Input-rightElement {\n  margin-left: 8px;\n  transition: all 0.15s;\n  flex: 0 0 auto; }\n\n.acl-Timepicker {\n  display: flex;\n  align-items: center;\n  padding: 0 8px; }\n  .acl-Timepicker > .acl-Icon {\n    color: rgba(0, 0, 0, 0.5); }\n  .acl-Timepicker .acl-Timepicker--ampm {\n    position: relative; }\n    .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker {\n      display: flex; }\n      .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row {\n        display: flex;\n        align-items: center; }\n        .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.bp3-timepicker-input {\n          background: transparent;\n          width: 34px !important;\n          text-align: center;\n          font-size: 14px;\n          line-height: 16px; }\n          .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row input.bp3-timepicker-input[disabled] {\n            background: none; }\n        .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-input-row .bp3-timepicker-divider-text {\n          width: 8px;\n          text-align: center; }\n      .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-ampm-select {\n        margin-left: 8px;\n        position: relative; }\n        .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-ampm-select > .bp3-icon {\n          display: none; }\n        .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-ampm-select select {\n          font-size: 14px;\n          line-height: 16px;\n          display: block;\n          padding: 4px 32px 4px 8px;\n          width: 100%;\n          max-width: 100%;\n          box-sizing: border-box;\n          margin: 3px 0;\n          color: rgba(0, 0, 0, 0.7);\n          border: none;\n          box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.3);\n          border-radius: 0;\n          -moz-appearance: none;\n          -webkit-appearance: none;\n          appearance: none;\n          background-color: transparent;\n          position: relative;\n          z-index: 1; }\n        .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-ampm-select select::-ms-expand {\n          display: none; }\n        .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-ampm-select select:hover {\n          border-radius: 4px;\n          box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3); }\n        .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-ampm-select select:focus {\n          outline: none; }\n        .acl-Timepicker .acl-Timepicker--ampm .bp3-timepicker .bp3-timepicker-ampm-select select option {\n          font-weight: normal; }\n    .acl-Timepicker .acl-Timepicker--ampm .acl-Timepicker--arrow {\n      position: absolute;\n      right: 8px;\n      left: auto;\n      top: 4px;\n      z-index: 0;\n      color: rgba(0, 0, 0, 0.5); }\n  .acl-Timepicker.disabled .bp3-timepicker .bp3-timepicker-ampm-select {\n    opacity: 0.3; }\n    .acl-Timepicker.disabled .bp3-timepicker .bp3-timepicker-ampm-select select {\n      color: rgba(0, 0, 0, 0.7); }\n    .acl-Timepicker.disabled .bp3-timepicker .bp3-timepicker-ampm-select select:hover {\n      border-radius: 0;\n      box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.3); }\n  .acl-Timepicker.disabled .acl-Timepicker--arrow {\n    opacity: 0.3; }\n";
styleInject(css_248z$L);

var Timepicker = function (_a) {
    var _b = _a.showIcon, showIcon = _b === void 0 ? true : _b, style = _a.style, props = __rest(_a, ["showIcon", "style"]);
    return (React.createElement("div", { className: classNames('acl-Timepicker', { disabled: props.disabled }, props.className), style: style },
        showIcon && React.createElement(Icon, { name: "Time", style: { marginRight: 8 } }),
        React.createElement("span", { className: "acl-Timepicker--ampm" },
            React.createElement(TimePicker, __assign({}, props)),
            props.useAmPm && (React.createElement("span", { className: "acl-Timepicker--arrow" },
                React.createElement(Icon, { name: "ChevronDown" }))))));
};

var DatePickerReducer = function (state, action) {
    switch (action.type) {
        case 'SET_DATE':
            return __assign(__assign({}, state), { date: action.payload });
        case 'TOGGLE_ALL_DAY': {
            if (!state.date) {
                return __assign(__assign({}, state), { allDay: !state.allDay });
            }
            var time = startOfLocalDay(action.payload).toJSDate();
            return __assign(__assign({}, state), { allDay: !state.allDay, date: setTime(time, state.date) });
        }
        case 'RESET':
            return __assign(__assign({}, state), { date: null });
        default:
            return __assign({}, state);
    }
};

var css_248z$M = ".acl-DatePicker-Toolbar {\n  display: flex;\n  align-items: stretch;\n  height: 32px; }\n  .acl-DatePicker-Toolbar > .toolbar-section {\n    display: flex;\n    align-items: center;\n    justify-content: space-between;\n    flex: 1 1 auto; }\n  .acl-DatePicker-Toolbar.acl-DatePicker-Toolbar--double {\n    align-items: stretch; }\n    .acl-DatePicker-Toolbar.acl-DatePicker-Toolbar--double .acl-FormControl {\n      padding: 0; }\n    .acl-DatePicker-Toolbar.acl-DatePicker-Toolbar--double > .left,\n    .acl-DatePicker-Toolbar.acl-DatePicker-Toolbar--double > .right {\n      flex: 0 0 50%;\n      padding-right: 16px; }\n    .acl-DatePicker-Toolbar.acl-DatePicker-Toolbar--double > .left {\n      border-right: 1px solid rgba(0, 0, 0, 0.1); }\n    .acl-DatePicker-Toolbar.acl-DatePicker-Toolbar--double > .right {\n      padding-left: 16px;\n      padding-right: 0; }\n  .acl-DatePicker-Toolbar strong {\n    flex: 1 1 0;\n    text-align: center;\n    font-weight: bold;\n    font-size: 14px;\n    line-height: 16px;\n    color: rgba(0, 0, 0, 0.7); }\n";
styleInject(css_248z$M);

var getYears = function () {
    var year = new Date().getFullYear();
    return [year - 2, year - 1, year, year + 1, year + 2].map(function (y) {
        return y.toString(10);
    });
};
var DatepickerToolbar = function (props) {
    var handleMonthChange = function (month, isEndMonth) {
        var getYear = function () {
            if (!props.dayPickerRef.current)
                return undefined;
            return props.dayPickerRef.current.state.currentMonth.getFullYear();
        };
        var monthToDate = function (monthString) {
            return DateTime.fromObject({
                month: Number(monthString) + 1,
                year: getYear()
            });
        };
        var monthToSet = monthToDate(month).toJSDate();
        if (isEndMonth) {
            monthToSet = monthToDate(month).minus({ month: 1 }).toJSDate();
        }
        if (props.dayPickerRef.current) {
            props.dayPickerRef.current.showMonth(monthToSet);
        }
    };
    var handleYearChange = function (year) {
        var yearToSet = DateTime.fromJSDate(props.month)
            .set({ year: Number(year) })
            .toJSDate();
        if (props.dayPickerRef.current) {
            props.dayPickerRef.current.showMonth(yearToSet);
        }
    };
    return props.numberOfMonths === 1 ? (React.createElement("div", { className: "acl-DatePicker-Toolbar acl-DatePicker-Toolbar--single" },
        React.createElement("div", { className: "toolbar-section" },
            React.createElement(Button, { onClick: function () { return props.onPreviousClick(); }, variant: ButtonVariant.STATIC_BORDERLESS, disabled: props.disablePreviousButton, compact: true },
                React.createElement(Icon, { name: "ChevronLeft" })),
            React.createElement("strong", null, props.month.toLocaleDateString('en-us', {
                month: 'long',
                year: 'numeric'
            })),
            React.createElement(Button, { onClick: function () { return props.onNextClick(); }, variant: ButtonVariant.STATIC_BORDERLESS, disabled: props.disableNextButton, compact: true },
                React.createElement(Icon, { name: "ChevronRight" }))))) : (React.createElement("div", { className: "acl-DatePicker-Toolbar acl-DatePicker-Toolbar--double" },
        React.createElement("div", { className: "toolbar-section left" },
            React.createElement(Button, { onClick: function () { return props.onPreviousClick(); }, variant: ButtonVariant.STATIC_BORDERLESS, disabled: props.disablePreviousButton, compact: true },
                React.createElement(Icon, { name: "ChevronLeft" })),
            React.createElement(Select, { irreversible: true, menuWidth: 102, hideWithCss: true, usePortal: false, value: {
                    label: DateTime.fromJSDate(props.month).monthLong,
                    value: (props.month.getUTCMonth() + 1).toString(10)
                }, onChange: function (_a) {
                    var value = _a.value;
                    return handleMonthChange(value);
                } }, months.map(function (m, idx) { return (React.createElement(Option, { key: m, label: m, value: idx.toString(10) })); })),
            React.createElement(Select, { irreversible: true, hideWithCss: true, usePortal: false, value: { value: props.month.getFullYear().toString(10) }, onChange: function (_a) {
                    var value = _a.value;
                    return handleYearChange(value);
                } }, getYears().map(function (y) { return (React.createElement(Option, { key: y, label: y, value: y })); }))),
        React.createElement("div", { className: "toolbar-section right" },
            React.createElement(Select, { irreversible: true, menuWidth: 102, hideWithCss: true, usePortal: false, value: {
                    label: DateTime.fromJSDate(props.month).plus({
                        month: 1
                    }).monthLong,
                    value: (props.month.getMonth() + 2).toString(10)
                }, onChange: function (_a) {
                    var value = _a.value;
                    return handleMonthChange(value, true);
                } }, months.map(function (m, idx) { return (React.createElement(Option, { key: m, label: m, value: idx.toString(10) })); })),
            React.createElement(Select, { irreversible: true, hideWithCss: true, usePortal: false, value: {
                    label: DateTime.fromJSDate(props.month)
                        .plus({ month: 1 })
                        .year.toString(10),
                    value: props.month.getFullYear().toString(10)
                }, onChange: function (_a) {
                    var value = _a.value;
                    return handleYearChange(value);
                } }, getYears().map(function (y) { return (React.createElement(Option, { key: y, label: y, value: y })); })),
            React.createElement(Button, { onClick: function () { return props.onNextClick(); }, variant: ButtonVariant.STATIC_BORDERLESS, disabled: props.disableNextButton, compact: true },
                React.createElement(Icon, { name: "ChevronRight" })))));
};

var css_248z$N = ".acl-Datepicker {\n  display: inline-flex;\n  flex-direction: column;\n  background: #ffffff;\n  padding: 12px 16px; }\n  .acl-Datepicker .acl-Datepicker-row {\n    display: flex;\n    justify-content: center; }\n    .acl-Datepicker .acl-Datepicker-row:not(:last-child) {\n      border-bottom: 1px solid rgba(0, 0, 0, 0.05); }\n  .acl-Datepicker .DayPicker-wrapper {\n    padding-bottom: 16px; }\n    .acl-Datepicker .DayPicker-wrapper:focus {\n      outline: 0; }\n    .acl-Datepicker .DayPicker-wrapper .DayPicker-Months {\n      flex-wrap: nowrap; }\n      .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month {\n        margin: 0;\n        display: unset; }\n        .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Weekdays {\n          display: block;\n          margin: 0; }\n          .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Weekdays .DayPicker-WeekdaysRow {\n            display: flex;\n            justify-content: space-between; }\n            .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Weekdays .DayPicker-WeekdaysRow .DayPicker-Weekday {\n              color: rgba(0, 0, 0, 0.3);\n              font-size: 14px;\n              line-height: 16px;\n              width: 32px;\n              padding: 4px 0; }\n        .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body {\n          display: flex;\n          flex-direction: column; }\n          .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body .DayPicker-Week {\n            display: flex;\n            margin-top: 8px; }\n            .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body .DayPicker-Week .DayPicker-Day {\n              font-weight: bold;\n              font-size: 14px;\n              color: rgba(0, 0, 0, 0.7);\n              width: 32px;\n              height: 32px;\n              text-align: center;\n              border-radius: 4px;\n              outline: 0; }\n              .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body .DayPicker-Week .DayPicker-Day:hover:not(.DayPicker-Day--selected) {\n                background: #bfe7ef;\n                cursor: pointer;\n                color: #004f5f !important; }\n              .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body .DayPicker-Week .DayPicker-Day.DayPicker-Day--disabled {\n                color: rgba(0, 0, 0, 0.3); }\n                .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body .DayPicker-Week .DayPicker-Day.DayPicker-Day--disabled:hover, .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body .DayPicker-Week .DayPicker-Day.DayPicker-Day--disabled:focus {\n                  background: unset;\n                  cursor: unset;\n                  color: rgba(0, 0, 0, 0.3) !important; }\n              .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body .DayPicker-Week .DayPicker-Day.DayPicker-Day--outside {\n                pointer-events: none;\n                visibility: hidden; }\n              .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body .DayPicker-Week .DayPicker-Day.DayPicker-Day--today {\n                background: rgba(0, 0, 0, 0.05); }\n              .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body .DayPicker-Week .DayPicker-Day.DayPicker-Day--selected {\n                background: #009ebd;\n                color: #ffffff; }\n              .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body .DayPicker-Week .DayPicker-Day.DayPicker-Day--range-start {\n                background: #009ebd;\n                color: #ffffff;\n                border-radius: 4px 0 0 4px; }\n                .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body .DayPicker-Week .DayPicker-Day.DayPicker-Day--range-start.DayPicker-Day--range-end {\n                  border-radius: 4px; }\n              .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body .DayPicker-Week .DayPicker-Day.DayPicker-Day--range-end {\n                background: #009ebd;\n                color: #ffffff;\n                border-radius: 0 4px 4px 0; }\n              .acl-Datepicker .DayPicker-wrapper .DayPicker-Months .DayPicker-Month .DayPicker-Body .DayPicker-Week .DayPicker-Day.DayPicker-Day--range {\n                background: #009ebd;\n                color: #ffffff;\n                border-radius: 0; }\n  .acl-Datepicker .acl-Datepicker-timeRow {\n    padding: 8px 0;\n    border-top: 1px solid rgba(0, 0, 0, 0.05);\n    border-bottom: 1px solid rgba(0, 0, 0, 0.05);\n    align-items: center;\n    justify-content: flex-start;\n    flex-wrap: wrap; }\n    .acl-Datepicker .acl-Datepicker-timeRow .acl-FormControl {\n      padding: 0; }\n    .acl-Datepicker .acl-Datepicker-timeRow .tzString {\n      color: rgba(0, 0, 0, 0.3);\n      margin-right: 12px; }\n";
styleInject(css_248z$N);

var Datepicker = React.forwardRef(function (_a, ref) {
    var minDate = _a.minDate, maxDate = _a.maxDate, className = _a.className, onDaySelected = _a.onDaySelected, _b = _a.numberOfMonths, numberOfMonths = _b === void 0 ? 1 : _b, showFooter = _a.showFooter, showFooterClearButton = _a.showFooterClearButton, footerClearButtonText = _a.footerClearButtonText, renderFooterContent = _a.renderFooterContent, onClear = _a.onClear, showTimePicker = _a.showTimePicker, allowAllDay = _a.allowAllDay, timezone = _a.timezone, initialDate = _a.initialDate, _c = _a.initialAllDay, initialAllDay = _c === void 0 ? false : _c, disabledDayMessage = _a.disabledDayMessage, datePickerProps = __rest(_a, ["minDate", "maxDate", "className", "onDaySelected", "numberOfMonths", "showFooter", "showFooterClearButton", "footerClearButtonText", "renderFooterContent", "onClear", "showTimePicker", "allowAllDay", "timezone", "initialDate", "initialAllDay", "disabledDayMessage"]);
    var dpRef = useRef(null);
    var initialState = {
        date: initialDate &&
            DateTime.fromSeconds(initialDate, { zone: timezone }),
        allDay: initialAllDay
    };
    var _d = useReducer(DatePickerReducer, initialState), state = _d[0], dispatch = _d[1];
    var initialDateSet = useRef(false);
    if (timezone && !Info.isValidIANAZone(timezone)) {
        throw new Error('`timezone` is not a valid IANA Timezone string.');
    }
    /* * * *
// OUTPUTS
* * * */
    useEffect(function () {
        if ((initialDateSet.current && initialDate) ||
            initialDateSet.current) {
            if (typeof onDaySelected === 'function') {
                onDaySelected({
                    // Convert to desired timezone.
                    // see https://moment.github.io/luxon/docs/class/src/datetime.js~DateTime.html#instance-method-setZone
                    date: state.date &&
                        state.date
                            .setZone(timezone, { keepLocalTime: true })
                            .toSeconds(),
                    allDay: state.allDay
                });
            }
        }
        else {
            initialDateSet.current = true;
        }
    }, [state.date, state.allDay]);
    // -----
    var handleDayClick = function (day) {
        if (!ModifiersUtils.dayMatchesModifier(day, getDisabledModifiers())) {
            dispatch({
                type: 'SET_DATE',
                payload: setDate(day, state.date)
            });
        }
    };
    var handleTimeChange = function (time) {
        dispatch({
            type: 'SET_DATE',
            payload: setTime(time, state.date)
        });
    };
    var handleClear = function () {
        dispatch({ type: 'RESET' });
        if (typeof onClear === 'function')
            onClear();
    };
    var hasSelection = function () {
        return ((datePickerProps.modifiers &&
            typeof datePickerProps.modifiers.range === 'object') ||
            !!state.date);
    };
    var getDisabledModifiers = function () { return ({
        before: minDate === 'today' ? new Date() : minDate,
        after: maxDate === 'today' ? new Date() : maxDate
    }); };
    var renderDay = function (day) {
        if (ModifiersUtils.dayMatchesModifier(day, getDisabledModifiers()) &&
            disabledDayMessage) {
            return (React.createElement(Tooltip, { text: disabledDayMessage, trigger: function () { return React.createElement("div", null, day.getDate()); } }));
        }
        return React.createElement("div", null, day.getDate());
    };
    return (React.createElement("div", { className: "acl-Datepicker" },
        React.createElement("div", { className: "acl-Datepicker-row" },
            React.createElement(DayPicker, __assign({ ref: composeRefs(dpRef, ref), renderDay: renderDay }, datePickerProps, { showOutsideDays: false, enableOutsideDaysClick: false, className: "acl-Daypicker " + (className || ''), navbarElement: function (props) {
                    return createElement(DatepickerToolbar, __assign(__assign({}, props), { numberOfMonths: numberOfMonths, dayPickerRef: dpRef }));
                }, captionElement: function () { return null; }, disabledDays: getDisabledModifiers(), numberOfMonths: numberOfMonths, onDayClick: handleDayClick, modifiers: __assign(__assign({}, datePickerProps.modifiers), { today: !hasSelection() && new Date() }), selectedDays: datePickerProps.modifiers &&
                    datePickerProps.modifiers.range &&
                    typeof datePickerProps.modifiers.range === // angular wrapper hack
                        'function'
                    ? null
                    : state.date &&
                        state.date
                            .setZone('local', { keepLocalTime: true })
                            .toJSDate() }))),
        showTimePicker && (React.createElement("div", { style: { paddingTop: 8 } },
            allowAllDay && (React.createElement("div", { className: "acl-Datepicker-row acl-Datepicker-timeRow", style: { border: 0 } },
                React.createElement(Checkbox, { fluid: false, label: "All Day", checked: state.allDay, onChange: function () {
                        return dispatch({ type: 'TOGGLE_ALL_DAY' });
                    } }))),
            React.createElement("div", { className: "acl-Datepicker-row acl-Datepicker-timeRow", style: { border: 0 } },
                React.createElement(Timepicker, { style: { padding: 0 }, disabled: state.allDay || !state.date, value: state.date &&
                        state.date
                            .setZone('local', {
                            keepLocalTime: true
                        })
                            .toJSDate(), useAmPm: true, onChange: handleTimeChange }),
                React.createElement("span", { className: "tzString" }, getTzString(timezone))))),
        React.createElement(DatepickerFooter, { enabled: showFooter || false, showClearButton: showFooterClearButton, onClear: handleClear, clearButtonText: footerClearButtonText, renderChildren: typeof renderFooterContent === 'function' &&
                renderFooterContent })));
});

var DateRangePickerReducer = function (state, action) {
    switch (action.type) {
        case 'SET_RANGE':
            return __assign(__assign({}, state), { range: {
                    start: action.payload.start,
                    end: action.payload.end
                }, hoveredTo: null, selectedShortcut: action.payload.selectedShortcut || null });
        case 'UPDATE_START':
            return __assign(__assign({}, state), { range: __assign(__assign({}, state.range), { start: action.payload }) });
        case 'UPDATE_END':
            return __assign(__assign({}, state), { range: __assign(__assign({}, state.range), { end: action.payload }) });
        case 'SET_HOVER':
            return __assign(__assign({}, state), { hoveredTo: action.payload });
        case 'TOGGLE_ALL_DAY': {
            return __assign(__assign({}, state), { allDay: !state.allDay, range: {
                    start: state.range.start &&
                        setTime(startOfLocalDay().toJSDate(), state.range.start),
                    end: state.range.end &&
                        setTime(endOfLocalDay().toJSDate(), state.range.end)
                } });
        }
        case 'RESET': {
            return __assign(__assign({}, state), { range: { start: null, end: null }, hoveredTo: null });
        }
        default:
            return __assign({}, state);
    }
};

var DateRangePresets = /** @class */ (function () {
    function DateRangePresets(timezone) {
        if (timezone === void 0) { timezone = 'local'; }
        this.START_OF_TODAY = DateTime.fromObject({
            hour: 0,
            minute: 0,
            second: 0
        });
        this.END_OF_TODAY = DateTime.fromObject({
            hour: 23,
            minute: 59,
            second: 59
        });
        this.timezone = timezone;
        if (timezone &&
            timezone !== 'local' &&
            !Info.isValidIANAZone(timezone)) {
            throw new Error('Date Range Shortcut timezone is not a valid IANA timezone string.');
        }
    }
    Object.defineProperty(DateRangePresets.prototype, "START_OF_LOCAL_DAY", {
        get: function () {
            return this.START_OF_TODAY.setZone(this.timezone, {
                keepLocalTime: true
            });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePresets.prototype, "END_OF_LOCAL_DAY", {
        get: function () {
            return this.END_OF_TODAY.setZone(this.timezone, {
                keepLocalTime: true
            });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePresets.prototype, "past", {
        get: function () {
            return [
                {
                    label: 'Today',
                    dateRange: {
                        from: this.START_OF_LOCAL_DAY.toJSDate(),
                        to: DateTime.fromObject({
                            hour: 23,
                            minute: 59,
                            second: 59
                        })
                            .setZone(this.timezone, { keepLocalTime: true })
                            .toJSDate()
                    }
                },
                {
                    label: 'Yesterday',
                    dateRange: {
                        from: this.START_OF_LOCAL_DAY.minus({ days: 1 }).toJSDate(),
                        to: this.END_OF_LOCAL_DAY.minus({ days: 1 }).toJSDate()
                    }
                },
                {
                    label: 'Past week',
                    dateRange: {
                        from: this.START_OF_LOCAL_DAY.minus({ week: 1 }).toJSDate(),
                        to: this.END_OF_LOCAL_DAY.toJSDate()
                    }
                },
                {
                    label: 'Past month',
                    dateRange: {
                        from: this.START_OF_LOCAL_DAY.minus({
                            month: 1
                        }).toJSDate(),
                        to: this.END_OF_LOCAL_DAY.toJSDate()
                    }
                },
                {
                    label: 'Past year',
                    dateRange: {
                        from: this.START_OF_LOCAL_DAY.minus({ year: 1 }).toJSDate(),
                        to: this.END_OF_LOCAL_DAY.toJSDate()
                    }
                }
            ];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DateRangePresets.prototype, "future", {
        get: function () {
            return [
                {
                    label: 'Tomorrow',
                    dateRange: {
                        from: this.START_OF_LOCAL_DAY.plus({ days: 1 }).toJSDate(),
                        to: this.END_OF_LOCAL_DAY.plus({ days: 1 }).toJSDate()
                    }
                },
                {
                    label: 'Next week',
                    dateRange: {
                        from: this.START_OF_LOCAL_DAY.toJSDate(),
                        to: this.END_OF_LOCAL_DAY.plus({ weeks: 1 }).toJSDate()
                    }
                },
                {
                    label: 'Next month',
                    dateRange: {
                        from: this.START_OF_LOCAL_DAY.toJSDate(),
                        to: this.END_OF_LOCAL_DAY.plus({ months: 1 }).toJSDate()
                    }
                },
                {
                    label: 'Next year',
                    dateRange: {
                        from: this.START_OF_LOCAL_DAY.toJSDate(),
                        to: this.END_OF_LOCAL_DAY.plus({ years: 1 }).toJSDate()
                    }
                }
            ];
        },
        enumerable: true,
        configurable: true
    });
    return DateRangePresets;
}());

var DateRangeShortcuts = function (props) {
    var presets = new DateRangePresets(props.timezone);
    var getShortcuts = function () {
        if (props.shortcuts) {
            if (props.shortcuts === 'FUTURE') {
                return presets.future;
            }
            if (props.shortcuts === 'PAST') {
                return presets.past;
            }
            return props.shortcuts;
        }
        return presets.past;
    };
    var handleChange = function (tab) {
        if (props.onChange) {
            props.onChange(getShortcuts().find(function (s) { return s.label === tab.title; }));
        }
    };
    return (React.createElement("div", { className: "acl-DateRangeShortcuts" },
        React.createElement(Tabs, { navOnly: true, align: alignTabs.RIGHT, onTabChange: handleChange }, getShortcuts().map(function (s, i) { return (React.createElement(Tab, { title: s.label, key: i, id: s.label.toLowerCase().replace(' ', '-') })); }))));
};

var css_248z$O = ".acl-DateRangePicker {\n  display: inline-flex;\n  flex-direction: column;\n  background: #ffffff; }\n  .acl-DateRangePicker .acl-Datepicker.acl-DateRangePicker--is-selecting .DayPicker-Day--range {\n    color: #004f5f !important; }\n  .acl-DateRangePicker .acl-Datepicker .DayPicker-Month {\n    padding-right: 16px;\n    padding-top: 8px; }\n    .acl-DateRangePicker .acl-Datepicker .DayPicker-Month:not(:first-of-type) {\n      border-left: 1px solid rgba(0, 0, 0, 0.1);\n      padding-right: 0;\n      padding-left: 16px; }\n  .acl-DateRangePicker .acl-Datepicker .DayPicker-Day--start {\n    background: #00849e;\n    color: white;\n    border-radius: 3px; }\n  .acl-DateRangePicker .acl-DateRangePicker-row {\n    display: flex;\n    justify-content: flex-start; }\n    .acl-DateRangePicker .acl-DateRangePicker-row.acl-DateRangePicker-timeRow {\n      padding: 8px 16px;\n      border-top: 1px solid rgba(0, 0, 0, 0.05);\n      border-bottom: 1px solid rgba(0, 0, 0, 0.05);\n      align-items: center;\n      justify-content: flex-start; }\n      .acl-DateRangePicker .acl-DateRangePicker-row.acl-DateRangePicker-timeRow .acl-FormControl {\n        padding: 0; }\n      .acl-DateRangePicker .acl-DateRangePicker-row.acl-DateRangePicker-timeRow .tzString {\n        color: rgba(0, 0, 0, 0.3);\n        margin-right: 12px; }\n  .acl-DateRangePicker .acl-DateRangeShortcuts {\n    margin: 0 0 16px; }\n    .acl-DateRangePicker .acl-DateRangeShortcuts .acl-Tabs {\n      min-height: 100%; }\n";
styleInject(css_248z$O);

var DateRangePicker = function (props) {
    var initialRange = (props.initialRange &&
        Interval.fromDateTimes(timestampToDateTimeWithTz(props.initialRange.start, props.timezone), timestampToDateTimeWithTz(props.initialRange.end, props.timezone))) || { start: undefined, end: undefined };
    var getInitialAllDay = function () {
        if (props.initialAllDay !== undefined)
            return props.initialAllDay;
        if (props.initialRange) {
            return isRangeAllDay(initialRange);
        }
        return true;
    };
    var initialState = {
        range: initialRange,
        hoveredTo: null,
        allDay: getInitialAllDay(),
        selectedShortcut: null
    };
    var _a = useReducer(DateRangePickerReducer, initialState), state = _a[0], dispatch = _a[1];
    var initialDateSet = useRef(false);
    var dpRef = useRef(null);
    var shouldChangeEffectsFire = function () {
        return !!((initialDateSet.current && initialRange) ||
            initialDateSet.current);
    };
    /* * * *
    // OUTPUTS
    * * * */
    // Emit onRangeSelected when from and to change
    useEffect(function () {
        if (shouldChangeEffectsFire()) {
            var _a = state.range, start = _a.start, end = _a.end;
            if (props.onRangeSelected) {
                if (start && end) {
                    props.onRangeSelected({
                        selectedRange: {
                            start: dateTimeForOutput(start, props.timezone),
                            end: dateTimeForOutput(end, props.timezone)
                        },
                        selectedShortcut: state.selectedShortcut,
                        allDay: state.allDay
                    });
                }
                // Set the display month when shortcut is selected
                if (dpRef.current && start) {
                    dpRef.current.showMonth(end.toJSDate());
                }
            }
        }
        else {
            initialDateSet.current = true;
        }
    }, [state.range.start, state.range.end]);
    // Emit onChange any time state changes
    useEffect(function () {
        if (props.onChange) {
            props.onChange(stateToTimestamp(state, props.timezone));
        }
    }, [state]);
    // --
    var isSelectingFirstDay = function (selection) {
        var isBeforeFirstDay = !!state.range.start &&
            Number(selection) < Number(state.range.start);
        var isRangeSelected = !!state.range.start && !!state.range.end;
        return !state.range.start || isBeforeFirstDay || isRangeSelected;
    };
    var handleReset = function () {
        dispatch({ type: 'RESET' });
        if (typeof props.onClear === 'function')
            props.onClear();
    };
    var handleDayClick = function (_a) {
        var date = _a.date;
        var _b = state.range, start = _b.start, end = _b.end;
        var dateObject = new Date(date * 1000);
        // If selecting inside the existing range
        if (start &&
            end &&
            Number(date) >= Number(start) &&
            Number(date) <= Number(end)) {
            handleReset();
            dispatch({
                type: 'SET_RANGE',
                payload: {
                    start: setDate(dateObject, state.range.start),
                    end: null,
                    hoveredTo: null
                }
            });
            return;
        }
        if (isSelectingFirstDay(dateObject)) {
            dispatch({
                type: 'SET_RANGE',
                payload: {
                    start: setDate(dateObject, start),
                    end: null,
                    hoveredTo: null
                }
            });
        }
        else {
            if (Number(dateObject) < Number(end)) {
                dispatch({
                    type: 'SET_RANGE',
                    payload: {
                        start: setDate(dateObject, start),
                        end: end,
                        hoveredTo: start.toJSDate()
                    }
                });
            }
            else {
                dispatch({
                    type: 'SET_RANGE',
                    payload: {
                        start: start,
                        end: setDate(dateObject, end, 'end'),
                        hoveredTo: end && end.toJSDate()
                    }
                });
            }
        }
    };
    var handleDayMouseEnter = function (day) {
        if (!isSelectingFirstDay(day)) {
            dispatch({
                type: 'SET_HOVER',
                payload: day
            });
        }
    };
    var handleShortcutChange = function (shortcut) {
        var _a = shortcut.dateRange, from = _a.from, to = _a.to;
        var _b = state.range, start = _b.start, end = _b.end;
        var newStart = start
            ? setTime(start.toJSDate(), DateTime.fromJSDate(from))
            : DateTime.fromJSDate(from);
        var newEnd = end
            ? setTime(end.toJSDate(), DateTime.fromJSDate(to))
            : DateTime.fromJSDate(to);
        dispatch({
            type: 'SET_RANGE',
            payload: {
                start: newStart,
                end: newEnd,
                hoveredTo: to,
                selectedShortcut: shortcut
            }
        });
        if (!state.allDay) {
            dispatch({ type: 'TOGGLE_ALL_DAY' });
        }
    };
    var handleStartTimeChange = function (date) {
        dispatch({
            type: 'UPDATE_START',
            payload: setTime(date, state.range.start)
        });
    };
    var handleEndTimeChange = function (date) {
        dispatch({
            type: 'UPDATE_END',
            payload: setTime(date, state.range.end)
        });
    };
    var getRangeModifier = function (day) {
        var start = (state.range.start && state.range.start.toJSDate()) || null;
        var end = state.range.end && state.range.end.toJSDate();
        return (DateUtils.isDayInRange(day, {
            from: start,
            to: state.hoveredTo || dateTimeToJSDate(state.range.end)
        }) && // Is it in range
            !isSameDate(day, start) && // Not the start date
            !isSameDate(day, end) // Not the end date
        );
    };
    var modifiers = {
        range: function (day) { return getRangeModifier(day); },
        'range-start': function (day) {
            return isSameDate(dateTimeToJSDate(state.range.start), day);
        },
        'range-end': function (day) { return isSameDate(dateTimeToJSDate(state.range.end), day); }
    };
    return (React.createElement("div", { className: "acl-DateRangePicker " + (props.className || '') },
        React.createElement("div", { className: "acl-DateRangePicker-row" },
            React.createElement(Datepicker, { dayPickerRef: dpRef, onDaySelected: handleDayClick, onDayMouseEnter: handleDayMouseEnter, numberOfMonths: 2, pagedNavigation: true, disabledDays: {
                    before: props.minDate === 'today'
                        ? new Date()
                        : props.minDate,
                    after: props.maxDate === 'today'
                        ? new Date()
                        : props.maxDate
                }, disabledDayMessage: props.disabledDayMessage, modifiers: modifiers, minDate: props.minDate, maxDate: props.maxDate, className: state.hoveredTo
                    ? 'acl-DateRangePicker--is-selecting'
                    : undefined }),
            props.showShortcuts && (React.createElement(DateRangeShortcuts, __assign({}, props.shortcutsProps, { onChange: handleShortcutChange })))),
        props.showTimePicker && (React.createElement("div", { className: "acl-DateRangePicker-row acl-DateRangePicker-timeRow" },
            props.allowAllDay && (React.createElement(Checkbox, { label: "All Day", checked: state.allDay, onChange: function () {
                    return dispatch({ type: 'TOGGLE_ALL_DAY' });
                }, style: { marginRight: 12 } })),
            React.createElement(Timepicker, { disabled: (props.allowAllDay && state.allDay) ||
                    (!state.range.start && !state.range.end), value: state.range.start &&
                    state.range.start
                        .setZone('local', {
                        keepLocalTime: true
                    })
                        .toJSDate(), useAmPm: true, onChange: handleStartTimeChange }),
            React.createElement("span", null, "to"),
            React.createElement(Timepicker, { disabled: (props.allowAllDay && state.allDay) ||
                    (!state.range.start && !state.range.end), value: state.range.end &&
                    state.range.end
                        .setZone('local', {
                        keepLocalTime: true
                    })
                        .toJSDate(), useAmPm: true, showIcon: false, onChange: handleEndTimeChange }),
            React.createElement("span", { className: "tzString" }, getTzString(props.timezone)))),
        React.createElement(DatepickerFooter, { enabled: props.showFooter || false, showClearButton: props.showFooterClearButton, clearButtonText: props.footerClearButtonText, onClear: handleReset, renderChildren: typeof props.renderFooterContent === 'function' &&
                props.renderFooterContent })));
};

var DatepickerButton = function (_a) {
    var _b = _a.placeholderText, placeholderText = _b === void 0 ? 'Select a date' : _b, className = _a.className, dateFormatString = _a.dateFormatString, month = _a.month, _c = _a.dark, dark = _c === void 0 ? false : _c, _d = _a.error, error = _d === void 0 ? false : _d, _e = _a.disabled, disabled = _e === void 0 ? false : _e, _f = _a.compact, compact = _f === void 0 ? false : _f, datepickerProps = __rest(_a, ["placeholderText", "className", "dateFormatString", "month", "dark", "error", "disabled", "compact"]);
    var _g = useState(false), open = _g[0], setOpen = _g[1];
    var _h = useState(datepickerProps.initialDate || null), date = _h[0], setDate = _h[1];
    var _j = useState(datepickerProps.initialAllDay || false), allDay = _j[0], setAllDay = _j[1];
    var _k = useState(null), dateString = _k[0], setDateString = _k[1];
    var _l = useState(null), dpRef = _l[0], setDpRef = _l[1];
    var _m = useState(null), buttonRef = _m[0], setButtonRef = _m[1];
    var divRef = useRef(null);
    var popperProps = {
        placement: 'bottom-start',
        strategy: 'fixed'
    };
    var _o = usePopper(buttonRef, dpRef, popperProps), styles = _o.styles, attributes = _o.attributes, update = _o.update;
    useDetectOutsideClick([divRef, { current: dpRef }], function () {
        setOpen(false);
    });
    useEffect(function () {
        if (date === null)
            return undefined;
        // tslint:disable-next-line: no-use-before-declare
        setDateString(getDateString());
    }, [date, allDay]);
    useEffect(function () {
        if (datepickerProps.initialDate) {
            setDate(datepickerProps.initialDate);
        }
    }, [datepickerProps.initialDate]);
    var handleDayClick = function (event) {
        // Close dp if timepicker isn't shown since the user can't do anything else.
        if (!datepickerProps.showTimePicker) {
            setOpen(false);
        }
        if (event.date) {
            setDate(DateTime.fromSeconds(event.date)
                .setZone(datepickerProps.timezone)
                .toSeconds());
        }
        else {
            setDate(null);
        }
        setAllDay(event.allDay);
        if (datepickerProps.onDaySelected)
            datepickerProps.onDaySelected(event);
    };
    var handleBlur = function () {
        if (datepickerProps.onBlur &&
            typeof datepickerProps.onBlur === 'function') {
            datepickerProps.onBlur();
        }
    };
    var handleButtonClick = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setOpen(!open);
                    return [4 /*yield*/, update()];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    var getDateString = function () {
        if (!date)
            return null;
        if (datepickerProps.showTimePicker && allDay) {
            return DateTime.fromSeconds(date)
                .setZone(datepickerProps.timezone)
                .toFormat(dateFormatString || dateFormatStrings.DATE);
        }
        if (!allDay && datepickerProps.showTimePicker) {
            return DateTime.fromSeconds(date)
                .setZone(datepickerProps.timezone)
                .toFormat(dateFormatString || dateFormatStrings.DATE_TIME_TZ);
        }
        return DateTime.fromSeconds(date)
            .setZone(datepickerProps.timezone)
            .toFormat(dateFormatString || dateFormatStrings.DATE);
    };
    return (React.createElement("div", { className: "acl-DatepickerButton " + (className || ''), style: { display: 'inline-block' }, ref: divRef },
        React.createElement(Button, { active: open, variant: ButtonVariant.UNDERLINE, icon: "Date", label: !date ? placeholderText : dateString, ref: setButtonRef, onMouseMove: update, dark: dark, disabled: disabled, error: error, compact: compact, onClick: handleButtonClick, onBlur: handleBlur, allowPropagation: true }),
        React.createElement(Portal, null,
            React.createElement("div", __assign({}, attributes.popper, { style: __assign({ zIndex: 1000 }, styles.popper), ref: setDpRef }), !disabled && open && (React.createElement("div", { className: "acl-DatepickerButton-picker", style: {
                    background: 'white',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.1), 0 8px 16px 0 rgba(0,0,0,0.2)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10
                } },
                React.createElement(Datepicker, __assign({}, datepickerProps, { month: month || date
                        ? new Date(date * 1000)
                        : undefined, onDaySelected: handleDayClick, initialDate: date || datepickerProps.initialDate, initialAllDay: allDay || datepickerProps.initialAllDay }))))))));
};

var DateRangePickerButton = function (_a) {
    var _b = _a.placeholderText, placeholderText = _b === void 0 ? 'Select a date range' : _b, _c = _a.startDateFormat, startDateFormat = _c === void 0 ? dateFormatStrings.DATE : _c, _d = _a.startTimeFormat, startTimeFormat = _d === void 0 ? dateFormatStrings.TIME : _d, _e = _a.endDateFormat, endDateFormat = _e === void 0 ? dateFormatStrings.DATE : _e, _f = _a.endTimeFormat, endTimeFormat = _f === void 0 ? dateFormatStrings.TIME_TZ : _f, _g = _a.dark, dark = _g === void 0 ? false : _g, _h = _a.error, error = _h === void 0 ? false : _h, _j = _a.disabled, disabled = _j === void 0 ? false : _j, _k = _a.compact, compact = _k === void 0 ? false : _k, dateRangePickerProps = __rest(_a, ["placeholderText", "startDateFormat", "startTimeFormat", "endDateFormat", "endTimeFormat", "dark", "error", "disabled", "compact"]);
    // State things
    var _l = useState(false), open = _l[0], setOpen = _l[1];
    var _m = useState(dateRangePickerProps.initialRange || null), range = _m[0], setRange = _m[1];
    var _o = useState(dateRangePickerProps.initialAllDay), allDay = _o[0], setAllDay = _o[1];
    var _p = useState(null), rangeString = _p[0], setRangeString = _p[1];
    // Refs
    var _q = useState(null), drpRef = _q[0], setDrpRef = _q[1];
    var _r = useState(null), buttonRef = _r[0], setButtonRef = _r[1];
    // Effects and hooks
    useEffect(function () {
        if (range === null)
            return;
        // Disabling tslint because this function is called in a hook...
        // tslint:disable-next-line: no-use-before-declare
        setRangeString(getDateString());
    }, [range]);
    useEffect(function () {
        if (dateRangePickerProps.initialRange) {
            setRange(dateRangePickerProps.initialRange);
        }
    }, [dateRangePickerProps.initialRange]);
    useDetectOutsideClick([{ current: buttonRef }, { current: drpRef }], function () {
        setOpen(false);
    });
    var rangeTimestampToRangeString = function (selection) {
        var start = DateTime.fromSeconds(selection.start).setZone(dateRangePickerProps.timezone);
        var end = DateTime.fromSeconds(selection.end).setZone(dateRangePickerProps.timezone);
        var fromString, toString;
        if (!dateRangePickerProps.showTimePicker || allDay) {
            fromString = start.toFormat(startDateFormat);
            toString = end.toFormat(endDateFormat);
            return fromString + " \u2013 " + toString;
        }
        fromString = start.toFormat(startDateFormat);
        var fromTime = start.toFormat(startTimeFormat);
        toString = end.toFormat(endDateFormat);
        var toTime = end.toFormat(endTimeFormat);
        return fromString + " \u2013 " + toString + ", " + fromTime + " \u2013 " + toTime;
    };
    var getDateString = function () { return rangeTimestampToRangeString(range); };
    var handleRangeSelected = function (selection) {
        setRange(selection.selectedRange);
        setAllDay(selection.allDay);
        if (dateRangePickerProps.onRangeSelected) {
            dateRangePickerProps.onRangeSelected(selection);
        }
    };
    var handleBlur = function () {
        if (dateRangePickerProps.onBlur &&
            typeof dateRangePickerProps.onBlur === 'function') {
            dateRangePickerProps.onBlur();
        }
    };
    var popperProps = {
        placement: 'bottom-start'
    };
    var _s = usePopper(buttonRef, drpRef, popperProps), styles = _s.styles, attributes = _s.attributes, update = _s.update;
    var handleButtonClick = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setOpen(!open);
                    return [4 /*yield*/, update()];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    return (React.createElement("div", { className: "acl-DatepickerButton", style: { display: 'inline-block' } },
        React.createElement(Button, { active: open, variant: ButtonVariant.UNDERLINE, icon: "Date", label: !range ? placeholderText : rangeString, ref: setButtonRef, disabled: disabled, dark: dark, error: error, compact: compact, onClick: handleButtonClick, onBlur: handleBlur }),
        React.createElement(Portal, null,
            React.createElement("div", __assign({}, attributes.popper, { style: __assign({ zIndex: 1000 }, styles.popper), ref: setDrpRef }), !disabled && open && (React.createElement("div", { className: "acl-DateRangePickerButton-picker", style: {
                    background: 'white',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.1), 0 8px 16px 0 rgba(0,0,0,0.2)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                } },
                React.createElement(DateRangePicker, __assign({}, dateRangePickerProps, { initialRange: range || dateRangePickerProps.initialRange, initialAllDay: allDay || dateRangePickerProps.initialAllDay, onRangeSelected: handleRangeSelected }))))))));
};

var css_248z$P = ".acl-Card {\n  width: 100%;\n  margin: 0;\n  display: flex;\n  flex-direction: column;\n  padding: 16px;\n  border-radius: 6px;\n  background: #ffffff;\n  box-shadow: 0 4px 8px 2px rgba(0, 0, 0, 0.1); }\n  .acl-Card.acl-Card-OneByOne {\n    grid-column-start: span 3;\n    grid-row-start: span 1; }\n  .acl-Card.acl-Card-OneByTwo {\n    grid-column-start: span 3;\n    grid-row-start: span 2; }\n  .acl-Card.acl-Card-TwoByTwo {\n    grid-column-start: span 6;\n    grid-row-start: span 2; }\n  .acl-Card.acl-Card-ThreeByTwo {\n    grid-column-start: span 9;\n    grid-row-start: span 2; }\n  .acl-Card.acl-Card-FourByTwo {\n    grid-column-start: span 12;\n    grid-row-start: span 2; }\n  .acl-Card .acl-Card--header {\n    margin-bottom: 16px;\n    display: flex; }\n    .acl-Card .acl-Card--header.show-divider {\n      padding: 0 0 15px 0;\n      border-bottom: 1px rgba(0, 0, 0, 0.1) solid; }\n  .acl-Card .acl-Card--body {\n    overflow-y: auto;\n    height: 100%; }\n  .acl-Card .acl-Card--footer {\n    margin-top: 16px;\n    padding: 15px 0 0 0;\n    min-height: 1px; }\n    .acl-Card .acl-Card--footer.show-divider {\n      border-top: 1px rgba(0, 0, 0, 0.1) solid; }\n";
styleInject(css_248z$P);

var Card = function (props) {
    var footer = safeInvoke(props.renderFooter);
    var header = safeInvoke(props.renderHeader);
    var headerClasses = classNames('acl-Card--header', {
        'show-divider': props.showHeaderDivider
    });
    var footerClasses = classNames('acl-Card--footer', {
        'show-divider': props.showFooterDivider
    });
    var renderHeader = function () {
        return header ? React.createElement("div", { className: headerClasses }, header) : null;
    };
    var renderFooter = function () {
        return footer ? React.createElement("div", { className: footerClasses }, footer) : null;
    };
    return (React.createElement("div", { className: "acl-Card acl-Card-" + (props.size ? props.size.toString() : 'none') },
        renderHeader(),
        React.createElement("div", { className: "acl-Card--body" }, safeInvoke(props.renderBody, null, '`props.renderBody` must be a function', true)),
        renderFooter()));
};

var CardSize;
(function (CardSize) {
    CardSize["OneByOne"] = "OneByOne";
    CardSize["OneByTwo"] = "OneByTwo";
    CardSize["TwoByTwo"] = "TwoByTwo";
    CardSize["ThreeByTwo"] = "ThreeByTwo";
    CardSize["FourByTwo"] = "FourByTwo";
})(CardSize || (CardSize = {}));

var css_248z$Q = ".acl-CardHeader--body .acl-CardHeader--title {\n  color: rgba(0, 0, 0, 0.7);\n  width: 100%; }\n\n.acl-CardHeader--body .acl-CardHeader--subTitle {\n  font-size: 12px;\n  color: rgba(0, 0, 0, 0.5);\n  width: 100%; }\n\n.acl-CardHeader--menu {\n  padding: 0px;\n  margin-left: auto; }\n";
styleInject(css_248z$Q);

var CardHeader = function (props) {
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "acl-CardHeader--body" },
            React.createElement("div", { className: "acl-CardHeader--title" }, props.title),
            React.createElement("div", { className: "acl-CardHeader--subTitle" }, props.subTitle)),
        props.showMenuButton && (React.createElement(DropdownButton, __assign({}, props.menuButtonProps, { className: "acl-CardHeader--menu", buttonProps: {
                icon: 'Overflow',
                variant: ButtonVariant.BORDERLESS
            }, closeOnItemSelect: true })))));
};

var css_248z$R = "div.acl-DashboardContainer {\n  background-color: #e2e2e2;\n  width: 100%;\n  display: grid;\n  grid-template-columns: repeat(12, minmax(60px, 1fr));\n  grid-auto-rows: 168px;\n  grid-auto-flow: row dense;\n  row-gap: 16px;\n  column-gap: 16px;\n  padding: 16px;\n  overflow: scroll; }\n  div.acl-DashboardContainer.dark {\n    background-color: #303030; }\n";
styleInject(css_248z$R);

var DashboardContainer = function (props) {
    return (React.createElement("div", { className: "acl-DashboardContainer " + (props.dark ? 'dark' : '') }, props.children));
};

var css_248z$S = ".acl-DashboardSectionDivider .acl-Accordion {\n  background-color: #e2e2e2; }\n  .acl-DashboardSectionDivider .acl-Accordion .acl-Accordion-title {\n    padding-left: 16px;\n    padding-right: 16px; }\n    .acl-DashboardSectionDivider .acl-Accordion .acl-Accordion-title .acl-Accordion-icon .acl-Icon {\n      color: rgba(1, 1, 1, 0.6) !important;\n      fill: rgba(1, 1, 1, 0.6) !important; }\n    .acl-DashboardSectionDivider .acl-Accordion .acl-Accordion-title .acl-Accordion-titleText {\n      color: rgba(1, 1, 1, 0.6);\n      font-size: 14px;\n      font-weight: normal;\n      white-space: nowrap;\n      padding-left: calc($padding-small / 2);\n      padding-right: 8px; }\n    .acl-DashboardSectionDivider .acl-Accordion .acl-Accordion-title .acl-Accordion-rightElement {\n      width: 100%; }\n      .acl-DashboardSectionDivider .acl-Accordion .acl-Accordion-title .acl-Accordion-rightElement hr {\n        border-width: 1px;\n        border-style: solid;\n        border-color: #ababab; }\n  .acl-DashboardSectionDivider .acl-Accordion .acl-Accordion-body .acl-Accordion-content {\n    padding-left: 0px;\n    padding-right: 0px; }\n\n.acl-DashboardSectionDivider .dark .acl-Accordion {\n  background-color: #303030; }\n  .acl-DashboardSectionDivider .dark .acl-Accordion .acl-Accordion-icon .acl-Icon {\n    color: rgba(255, 255, 255, 0.7) !important;\n    fill: rgba(255, 255, 255, 0.7) !important; }\n  .acl-DashboardSectionDivider .dark .acl-Accordion .acl-Accordion-title .acl-Accordion-titleText {\n    color: rgba(255, 255, 255, 0.7); }\n";
styleInject(css_248z$S);

var DashboardSectionDivider = function (props) {
    var renderBar = function () {
        return (React.createElement("span", { style: { width: '100%' } },
            React.createElement("hr", null)));
    };
    return (React.createElement("div", { className: classNames('acl-DashboardSectionDivider', {
            dark: props.dark
        }) },
        React.createElement(Accordion, __assign({}, props, { renderRightElement: renderBar }),
            React.createElement(DashboardContainer, { dark: props.dark }, props.children))));
};

var css_248z$T = "div.acl-dashboard {\n  width: 100%;\n  height: 100%;\n  background-color: #e2e2e2; }\n";
styleInject(css_248z$T);

var Dashboard = function (props) {
    var toolbar = safeInvoke(props.renderToolbar);
    var childArray = React.Children.toArray(props.children);
    return (React.createElement("div", { className: "acl-dashboard" },
        toolbar ? toolbar : null,
        React.createElement(DashboardContainer, { dark: props.dark }, childArray.filter(function (item) { return item.type !== DashboardSectionDivider; })),
        childArray.filter(function (item) { return item.type === DashboardSectionDivider; })));
};

var css_248z$U = ".acl-dashboard-toolbar {\n  height: 56px;\n  width: 100%;\n  padding-left: 16px;\n  padding-right: 16px;\n  background-color: #ffffff;\n  vertical-align: middle;\n  display: flex;\n  align-items: center;\n  justify-content: space-between; }\n\n.acl-dashboard-toolbar.dark {\n  background-color: #303030; }\n";
styleInject(css_248z$U);

var DashboardToolbar = function (props) {
    var renderLeft = safeInvoke(props.renderLeft);
    var renderRight = safeInvoke(props.renderRight);
    return (React.createElement("div", { className: "acl-dashboard-toolbar " + (props.darkMode ? 'dark' : '') },
        React.createElement("div", { className: "acl-dashboard-left" }, renderLeft ? renderLeft : null),
        React.createElement("div", { className: "acl-dashboard-right" }, renderRight ? renderRight : null)));
};

var css_248z$V = ".acl-BasicMenu {\n  overflow: hidden;\n  overflow-y: auto; }\n  .acl-BasicMenu .acl-MenuItem--selected:hover svg, .acl-BasicMenu .acl-MenuItem--selected.acl-MenuItem--hovered svg {\n    color: white; }\n  .acl-BasicMenu .acl-MenuItem--selected svg {\n    color: #009ebd; }\n  .acl-BasicMenu .acl-MenuItem--hovered {\n    color: #ffffff;\n    background-color: #009ebd; }\n";
styleInject(css_248z$V);

var css_248z$W = ".acl-MultiSelectDropdown .acl-Button svg.acl-Icon {\n  margin-left: 10px; }\n";
styleInject(css_248z$W);

var MultiSelectDropdownButton = function (_a) {
    var _b = _a.closeOnItemSelect, closeOnItemSelect = _b === void 0 ? false : _b, _c = _a.closeOnClickOutside, closeOnClickOutside = _c === void 0 ? true : _c, _d = _a.uniqueIdAccessor, uniqueIdAccessor = _d === void 0 ? 'id' : _d, _e = _a.itemLabel, itemLabel = _e === void 0 ? 'label' : _e, menuItems = _a.menuItems, props = __rest(_a, ["closeOnItemSelect", "closeOnClickOutside", "uniqueIdAccessor", "itemLabel", "menuItems"]);
    var _f = useState(false), open = _f[0], setOpen = _f[1];
    var containerRef = useRef(null);
    var iconRef = useRef(null);
    var _g = useState(props.selected || []), selected = _g[0], setSelected = _g[1];
    var _h = useState(null), buttonRef = _h[0], setButtonRef = _h[1];
    var _j = useState(null), popperRef = _j[0], setPopperRef = _j[1];
    var popperProps = {
        placement: props.placement || 'bottom-start',
        strategy: 'fixed',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 4]
                }
            }
        ]
    };
    var _k = usePopper(buttonRef, popperRef, popperProps), styles = _k.styles, attributes = _k.attributes, update = _k.update;
    useEffect(function () {
        if (!isEqual(selected, props.selected)) {
            setSelected(props.selected || []);
        }
    }, [props.selected]);
    useDetectOutsideClick([containerRef, { current: popperRef }, iconRef], function () {
        if (closeOnClickOutside) {
            setOpen(false);
        }
    });
    var handleOnSelection = function (selection) {
        var exists = !!selected.find(function (i) { return get(i, uniqueIdAccessor) === get(selection, uniqueIdAccessor); });
        var newItems;
        if (!exists) {
            newItems = __spreadArrays([selection], selected);
        }
        else {
            newItems = selected.filter(function (i) {
                return get(i, uniqueIdAccessor) !==
                    get(selection, uniqueIdAccessor);
            });
        }
        setSelected(newItems);
        if (props.onSelection) {
            props.onSelection(newItems);
        }
        if (closeOnItemSelect) {
            setOpen(false);
        }
    };
    var handleItemClick = function (item, event) {
        handleOnSelection(item);
        if (props.onItemClick && typeof props.onItemClick === 'function') {
            props.onItemClick(event);
        }
    };
    var handleButtonClick = function (e) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (props.onClick && typeof props.onClick === 'function') {
                        props.onClick(e);
                    }
                    return [4 /*yield*/, update()];
                case 1:
                    _a.sent();
                    setOpen(!open);
                    return [2 /*return*/];
            }
        });
    }); };
    return (React.createElement("div", { className: "acl-MultiSelectDropdown " + (props.className || ''), ref: containerRef, style: props.style },
        React.createElement(Button, __assign({ allowPropagation: true, ref: setButtonRef, variant: props.variant }, props.buttonProps, { active: open, dark: (props.buttonProps && props.buttonProps.dark) || props.dark, onClick: handleButtonClick }),
            props.buttonText, " (" + (selected.length || 0) + ")",
            React.createElement(Icon, { name: "ChevronDown", color: ColorTheme.buttons.neutral, size: IconSize.base })),
        React.createElement(Portal, null,
            React.createElement("div", __assign({}, attributes.popper, { style: __assign({ zIndex: 2000 }, styles.popper), ref: setPopperRef }), open && menuItems && (React.createElement(BasicMenu, { minWidth: props.minWidth, maxWidth: props.maxWidth, width: props.width }, menuItems.map(function (item, idx) {
                var uniqueId = get(item, uniqueIdAccessor);
                var isSelected = selected &&
                    selected.find(function (i) {
                        return get(i, uniqueIdAccessor) ===
                            uniqueId;
                    });
                var isDisabled = get(item, 'disabled', false);
                return (React.createElement(MenuItem, { className: classNames({
                        'acl-MenuItem--selected': isSelected
                    }), label: item[itemLabel], key: uniqueId || idx, disabled: isDisabled, leftElement: React.createElement("span", { ref: iconRef, onClick: function (e) {
                            if (isDisabled)
                                return;
                            e.preventDefault();
                            e.stopPropagation();
                            handleItemClick(item, e);
                        } },
                        React.createElement(Icon, { name: isSelected
                                ? 'CheckboxCheck'
                                : 'CheckboxNone' })), onClick: function (menuItem) {
                        handleItemClick(item, menuItem.event);
                    } }));
            })))))));
};

var css_248z$X = ".acl-GlobalHeader-productSelector {\n  position: fixed;\n  top: 52px;\n  height: calc(100vh - 52px);\n  overflow-y: auto; }\n";
styleInject(css_248z$X);

var GlobalHeader = function (_a) {
    var 
    // Defaults
    _b = _a.navbarZIndex, 
    // Defaults
    navbarZIndex = _b === void 0 ? 2 : _b, _c = _a.dimmerZIndex, dimmerZIndex = _c === void 0 ? 1 : _c, props = __rest(_a, ["navbarZIndex", "dimmerZIndex"]);
    // This controls the open state since the menu toggle button is also part of this.
    var _d = useState(false), menuOpen = _d[0], setMenuOpen = _d[1];
    // Disable scrolling when menu is open
    useEffect(function () {
        document.body.style.overflow = menuOpen ? 'hidden' : null;
        return function () {
            document.body.style.overflow = null;
        };
    }, [menuOpen]);
    var toggleMenu = function () { return setMenuOpen(!menuOpen); };
    var MenuButton = function () {
        if (!props.menuItems || !props.menuItems.length)
            return null;
        return (React.createElement(Button, { dark: true, variant: ButtonVariant.STATIC_BORDERLESS, onClick: toggleMenu },
            React.createElement(Icon, { name: "Menu" })));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Navbar, { tagText: props.tagText, productName: props.productName, accentColor: props.accentColor, menuButtonEl: MenuButton(), renderCustomLogo: props.renderCustomLogo, zIndex: navbarZIndex },
            React.createElement(Navbar.Center, null, !props.disableSearch && (React.createElement("div", { style: {
                    paddingLeft: 8,
                    paddingRight: 8,
                    borderLeft: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRight: '1px solid rgba(255, 255, 255, 0.3)'
                } },
                React.createElement(Input, { compact: true, variant: InputVariant.BORDERLESS, leftElement: React.createElement(Icon, { name: "Search" }), placeholder: "Search...", dark: true })))),
            React.createElement(Navbar.Right, null, props.buttons &&
                props.buttons.map(function (button, i) {
                    // If the button is a dropdown menu, render it.
                    if (button.menuItems &&
                        button.menuItems.length) {
                        var trigger = (React.createElement(Button, { dark: true, variant: ButtonVariant.STATIC_BORDERLESS },
                            React.createElement(Icon, { name: button.icon })));
                        return (React.createElement(DropdownButton, { key: i, dark: true, triggerElement: trigger, menuItems: button.menuItems }));
                    }
                    // Otherwise, just render a button with an onClick.
                    return (React.createElement(Button, { key: i, dark: true, variant: ButtonVariant.STATIC_BORDERLESS, onClick: function (e) { return button.onClick(e); } },
                        React.createElement(Icon, { name: button.icon })));
                }))),
        React.createElement(SlideoutMenu, { className: "acl-GlobalHeader-productSelector", open: menuOpen, items: props.menuItems, closeOnDimmerClick: true, toggle: toggleMenu, dimmerZIndex: dimmerZIndex })));
};

var ProductSelector = function (props) {
    var items = [
        {
            label: 'Dashboard',
            icon: 'ProductDashboard',
            linkProps: {
                href: props.baseURL + "/webapp/#/home-dashboard"
            }
        },
        {
            label: 'Audiences',
            icon: 'ProductAudiences',
            linkProps: {
                href: props.baseURL + "/audience"
            }
        },
        {
            label: 'Media Plans',
            icon: 'ProductMediaPlans',
            linkProps: {
                href: props.baseURL + "/planning-center"
            }
        },
        {
            label: 'Campaigns',
            icon: 'ProductCampaigns',
            linkProps: {
                href: props.baseURL + "/webapp/#/campaigns"
            }
        },
        {
            label: 'Reports',
            icon: 'Report',
            linkProps: {
                href: props.baseURL + "/reportCenter"
            }
        },
        {
            label: 'Insights',
            icon: 'ProductInsights',
            linkProps: {
                href: props.baseURL + "/datamine/#/insights"
            }
        },
        {
            label: 'Social',
            icon: 'ChannelSocial',
            linkProps: {
                href: props.baseURL + "/app/unity/learnMore.htm"
            }
        },
        {
            label: 'Brand Intelligence',
            icon: 'ProductBi',
            linkProps: {
                href: 'https://intelligence.amobee.com'
            }
        }
    ];
    return (React.createElement(SlideoutMenu, { className: props.className, items: items, open: props.open }));
};

var css_248z$Y = ".acl-SubHeader {\n  display: flex;\n  align-items: center;\n  background: #303030;\n  color: #ffffff;\n  border-bottom: 1px solid #777777;\n  position: relative;\n  padding: 0; }\n";
styleInject(css_248z$Y);

var SubHeader = function (props) { return (React.createElement("div", { className: "acl-SubHeader " + (props.className || ''), style: props.style }, props.children)); };

var css_248z$Z = ".acl-Typeahead {\n  display: inline-block;\n  position: relative;\n  padding: 8px 12px; }\n  .acl-Typeahead.fluid {\n    display: block; }\n  .acl-Typeahead.acl-Typeahead-scoped .acl-Typeahead-options {\n    display: flex;\n    justify-content: flex-start;\n    align-items: center;\n    position: relative; }\n    .acl-Typeahead.acl-Typeahead-scoped .acl-Typeahead-options .acl-Button {\n      margin: 0 8px 0 0; }\n    .acl-Typeahead.acl-Typeahead-scoped .acl-Typeahead-options .typeaheadDivider {\n      display: inline-flex;\n      width: 1px;\n      height: 24px;\n      background: rgba(0, 0, 0, 0.1);\n      margin: 0px 8px 0px 8px; }\n  .acl-Typeahead .acl-Typeahead-wrapper {\n    display: block;\n    position: relative; }\n  .acl-Typeahead .acl-FormControl {\n    padding: 0; }\n  .acl-Typeahead .acl-Input-wrapper {\n    flex-wrap: wrap;\n    overflow: visible; }\n  .acl-Typeahead .acl-Input-leftElement {\n    display: inline-flex;\n    flex-wrap: wrap;\n    flex: 0 1 auto; }\n    .acl-Typeahead .acl-Input-leftElement .acl-Typeahead-chips {\n      margin: 0 0 0 4px; }\n      .acl-Typeahead .acl-Input-leftElement .acl-Typeahead-chips .acl-chip {\n        margin: calc(4px/ 2) 4px calc(4px/ 2) 0; }\n        .acl-Typeahead .acl-Input-leftElement .acl-Typeahead-chips .acl-chip .close-chip {\n          height: 24px; }\n          .acl-Typeahead .acl-Input-leftElement .acl-Typeahead-chips .acl-chip .close-chip a {\n            top: 4px; }\n  .acl-Typeahead input {\n    display: inline-flex;\n    flex-basis: 100px;\n    margin: 7px 8px; }\n  .acl-Typeahead .acl-Input--default,\n  .acl-Typeahead .acl-Input--borderless,\n  .acl-Typeahead .acl-Input--underlined,\n  .acl-Typeahead .acl-Input--table {\n    padding: 0 12px 0 0; }\n  .acl-Typeahead .acl-Input-rightElement {\n    display: inline-flex;\n    justify-content: flex-end; }\n  .acl-Typeahead.acl-Typeahead-highlighted-selections .acl-Typeahead-wrapper > .acl-BasicMenu .acl-MenuItem--selected svg {\n    color: #ffffff; }\n  .acl-Typeahead .acl-Typeahead-wrapper > .acl-Typeahead-popper > .acl-BasicMenu.acl-Typeahead-loading .acl-BasicMenu-Items {\n    display: flex;\n    justify-content: center; }\n    .acl-Typeahead .acl-Typeahead-wrapper > .acl-Typeahead-popper > .acl-BasicMenu.acl-Typeahead-loading .acl-BasicMenu-Items.acl-BasicMenu-Items--scroll {\n      overflow-y: hidden;\n      overflow-x: hidden; }\n    .acl-Typeahead .acl-Typeahead-wrapper > .acl-Typeahead-popper > .acl-BasicMenu.acl-Typeahead-loading .acl-BasicMenu-Items .acl-Spinner {\n      display: block;\n      margin: 20px; }\n  .acl-Typeahead .acl-Typeahead-wrapper > .acl-Typeahead-popper > .acl-BasicMenu .acl-MenuItem:hover svg, .acl-Typeahead .acl-Typeahead-wrapper > .acl-Typeahead-popper > .acl-BasicMenu .acl-MenuItem.acl-BasicMenu--active svg {\n    color: #ffffff; }\n  .acl-Typeahead .acl-Typeahead-wrapper > .acl-Typeahead-popper > .acl-BasicMenu .acl-MenuItem.acl-Typeahead-selectedItem:not(:hover):not(.acl-BasicMenu--active) .acl-MenuItem-leftElement svg {\n    color: #009ebd; }\n  .acl-Typeahead .acl-Typeahead-wrapper > .acl-Typeahead-popper > .acl-BasicMenu .acl-MenuItem.acl-Typeahead-selectedItem:not(:hover):not(.acl-BasicMenu--active) .acl-MenuItem-leftElement .hideLeftElement {\n    visibility: hidden;\n    margin-right: -8px; }\n";
styleInject(css_248z$Z);

var Typeahead = function (_a) {
    var _b = _a.placeholder, placeholder = _b === void 0 ? 'Select' : _b, _c = _a.resultNameAccessor, resultNameAccessor = _c === void 0 ? 'label' : _c, _d = _a.uniqueIdAccessor, uniqueIdAccessor = _d === void 0 ? 'label' : _d, _e = _a.optionNameAccessor, optionNameAccessor = _e === void 0 ? 'label' : _e, _f = _a.optionUniqueIdAccessor, optionUniqueIdAccessor = _f === void 0 ? 'id' : _f, _g = _a.variant, variant = _g === void 0 ? InputVariant.UNDERLINED : _g, _h = _a.optionsButtonVariant, optionsButtonVariant = _h === void 0 ? ButtonVariant.BORDERLESS : _h, _j = _a.maxResultRows, maxResultRows = _j === void 0 ? 10 : _j, _k = _a.fluid, fluid = _k === void 0 ? true : _k, _l = _a.error, error = _l === void 0 ? false : _l, _m = _a.disabled, disabled = _m === void 0 ? false : _m, _o = _a.rightElementClearsSelection, rightElementClearsSelection = _o === void 0 ? true : _o, _p = _a.loading, loading = _p === void 0 ? false : _p, dark = _a.dark, _q = _a.noResultsText, noResultsText = _q === void 0 ? 'No match found' : _q, _r = _a.selectionClearsQuery, selectionClearsQuery = _r === void 0 ? false : _r, props = __rest(_a, ["placeholder", "resultNameAccessor", "uniqueIdAccessor", "optionNameAccessor", "optionUniqueIdAccessor", "variant", "optionsButtonVariant", "maxResultRows", "fluid", "error", "disabled", "rightElementClearsSelection", "loading", "dark", "noResultsText", "selectionClearsQuery"]);
    var IS_MULTI = !!props.allowMultiple;
    var getInitialQuery = function () {
        if (props.selected && !IS_MULTI) {
            if (Array.isArray(props.selected) && props.selected.length) {
                return get(props.selected[0], resultNameAccessor) || '';
            }
            return get(props.selected, resultNameAccessor) || '';
        }
        return '';
    };
    var renderHighlightedResult = function (result) {
        return (React.createElement(HighlightMatch, { match: query }, get(result, resultNameAccessor)));
    };
    var isItemSelected = function (item) {
        if (!selected || (IS_MULTI && !Array.isArray(selected))) {
            return false;
        }
        var itemId = get(item, "original." + uniqueIdAccessor);
        if (!IS_MULTI && !Array.isArray(selected)) {
            return get(selected, uniqueIdAccessor) === itemId;
        }
        return (selected.findIndex(function (i) { return get(i, uniqueIdAccessor) === itemId; }) > -1);
    };
    var getCategorizedItems = function (results) {
        var mapped = results &&
            results.map(function (r) {
                var menuItem = {
                    label: renderHighlightedResult(r),
                    subLabel: (props.subLabelNameAccessor &&
                        r[props.subLabelNameAccessor]) ||
                        (props.options &&
                            optionNameAccessor &&
                            r[optionUniqueIdAccessor] &&
                            optionsMap[r[optionUniqueIdAccessor]]) ||
                        null,
                    rightIcon: props.rightIconAccessor && r[props.rightIconAccessor],
                    rightIconColor: props.rightIconColorAccessor &&
                        r[props.rightIconColorAccessor],
                    rightIconText: props.rightIconPopoverText &&
                        r[props.rightIconPopoverText],
                    original: r
                };
                if (props.categoryAccessor) {
                    menuItem[props.categoryAccessor] =
                        r[props.categoryAccessor];
                }
                return menuItem;
            });
        return BasicMenu.getCategorizedItems(mapped, props.categoryAccessor);
    };
    var generateOptionsMap = function () {
        var newMap = {};
        if (!props.options) {
            return newMap;
        }
        props.options.forEach(function (option) {
            newMap[option[optionUniqueIdAccessor]] = option[optionNameAccessor];
        });
        return newMap;
    };
    var _s = useState(getInitialQuery()), query = _s[0], setQuery = _s[1];
    var _t = useState(false), open = _t[0], setOpen = _t[1];
    var _u = useState(false), focused = _u[0], setFocused = _u[1];
    var _v = useState(props.selected), selected = _v[0], setSelected = _v[1];
    var _w = useState(generateOptionsMap()), optionsMap = _w[0], setOptionsMap = _w[1];
    var _x = useState(getCategorizedItems(props.results)), results = _x[0], setResults = _x[1];
    var iconRef = useRef();
    var setIconRef = function (element) {
        iconRef.current = element;
    };
    var typeaheadRef = useRef(null);
    var basicMenuRef = useRef(null);
    var _y = useState(null), inputRef = _y[0], setInputRef = _y[1];
    var _z = useState(null), menuRef = _z[0], setMenuRef = _z[1];
    var _0 = useState(false), isEmpty$1 = _0[0], setIsEmpty = _0[1];
    useHandleKeyPress('Tab', function () {
        setOpen(false);
    }, inputRef, undefined, 'keydown');
    var popperProps = {
        placement: 'bottom-start',
        strategy: 'fixed',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 4]
                }
            },
            {
                name: 'flip',
                options: {
                    fallbackPlacements: ['top']
                }
            }
        ]
    };
    var _1 = usePopper(inputRef, menuRef, popperProps), styles = _1.styles, attributes = _1.attributes, update = _1.update;
    useDetectOutsideClick([typeaheadRef, { current: menuRef }, iconRef], function () {
        setOpen(false);
    });
    var handleFreeformEntry = function () {
        if (!props.allowFreeformEntry)
            return;
        safeInvoke(props.onSelectionChange, [query]);
    };
    useHandleKeyPress('Escape', function () { return setOpen(false); }, inputRef);
    useHandleKeyPress('Enter', handleFreeformEntry, inputRef);
    /**
     * Rebuild categorized items if categoryAccessor changes
     */
    useEffect(function () {
        setResults(getCategorizedItems(props.results));
    }, [props.categoryAccessor]);
    /**
     * Set current results and categorized results if incoming results have changed
     */
    useEffect(function () {
        if (props && props.results && props.results.length === 0) {
            setIsEmpty(true);
        }
        else {
            setIsEmpty(false);
        }
        setResults(getCategorizedItems(props.results));
    }, [props.results]);
    /**
     * Auto set query input to currently selected item for single select typeahead
     */
    useEffect(function () {
        if (!open && !IS_MULTI && selected) {
            if (Array.isArray(selected) && selected.length) {
                setQuery(selected[0][resultNameAccessor]);
            }
            if (!Array.isArray(selected) && get(selected, resultNameAccessor)) {
                setQuery(get(selected, resultNameAccessor));
            }
        }
    }, [open, selected]);
    /**
     * On query change, open dropdown and fire onQueryChange
     */
    useEffect(function () {
        if (typeof props.onQueryChange === 'function') {
            props.onQueryChange(query);
        }
        if (query === '' && focused && !props.disableInitialOpen) {
            setOpen(true);
        }
        setResults(getCategorizedItems(props.results));
    }, [query]);
    /**
     * Generate options map from supplied options if they have changed
     */
    useEffect(function () {
        if (props.options && optionNameAccessor) {
            var newMap = generateOptionsMap();
            if (optionsMap !== newMap) {
                setOptionsMap(newMap);
            }
        }
    }, [props.options, optionNameAccessor]);
    /**
     * Set selected items if they have changed
     * Single select typeahead: Auto set query input to currently selected item
     */
    useEffect(function () {
        var sel = Array.isArray(selected) ? __spreadArrays(selected).sort() : selected;
        var propsSel = Array.isArray(props.selected)
            ? __spreadArrays(props.selected).sort()
            : props.selected;
        if (!isEqual(sel, propsSel)) {
            setSelected(props.selected);
        }
        if (!IS_MULTI) {
            if (!props.selected || isEqual(props.selected, [])) {
                setQuery('');
            }
            else if (!Array.isArray(selected) &&
                get(selected, resultNameAccessor) !== query) {
                setQuery(get(selected, resultNameAccessor) || '');
            }
            else if (Array.isArray(selected) &&
                selected.length > 0 &&
                get(selected[0], resultNameAccessor) !== query) {
                setQuery(get(selected[0], resultNameAccessor) || '');
            }
        }
        else if (selectionClearsQuery) {
            var selectedLength = undefined;
            if (Array.isArray(selected) || selected) {
                selectedLength = Array.isArray(selected)
                    ? selected.length
                    : isEmpty(selected)
                        ? 0
                        : 1;
            }
            var propsSelLength = Array.isArray(props.selected)
                ? props.selected.length
                : undefined;
            if (selectedLength !== undefined &&
                propsSelLength !== undefined &&
                propsSelLength > selectedLength) {
                setQuery('');
                setOpen(false);
            }
        }
    }, [props.selected]);
    useEffect(function () {
        if (disabled) {
            setOpen(false);
        }
    }, [disabled]);
    var handleRemoveSelection = function (resultItem, selectedItem) { return __awaiter(void 0, void 0, void 0, function () {
        var idToRemove_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (props.onSelectionChange &&
                        typeof props.onSelectionChange === 'function') {
                        if (!IS_MULTI && !Array.isArray(selected)) {
                            props.onSelectionChange(null);
                            setOpen(true);
                        }
                        idToRemove_1 = resultItem && resultItem.original
                            ? resultItem.original[uniqueIdAccessor]
                            : selectedItem && selectedItem[uniqueIdAccessor];
                        props.onSelectionChange(selected.filter(function (i) { return i[uniqueIdAccessor] !== idToRemove_1; }));
                    }
                    if (selected && Array.isArray(selected) && selected.length > 1) {
                        return [2 /*return*/];
                    }
                    setOpen(true);
                    return [4 /*yield*/, update()];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    var handleResultSelect = function (item) { return __awaiter(void 0, void 0, void 0, function () {
        var itemToSend, selectedArray;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!!IS_MULTI) return [3 /*break*/, 1];
                    if (typeof props.onSelectionChange === 'function') {
                        itemToSend = Array.isArray(selected)
                            ? [item.original]
                            : item.original;
                        props.onSelectionChange(itemToSend);
                    }
                    setOpen(false);
                    return [3 /*break*/, 7];
                case 1:
                    if (!!isItemSelected(item)) return [3 /*break*/, 4];
                    if (!(typeof props.onSelectionChange === 'function')) return [3 /*break*/, 3];
                    selectedArray = Array.isArray(selected)
                        ? selected
                        : [];
                    props.onSelectionChange(selectedArray.concat([item.original]));
                    return [4 /*yield*/, update()];
                case 2:
                    _a.sent();
                    _a.label = 3;
                case 3: return [3 /*break*/, 7];
                case 4: return [4 /*yield*/, handleRemoveSelection(item)];
                case 5:
                    _a.sent();
                    return [4 /*yield*/, update()];
                case 6:
                    _a.sent();
                    _a.label = 7;
                case 7: return [2 /*return*/];
            }
        });
    }); };
    var handleInputChange = function (e) {
        setQuery(e.target.value);
        if (props.disableInitialOpen &&
            (e.target.value === '' || !e.target.value)) {
            setOpen(false);
            return;
        }
        if (!open) {
            // Delayed opening because consuming app needs a chance to react to query before can set the loading state or results
            setTimeout(function () {
                setOpen(true);
            }, 1000);
        }
    };
    var handleFocus = function (e) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setFocused(true);
                    if (e.relatedTarget &&
                        e.relatedTarget.type === 'button') {
                        if (open) {
                            setOpen(false);
                        }
                        return [2 /*return*/];
                    }
                    if (open ||
                        !props.results ||
                        (selected &&
                            Array.isArray(selected) &&
                            selected.length > 1 &&
                            open) ||
                        (selected && get(selected, uniqueIdAccessor) && open)) {
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, update()];
                case 1:
                    _a.sent();
                    if (!props.disableInitialOpen) {
                        setOpen(true);
                    }
                    return [2 /*return*/];
            }
        });
    }); };
    var handleBlur = function () {
        setFocused(false);
        if (typeof props.onBlur === 'function') {
            props.onBlur();
        }
    };
    var reset = function (e) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    // Prevent focusing typeahead and opening dropdown
                    e.stopPropagation();
                    if (!rightElementClearsSelection) return [3 /*break*/, 2];
                    if (!IS_MULTI && !Array.isArray(selected)) {
                        setSelected(null);
                    }
                    else {
                        setSelected([]);
                    }
                    if (typeof props.onResetClick === 'function')
                        props.onResetClick();
                    if (typeof props.onSelectionChange === 'function') {
                        if (!IS_MULTI && !Array.isArray(selected)) {
                            props.onSelectionChange(null);
                        }
                        else {
                            props.onSelectionChange([]);
                        }
                    }
                    return [4 /*yield*/, update()];
                case 1:
                    _a.sent();
                    _a.label = 2;
                case 2:
                    setQuery('');
                    return [2 /*return*/];
            }
        });
    }); };
    var handleKeyDown = function (e) { return __awaiter(void 0, void 0, void 0, function () {
        var _a, selectedArray;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _a = e.key;
                    switch (_a) {
                        case 'Backspace': return [3 /*break*/, 1];
                    }
                    return [3 /*break*/, 4];
                case 1:
                    if (!(!props.disableChips &&
                        IS_MULTI &&
                        inputRef &&
                        (query.length === 0 || !query))) return [3 /*break*/, 3];
                    if (!(selected && Array.isArray(selected))) return [3 /*break*/, 3];
                    selectedArray = selected;
                    selectedArray.pop();
                    setSelected(selectedArray);
                    props.onSelectionChange(selectedArray);
                    basicMenuRef.current.handleOnInputKey(e);
                    return [4 /*yield*/, update()];
                case 2:
                    _b.sent();
                    _b.label = 3;
                case 3: return [3 /*break*/, 5];
                case 4:
                    if (basicMenuRef && basicMenuRef.current) {
                        basicMenuRef.current.handleOnInputKey(e, false, IS_MULTI);
                    }
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var onOptionClick = function (e) {
        e.preventDefault();
        e.stopPropagation();
    };
    var onOptionsSelection = function (options) {
        if (props.onOptionsSelectionChange &&
            typeof props.onOptionsSelectionChange === 'function') {
            props.onOptionsSelectionChange(options);
        }
    };
    var handleOnChipClose = function (item) { return __awaiter(void 0, void 0, void 0, function () {
        var currentMenuItem;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    currentMenuItem = Array.isArray(selected) &&
                        selected.find(function (i) { return i[uniqueIdAccessor] === item[uniqueIdAccessor]; });
                    if (!currentMenuItem) return [3 /*break*/, 2];
                    return [4 /*yield*/, handleRemoveSelection(null, currentMenuItem)];
                case 1:
                    _a.sent();
                    _a.label = 2;
                case 2: return [2 /*return*/];
            }
        });
    }); };
    var renderLeftElements = function () {
        return (React.createElement("div", null,
            props.leftElement,
            props && props.options && props.options.length && (React.createElement("div", { className: "acl-Typeahead-options" },
                React.createElement(MultiSelectDropdownButton, { variant: optionsButtonVariant, buttonText: props.optionButtonText, width: "100%", menuItems: props.options, selected: props.selectedOptions, dark: dark, closeOnItemSelect: false, itemLabel: optionNameAccessor, uniqueIdAccessor: optionUniqueIdAccessor, onItemClick: onOptionClick, onSelection: onOptionsSelection }),
                React.createElement("div", { className: "typeaheadDivider" }))),
            IS_MULTI &&
                selected &&
                Array.isArray(selected) &&
                !!selected.length &&
                !props.disableChips && (React.createElement("div", { className: "acl-Typeahead-chips" }, selected &&
                selected.map(function (item, idx) {
                    return (React.createElement(Chip, { key: idx, text: get(item, resultNameAccessor), hideOnClose: false, onClose: function () { return __awaiter(void 0, void 0, void 0, function () {
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0: return [4 /*yield*/, handleOnChipClose(item)];
                                    case 1:
                                        _a.sent();
                                        return [2 /*return*/];
                                }
                            });
                        }); } }));
                })))));
    };
    var getShowRightElement = function () {
        if (!props.disableChips &&
            IS_MULTI &&
            selected &&
            Array.isArray(selected) &&
            selected.length) {
            return 'always';
        }
        return 'whenFilledOnHover';
    };
    var renderLeftElement = function (sel, r) {
        return IS_MULTI &&
            props &&
            props.results &&
            props.results.length > 0 ? (React.createElement("span", { ref: setIconRef, onClick: function (e) { return __awaiter(void 0, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            e.preventDefault();
                            e.stopPropagation();
                            return [4 /*yield*/, handleResultSelect(r)];
                        case 1:
                            _a.sent();
                            return [2 /*return*/];
                    }
                });
            }); } },
            React.createElement(Icon, { name: sel ? 'CheckboxCheck' : 'CheckboxNone' }))) : (React.createElement("span", { ref: setIconRef, className: "hideLeftElement" },
            React.createElement(React.Fragment, null)));
    };
    var renderMenuItems = function () {
        return (results &&
            results.length &&
            results.map(function (r, idx) {
                if (!r.label && r.category) {
                    return React.createElement(MenuCategory, { key: idx, category: r.category });
                }
                var itemSelected = isItemSelected(r);
                var resultProps = {
                    key: idx,
                    label: r.label,
                    subLabel: r.subLabel,
                    className: classNames({
                        'acl-Typeahead-selectedItem': itemSelected
                    }),
                    rightIcon: r.rightIcon,
                    rightIconColor: r.rightIconColor,
                    rightIconText: r.rightIconText,
                    renderMenuItemControls: props.renderMenuItemControls,
                    leftElement: renderLeftElement(itemSelected, r),
                    onSelection: function () { return __awaiter(void 0, void 0, void 0, function () {
                        return __generator(this, function (_a) {
                            switch (_a.label) {
                                case 0: return [4 /*yield*/, handleResultSelect(r)];
                                case 1:
                                    _a.sent();
                                    return [4 /*yield*/, update()];
                                case 2:
                                    _a.sent();
                                    return [2 /*return*/];
                            }
                        });
                    }); }
                };
                return React.createElement(MenuItem, __assign({}, resultProps));
            }));
    };
    return (React.createElement("div", { className: classNames("acl-Typeahead " + (props.className || ''), {
            fluid: fluid,
            'acl-Typeahead-scoped': props && props.options && props.options.length,
            'acl-Typeahead-highlighted-selections': props.allowMultiple
        }), ref: typeaheadRef, onKeyDown: handleKeyDown, style: props.style },
        React.createElement("div", { className: "acl-Typeahead-wrapper" },
            React.createElement(Input, { placeholder: placeholder, onChange: handleInputChange, value: query, onFocus: handleFocus, onClick: handleFocus, onBlur: handleBlur, compact: props.compact, error: error, fluid: fluid, disabled: disabled, variant: variant, ref: setInputRef, leftElement: ((props.options && props.options.length) ||
                    IS_MULTI ||
                    props.leftElement) &&
                    renderLeftElements(), showRightElement: getShowRightElement(), rightElement: !props.disableRightElement && (React.createElement(Button, { variant: ButtonVariant.BORDERLESS, onClick: reset, ultraCompact: true },
                    React.createElement(Icon, { name: "Remove" }))) }),
            React.createElement("div", __assign({ className: "acl-Typeahead-popper" }, attributes.popper, { style: __assign({ zIndex: 1000, visibility: open ? 'visible' : 'hidden' }, styles.popper), ref: setMenuRef }),
                React.createElement(BasicMenu, { ref: basicMenuRef, className: classNames({
                        'acl-Typeahead-loading': loading
                    }), renderEmptyMenuItemWithIconRef: isEmpty$1 ? renderLeftElement() : undefined, width: (inputRef && inputRef.clientWidth) + "px", maxResultRows: maxResultRows, emptyText: noResultsText, infiniteScrollCallback: props.onLoadMore }, loading ? React.createElement(Spinner, null) : renderMenuItems())))));
};

var css_248z$_ = ".acl-ListSelector {\n  border-radius: 4px;\n  border: 1px solid rgba(0, 0, 0, 0.3); }\n  .acl-ListSelector .acl-ListSelector--summary {\n    padding: 10px;\n    margin: 0;\n    border-bottom: 1px solid rgba(0, 0, 0, 0.3); }\n    .acl-ListSelector .acl-ListSelector--summary > .acl-ListSelector--summaryTitle {\n      margin: 0 4px 0 0; }\n  .acl-ListSelector .acl-ListSelector--search {\n    border-bottom: 1px solid rgba(0, 0, 0, 0.3);\n    display: flex;\n    flex-direction: row;\n    justify-content: space-between;\n    align-items: center; }\n    .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--typeahead {\n      flex-grow: 1;\n      padding: 0;\n      margin: 0;\n      position: relative;\n      z-index: 1; }\n      .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--typeahead .acl-Input-wrapper {\n        background-color: transparent;\n        padding: 3px 8px; }\n        .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--typeahead .acl-Input-wrapper:hover, .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--typeahead .acl-Input-wrapper:focus, .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--typeahead .acl-Input-wrapper:focus-within {\n          box-shadow: none; }\n      .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--typeahead .acl-FormControl {\n        padding: 0; }\n        .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--typeahead .acl-FormControl input {\n          padding: 0;\n          height: auto; }\n      .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--typeahead .acl-BasicMenu {\n        width: 100%; }\n      .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--typeahead .acl-Button {\n        padding: 3px 8px; }\n      .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--typeahead .acl-Input-leftElement {\n        margin-right: 0; }\n        .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--typeahead .acl-Input-leftElement > svg {\n          margin: 0 4px 0 3px; }\n        .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--typeahead .acl-Input-leftElement .typeaheadDivider {\n          display: none; }\n    .acl-ListSelector .acl-ListSelector--search .acl-Button {\n      margin: 0;\n      padding: 3px 16px;\n      height: auto; }\n    .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--typeaheadDivider {\n      display: inline-flex;\n      width: 1px;\n      height: 24px;\n      background: rgba(0, 0, 0, 0.1);\n      margin: 0 8px 0 0; }\n    .acl-ListSelector .acl-ListSelector--search .acl-ListSelector--resetButton {\n      margin-right: 8px; }\n  .acl-ListSelector .acl-SelectedItemsList {\n    border: none; }\n";
styleInject(css_248z$_);

var css_248z$$ = ".acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .acl-SelectedItemsList--selectedMenuItem .acl-MenuItem, .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--categorizedMenu .acl-BasicMenu .acl-MenuItem {\n  z-index: 1;\n  box-shadow: inset 0 -2px 0 -1px rgba(0, 0, 0, 0.1);\n  align-items: center; }\n  .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .acl-SelectedItemsList--selectedMenuItem .acl-MenuItem .acl-MenuItem-rightElement, .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--categorizedMenu .acl-BasicMenu .acl-MenuItem .acl-MenuItem-rightElement {\n    margin-right: 8px; }\n  .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .acl-SelectedItemsList--selectedMenuItem .acl-MenuItem .acl-MenuItem-rightElement button, .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--categorizedMenu .acl-BasicMenu .acl-MenuItem .acl-MenuItem-rightElement button {\n    opacity: 0; }\n\n.acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .acl-SelectedItemsList--selectedMenuItem:hover .acl-MenuItem, .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--categorizedMenu .acl-BasicMenu .acl-MenuItem:hover {\n  background-color: #dff3f7;\n  color: inherit; }\n  .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .acl-SelectedItemsList--selectedMenuItem:hover .acl-MenuItem .acl-MenuItem-subLabel, .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--categorizedMenu .acl-BasicMenu .acl-MenuItem:hover .acl-MenuItem-subLabel {\n    color: rgba(0, 0, 0, 0.5); }\n  .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .acl-SelectedItemsList--selectedMenuItem:hover .acl-MenuItem .acl-MenuItem-rightElement button, .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--categorizedMenu .acl-BasicMenu .acl-MenuItem:hover .acl-MenuItem-rightElement button {\n    opacity: 1; }\n\n.acl-SelectedItemsList {\n  border-radius: 4px;\n  border: 1px solid rgba(0, 0, 0, 0.3);\n  overflow: hidden; }\n  .acl-SelectedItemsList .acl-SelectedItemsList--selected {\n    min-height: 120px;\n    overflow: hidden;\n    display: flex;\n    position: relative; }\n    .acl-SelectedItemsList .acl-SelectedItemsList--selected:hover {\n      overflow-y: auto; }\n    .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--empty {\n      align-self: center;\n      text-align: center;\n      width: 100%;\n      color: rgba(0, 0, 0, 0.7);\n      font-size: 14px;\n      font-style: italic;\n      letter-spacing: 0.1px;\n      line-height: 16px; }\n    .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu {\n      width: 100%; }\n      .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .draggable .acl-MenuItem {\n        cursor: move; }\n        .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .draggable .acl-MenuItem:active {\n          cursor: move; }\n        .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .draggable .acl-MenuItem button {\n          opacity: 0; }\n      .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .draggable.dragging {\n        z-index: 2; }\n        .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .draggable.dragging .acl-MenuItem {\n          box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.1);\n          background-color: #dff3f7;\n          color: inherit; }\n          .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .draggable.dragging .acl-MenuItem .acl-MenuItem-subLabel {\n            color: rgba(0, 0, 0, 0.5); }\n      .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .acl-SelectedItemsList--selectedMenuItem .acl-MenuItem .acl-MenuItem-rightElement button {\n        color: unset; }\n        .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu .acl-SelectedItemsList--selectedMenuItem .acl-MenuItem .acl-MenuItem-rightElement button:hover {\n          color: unset; }\n      .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu.dragging .acl-SelectedItemsList--selectedMenuItem:not(.dragging) .acl-MenuItem, .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu.hoverOff .acl-SelectedItemsList--selectedMenuItem:not(.dragging) .acl-MenuItem {\n        background-color: transparent;\n        color: inherit; }\n        .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu.dragging .acl-SelectedItemsList--selectedMenuItem:not(.dragging) .acl-MenuItem .acl-MenuItem-subLabel, .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu.hoverOff .acl-SelectedItemsList--selectedMenuItem:not(.dragging) .acl-MenuItem .acl-MenuItem-subLabel {\n          color: inherit; }\n        .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu.dragging .acl-SelectedItemsList--selectedMenuItem:not(.dragging) .acl-MenuItem .acl-MenuItem-rightElement button, .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--selectedMenu.hoverOff .acl-SelectedItemsList--selectedMenuItem:not(.dragging) .acl-MenuItem .acl-MenuItem-rightElement button {\n          opacity: 0; }\n    .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--categorizedMenu {\n      width: 100%; }\n      .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--categorizedMenu .acl-BasicMenu {\n        box-shadow: none;\n        background: transparent; }\n        .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--categorizedMenu .acl-BasicMenu .acl-MenuItem .acl-MenuItem-rightElement button {\n          opacity: 0; }\n        .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--categorizedMenu .acl-BasicMenu .acl-MenuItem:hover .acl-MenuItem-rightElement button {\n          opacity: 1;\n          color: unset; }\n          .acl-SelectedItemsList .acl-SelectedItemsList--selected .acl-SelectedItemsList--categorizedMenu .acl-BasicMenu .acl-MenuItem:hover .acl-MenuItem-rightElement button:hover {\n            color: unset; }\n";
styleInject(css_248z$$);

var SelectedItemsList = function (_a) {
    var _b = _a.emptyText, emptyText = _b === void 0 ? 'No selected Entities' : _b, _c = _a.resultNameAccessor, resultNameAccessor = _c === void 0 ? 'label' : _c, _d = _a.optionNameAccessor, optionNameAccessor = _d === void 0 ? 'label' : _d, _e = _a.optionUniqueIdAccessor, optionUniqueIdAccessor = _e === void 0 ? 'id' : _e, _f = _a.reorderEnabled, reorderEnabled = _f === void 0 ? false : _f, _g = _a.disableRemoveButton, disableRemoveButton = _g === void 0 ? false : _g, props = __rest(_a, ["emptyText", "resultNameAccessor", "optionNameAccessor", "optionUniqueIdAccessor", "reorderEnabled", "disableRemoveButton"]);
    var _h = useState(props.selected || []), selectedItems = _h[0], setSelectedItems = _h[1];
    var _j = useState(null), draggedItem = _j[0], setDraggedItem = _j[1];
    var _k = useState(false), hoverOff = _k[0], setHoverOff = _k[1];
    var generateOptionsMap = function () {
        var newMap = {};
        if (!props.options) {
            return newMap;
        }
        props.options.forEach(function (option) {
            newMap[option[optionUniqueIdAccessor]] = option[optionNameAccessor];
        });
        return newMap;
    };
    var getCategorizedItems = function (results) {
        var mapped = Array.isArray(results) &&
            results.map(function (r) {
                var menuItem = {
                    id: r[props.uniqueIdAccessor],
                    label: r[resultNameAccessor],
                    subLabel: r[props.subLabelNameAccessor] ||
                        (props.options &&
                            optionNameAccessor &&
                            optionsMap[r[optionUniqueIdAccessor]]) ||
                        null,
                    rightIcon: props.rightIconAccessor && r[props.rightIconAccessor],
                    rightIconColor: props.rightIconColorAccessor &&
                        r[props.rightIconColorAccessor],
                    rightIconText: props.rightIconPopoverText &&
                        r[props.rightIconPopoverText],
                    original: r
                };
                if (props.categoryAccessor) {
                    menuItem[props.categoryAccessor] =
                        r[props.categoryAccessor];
                }
                return menuItem;
            });
        return BasicMenu.getCategorizedItems(mapped, props.categoryAccessor);
    };
    var _l = useState(generateOptionsMap()), optionsMap = _l[0], setOptionsMap = _l[1];
    var _m = useState(getCategorizedItems(props.selected || [])), categorizedItems = _m[0], setCategorizedItems = _m[1];
    useEffect(function () {
        if (!isEqual(selectedItems, props.selected)) {
            setSelectedItems(props.selected || []);
            if (props.categoryAccessor) {
                setCategorizedItems(getCategorizedItems(props.selected || []));
            }
        }
    }, [props.selected]);
    useEffect(function () {
        setCategorizedItems(getCategorizedItems(props.selected || []));
    }, [props.categoryAccessor]);
    useEffect(function () {
        if (props.options && optionNameAccessor) {
            var newMap = generateOptionsMap();
            if (optionsMap !== newMap) {
                setOptionsMap(newMap);
            }
        }
    }, [props.options, optionNameAccessor]);
    var removeItem = function (item) {
        var compareItem = props.categoryAccessor ? item.original : item;
        var newSelectedItems = selectedItems.filter(function (_item) { return !isEqual(compareItem, _item); });
        setSelectedItems(newSelectedItems);
        if (props.categoryAccessor) {
            setCategorizedItems(getCategorizedItems(newSelectedItems || []));
        }
        if (typeof props.onSelectionChange === 'function') {
            props.onSelectionChange(newSelectedItems);
        }
    };
    var onDragStart = function (e, index) {
        setDraggedItem(selectedItems[index]);
        // replace drag ghost image with transparent image
        if (e.dataTransfer) {
            var dragImg = new Image(0, 0);
            dragImg.src =
                'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
            e.dataTransfer.setDragImage(new Image(0, 0), 0, 0);
            e.dataTransfer.effectAllowed = 'move';
        }
    };
    var onDragOver = function (e, index) {
        var draggedOverItem = selectedItems[index];
        if (draggedItem === draggedOverItem) {
            return;
        }
        var items = selectedItems.filter(function (item) { return item !== draggedItem; });
        items.splice(index, 0, draggedItem);
        setSelectedItems(items);
        if (props.onSelectionChange &&
            typeof props.onSelectionChange === 'function') {
            props.onSelectionChange(items);
        }
        e.dataTransfer.clearData();
        setHoverOff(true);
    };
    var onDragEnd = function () {
        setDraggedItem(null);
    };
    var renderMenuItem = function (item, idx) {
        var label = item[resultNameAccessor];
        var subLabel = (props.subLabelNameAccessor && item[props.subLabelNameAccessor]) ||
            (props.options &&
                optionNameAccessor &&
                item[optionUniqueIdAccessor] &&
                optionsMap[item[optionUniqueIdAccessor]]);
        if (props.categoryAccessor) {
            label = item.label;
            subLabel = item.subLabel;
        }
        return (React.createElement(MenuItem, { label: label, key: idx, onClick: item.onClick, subLabel: subLabel, rightElement: React.createElement(Button, { variant: ButtonVariant.BORDERLESS, ultraCompact: true, disabled: disableRemoveButton, onClick: function () { return removeItem(item); } },
                React.createElement(Icon, { name: "Remove" })), rightIcon: props.rightIconAccessor && item[props.rightIconAccessor], rightIconColor: props.rightIconColorAccessor &&
                item[props.rightIconColorAccessor], rightIconText: props.rightIconPopoverText &&
                item[props.rightIconPopoverText], renderMenuItemControls: props.renderMenuItemControls }));
    };
    var renderSelectedItems = function () {
        if (selectedItems && !selectedItems.length) {
            return (React.createElement("span", { className: "acl-SelectedItemsList--empty" }, emptyText));
        }
        if (props.categoryAccessor) {
            return (React.createElement("div", { className: "acl-SelectedItemsList--categorizedMenu" },
                React.createElement(BasicMenu, { width: "100%" }, categorizedItems &&
                    categorizedItems.map(function (item, idx) {
                        if (!item.label &&
                            item.category) {
                            return (React.createElement(MenuCategory, { key: idx, category: item.category }));
                        }
                        return renderMenuItem(item, idx);
                    }))));
        }
        return (React.createElement("div", { className: classNames('acl-SelectedItemsList--selectedMenu', {
                dragging: !!draggedItem,
                hoverOff: hoverOff
            }) }, selectedItems &&
            selectedItems.map(function (item, idx) { return (React.createElement("div", { key: idx, draggable: reorderEnabled, onMouseLeave: function () {
                    if (hoverOff) {
                        setHoverOff(false);
                    }
                }, onDragStart: reorderEnabled
                    ? function (e) { return onDragStart(e, idx); }
                    : undefined, onDragOver: reorderEnabled
                    ? function (e) { return onDragOver(e, idx); }
                    : undefined, onDragEnd: reorderEnabled ? onDragEnd : undefined, className: classNames('acl-SelectedItemsList--selectedMenuItem', {
                    draggable: reorderEnabled,
                    dragging: draggedItem === selectedItems[idx]
                }) }, renderMenuItem(item, idx))); })));
    };
    return (React.createElement("div", { className: "acl-SelectedItemsList" },
        React.createElement("div", { className: "acl-SelectedItemsList--selected" }, renderSelectedItems())));
};

var ListSelector = function (_a) {
    var _b = _a.placeholder, placeholder = _b === void 0 ? 'Add objects' : _b, _c = _a.emptyText, emptyText = _c === void 0 ? 'Please start searching above in the field to select Entities' : _c, _d = _a.uniqueIdAccessor, uniqueIdAccessor = _d === void 0 ? 'label' : _d, _e = _a.resultNameAccessor, resultNameAccessor = _e === void 0 ? 'label' : _e, _f = _a.optionNameAccessor, optionNameAccessor = _f === void 0 ? 'label' : _f, _g = _a.optionUniqueIdAccessor, optionUniqueIdAccessor = _g === void 0 ? 'id' : _g, _h = _a.selectedOptions, selectedOptions = _h === void 0 ? [] : _h, _j = _a.reorderEnabled, reorderEnabled = _j === void 0 ? false : _j, props = __rest(_a, ["placeholder", "emptyText", "uniqueIdAccessor", "resultNameAccessor", "optionNameAccessor", "optionUniqueIdAccessor", "selectedOptions", "reorderEnabled"]);
    var _k = useState(props.selected || []), selectedItems = _k[0], setSelectedItems = _k[1];
    var _l = useState(true), buttonDisabled = _l[0], setButtonDisabled = _l[1];
    var _m = useState(''), query = _m[0], setQuery = _m[1];
    useEffect(function () {
        if (props.resetDisabled) {
            setButtonDisabled(props.resetDisabled);
        }
        else {
            setButtonDisabled(selectedItems && !selectedItems.length);
        }
    }, [selectedItems]);
    useEffect(function () {
        if (!isEqual(selectedItems, props.selected)) {
            setSelectedItems(props.selected || []);
        }
    }, [props.selected]);
    var handleSelectionChange = function (selItems) {
        if (props.onSelectionChange) {
            props.onSelectionChange(selItems);
        }
        setSelectedItems(selItems);
    };
    var handleOptionSelectionChange = function (selOptions) {
        if (props.onOptionsSelectionChange) {
            props.onOptionsSelectionChange(selOptions);
        }
    };
    var handleQueryChange = function (q) {
        if (q !== query &&
            props.onQueryChange &&
            typeof props.onQueryChange === 'function') {
            props.onQueryChange(q);
        }
        setQuery(q);
    };
    var resetSelection = function () {
        safeInvoke(props.onResetClick);
    };
    var renderSummary = function () {
        return (React.createElement("div", { className: "acl-ListSelector--summary" },
            React.createElement("span", { className: "acl-ListSelector--summaryTitle" }, "Summary:"),
            React.createElement("span", { className: "acl-ListSelector--summaryText" }, props.summary)));
    };
    return (React.createElement("div", { className: "acl-ListSelector " + (props.className || ''), style: props.style },
        props.summary && renderSummary(),
        React.createElement("div", { className: "acl-ListSelector--search" },
            React.createElement(Typeahead, __assign({}, props, { className: "acl-ListSelector--typeahead", results: props.results, options: props.options, optionNameAccessor: optionNameAccessor, optionUniqueIdAccessor: optionUniqueIdAccessor, selected: selectedItems, selectedOptions: selectedOptions, compact: true, disableChips: true, optionsButtonVariant: ButtonVariant.STATIC_BORDERLESS, onSelectionChange: handleSelectionChange, onOptionsSelectionChange: handleOptionSelectionChange, allowMultiple: true, placeholder: placeholder, onQueryChange: handleQueryChange, variant: InputVariant.BORDERLESS, leftElement: React.createElement(Icon, { name: "Search" }), uniqueIdAccessor: uniqueIdAccessor, resultNameAccessor: resultNameAccessor, rightElementClearsSelection: false, noResultsText: props.noResultsText })),
            React.createElement("div", { className: "acl-ListSelector--typeaheadDivider" }),
            React.createElement(Button, { className: "acl-ListSelector--resetButton", variant: ButtonVariant.STATIC_BORDERLESS, disabled: buttonDisabled, onClick: resetSelection }, "Reset")),
        React.createElement("div", { className: "acl-ListSelector--selected" },
            React.createElement(SelectedItemsList, { selected: selectedItems, options: props.options, emptyText: emptyText, resultNameAccessor: resultNameAccessor, optionNameAccessor: optionNameAccessor, optionUniqueIdAccessor: optionUniqueIdAccessor, categoryAccessor: props.categoryAccessor, reorderEnabled: reorderEnabled, onSelectionChange: handleSelectionChange, renderMenuItemControls: props.renderMenuItemControls, subLabelNameAccessor: props.subLabelNameAccessor, disableRemoveButton: props.disabled }))));
};

var css_248z$10 = ".acl-FormRepeater {\n  display: block;\n  padding: 0 16px;\n  overflow: auto; }\n  .acl-FormRepeater .acl-FormRepeaterRow {\n    display: flex;\n    white-space: nowrap;\n    align-items: center;\n    min-height: 48px;\n    padding: 8px 0;\n    border-bottom: 1px solid #e2e2e2; }\n    .acl-FormRepeater .acl-FormRepeaterRow > * {\n      display: flex;\n      align-self: stretch;\n      align-items: center; }\n    .acl-FormRepeater .acl-FormRepeaterRow:first-child {\n      border-top: 1px solid #e2e2e2; }\n    .acl-FormRepeater .acl-FormRepeaterRow.autoDivide :not(:first-child):before {\n      content: '';\n      align-self: stretch;\n      flex: 0 0 1px;\n      background: #e2e2e2;\n      margin: 0 16px; }\n  .acl-FormRepeater acl-ngx-form-repeater-row .acl-FormRepeaterRow:first-child {\n    border-top: none; }\n  .acl-FormRepeater acl-ngx-form-repeater-row .acl-FormRepeaterRow.autoDivide :nth-child(2):before {\n    display: none; }\n  .acl-FormRepeater acl-ngx-form-repeater-row .acl-FormRepeaterRow.autoDivide :not(:nth-child(2)):before {\n    content: '';\n    align-self: stretch;\n    flex: 0 0 1px;\n    background: #e2e2e2;\n    margin: 0 16px; }\n";
styleInject(css_248z$10);

var FormRepeater = function (props) {
    return (React.createElement("div", { className: "acl-FormRepeater " + (props.className || ''), style: __assign({}, props.style) }, props.children));
};

var FormRepeaterRow = function (props) {
    return (React.createElement("section", { className: classNames('acl-FormRepeaterRow', props.className, {
            autoDivide: props.autoDivide
        }), style: __assign({}, props.style) }, props.children));
};

var css_248z$11 = ".acl-BooleanTreeCard {\n  display: flex; }\n  .acl-BooleanTreeCard .acl-BooleanTreeCard--container {\n    position: relative;\n    overflow: hidden;\n    width: 450px; }\n    .acl-BooleanTreeCard .acl-BooleanTreeCard--container .acl-BooleanTreeCard--body {\n      position: relative;\n      display: flex;\n      max-height: 100vh;\n      background-color: #f6f6f6; }\n      .acl-BooleanTreeCard .acl-BooleanTreeCard--container .acl-BooleanTreeCard--body .acl-BooleanTreeCard--bodyContent {\n        padding: 16px;\n        border: 1px solid #ababab;\n        border-radius: 4px;\n        width: 100%;\n        position: relative;\n        display: flex; }\n    .acl-BooleanTreeCard .acl-BooleanTreeCard--container .acl-BooleanTreeCard--header {\n      position: relative;\n      transition: background-color 0.3s ease; }\n      .acl-BooleanTreeCard .acl-BooleanTreeCard--container .acl-BooleanTreeCard--header .acl-BooleanTreeCard--headerContent {\n        position: relative;\n        border: 1px solid #ababab;\n        border-radius: 4px 4px 0 0;\n        display: flex;\n        height: 64px;\n        overflow: hidden; }\n        .acl-BooleanTreeCard .acl-BooleanTreeCard--container .acl-BooleanTreeCard--header .acl-BooleanTreeCard--headerContent .acl-BooleanTreeCard--toggle {\n          flex: 0;\n          border-right: 1px solid #ababab;\n          border-radius: 0;\n          padding: 26px 15px;\n          height: auto;\n          display: flex;\n          align-items: center; }\n          .acl-BooleanTreeCard .acl-BooleanTreeCard--container .acl-BooleanTreeCard--header .acl-BooleanTreeCard--headerContent .acl-BooleanTreeCard--toggle svg {\n            transition: all 0.3s ease;\n            transform: rotate(0deg);\n            fill: rgba(0, 0, 0, 0.6);\n            opacity: 1; }\n        .acl-BooleanTreeCard .acl-BooleanTreeCard--container .acl-BooleanTreeCard--header .acl-BooleanTreeCard--headerContent .acl-BooleanTreeCard--renderHeader {\n          padding: 16px;\n          align-items: center;\n          display: flex; }\n      .acl-BooleanTreeCard .acl-BooleanTreeCard--container .acl-BooleanTreeCard--header.draggable:hover {\n        cursor: move; }\n      .acl-BooleanTreeCard .acl-BooleanTreeCard--container .acl-BooleanTreeCard--header:hover {\n        background-color: #dff3f7; }\n    .acl-BooleanTreeCard .acl-BooleanTreeCard--container .acl-BooleanTreeCard--body.draggable {\n      cursor: inherit; }\n    .acl-BooleanTreeCard .acl-BooleanTreeCard--container .acl-BooleanTreeCard--body .acl-BooleanTreeCard--bodyContent {\n      border-top: none;\n      border-radius: 0 0 4px 4px; }\n      .acl-BooleanTreeCard .acl-BooleanTreeCard--container .acl-BooleanTreeCard--body .acl-BooleanTreeCard--bodyContent:before {\n        display: none; }\n    .acl-BooleanTreeCard .acl-BooleanTreeCard--container.collapsed .acl-BooleanTreeCard--body {\n      max-height: 0; }\n    .acl-BooleanTreeCard .acl-BooleanTreeCard--container.collapsed .acl-BooleanTreeCard--headerContent {\n      border-radius: 4px; }\n      .acl-BooleanTreeCard .acl-BooleanTreeCard--container.collapsed .acl-BooleanTreeCard--headerContent .acl-BooleanTreeCard--toggle svg {\n        transform: rotate(-90deg); }\n  .acl-BooleanTreeCard .acl-BooleanTreeCard--toolbar {\n    position: absolute;\n    right: 0;\n    padding-right: 4px;\n    text-align: right;\n    display: flex;\n    height: 100%;\n    border-radius: 0 4px 4px 0;\n    align-items: center;\n    top: 0;\n    z-index: 10; }\n    .acl-BooleanTreeCard .acl-BooleanTreeCard--toolbar:before {\n      content: '';\n      width: 48px;\n      height: 100%;\n      right: 100%;\n      position: absolute;\n      z-index: 3; }\n    .acl-BooleanTreeCard .acl-BooleanTreeCard--toolbar .acl-Button {\n      padding: 0;\n      width: 32px;\n      margin: 0 4px; }\n      .acl-BooleanTreeCard .acl-BooleanTreeCard--toolbar .acl-Button svg {\n        fill: #777777;\n        opacity: 1; }\n  .acl-BooleanTreeCard .acl-BooleanTreeCard--headerContent:hover .acl-BooleanTreeCard--toolbar {\n    background: #dff3f7; }\n    .acl-BooleanTreeCard .acl-BooleanTreeCard--headerContent:hover .acl-BooleanTreeCard--toolbar:before {\n      background: linear-gradient(90deg, rgba(223, 243, 247, 0) 0%, #dff3f7 100%); }\n  .acl-BooleanTreeCard .acl-BooleanTreeCard--rightIconPopover > div {\n    height: 100%; }\n    .acl-BooleanTreeCard .acl-BooleanTreeCard--rightIconPopover > div > div {\n      display: flex;\n      height: 100%; }\n  .acl-BooleanTreeCard .acl-BooleanTreeCard--rightIcon {\n    display: flex;\n    align-self: center;\n    margin-left: 8px; }\n";
styleInject(css_248z$11);

var BooleanTreeCard = function (_a) {
    var _b = _a.dragEnabled, dragEnabled = _b === void 0 ? true : _b, _c = _a.collapsable, collapsable = _c === void 0 ? true : _c, _d = _a.isCollapsed, isCollapsed = _d === void 0 ? true : _d, props = __rest(_a, ["dragEnabled", "collapsable", "isCollapsed"]);
    var _e = useState(false), showButtons = _e[0], setShowButtons = _e[1];
    var _f = useState(isCollapsed), collapsed = _f[0], setCollapsed = _f[1];
    var cardRef = useRef(null);
    var setTempHeight = function () {
        if (props.node) {
            props.node.tempHeight =
                cardRef.current && cardRef.current.clientHeight + 24;
            props.node.path = props.path;
        }
        if (props.resetData) {
            props.resetData();
        }
    };
    useEffect(function () {
        setTempHeight();
        if ((props.node && props.node.isDragging) || props.isDragging) {
            setCollapsed(true);
        }
    }, [cardRef, collapsed, props.node, props.isDragging]);
    var onCardDrag = function () {
        setCollapsed(true);
        setTimeout(function () {
            setTempHeight();
        }, 100);
        props.onDrag(props.node);
    };
    var renderRightIcon = function () { return (React.createElement(Icon, { color: props.rightIconColor, name: props.rightIcon, className: "acl-BooleanTreeCard--rightIcon" })); };
    return (React.createElement("div", { className: "acl-BooleanTreeCard " + (props.className || ''), onClick: function () { return onCardDrag; } },
        React.createElement("div", { className: classNames('acl-BooleanTreeCard--container', {
                collapsable: collapsable,
                collapsed: collapsed,
                isActive: props.isActive
            }), ref: cardRef },
            React.createElement("div", { className: classNames('acl-BooleanTreeCard--header', {
                    draggable: dragEnabled
                }), onMouseOver: function () { return setShowButtons(true); }, onMouseLeave: function () { return setShowButtons(false); } },
                React.createElement("div", { className: "acl-BooleanTreeCard--headerContent" },
                    collapsable && (React.createElement(Button, { ultraCompact: true, className: "acl-BooleanTreeCard--toggle", icon: "TriangleDown", onClick: function () { return setCollapsed(!collapsed); }, variant: ButtonVariant.BORDERLESS })),
                    React.createElement("div", { className: "acl-BooleanTreeCard--renderHeader" }, props.renderHeader ? (props.renderHeader()) : (React.createElement("span", null, props.title))),
                    showButtons && (React.createElement("div", { className: "acl-BooleanTreeCard--toolbar" }, props.buttons &&
                        props.buttons.map(function (b, i) { return (React.createElement(Button, __assign({}, b, { ultraCompact: true, variant: ButtonVariant.BORDERLESS, key: i, onClick: function () {
                                return b.onButtonClick({
                                    node: props.node,
                                    path: props.path,
                                    treeIndex: props.treeIndex
                                });
                            } }))); }))))),
            props.renderCard && (React.createElement("div", { className: classNames('acl-BooleanTreeCard--body', {
                    draggable: dragEnabled
                }), onDrag: function () { return dragEnabled && props.onDrag(props.node); } },
                React.createElement("div", { className: "acl-BooleanTreeCard--bodyContent" }, props.renderCard())))),
        props.rightIcon &&
            (props.renderRightIconPopover ? (React.createElement("div", { className: "acl-BooleanTreeCard--rightIconPopover" },
                React.createElement(Popover, { openOn: "hover", renderTrigger: renderRightIcon }, props.renderRightIconPopover()))) : (renderRightIcon()))));
};

var css_248z$12 = ".acl-BooleanSummary {\n  color: #000000;\n  display: inline-flex;\n  position: relative;\n  transition: opacity 0.3s ease; }\n  .acl-BooleanSummary .acl-BooleanSummary-section {\n    height: 32px;\n    align-items: center;\n    display: flex; }\n    .acl-BooleanSummary .acl-BooleanSummary-section.border {\n      box-sizing: border-box;\n      border: 1px dashed black;\n      padding: 8px; }\n  .acl-BooleanSummary .andColor {\n    color: #52a145;\n    font-weight: bold; }\n  .acl-BooleanSummary .orColor {\n    color: #8240a0;\n    font-weight: bold; }\n  .acl-BooleanSummary .acl-BooleanSummary--text {\n    align-items: center;\n    display: flex;\n    padding: 0 8px; }\n    .acl-BooleanSummary .acl-BooleanSummary--text > span {\n      margin-right: 8px; }\n  .acl-BooleanSummary .acl-BooleanSummary--rule {\n    cursor: pointer; }\n    .acl-BooleanSummary .acl-BooleanSummary--rule:hover, .acl-BooleanSummary .acl-BooleanSummary--rule.selected {\n      color: #009ebd;\n      text-decoration: underline; }\n  .acl-BooleanSummary.hovered .acl-BooleanSummary--text {\n    opacity: 0.3; }\n  .acl-BooleanSummary.hovered .acl-BooleanSummary-section > span:not(.selected) {\n    opacity: 0.3; }\n  .acl-BooleanSummary.hovered .acl-BooleanSummary-section .orColor,\n  .acl-BooleanSummary.hovered .acl-BooleanSummary-section .andColor,\n  .acl-BooleanSummary.hovered .acl-BooleanSummary-section .bracket {\n    opacity: 0.3; }\n";
styleInject(css_248z$12);

var BooleanTreeSummary = function (props) {
    var _a = useState(props.hovered || false), hovered = _a[0], setHovered = _a[1];
    var _b = useState(props.selectedNodeKey || null), selectedNode = _b[0], setSelectedNode = _b[1];
    var cssClasses = classNames('acl-BooleanSummary-section', {
        border: props.showBorder
    });
    var conjunctionTextClasses = function (andOrVal) {
        return classNames({
            andColor: andOrVal === 'AND',
            orColor: andOrVal === 'OR'
        });
    };
    var renderConjunction = function (conjunction) { return (React.createElement("span", { className: conjunctionTextClasses(conjunction) },
        conjunction,
        "\u00A0")); };
    var shouldShowNotKeyword = function (isExcluded, isExclusionGroup) {
        if (!isExclusionGroup && isExcluded === true) {
            // includes array, excluded item
            return true;
        }
        if (isExclusionGroup && !isExcluded) {
            //excludes array, included item
            return true;
        }
        return false; // dont show
    };
    var hasInclusions = function () {
        return (props.inclusions && props.inclusions && props.inclusions.length > 0);
    };
    var renderRule = function (rule, isExclusionGroup) { return (React.createElement("span", { className: classNames('acl-BooleanSummary--rule', {
            selected: selectedNode === rule.nodeKey
        }), key: rule.nodeKey, onClick: function () { return props.onSelectRule && props.onSelectRule(rule); }, onMouseEnter: function () {
            setHovered(true);
            props.onHoverRule(rule);
            setSelectedNode(rule.nodeKey);
        }, onMouseLeave: function () {
            setHovered(false);
            props.onHoverRule(null);
            setSelectedNode(null);
        } },
        shouldShowNotKeyword(rule.isExcluded, isExclusionGroup) && 'NOT ',
        rule.nodeKey)); };
    var renderTree = function (tree, isExclusionGroup) {
        if (isExclusionGroup === void 0) { isExclusionGroup = false; }
        return (React.createElement(React.Fragment, { key: tree.nodeKey }, tree.children &&
            tree.children.map(function (c, i) {
                if (!c.isOperator) {
                    var rule = c;
                    return (React.createElement(React.Fragment, { key: i },
                        renderRule(rule, isExclusionGroup),
                        "\u00A0",
                        i < tree.children.length - 1 &&
                            renderConjunction(tree.nodeType)));
                }
                var subTree = c;
                return (React.createElement(React.Fragment, { key: i },
                    React.createElement("span", { className: "bracket" }, "("),
                    "\u00A0",
                    renderTree(subTree, isExclusionGroup),
                    React.createElement("span", { className: "bracket" }, ")"),
                    "\u00A0",
                    i < tree.children.length - 1 &&
                        renderConjunction(tree.nodeType)));
            })));
    };
    useEffect(function () {
        setSelectedNode(props.selectedNodeKey);
    }, [props.selectedNodeKey]);
    return (React.createElement("div", { className: classNames('acl-BooleanSummary', { hovered: hovered }) },
        hasInclusions() && (React.createElement("span", { className: cssClasses, style: {
                borderColor: props.inclusionBorderColor || ColorTheme.amobee40
            } }, props.inclusions.map(function (incTree) {
            return renderTree(incTree, false);
        }))),
        props.exclusions &&
            props.exclusions &&
            props.exclusions.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement("span", { className: "acl-BooleanSummary--text" },
                hasInclusions() && (React.createElement("span", { className: "andColor" }, "AND ")),
                "NOT"),
            React.createElement("span", { className: cssClasses, style: {
                    borderColor: props.exclusionBorderColor ||
                        ColorTheme.red40
                } },
                "(\u00A0",
                props.exclusions.map(function (excTree) {
                    return renderTree(excTree, true);
                }),
                ")")))));
};

var css_248z$13 = ".acl-BooleanTreeOperator {\n  display: flex;\n  cursor: pointer;\n  color: #17520e;\n  font-weight: bold;\n  background-color: #e9f3e8;\n  border-radius: 4px;\n  padding: 8px;\n  transition: all 0.3s ease;\n  box-shadow: none;\n  margin-top: 2px; }\n  .acl-BooleanTreeOperator .acl-BooleanTreeOperator--icon {\n    margin-right: 8px; }\n  .acl-BooleanTreeOperator .acl-BooleanTreeOperator--collapse {\n    margin-left: 8px; }\n  .acl-BooleanTreeOperator.matchAny {\n    color: #623084;\n    background-color: #f5eff8; }\n    .acl-BooleanTreeOperator.matchAny.active, .acl-BooleanTreeOperator.matchAny:hover {\n      background-color: #eadff2;\n      box-shadow: none; }\n    .acl-BooleanTreeOperator.matchAny:not(.disabled):hover, .acl-BooleanTreeOperator.matchAny:not(.disabled):hover:not(:focus):not(.focus):hover, .acl-BooleanTreeOperator.matchAny:not(.disabled):active, .acl-BooleanTreeOperator.matchAny:not(.disabled).active {\n      background-color: #eadff2;\n      box-shadow: none; }\n  .acl-BooleanTreeOperator.active, .acl-BooleanTreeOperator:hover {\n    background-color: #d4e6d1;\n    box-shadow: none; }\n  .acl-BooleanTreeOperator:not(.disabled):hover, .acl-BooleanTreeOperator:not(.disabled):hover:not(:focus):not(.focus):hover, .acl-BooleanTreeOperator:not(.disabled):active, .acl-BooleanTreeOperator:not(.disabled).active {\n    background-color: #d4e6d1;\n    box-shadow: none; }\n  .acl-BooleanTreeOperator:not(.disabled):focus:not(:active):not(.active):not(.error), .acl-BooleanTreeOperator:not(.disabled).focus:not(:active):not(.active):not(.error) {\n    box-shadow: none; }\n\n.acl-BooleanTreeOperator--toolbar {\n  position: absolute;\n  left: 100%;\n  padding-left: 8px;\n  text-align: right;\n  display: flex;\n  height: 100%;\n  border-radius: 0 4px 4px 0;\n  align-items: center;\n  top: 0;\n  z-index: 2; }\n  .acl-BooleanTreeOperator--toolbar .acl-Button {\n    padding: 0;\n    width: 32px;\n    margin: 0 4px; }\n    .acl-BooleanTreeOperator--toolbar .acl-Button svg {\n      fill: #777777;\n      opacity: 1; }\n  .acl-BooleanTreeOperator--toolbar .acl-BooleanTree--AddNode {\n    margin-top: 4px; }\n\n.acl-BooleanTreeOperator--rightIcon {\n  display: flex;\n  align-self: center;\n  margin-left: 8px; }\n\n.acl-BasicMenu.acl-BooleanTreeOperator--wrapper .acl-MenuItem.active {\n  font-weight: bold; }\n";
styleInject(css_248z$13);

var BooleanTreeOperator = function (_a) {
    var _b = _a.nodeType, nodeType = _b === void 0 ? 'AND' : _b, _c = _a.hasDropdown, hasDropdown = _c === void 0 ? true : _c, props = __rest(_a, ["nodeType", "hasDropdown"]);
    var nodeRef = useRef(null);
    useEffect(function () {
        if (props.node) {
            props.node.tempHeight = 57;
        }
    }, []);
    var renderLabel = function () {
        if (nodeType === 'OR') {
            return props.orLabel ? props.orLabel : 'Match Any';
        }
        return props.andLabel ? props.andLabel : 'Match All';
    };
    var renderTrigger = function () {
        return (React.createElement("div", { className: classNames('acl-BooleanTreeOperator', {
                matchAny: nodeType === 'OR'
            }) },
            props.icon && (React.createElement(Icon, { color: props.iconColor, name: props.icon, className: "acl-BooleanTreeOperator--icon" })),
            React.createElement("div", { className: "acl-BooleanTreeOperator--label" }, renderLabel()),
            hasDropdown && (React.createElement(Icon, { name: "ChevronDown", className: "acl-BooleanTreeOperator--collapse" }))));
    };
    var items = [
        {
            label: 'Match All',
            onClick: function () {
                return props.onSelection &&
                    props.onSelection({
                        nodeType: 'AND',
                        node: props.node,
                        path: props.path,
                        treeIndex: props.treeIndex
                    });
            }
        },
        {
            label: 'Match Any',
            onClick: function () {
                return props.onSelection &&
                    props.onSelection({
                        nodeType: 'OR',
                        node: props.node,
                        path: props.path,
                        treeIndex: props.treeIndex
                    });
            }
        }
    ];
    return (React.createElement("div", { className: "acl-BooleanTreeOperator--wrapper", ref: nodeRef },
        React.createElement(DropdownButton, { className: "acl-BooleanTreeOperator--dropdown", triggerElement: renderTrigger(), disabled: !hasDropdown, menuItems: items, minWidth: nodeType === 'OR' ? 135 : 125, maxWidth: nodeType === 'OR' ? 135 : 125 }),
        React.createElement("div", { className: "acl-BooleanTreeOperator--toolbar" }, props.node &&
            props.node.buttons &&
            props.node.buttons.map(function (b, i) { return (React.createElement(Button, __assign({}, b, { ultraCompact: true, variant: ButtonVariant.BORDERLESS, key: i, onClick: function () {
                    return b.onButtonClick({
                        node: props.node,
                        path: props.path,
                        treeIndex: props.treeIndex
                    });
                } }))); }))));
};

var NodeContentRenderer = function (_a) {
    var scaffoldBlockPxWidth = _a.scaffoldBlockPxWidth, connectDragPreview = _a.connectDragPreview, connectDragSource = _a.connectDragSource, isDragging = _a.isDragging, canDrop = _a.canDrop, canDrag = _a.canDrag, node = _a.node, path = _a.path, draggedNode = _a.draggedNode, isSearchMatch = _a.isSearchMatch, isSearchFocus = _a.isSearchFocus, className = _a.className, style = _a.style, treeIndex = _a.treeIndex, 
    /* eslint-disable @typescript-eslint/no-unused-vars */
    toggleChildrenVisibility = _a.toggleChildrenVisibility, didDrop = _a.didDrop, 
    /* eslint-disable @typescript-eslint/no-unused-vars */
    rowDirection = _a.rowDirection, 
    /* eslint-disable @typescript-eslint/no-unused-vars */
    treeId = _a.treeId, 
    /* eslint-disable @typescript-eslint/no-unused-vars */
    isOver = _a.isOver, parentNode = _a.parentNode, // Needed for dndManager
    props = __rest(_a, ["scaffoldBlockPxWidth", "connectDragPreview", "connectDragSource", "isDragging", "canDrop", "canDrag", "node", "path", "draggedNode", "isSearchMatch", "isSearchFocus", "className", "style", "treeIndex", "toggleChildrenVisibility", "didDrop", "rowDirection", "treeId", "isOver", "parentNode"]);
    useEffect(function () {
        var img = new Image();
        img.src =
            'data:image/svg+xml;base64,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';
        img.onload = function () { return connectDragPreview(img); };
    }, []);
    var isDescendant = function (older, younger) {
        return (!!older.children &&
            typeof older.children !== 'function' &&
            older.children.some(function (child) {
                return child === younger || isDescendant(child, younger);
            }));
    };
    var isDraggedDescendant = draggedNode && isDescendant(draggedNode, node);
    var isLandingPadActive = !didDrop && isDragging && canDrop;
    var _treeIndex = treeIndex || node.treeIndex;
    var _path = path || node.path;
    var _node = node || node.node;
    var nodeContent = function () {
        return (React.createElement("div", { className: classNames('row', className, {
                rowLandingPad: isLandingPadActive,
                rowCancelPad: isLandingPadActive && !canDrop,
                rowSearchMatch: isSearchMatch,
                rowSearchFocus: isSearchFocus,
                isDragging: isDragging || node.isDragging,
                or: renderDotClass()
            }), style: __assign({ opacity: isDraggedDescendant ? 0.5 : 1 }, style) },
            node.isOperator && (React.createElement(BooleanTreeOperator, { nodeType: node.nodeType, resetData: node.resetData, node: _node, treeIndex: _treeIndex, parentNode: parentNode, path: _path, andLabel: node.andLabel, orLabel: node.orLabel, icon: node.icon, iconColor: node.iconColor, hasDropdown: node.hasDropdown, onSelection: node.onSelection, onDrag: node.onDrag })),
            !node.isOperator && (React.createElement(BooleanTreeCard, { resetData: node.resetData, node: _node, treeIndex: _treeIndex, className: "rowContents", renderHeader: node.renderHeader, renderCard: node.renderCard, renderRightIconPopover: node.renderRightIconPopover, dragEnabled: node.dragEnabled, buttons: node.buttons, isCollapsed: node.isCollapsed, collapsable: node.collapsable, title: node.title, parentNode: parentNode, path: _path, rightIcon: node.rightIcon, rightIconColor: node.rightIconColor, onDrag: node.onDrag, isDragging: node.isDragging || isDragging }))));
    };
    var renderDotClass = function () {
        if (parentNode) {
            return parentNode.nodeType === 'OR' ? true : false;
        }
        return node.nodeType === 'OR' ? true : false;
    };
    var renderChildLines = function () {
        if (node.children && node.children.length) {
            if (!isDragging || canDrop) {
                return (React.createElement("div", { style: { width: scaffoldBlockPxWidth }, className: "lineChildren" }));
            }
        }
    };
    var isLastItem = false;
    if (parentNode && parentNode.children && parentNode.children.length > 0) {
        isLastItem =
            parentNode.children[parentNode.children.length - 1].treeIndex ===
                node.treeIndex;
    }
    ////parentNode.children[0].treeIndex === node.treeIndex;
    /*const isLastItem =
        parentNode?.children[parentNode?.children?.length - 1 ?? 0]
            .treeIndex === node.treeIndex; */
    return (React.createElement("div", __assign({ style: { height: '100%' } }, props, { className: classNames('acl-BooleanTreeNodeWrapper', {
            isActive: node.isActive
        }) }),
        React.createElement("div", null,
            React.createElement("div", { className: classNames('acl-BooleanTreeCard--conjuction', {
                    display: !node.isOperator && // if it's an operator, we don't weant conjunction shown.
                        !(isDragging || node.isDragging) && // if it's being dragged,  we don't weant conjunction shown.
                        !isLastItem,
                    or: renderDotClass()
                }) }, renderDotClass() ? 'OR' : 'AND'),
            React.createElement("div", { style: { left: -0.5 * scaffoldBlockPxWidth }, className: classNames('acl-BooleanTreeNode-dot', {
                    isRule: !node.isOperator,
                    isOperator: node.isOperator,
                    rowLandingPad: isLandingPadActive,
                    isDragging: isDragging || node.isDragging,
                    rowWrapperDragDisabled: !canDrop || !canDrag,
                    or: renderDotClass()
                }) }, node.isOperator ? null : node.nodeKey),
            renderChildLines()),
        React.createElement("div", { className: classNames('rowWrapper', {
                rowWrapperDragDisabled: !canDrag || !canDrop
            }) }, canDrag
            ? connectDragSource(nodeContent(), { dropEffect: 'copy' })
            : nodeContent())));
};

var css_248z$14 = ".acl-BooleanTree {\n  position: relative;\n  overflow: hidden;\n  /**\n * The outline of where the element will go if dropped, displayed while dragging\n */\n  /**\n * Alternate appearance of the landing pad when the dragged location is invalid\n */\n  /**\n   * Line for under a node with children\n   */ }\n  .acl-BooleanTree .rst__tree {\n    position: relative;\n    left: -55px; }\n  .acl-BooleanTree .rst__lineHalfHorizontalRight::before,\n  .acl-BooleanTree .rst__lineFullVertical::after,\n  .acl-BooleanTree .rst__lineHalfVerticalTop::after,\n  .acl-BooleanTree .rst__lineHalfVerticalBottom::after,\n  .acl-BooleanTree .rst__lineChildren::after {\n    background: #e2e2e2; }\n  .acl-BooleanTree .rowWrapper {\n    box-sizing: border-box;\n    margin-top: 12px; }\n    .acl-BooleanTree .rowWrapper .row {\n      white-space: nowrap;\n      display: flex; }\n      .acl-BooleanTree .rowWrapper .row > * {\n        box-sizing: border-box; }\n      .acl-BooleanTree .rowWrapper .row.isDragging .acl-BooleanTreeCard--headerContent {\n        border: 1px dashed #ddd;\n        background-color: #ddd;\n        box-shadow: none !important;\n        opacity: 0.3; }\n    .acl-BooleanTree .rowWrapper.rowWrapperDragDisabled {\n      cursor: default; }\n      .acl-BooleanTree .rowWrapper.rowWrapperDragDisabled .isDragging .acl-BooleanTreeCard--headerContent {\n        border: 1px dashed #ddd;\n        background-color: #ddd !important;\n        box-shadow: none !important;\n        opacity: 0.3; }\n  .acl-BooleanTree .rowLandingPad, .acl-BooleanTree .rowCancelPad {\n    background-color: #e9f3e8;\n    border: 1px dashed #17520e;\n    box-shadow: none !important;\n    border-radius: 4px;\n    outline: none !important;\n    max-height: 64px; }\n    .acl-BooleanTree .rowLandingPad.or, .acl-BooleanTree .or.rowCancelPad {\n      background-color: #f5eff8;\n      border-color: #692a89; }\n    .acl-BooleanTree .rowLandingPad *, .acl-BooleanTree .rowCancelPad * {\n      opacity: 0 !important; }\n  .acl-BooleanTree .acl-BooleanTreeNode-dot {\n    border-radius: 50%;\n    height: 25px;\n    z-index: 2; }\n    .acl-BooleanTree .acl-BooleanTreeNode-dot.isDragging.isRule.rowLandingPad, .acl-BooleanTree .acl-BooleanTreeNode-dot.isDragging.isRule.rowCancelPad {\n      background-color: #e9f3e8;\n      border: 1px dashed #17520e;\n      color: transparent; }\n      .acl-BooleanTree .acl-BooleanTreeNode-dot.isDragging.isRule.rowLandingPad.or, .acl-BooleanTree .acl-BooleanTreeNode-dot.isDragging.isRule.or.rowCancelPad {\n        background-color: #f5eff8;\n        border-color: #692a89; }\n    .acl-BooleanTree .acl-BooleanTreeNode-dot.isDragging.isRule.rowWrapperDragDisabled {\n      border: 1px dashed #eee;\n      background-color: #eee;\n      color: transparent; }\n  .acl-BooleanTree .rowCancelPad::before {\n    background-color: #e6a8ad; }\n  .acl-BooleanTree .lineChildren {\n    height: 100%;\n    display: inline-block;\n    position: absolute; }\n    .acl-BooleanTree .lineChildren::after {\n      content: '';\n      position: absolute;\n      background-color: #e2e2e2;\n      width: 1px;\n      left: 50%;\n      bottom: 0;\n      height: 24px; }\n";
styleInject(css_248z$14);

var TreeNodeRenderer = function (_a) {
    var children = _a.children, listIndex = _a.listIndex, 
    /* eslint-disable @typescript-eslint/no-unused-vars */
    swapFrom = _a.swapFrom, 
    /* eslint-disable @typescript-eslint/no-unused-vars */
    swapLength = _a.swapLength, swapDepth = _a.swapDepth, scaffoldBlockPxWidth = _a.scaffoldBlockPxWidth, lowerSiblingCounts = _a.lowerSiblingCounts, connectDropTarget = _a.connectDropTarget, isOver = _a.isOver, draggedNode = _a.draggedNode, canDrop = _a.canDrop, treeIndex = _a.treeIndex, 
    /* eslint-disable @typescript-eslint/no-unused-vars */
    treeId = _a.treeId, // Delete from props
    /* eslint-disable @typescript-eslint/no-unused-vars */
    getPrevRow = _a.getPrevRow, // Delete from props
    /* eslint-disable @typescript-eslint/no-unused-vars */
    node = _a.node, // Delete from props
    /* eslint-disable @typescript-eslint/no-unused-vars */
    path = _a.path, // Delete from props
    rowDirection = _a.rowDirection, props = __rest(_a, ["children", "listIndex", "swapFrom", "swapLength", "swapDepth", "scaffoldBlockPxWidth", "lowerSiblingCounts", "connectDropTarget", "isOver", "draggedNode", "canDrop", "treeIndex", "treeId", "getPrevRow", "node", "path", "rowDirection"]);
    var rowDirectionClass = rowDirection === 'rtl' ? 'rst__rtl' : null;
    // Construct the scaffold representing the structure of the tree
    var scaffoldBlockCount = lowerSiblingCounts.length;
    var scaffold = [];
    lowerSiblingCounts.forEach(function (lowerSiblingCount, i) {
        var lineClass = '';
        if (lowerSiblingCount >= 1) {
            // At this level in the tree, the nodes had sibling nodes further down
            if (listIndex === 0) {
                // Top-left corner of the tree
                // +-----+
                // |     |
                // |  +--+
                // |  |  |
                // +--+--+
                lineClass =
                    'rst__lineHalfHorizontalRight rst__lineHalfVerticalBottom';
            }
            else if (i === scaffoldBlockCount - 1) {
                // Last scaffold block in the row, right before the row content
                // +--+--+
                // |  |  |
                // |  +--+
                // |  |  |
                // +--+--+
                lineClass =
                    'rst__lineHalfHorizontalRight rst__lineFullVertical';
            }
            else {
                // Simply connecting the line extending down to the next sibling on this level
                // +--+--+
                // |  |  |
                // |  |  |
                // |  |  |
                // +--+--+
                if (treeIndex !== listIndex && i === swapDepth) {
                    lineClass = '';
                }
                else {
                    lineClass = 'rst__lineFullVertical';
                }
            }
        }
        else if (listIndex === 0) {
            // Top-left corner of the tree, but has no siblings
            // +-----+
            // |     |
            // |  +--+
            // |     |
            // +-----+
            lineClass = 'rst__lineHalfHorizontalRight';
        }
        else if (i === scaffoldBlockCount - 1) {
            // The last or only node in this level of the tree
            // +--+--+
            // |  |  |
            // |  +--+
            // |     |
            // +-----+
            lineClass = 'rst__lineHalfVerticalTop rst__lineHalfHorizontalRight';
        }
        scaffold.push(React.createElement("div", { key: "pre_" + (1 + i), style: { width: scaffoldBlockPxWidth }, className: classNames('rst__lineBlock', lineClass, rowDirectionClass) }));
        if (treeIndex !== listIndex && i === swapDepth) {
            var style_1;
            if (rowDirection === 'rtl') {
                style_1 = {
                    width: scaffoldBlockPxWidth,
                    right: scaffoldBlockPxWidth * i
                };
            }
            else {
                // Default ltr
                style_1 = {
                    width: scaffoldBlockPxWidth,
                    left: scaffoldBlockPxWidth * i
                };
            }
            scaffold.push(React.createElement("div", { 
                // eslint-disable-next-line react/no-array-index-key
                key: i, style: style_1, className: classNames('rst__absoluteLineBlock', rowDirectionClass) }));
        }
    });
    var style;
    if (rowDirection === 'rtl') {
        style = { right: scaffoldBlockPxWidth * scaffoldBlockCount };
    }
    else {
        // Default ltr
        style = { left: scaffoldBlockPxWidth * scaffoldBlockCount };
    }
    return connectDropTarget(React.createElement("div", __assign({}, props, { className: classNames('rst__node', rowDirectionClass) }),
        scaffold,
        React.createElement("div", { className: "rst__nodeContent", style: style }, Children.map(children, function (child) {
            return cloneElement(child, {
                isOver: isOver,
                canDrop: canDrop,
                draggedNode: draggedNode
            });
        }))));
};

var theme = {
    nodeContentRenderer: NodeContentRenderer,
    treeNodeRenderer: TreeNodeRenderer
};

var css_248z$15 = ".rst__node:hover .acl-BooleanTreeNode-dot.isRule {\n  background-color: #009ebd; }\n\n.rst__node.ghost {\n  height: 88px;\n  top: -11px;\n  left: -55px; }\n  .rst__node.ghost .rst__lineBlock {\n    width: 80px; }\n    .rst__node.ghost .rst__lineBlock.rst__lineHalfVerticalTop {\n      width: 55px; }\n      .rst__node.ghost .rst__lineBlock.rst__lineHalfVerticalTop:before {\n        background: transparent;\n        border-bottom: 1px dashed rgba(0, 0, 0, 0.3); }\n      .rst__node.ghost .rst__lineBlock.rst__lineHalfVerticalTop:after {\n        background: transparent;\n        border-left: 1px dashed rgba(0, 0, 0, 0.3); }\n  .rst__node.ghost .rst__nodeContent {\n    left: 135px;\n    padding-top: 8px; }\n    .rst__node.ghost .rst__nodeContent .acl-BooleanTreeNode-dot {\n      left: -27.5px;\n      background: #f5eff8; }\n      .rst__node.ghost .rst__nodeContent .acl-BooleanTreeNode-dot.and {\n        background: #e9f3e8; }\n    .rst__node.ghost .rst__nodeContent .acl-BooleanTreeCard-Header {\n      border: 1px dashed rgba(0, 0, 0, 0.3);\n      border-radius: 4px;\n      height: auto;\n      color: #adadad;\n      padding: 16px;\n      margin-top: 10px;\n      min-width: 419px; }\n\n.acl-BooleanTreeNodeWrapper.isActive .acl-BooleanTreeNode-dot {\n  background-color: #009ebd; }\n\n.acl-BooleanTreeNodeWrapper.isActive .acl-BooleanTreeCard--header {\n  background-color: #dff3f7; }\n\n.acl-BooleanTreeNode-dot {\n  appearance: none;\n  position: absolute;\n  top: 50%;\n  transform: translate(-50%, -50%);\n  display: flex;\n  flex: 0 0 18px;\n  align-items: center;\n  justify-content: center;\n  font-size: 12px;\n  line-height: 14px;\n  height: 25px;\n  width: 25px;\n  color: white;\n  border-radius: 20px;\n  background-color: #52a145;\n  z-index: 5; }\n  .acl-BooleanTreeNode-dot.isOperator {\n    height: 16px;\n    width: 16px; }\n  .acl-BooleanTreeNode-dot.or {\n    background-color: #623084; }\n\n.acl-BooleanTreeCard--conjuction {\n  display: none;\n  position: absolute;\n  background-color: white;\n  left: -26px;\n  color: #52a145;\n  text-transform: uppercase;\n  top: 50%;\n  transform: translate(-50%, 200%);\n  z-index: 5; }\n  .acl-BooleanTreeCard--conjuction.display {\n    display: block; }\n  .acl-BooleanTreeCard--conjuction.or {\n    color: #623084; }\n";
styleInject(css_248z$15);

// @ts-nocheck
var BooleanTree = function (_a) {
    var onChange = _a.onChange, treeData = _a.treeData, activeNode = _a.activeNode, _b = _a.defaultText, defaultText = _b === void 0 ? 'Added categories are draggable.' : _b, _c = _a.defaultNodeKey, defaultNodeKey = _c === void 0 ? 'A' : _c, reset = _a.reset;
    var _d = useState(treeData), _treeData = _d[0], set_treeData = _d[1];
    var _e = useState(false), isDragging = _e[0], setIsDragging = _e[1];
    useEffect(function () {
        set_treeData(treeData);
    }, [treeData]);
    var getHeight = function (treeIndex, node) {
        return node.tempHeight;
    };
    var resetData = function () {
        reset();
    };
    var updateData = function (newData) {
        onChange(newData);
    };
    var canDropItem = function (object) {
        if (!object.nextParent || !object.nextParent.isOperator) {
            return false;
        }
        return true;
    };
    var canDragItem = function (object) {
        if (object.isOperator ||
            object.node.isOperator ||
            object.treeIndex === 0 ||
            object.expanded) {
            return false;
        }
        return true;
    };
    var onDrag = function (_a) {
        var dragging = _a.dragging;
        setIsDragging(dragging);
    };
    var ghostNode = (React.createElement("div", { className: "rst__node ghost" },
        React.createElement("div", { className: "rst__lineBlock" }),
        React.createElement("div", { className: "rst__lineBlock rst__lineHalfVerticalTop rst__lineHalfHorizontalRight" }),
        React.createElement("div", { className: "rst__nodeContent" },
            React.createElement("div", { className: classNames('acl-BooleanTreeNode-dot', {
                    and: treeData[0].nodeType === 'AND'
                }) }, defaultNodeKey),
            React.createElement("div", { className: "acl-BooleanTreeCard-Header" }, defaultText))));
    return (React.createElement("div", { className: "acl-BooleanTree", style: { minHeight: 500 } },
        React.createElement(DragAndDrop, null,
            React.createElement(SortableTreeWithoutDndContext, { resetData: resetData, rowHeight: function (_a) {
                    var treeIndex = _a.treeIndex, node = _a.node;
                    return getHeight(treeIndex, node);
                }, isVirtualized: false, treeData: _treeData, theme: theme, generateNodeProps: function (_a) {
                    var node = _a.node, treeIndex = _a.treeIndex, path = _a.path;
                    node.resetData = resetData;
                    node.treeIndex = treeIndex;
                    node.path = path;
                    node.isDragging = isDragging;
                    node.isActive = node.nodeKey === activeNode;
                }, canDrag: function (object) {
                    return canDragItem(object);
                }, canDrop: function (object) {
                    return canDropItem(object);
                }, scaffoldBlockPxWidth: 55, onChange: function (d) { return updateData(d); }, onDragStateChanged: onDrag, canNodeHaveChildren: function (node) {
                    return canDropItem({ nextParent: node });
                } })),
        (!treeData[0].children || treeData[0].children.length === 0) &&
            ghostNode));
};

var css_248z$16 = ".acl-BrowseAndSearchContainer {\n  border-radius: 4px;\n  border: 1px solid rgba(0, 0, 0, 0.3);\n  margin: 0;\n  padding: 0;\n  width: 100%;\n  overflow: hidden; }\n  .acl-BrowseAndSearchContainer .acl-Tabs {\n    display: block; }\n    .acl-BrowseAndSearchContainer .acl-Tabs .tabNav {\n      background-color: #ffffff;\n      border-bottom: 1px solid rgba(0, 0, 0, 0.3);\n      border-top-left-radius: 4px;\n      border-top-right-radius: 4px; }\n      .acl-BrowseAndSearchContainer .acl-Tabs .tabNav .acl-Tab,\n      .acl-BrowseAndSearchContainer .acl-Tabs .tabNav a.acl-Tab,\n      .acl-BrowseAndSearchContainer .acl-Tabs .tabNav a:not([href]).acl-Tab {\n        height: auto;\n        padding: 16px 32px;\n        font-weight: 600;\n        border-bottom-width: 2px; }\n    .acl-BrowseAndSearchContainer .acl-Tabs .tabPane {\n      padding: 0;\n      overflow: hidden; }\n      .acl-BrowseAndSearchContainer .acl-Tabs .tabPane .acl-BrowseAndSearch {\n        border-top-left-radius: 0;\n        border-top-right-radius: 0;\n        margin-top: 0; }\n  .acl-BrowseAndSearchContainer .acl-Button.btnStaticBorderless {\n    border: none; }\n    .acl-BrowseAndSearchContainer .acl-Button.btnStaticBorderless:hover {\n      border: none;\n      box-shadow: none; }\n  .acl-BrowseAndSearchContainer .acl-Button:not(.disabled):active, .acl-BrowseAndSearchContainer .acl-Button:not(.disabled).active {\n    box-shadow: none;\n    background-color: transparent; }\n\n.acl-BrowseAndSearch {\n  background-color: #ffffff;\n  border-radius: 4px; }\n  .acl-BrowseAndSearch .acl-BrowseAndSearch--Toolbar {\n    display: flex;\n    padding: 0; }\n    .acl-BrowseAndSearch .acl-BrowseAndSearch--Toolbar > * {\n      display: block; }\n      .acl-BrowseAndSearch .acl-BrowseAndSearch--Toolbar > *:first-child {\n        flex-grow: 1; }\n    .acl-BrowseAndSearch .acl-BrowseAndSearch--Toolbar .acl-FormControl {\n      padding: 0; }\n      .acl-BrowseAndSearch .acl-BrowseAndSearch--Toolbar .acl-FormControl .acl-Input--table {\n        padding-top: 0px;\n        padding-bottom: 0px;\n        box-shadow: none;\n        border-right: 1px solid rgba(0, 0, 0, 0.3);\n        border-bottom: 1px solid rgba(0, 0, 0, 0.3);\n        margin: 0; }\n        .acl-BrowseAndSearch .acl-BrowseAndSearch--Toolbar .acl-FormControl .acl-Input--table:focus-within {\n          box-shadow: inset 0 4px 2px rgba(0, 0, 0, 0.1); }\n        .acl-BrowseAndSearch .acl-BrowseAndSearch--Toolbar .acl-FormControl .acl-Input--table:hover {\n          box-shadow: inset 0 4px 2px rgba(0, 0, 0, 0.05); }\n    .acl-BrowseAndSearch .acl-BrowseAndSearch--Toolbar .acl-DataFilter-Toolbar .acl-Button {\n      border-radius: 0; }\n    .acl-BrowseAndSearch .acl-BrowseAndSearch--Toolbar .acl-DataFilter-Toolbar .acl-DataFilter--button {\n      padding: 0;\n      border-bottom: 1px solid rgba(0, 0, 0, 0.3); }\n    .acl-BrowseAndSearch .acl-BrowseAndSearch--Toolbar .acl-DataFilter-Toolbar.open .acl-DataFilter--button .acl-Button:not(.disabled),\n    .acl-BrowseAndSearch .acl-BrowseAndSearch--Toolbar .acl-DataFilter-Toolbar.open .acl-DataFilter--button .acl-Button:not(:focus):not(.focus),\n    .acl-BrowseAndSearch .acl-BrowseAndSearch--Toolbar .acl-DataFilter-Toolbar.open .acl-DataFilter--button .acl-Button:not(:active):not(.active),\n    .acl-BrowseAndSearch .acl-BrowseAndSearch--Toolbar .acl-DataFilter-Toolbar.open .acl-DataFilter--button .acl-Button:not(.error) {\n      box-shadow: inset 0 4px 1px 0 rgba(0, 0, 0, 0.05); }\n  .acl-BrowseAndSearch .acl-BrowseAndSearch--Content {\n    display: flex; }\n    .acl-BrowseAndSearch .acl-BrowseAndSearch--Content .acl-BrowseAndSearch--ContentArea {\n      flex-grow: 1;\n      display: flex;\n      flex-direction: column;\n      overflow: auto; }\n      .acl-BrowseAndSearch .acl-BrowseAndSearch--Content .acl-BrowseAndSearch--ContentArea .acl-BrowseAndSearch--Breadcrumbs {\n        display: block;\n        position: relative;\n        border-bottom: 1px solid rgba(0, 0, 0, 0.3); }\n      .acl-BrowseAndSearch .acl-BrowseAndSearch--Content .acl-BrowseAndSearch--ContentArea .acl-BrowseAndSearch--Body {\n        display: block;\n        position: relative; }\n    .acl-BrowseAndSearch .acl-BrowseAndSearch--Content .acl-BrowseAndSearch--FilterContainer {\n      box-shadow: inset 1px 0 0 0 rgba(0, 0, 0, 0.3); }\n      .acl-BrowseAndSearch .acl-BrowseAndSearch--Content .acl-BrowseAndSearch--FilterContainer .acl-DataFilter .acl-DataFilter-Panel {\n        background-color: transparent; }\n        .acl-BrowseAndSearch .acl-BrowseAndSearch--Content .acl-BrowseAndSearch--FilterContainer .acl-DataFilter .acl-DataFilter-Panel .acl-DataFilter--filters {\n          padding: 0; }\n";
styleInject(css_248z$16);

var BrowseAndSearchContainer = /** @class */ (function (_super) {
    __extends(BrowseAndSearchContainer, _super);
    function BrowseAndSearchContainer() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    BrowseAndSearchContainer.prototype.render = function () {
        return (React.createElement("div", { className: "acl-BrowseAndSearchContainer" }, this.props.children));
    };
    return BrowseAndSearchContainer;
}(Component));

var BrowseAndSearch = function (props) {
    return (React.createElement("div", { className: classNames('acl-BrowseAndSearch') },
        React.createElement("div", { className: "acl-BrowseAndSearch--Toolbar" },
            React.createElement("div", { className: "acl-BrowseAndSearch--Toolbar-Input" }, safeInvoke(props.renderInput)),
            React.createElement("div", { className: "acl-BrowseAndSearch--Toolbar-Filter" },
                React.createElement(FilterToolbar, { activeFilterCount: props.activeFilterCount, filterButtonVariant: ButtonVariant.BORDERLESS, onFilterToggle: function (open) {
                        safeInvoke(props.onFilterToggle, [open]);
                    } }))),
        React.createElement("div", { className: "acl-BrowseAndSearch--Content" },
            React.createElement("div", { className: "acl-BrowseAndSearch--ContentArea" },
                typeof props.renderBreadcrumbs === 'function' && (React.createElement("div", { className: "acl-BrowseAndSearch--Breadcrumbs" }, safeInvoke(props.renderBreadcrumbs))),
                React.createElement("div", { className: "acl-BrowseAndSearch--Body" }, props.children)),
            React.createElement("div", { className: "acl-BrowseAndSearch--FilterContainer" }, safeInvoke(props.renderDataFilter)))));
};

export { Accordion, AmobeeLogo, BannerMessage, BannerMessageVariant, BarChart, BarLineChart, BasicMenu, BooleanTree, BooleanTreeCard, BooleanTreeOperator, BooleanTreeSummary, Breadcrumb, Breadcrumbs, BrowseAndSearch, BrowseAndSearchContainer, BulkOptions, BulletChart, Button, ButtonFormat, ButtonVariant, CallToAction, Card, CardHeader, CardSize, CellFactory, CellType, Checkbox, Chip, ChipGroup, CollapsibleNav, CollapsibleNavItem, ColorTheme, Dashboard, DashboardContainer, DashboardSectionDivider, DashboardToolbar, DataFilter, DataTable, DateRangePicker, DateRangePickerButton, DateRangePickerReducer, DateRangePresets, DateRangeShortcuts, Datepicker, DatepickerButton, DatepickerFooter, DatepickerToolbar, DefaultCell, Dimmer, DistributionBarChart, DragCell, DropdownButton, FilterSelect, FilterToolbar, Flyout, FlyoutBody, FlyoutFooter, FlyoutHeader, FlyoutWidthSize, FormControl, FormGroup, FormNav, FormNavItem, FormRepeater, FormRepeaterRow, FormSection, GlobalHeader, HeaderCell, Icon, IconNames, IconSize, InlineChipContainer, Input, InputVariant, LineChart, Link, LinkCell, ListSelector, MenuCategory, MenuItem, Modal, ModalBody, ModalFooter, ModalHeader, ModalWidth, MultiAxisChart, MultiSelectDropdownButton, Navbar, NavbarCenter, NavbarRight, Option, PageHeader, PageHeaderSection, Pagination, Popover, Portal, ProductSelector, Radio, RadioGroup, RowActionCell, Select, SelectedItemsList, SkeletonLoader, SlideoutMenu, SlideoutMenuItem, Slider, Spinner, Step, StepperButton, Steps, StepsOrientation, StepsType, SubHeader, Switch, SwitchCell, Tab, TableToolbar, TableToolbarOptions, Tabs, TextBreadcrumb, TextBreadcrumbs, TextCell, Timepicker, ToggleButton, Tooltip, TreeSelectCell, Typeahead, alignTabs, usePagination };
